import './styles/main.css';
import { SyncClient, fetchConfirmedJson, type SyncState, type SyncResult } from './sync-client.js';
import { ConferenceUI } from './conference/conference-ui.js';
import { acquireUserCameraStream } from './conference/livekit-client.js';
import { VORBELLO_LOGO } from './assets/logo.js';
import { createLiveKitToken, getStableClientDeviceId } from './conference/token-helper.js';
import { APP_VERSION, APP_BUILD_DATE, APP_BUILD_TAG } from './version.js';
import { captureAcademyInvitation, restoreAcademyInvitation, hasAcademyInvitation, getAcademyAccess, loadAcademyInvitation } from './conference/academy-invitation.js';
import { createPpAttendanceCsv } from './conference/academy-csv.js';

// Active tab types
type Tab = 'rooms' | 'meetings' | 'recordings' | 'attendance' | 'invitations' | 'members' | 'audit';

interface RoomItem {
  id: string;
  name: string;
  slug: string;
  description: string;
  visibility: string;
  type?: 'permanent' | 'meeting';
  maxParticipants: number;
  activeParticipants: number;
  defaultVideoEnabled: boolean;
  defaultAudioEnabled?: boolean;
  muteOnEntry?: boolean;
  cameraOffOnEntry?: boolean;
  waitingRoomEnabled?: boolean;
  waitForHost?: boolean;
  allowJoinBeforeHost?: boolean;
  allowGuestInvites?: boolean;
  allowScreenShare?: 'hosts_only' | 'everyone';
  autoRecord?: boolean;
  autoRecordFormat?: 'video-fhd' | 'video-hd' | 'audio';
  autoRecordLayout?: 'composite' | 'screen_only' | 'cameras_only';
  autoRecordContainer?: 'webm' | 'mp4';
  autoRecordNotify?: boolean;
  attendanceTracking?: boolean;
  pinCode?: string;
  creatorId?: string;
  creatorName?: string;
  creatorDeviceId?: string;
  createdAt: string;
}

interface MeetingItem {
  id: string;
  roomId: string;
  roomName: string;
  title: string;
  agenda?: string;
  startsAt: string;
  endsAt: string | null;
  status: string;
  joinPolicy: string;
  creatorName: string;
  creatorId?: string;
  creatorDeviceId?: string;
  pinCode?: string;
  timezone?: string;
  autoRecord?: boolean;
  autoRecordFormat?: 'video-fhd' | 'video-hd' | 'audio';
  autoRecordLayout?: 'composite' | 'screen_only' | 'cameras_only';
  autoRecordContainer?: 'webm' | 'mp4';
  autoRecordNotify?: boolean;
  muteOnEntry?: boolean;
  cameraOffOnEntry?: boolean;
  waitingRoomEnabled?: boolean;
  waitForHost?: boolean;
  allowJoinBeforeHost?: boolean;
  allowGuestInvites?: boolean;
  allowScreenShare?: 'hosts_only' | 'everyone';
  attendanceTracking?: boolean;
}

interface RecordingItem {
  id: string;
  roomName: string;
  roomSlug: string;
  meetingTitle?: string;
  createdAt: string;
  duration: string;
  durationSeconds?: number;
  fileSize: string;
  format: 'video-hd' | 'video-fhd' | 'audio';
  recordedBy: string;
  fileName?: string;
  filePath?: string;
  downloadUrl?: string;
  storageType?: 'disk' | 'download' | 'server';
}

interface AttendanceRecord {
  academySessionId?: number;
  memberNumber?: string;
  joinedAtUtc?: string;
  leftAtUtc?: string;
  sessionStartsAtUtc?: string;
  sessionEndsAtUtc?: string;
  id: string;
  meetingTitle: string;
  roomSlug: string;
  participantName: string;
  participantEmail?: string;
  organization: string;
  role: 'ADMIN' | 'MODERATOR' | 'MEMBER' | 'GUEST';
  joinedAt: string;
  leftAt: string | null;
  durationFormatted: string;
  status: 'PRESENT' | 'LATE' | 'LEFT';
  quorumVerified: boolean;
}

interface MeetingSession {
  id: string;
  sessionId?: string;
  meetingTitle: string;
  roomSlug: string;
  dateStr: string;
  dateKey?: string;
  displayDate: string;
  startTime: string;
  endTime: string;
  isLive: boolean;
  totalParticipants: number;
  uniqueParticipants?: number;
  presentCount?: number;
  activeCount: number;
  durationFormatted: string;
  records: AttendanceRecord[];
  syncStatus?: 'synced' | 'pending' | 'not_synced';
  isSynced?: boolean;
  lastSyncedAt?: string;
}

interface WebsiteSyncConfig {
  endpointUrl: string;
  apiUrl?: string;
  apiKey: string;
  autoSync: boolean;
  lastSyncTime: string | null;
}


interface InvitationItem {
  id: string;
  roomId: string;
  roomName: string;
  email: string | null;
  token: string;
  expiresAt: string;
  status: string;
  creatorName: string;
}

interface MemberItem {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  joinedAt: string;
  hasHostLicense: boolean;
}

interface AuditLogItem {
  id: string;
  action: string;
  actorName: string;
  targetType: string;
  targetId: string;
  ipHash: string;
  createdAt: string;
  metadata: string;
}

interface AdminAccount {
  id: string;
  name: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'CLIENT_ADMIN' | 'MODERATOR' | 'MEMBER';
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
}

interface IssuedLicense {
  id: string;
  licenseKey: string;
  planName: string;
  tier: 'standard' | 'pro' | 'enterprise' | 'starter' | 'custom';
  clientName: string;
  maxHosts: number;
  maxParticipantsPerRoom: number;
  durationMonths: number;
  issuedAt: string;
  expiresAt: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED';
  priceTag: string;
}

// Default Room Templates
const DEFAULT_PROD_ROOMS: RoomItem[] = [
  {
    id: 'prod-room-01',
    name: 'Sală Conferință Live',
    slug: 'sala-vps-live',
    description: 'Spațiu de conferință audio-video securizat de înaltă definiție (deschis 24/7).',
    visibility: 'internal',
    type: 'permanent',
    maxParticipants: 100,
    activeParticipants: 0,
    defaultVideoEnabled: true,
    muteOnEntry: false,
    cameraOffOnEntry: false,
    waitingRoomEnabled: false,
    allowGuestInvites: false,
    attendanceTracking: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'prod-room-02',
    name: 'Comitet Executiv & Management',
    slug: 'comitet-executiv-vps',
    description: 'Canal securizat privat pentru întâlniri de conducere (acces cu aprobare).',
    visibility: 'private',
    type: 'permanent',
    maxParticipants: 25,
    activeParticipants: 0,
    defaultVideoEnabled: true,
    muteOnEntry: true,
    cameraOffOnEntry: false,
    waitingRoomEnabled: true,
    allowGuestInvites: false,
    attendanceTracking: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'prod-room-03',
    name: 'Reuniune Birou Permanent & Cvorum Partid',
    slug: 'sedinta-partid-live',
    description: 'Ședință oficială de partid cu prezență automată statutară, sală de așteptare și cvorum garantat.',
    visibility: 'internal',
    type: 'meeting',
    maxParticipants: 100,
    activeParticipants: 0,
    defaultVideoEnabled: true,
    muteOnEntry: true,
    cameraOffOnEntry: false,
    waitingRoomEnabled: true,
    allowGuestInvites: false,
    attendanceTracking: true,
    createdAt: new Date().toISOString(),
  },
];

const DEFAULT_RECORDINGS: RecordingItem[] = [
  {
    id: 'rec-01',
    roomName: 'Sală Conferință Live',
    roomSlug: 'sala-vps-live',
    meetingTitle: 'Ședință Birou Politic & Organizare Teritorială',
    createdAt: '2026-10-20 18:30',
    duration: '00:48:22',
    durationSeconds: 2902,
    fileSize: '214 MB',
    format: 'video-fhd',
    recordedBy: 'Danny Master Admin',
    fileName: 'Vorbello_sala-vps-live_1080p_2026-10-20.webm',
  },
  {
    id: 'rec-02',
    roomName: 'Comitet Executiv & Management',
    roomSlug: 'comitet-executiv-vps',
    meetingTitle: 'Reuniune Coordonare Filiale Teritoriale',
    createdAt: '2026-10-18 16:00',
    duration: '01:12:05',
    durationSeconds: 4325,
    fileSize: '348 MB',
    format: 'video-hd',
    recordedBy: 'Danny Master Admin',
    fileName: 'Vorbello_comitet-executiv-vps_720p_2026-10-18.webm',
  },
  {
    id: 'rec-03',
    roomName: 'vorbello',
    roomSlug: 'vorbello',
    meetingTitle: 'Dezbateri Strategie & Consultare Membri',
    createdAt: '2026-10-15 11:15',
    duration: '00:34:10',
    durationSeconds: 2050,
    fileSize: '38 MB',
    format: 'audio',
    recordedBy: 'Danny Master Admin',
    fileName: 'Vorbello_vorbello_Audio_2026-10-15.webm',
  },
];

const DEFAULT_ATTENDANCE_RECORDS: AttendanceRecord[] = [
  // Sesiunea 1: Ședință Statutară Birou Permanent (sala-vps-live) - 21 Oct 2026
  {
    id: 'att-01',
    meetingTitle: 'Ședință Statutară Birou Permanent',
    roomSlug: 'sala-vps-live',
    participantName: 'Danny Master Admin',
    participantEmail: 'dannyopr@gmail.com',
    organization: 'Organizația Centrală',
    role: 'ADMIN',
    joinedAt: '2026-10-21 14:00:12',
    leftAt: null,
    durationFormatted: 'Conectat (Activ)',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-02',
    meetingTitle: 'Ședință Statutară Birou Permanent',
    roomSlug: 'sala-vps-live',
    participantName: 'Elena Popescu (Președinte Filială)',
    participantEmail: 'elena.popescu@partid.local',
    organization: 'Filiala Cluj',
    role: 'MODERATOR',
    joinedAt: '2026-10-21 14:02:45',
    leftAt: null,
    durationFormatted: 'Conectat (Activ)',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-03',
    meetingTitle: 'Ședință Statutară Birou Permanent',
    roomSlug: 'sala-vps-live',
    participantName: 'Andrei Ionescu (Secretar General)',
    participantEmail: 'andrei.ionescu@partid.local',
    organization: 'Filiala Iași',
    role: 'MODERATOR',
    joinedAt: '2026-10-21 14:04:10',
    leftAt: null,
    durationFormatted: 'Conectat (Activ)',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-04',
    meetingTitle: 'Ședință Statutară Birou Permanent',
    roomSlug: 'sala-vps-live',
    participantName: 'Mihai Vasilescu (Delegat)',
    participantEmail: 'm.vasilescu@partid.local',
    organization: 'Filiala Timiș',
    role: 'MEMBER',
    joinedAt: '2026-10-21 14:18:30',
    leftAt: null,
    durationFormatted: 'Conectat (Activ)',
    status: 'LATE',
    quorumVerified: true,
  },

  // Sesiunea 2: Reuniune Coordonare Filiale Teritoriale (comitet-executiv-vps) - 18 Oct 2026
  {
    id: 'att-05',
    meetingTitle: 'Reuniune Coordonare Filiale Teritoriale',
    roomSlug: 'comitet-executiv-vps',
    participantName: 'Cristian Matei (Membru)',
    participantEmail: 'cristian.matei@partid.local',
    organization: 'Filiala Brașov',
    role: 'MEMBER',
    joinedAt: '2026-10-18 16:05:00',
    leftAt: '2026-10-18 17:15:00',
    durationFormatted: '1h 10m',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-06',
    meetingTitle: 'Reuniune Coordonare Filiale Teritoriale',
    roomSlug: 'comitet-executiv-vps',
    participantName: 'Simona Radu (Coordonator Regional)',
    participantEmail: 'simona.radu@partid.local',
    organization: 'Filiala Sibiu',
    role: 'MODERATOR',
    joinedAt: '2026-10-18 16:00:15',
    leftAt: '2026-10-18 17:15:00',
    durationFormatted: '1h 15m',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-07',
    meetingTitle: 'Reuniune Coordonare Filiale Teritoriale',
    roomSlug: 'comitet-executiv-vps',
    participantName: 'Danny Master Admin',
    participantEmail: 'dannyopr@gmail.com',
    organization: 'Organizația Centrală',
    role: 'ADMIN',
    joinedAt: '2026-10-18 15:58:00',
    leftAt: '2026-10-18 17:15:00',
    durationFormatted: '1h 17m',
    status: 'PRESENT',
    quorumVerified: true,
  },

  // Sesiunea 3: Dezbateri Strategie & Consultare Membri (vorbello) - 15 Oct 2026
  {
    id: 'att-08',
    meetingTitle: 'Dezbateri Strategie & Consultare Membri',
    roomSlug: 'vorbello',
    participantName: 'Adrian Popa (Delegat)',
    participantEmail: 'adrian.popa@partid.local',
    organization: 'Filiala Constanța',
    role: 'MEMBER',
    joinedAt: '2026-10-15 11:15:00',
    leftAt: '2026-10-15 11:55:00',
    durationFormatted: '40 min',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-09',
    meetingTitle: 'Dezbateri Strategie & Consultare Membri',
    roomSlug: 'vorbello',
    participantName: 'Maria Dumitrescu (Membru)',
    participantEmail: 'maria.dumitrescu@partid.local',
    organization: 'Filiala Argeș',
    role: 'MEMBER',
    joinedAt: '2026-10-15 11:20:00',
    leftAt: '2026-10-15 11:55:00',
    durationFormatted: '35 min',
    status: 'PRESENT',
    quorumVerified: true,
  },
  {
    id: 'att-10',
    meetingTitle: 'Dezbateri Strategie & Consultare Membri',
    roomSlug: 'vorbello',
    participantName: 'Danny Master Admin',
    participantEmail: 'dannyopr@gmail.com',
    organization: 'Organizația Centrală',
    role: 'ADMIN',
    joinedAt: '2026-10-15 11:10:00',
    leftAt: '2026-10-15 11:55:00',
    durationFormatted: '45 min',
    status: 'PRESENT',
    quorumVerified: true,
  },
];

const DEFAULT_DEMO_ROOMS: RoomItem[] = [
  {
    id: 'room-01',
    name: 'Camera Principală — All Hands',
    slug: 'all-hands',
    description: 'Cameră generală pentru întâlniri săptămânale și anunțuri de echipă.',
    visibility: 'internal',
    maxParticipants: 100,
    activeParticipants: 8,
    defaultVideoEnabled: true,
    createdAt: '2026-09-24T10:00:00Z',
  },
  {
    id: 'room-02',
    name: 'Comitetul Director (Privat)',
    slug: 'comitet-director',
    description: 'Sesiuni confidențiale de strategie și decizie administrativă.',
    visibility: 'private',
    maxParticipants: 15,
    activeParticipants: 0,
    defaultVideoEnabled: true,
    createdAt: '2026-09-25T14:30:00Z',
  },
  {
    id: 'room-03',
    name: 'Dezvoltare Tehnică & Proiecte',
    slug: 'dev-projects',
    description: 'Discuții tehnice, screen sharing și revizuire de arhitectură.',
    visibility: 'internal',
    maxParticipants: 50,
    activeParticipants: 4,
    defaultVideoEnabled: true,
    createdAt: '2026-09-26T08:15:00Z',
  },
];

const DEFAULT_PROD_MEETINGS: MeetingItem[] = [];

const DEFAULT_DEMO_MEETINGS: MeetingItem[] = [
  {
    id: 'meet-01',
    roomId: 'room-01',
    roomName: 'Camera Principală — All Hands',
    title: 'Ședință Săptămânală de Coordonare',
    startsAt: '2026-09-26T10:00:00Z',
    endsAt: '2026-09-26T11:00:00Z',
    status: 'live',
    joinPolicy: 'org_members',
    creatorName: localStorage.getItem('vorbello_user_name') || 'Administrator Master',
  },
  {
    id: 'meet-02',
    roomId: 'room-02',
    roomName: 'Comitetul Director (Privat)',
    title: 'Revizuire Buget Trimestrul IV',
    startsAt: '2026-09-27T16:00:00Z',
    endsAt: '2026-09-27T17:30:00Z',
    status: 'scheduled',
    joinPolicy: 'invite_only',
    creatorName: 'Elena Administrator',
  },
];

const DEFAULT_DEMO_INVITATIONS: InvitationItem[] = [
  {
    id: 'inv-01',
    roomId: 'room-02',
    roomName: 'Comitetul Director (Privat)',
    email: 'consultant@extern.ro',
    token: 'f87b8d8e9a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d',
    expiresAt: '2026-09-28T23:59:59Z',
    status: 'Activ',
    creatorName: localStorage.getItem('vorbello_user_name') || 'Administrator Master',
  },
];

const DEFAULT_MASTER_EMAIL = 'dannyopr@gmail.com';
const DEFAULT_MASTER_PASSWORD = 'Testoso2026!!!???';

const DEFAULT_ADMIN_ACCOUNTS: AdminAccount[] = [
  {
    id: 'admin-master',
    name: 'Danny Master Admin',
    email: DEFAULT_MASTER_EMAIL,
    password: DEFAULT_MASTER_PASSWORD,
    role: 'ADMIN',
    status: 'ACTIVE',
    createdAt: '2026-10-19',
  }
];

const DEFAULT_ISSUED_LICENSES: IssuedLicense[] = [
  {
    id: 'lic-01',
    licenseKey: 'VRB-ENT-2026-8F4B-92A1',
    planName: 'Vorbello Enterprise Dedicated',
    tier: 'enterprise',
    clientName: 'Organizația Principală Vorbello',
    maxHosts: 10,
    maxParticipantsPerRoom: 250,
    durationMonths: 12,
    issuedAt: '2026-10-01',
    expiresAt: '2027-12-31',
    status: 'ACTIVE',
    priceTag: 'Abonament Enterprise (VPS & Cluster Dedicat)',
  },
  {
    id: 'lic-02',
    licenseKey: 'VRB-PRO-2026-4C19-33D7',
    planName: 'Vorbello Pro Business',
    tier: 'pro',
    clientName: 'Departament Management & Vânzări',
    maxHosts: 5,
    maxParticipantsPerRoom: 100,
    durationMonths: 1,
    issuedAt: '2026-10-15',
    expiresAt: '2027-10-15',
    status: 'ACTIVE',
    priceTag: 'Plan Pro (9,99€/lună • 100 Part.)',
  },
  {
    id: 'lic-03',
    licenseKey: 'VRB-STD-2026-7A12-55E8',
    planName: 'Vorbello Standard',
    tier: 'standard',
    clientName: 'Echipa Suport & Clienți',
    maxHosts: 1,
    maxParticipantsPerRoom: 50,
    durationMonths: 12,
    issuedAt: '2026-10-20',
    expiresAt: '2027-10-20',
    status: 'ACTIVE',
    priceTag: 'Plan Standard (5,99€/lună • 50 Part.)',
  }
];

function generateLicenseKey(tier: string = 'pro'): string {
  const prefix = tier === 'enterprise' ? 'VRB-ENT' : tier === 'standard' ? 'VRB-STD' : tier === 'starter' ? 'VRB-STR' : tier === 'custom' ? 'VRB-CST' : 'VRB-PRO';
  const year = new Date().getFullYear();
  const randPart1 = Math.random().toString(36).substring(2, 6).toUpperCase();
  const randPart2 = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${year}-${randPart1}-${randPart2}`;
}

const DEFAULT_PROD_MEMBERS: MemberItem[] = [
  {
    id: 'usr-admin-01',
    name: localStorage.getItem('vorbello_user_name') || 'Danny Master Admin',
    email: localStorage.getItem('vorbello_user_email') || 'dannyopr@gmail.com',
    role: 'ADMIN',
    status: 'ACTIVE',
    joinedAt: new Date().toISOString().split('T')[0],
    hasHostLicense: true,
  },
];

const DEFAULT_DEMO_MEMBERS: MemberItem[] = [
  { id: 'usr-01', name: 'Danny Master Admin', email: 'dannyopr@gmail.com', role: 'ADMIN', status: 'ACTIVE', joinedAt: '2026-09-01', hasHostLicense: true },
  { id: 'usr-02', name: 'Elena Popescu', email: 'elena@vorbello.local', role: 'MODERATOR', status: 'ACTIVE', joinedAt: '2026-09-05', hasHostLicense: true },
  { id: 'usr-03', name: 'Andrei Ionescu', email: 'andrei@vorbello.local', role: 'MODERATOR', status: 'ACTIVE', joinedAt: '2026-09-10', hasHostLicense: true },
  { id: 'usr-04', name: 'Cristian Matei', email: 'cristian@vorbello.local', role: 'MEMBER', status: 'BLOCKED', joinedAt: '2026-09-12', hasHostLicense: false },
];

function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn(`[Vorbello] Failed to load ${key} from storage:`, e);
  }
  return defaultValue;
}

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Bucharest', label: 'București, Chișinău (UTC+2 / România)' },
  { value: 'Europe/Rome', label: 'Roma, Berlin, Paris (UTC+1 / Europa Centrală)' },
  { value: 'Europe/London', label: 'Londra, Dublin (GMT / UTC+0)' },
  { value: 'Europe/Athens', label: 'Atena, Nicosia (UTC+2)' },
  { value: 'Europe/Madrid', label: 'Madrid, Barcelona (UTC+1)' },
  { value: 'America/New_York', label: 'New York, Miami (UTC-5 / EST)' },
  { value: 'America/Chicago', label: 'Chicago, Dallas (UTC-6 / CST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles, San Francisco (UTC-8 / PST)' },
  { value: 'Asia/Dubai', label: 'Dubai, Abu Dhabi (UTC+4 / GST)' },
  { value: 'UTC', label: 'Timp Universal Coordonat (UTC)' },
];

function getTimezoneLabel(tz: string): string {
  if (!tz) return 'București (UTC+2)';
  if (tz.includes('Bucharest') || tz.includes('Chisinau')) return 'București (UTC+2)';
  if (tz.includes('Rome') || tz.includes('Berlin') || tz.includes('Paris')) return 'Roma (UTC+1)';
  if (tz.includes('London')) return 'Londra (GMT)';
  if (tz.includes('Athens')) return 'Atena (UTC+2)';
  if (tz.includes('Madrid')) return 'Madrid (UTC+1)';
  if (tz.includes('New_York')) return 'New York (UTC-5)';
  if (tz.includes('Chicago')) return 'Chicago (UTC-6)';
  if (tz.includes('Los_Angeles')) return 'Los Angeles (UTC-8)';
  if (tz.includes('Dubai')) return 'Dubai (UTC+4)';
  return 'UTC';
}

function getDateKeyInTimezone(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(d);
    const y = parts.find(p => p.type === 'year')?.value;
    const m = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    return `${y}-${m}-${day}`;
  } catch {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}

function formatTimeInTimezone(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('ro-RO', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return d.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  }
}

function formatDateTimeInTimezone(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('ro-RO', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return d.toLocaleString('ro-RO');
  }
}

function isMeetingPast(m: MeetingItem, tz: string = state?.selectedTimezone || 'Europe/Bucharest'): boolean {
  if (m.status === 'live') return false;
  if (m.status === 'ended' || m.status === 'cancelled') return true;
  try {
    const now = new Date();
    if (m.endsAt) {
      const endMs = new Date(m.endsAt).getTime();
      if (!isNaN(endMs)) return endMs < now.getTime();
    }
    const startMs = new Date(m.startsAt).getTime();
    if (isNaN(startMs)) return false;
    const activeTz = tz || (state ? state.selectedTimezone : 'Europe/Bucharest');
    const todayIso = getDateKeyInTimezone(now, activeTz);
    const mDateKey = getDateKeyInTimezone(m.startsAt, m.timezone || activeTz);
    // If scheduled strictly on a day before today in the active timezone -> past
    if (mDateKey && todayIso && mDateKey < todayIso) return true;
    // If scheduled today, allow 2 hours buffer after startsAt before considering it past
    if (mDateKey === todayIso) {
      return (startMs + 2 * 3600 * 1000) < now.getTime();
    }
    return false;
  } catch {
    return false;
  }
}

// Vorbello Application State
const state = {
  selectedTimezone: localStorage.getItem('vorbello_timezone') || (() => {
    try {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return detected || 'Europe/Bucharest';
    } catch {
      return 'Europe/Bucharest';
    }
  })(),
  masterAdminEmail: DEFAULT_MASTER_EMAIL,
  masterAdminPassword: localStorage.getItem('vorbello_admin_master_password') || DEFAULT_MASTER_PASSWORD,
  issuedLicenses: (() => {
    const list = loadFromStorage<IssuedLicense[]>('vorbello_issued_licenses', DEFAULT_ISSUED_LICENSES);
    let changed = false;
    list.forEach(lic => {
      if (lic.tier === 'enterprise') {
        if (lic.maxParticipantsPerRoom !== 250) {
          lic.maxParticipantsPerRoom = 250;
          changed = true;
        }
        if (!lic.priceTag || lic.priceTag.includes('49€') || lic.priceTag.includes('79€') || lic.priceTag === 'Abonament Anual Enterprise') {
          lic.priceTag = 'Abonament Enterprise (VPS & Cluster Dedicat)';
          changed = true;
        }
      } else if (lic.tier === 'pro') {
        if (!lic.priceTag || lic.priceTag.includes('49€') || lic.priceTag.includes('79€') || lic.priceTag.includes('Abonament Lunar Pro')) {
          lic.priceTag = 'Plan Pro (9,99€/lună • 100 Part.)';
          changed = true;
        }
      } else if (lic.tier === 'standard') {
        if (!lic.priceTag || lic.priceTag.includes('49€') || lic.priceTag.includes('79€')) {
          lic.priceTag = 'Plan Standard (5,99€/lună • 50 Part.)';
          changed = true;
        }
      }
    });
    if (changed) {
      try { localStorage.setItem('vorbello_issued_licenses', JSON.stringify(list)); } catch (_) {}
    }
    return list;
  })(),
  adminAccounts: loadFromStorage<AdminAccount[]>('vorbello_admin_accounts', DEFAULT_ADMIN_ACCOUNTS),
  isAdminLoggedIn: localStorage.getItem('vorbello_admin_authenticated') === 'true',
  adminAuthError: null as string | null,
  appRoleView: (() => {
    const isAuthed = localStorage.getItem('vorbello_admin_authenticated') === 'true';
    const roleView = localStorage.getItem('vorbello_app_role_view') || 'user';
    if (roleView === 'admin' && !isAuthed) {
      return 'user';
    }
    return roleView as 'user' | 'admin';
  })(),
  userJoinRoomInput: '',
  userJoinNameInput: (() => {
    const isAuthed = localStorage.getItem('vorbello_admin_authenticated') === 'true';
    const stored = localStorage.getItem('vorbello_user_name');
    if (isAuthed) return stored || 'Danny Master Admin';
    if (!stored || stored === 'Danny Master Admin' || stored === 'Administrator Master') return '';
    return stored;
  })(),
  userJoinCamEnabled: true,
  userJoinMicEnabled: true,
  appMode: (localStorage.getItem('vorbello_app_mode') || 'production') as 'demo' | 'production',
  vpsModalTab: 'config' as 'config' | 'guide',
  vpsConfig: {
    apiUrl: (() => {
      let url = localStorage.getItem('vorbello_vps_api');
      if (!url || url.includes('domeniul-tau.ro') || url.includes(':3000') || url === 'https://live.vorbello.eu' || url === '217.217.233.20' || url.startsWith('http://')) {
        url = 'https://217.217.233.20';
        localStorage.setItem('vorbello_vps_api', url);
      }
      return url;
    })(),
    livekitUrl: (() => {
      let url = localStorage.getItem('vorbello_vps_livekit');
      if (!url || url.includes('app.vorbello.eu') || url.includes('domeniul-tau.ro') || url.includes('217.217.233.20') || url.includes(':7880') || !url.startsWith('wss://')) {
        url = 'wss://live.vorbello.eu';
        localStorage.setItem('vorbello_vps_livekit', url);
      }
      return url;
    })(),
    serverName: localStorage.getItem('vorbello_vps_name') || 'Server VPS Vorbello (Ubuntu 24.04 Docker)',
    status: (localStorage.getItem('vorbello_vps_status') || 'connected') as 'connected' | 'offline' | 'checking' | 'unknown',
    pingMs: 42 as number | null,
    statusText: 'Server VPS Operațional • LiveKit SFU activ (SSL WSS Securizat)',
  },
  prodRooms: loadFromStorage<RoomItem[]>('vorbello_prod_rooms', DEFAULT_PROD_ROOMS),
  prodMeetings: loadFromStorage<MeetingItem[]>('vorbello_prod_meetings', DEFAULT_PROD_MEETINGS),
  prodInvitations: loadFromStorage<InvitationItem[]>('vorbello_prod_invitations', []),
  prodMembers: loadFromStorage<MemberItem[]>('vorbello_prod_members', DEFAULT_PROD_MEMBERS),

  rooms: loadFromStorage<RoomItem[]>('vorbello_demo_rooms', DEFAULT_DEMO_ROOMS),
  meetings: loadFromStorage<MeetingItem[]>('vorbello_demo_meetings', DEFAULT_DEMO_MEETINGS),
  invitations: loadFromStorage<InvitationItem[]>('vorbello_demo_invitations', DEFAULT_DEMO_INVITATIONS),
  members: loadFromStorage<MemberItem[]>('vorbello_demo_members', DEFAULT_DEMO_MEMBERS),

  recordings: loadFromStorage<RecordingItem[]>('vorbello_recordings', DEFAULT_RECORDINGS),
  attendanceRecords: (() => {
    const isDemo = localStorage.getItem('vorbello_app_mode') === 'demo';
    return loadFromStorage<AttendanceRecord[]>(attendanceStorageKey(), isDemo ? DEFAULT_ATTENDANCE_RECORDS : []);
  })(),
  selectedRecordingForPlayer: null as RecordingItem | null,
  selectedRoomAttendanceFilter: '' as string,
  expandedAttendanceSessions: loadFromStorage<string[]>('vorbello_expanded_sessions', ['sess-sala-vps-live-2026-10-21']),
  attendanceSearchQuery: '' as string,
  attendanceStatusFilter: 'all' as 'all' | 'live' | 'completed',
  websiteSyncConfig: {
    endpointUrl: localStorage.getItem('vorbello_sync_endpoint') || 'https://vorbello.eu/wp-json/vorbello/v1/attendance',
    apiUrl: localStorage.getItem('vorbello_sync_endpoint') || 'https://vorbello.eu/wp-json/vorbello/v1/attendance',
    apiKey: localStorage.getItem('vorbello_sync_api_key') || '',
    autoSync: localStorage.getItem('vorbello_sync_auto') !== 'false',
    lastSyncTime: localStorage.getItem('vorbello_sync_last_time') || null,
  } as WebsiteSyncConfig,

  currentTab: 'rooms' as Tab,
  // Pagination State (Max 10 per page per user request)
  recordingsPage: 1,
  auditPage: 1,
  roomsPage: 1,
  meetingsPage: 1,
  attendancePage: 1,
  meetingSubTab: (localStorage.getItem('vorbello_meeting_subtab') as 'upcoming' | 'past' | 'all') || 'upcoming',
  meetingCalendarWeekOffset: 0 as number,
  meetingDateFilter: null as string | null,
  activeModal: null as string | null,
  pendingConfirm: null as {
    title: string;
    description: string;
    icon?: string;
    confirmText?: string;
    danger?: boolean;
    onConfirm: () => void | Promise<void>;
  } | null,
  selectedRoomForAction: null as RoomItem | null,
  selectedMeetingForInvite: null as MeetingItem | null,
  selectedMeetingForEdit: null as MeetingItem | null,
  pendingPinVerification: null as {
    roomSlug: string;
    roomName: string;
    userName: string;
    correctPin: string;
    camEnabled?: boolean;
    micEnabled?: boolean;
  } | null,
  userJoinError: null as string | null,
  pinError: null as string | null,
  toastMessage: null as string | null,

  user: {
    id: (() => {
      let uid = localStorage.getItem('vorbello_user_id');
      if (!uid) {
        uid = `usr-${Math.random().toString(36).substring(2, 8)}`;
        localStorage.setItem('vorbello_user_id', uid);
      }
      return uid;
    })(),
    name: (() => {
      const isAuthed = localStorage.getItem('vorbello_admin_authenticated') === 'true';
      const stored = localStorage.getItem('vorbello_user_name');
      if (isAuthed) {
        return stored || 'Danny Master Admin';
      }
      if (!stored || stored === 'Danny Master Admin' || stored === 'Administrator Master') {
        return '';
      }
      return stored;
    })(),
    email: (() => {
      const isAuthed = localStorage.getItem('vorbello_admin_authenticated') === 'true';
      const stored = localStorage.getItem('vorbello_user_email');
      if (isAuthed) {
        return stored || 'dannyopr@gmail.com';
      }
      if (!stored || stored === 'dannyopr@gmail.com' || stored.includes('@vorbello.') || stored === 'admin') {
        return '';
      }
      return stored;
    })(),
    role: (() => {
      const isAuthed = localStorage.getItem('vorbello_admin_authenticated') === 'true';
      const storedRole = localStorage.getItem('vorbello_user_role');
      if (isAuthed) {
        return (storedRole as 'ADMIN' | 'CLIENT_ADMIN' | 'MODERATOR' | 'MEMBER') || 'ADMIN';
      }
      return (storedRole as 'ADMIN' | 'CLIENT_ADMIN' | 'MODERATOR' | 'MEMBER') || 'MEMBER';
    })(),
    organizationName: localStorage.getItem('vorbello_user_org') || 'Organizația Principală Vorbello',
    organizationId: 'org-main-01',
  },

  clientViewActive: localStorage.getItem('vorbello_client_view_active') === 'true',

  organizationLicense: (() => {
    try {
      const stored = localStorage.getItem('vorbello_org_license');
      if (stored) return JSON.parse(stored);
    } catch (_) {}
    return {
      planName: 'Vorbello Pro Business',
      licenseKey: 'VRB-PRO-2026-4C19-33D7',
      maxHosts: 5,
      maxParticipantsPerRoom: 100,
      creationPolicy: 'admins_hosts' as 'admins_only' | 'admins_hosts' | 'all_members',
      status: 'ACTIVE' as 'ACTIVE' | 'SUSPENDED' | 'EXPIRED',
      expiresAt: '2027-10-15',
    };
  })(),

  auditLogs: loadFromStorage<AuditLogItem[]>('vorbello_audit_logs', [
    {
      id: 'log-01',
      action: 'meeting.started',
      actorName: localStorage.getItem('vorbello_user_name') || 'Administrator Master',
      targetType: 'meeting',
      targetId: 'meet-01',
      ipHash: '8f4b2c1a...e7',
      createdAt: '2026-09-26T10:00:05Z',
      metadata: '{"title": "Ședință Săptămânală de Coordonare"}',
    },
    {
      id: 'log-02',
      action: 'livekit.token_issued',
      actorName: localStorage.getItem('vorbello_user_name') || 'Administrator Master',
      targetType: 'room',
      targetId: 'room-01',
      ipHash: '8f4b2c1a...e7',
      createdAt: '2026-09-26T09:58:30Z',
      metadata: '{"role": "ADMIN", "roomAdmin": true}',
    },
  ] as AuditLogItem[]),

  deletedMeetingIds: loadFromStorage<string[]>('vorbello_deleted_meeting_ids', ['prod-meet-01', 'meet-test']),
  deletedRoomIds: loadFromStorage<string[]>('vorbello_deleted_room_ids', []),
  deletedMemberIds: loadFromStorage<string[]>('vorbello_deleted_member_ids', []),
};

// Sanitize meetings, rooms, and members immediately to strip any legacy test/default ghost entries
state.prodMeetings = (state.prodMeetings || []).filter(m => m && m.id && m.id !== 'prod-meet-01' && m.id !== 'meet-test' && !state.deletedMeetingIds.includes(m.id));
state.meetings = (state.meetings || []).filter(m => m && m.id && m.id !== 'prod-meet-01' && m.id !== 'meet-test' && !state.deletedMeetingIds.includes(m.id));
state.prodRooms = (state.prodRooms || []).filter(r => r && r.id && !state.deletedRoomIds.includes(r.id));
state.rooms = (state.rooms || []).filter(r => r && r.id && !state.deletedRoomIds.includes(r.id));
state.prodMembers = (state.prodMembers || []).filter(m => m && m.id && !state.deletedMemberIds.includes(m.id));
state.members = (state.members || []).filter(m => m && m.id && !state.deletedMemberIds.includes(m.id));

// Synchronize state.user with active member record so roles and identity never drift
const currentTargetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
const matchedSelf = currentTargetMembers.find(m => m.id === state.user.id || (m.email && m.email === state.user.email));
if (matchedSelf) {
  state.user.id = matchedSelf.id;
  state.user.name = matchedSelf.name;
  state.user.email = matchedSelf.email;
  state.user.role = matchedSelf.role as any;
  try {
    localStorage.setItem('vorbello_user_id', state.user.id);
    localStorage.setItem('vorbello_user_name', state.user.name);
    localStorage.setItem('vorbello_user_email', state.user.email);
    localStorage.setItem('vorbello_user_role', state.user.role);
    localStorage.setItem('vorbello_user', JSON.stringify(state.user));
  } catch (_) {}
}

try {
  localStorage.setItem('vorbello_prod_meetings', JSON.stringify(state.prodMeetings));
  localStorage.setItem('vorbello_prod_members', JSON.stringify(state.prodMembers));
  localStorage.setItem('vorbello_deleted_meeting_ids', JSON.stringify(state.deletedMeetingIds));
  localStorage.setItem('vorbello_deleted_room_ids', JSON.stringify(state.deletedRoomIds));
  localStorage.setItem('vorbello_deleted_member_ids', JSON.stringify(state.deletedMemberIds));
} catch (_) {}

// Server Synchronization (Cross-Platform: Desktop, Web & Mobile)
function isDesktopApp(): boolean {
  return typeof window !== 'undefined' && Boolean((window as any).vorbelloDesktop?.isDesktop);
}

function isMp4RecordingAllowed(): boolean {
  // Formatul container MP4 necesită encoderul nativ din aplicația Desktop Vorbello
  // Pe web (app.vorbello.eu) este dezactivat pentru a preveni erorile de codecuri din browsere
  return isDesktopApp();
}

function renderContainerOptions(currentValue?: 'webm' | 'mp4'): string {
  const mp4Allowed = isMp4RecordingAllowed();
  if (mp4Allowed) {
    return `
      <option value="webm" ${(!currentValue || currentValue === 'webm') ? 'selected' : ''}>⚡ WebM (Recomandat — Performanță & Derulare Rapidă)</option>
      <option value="mp4" ${currentValue === 'mp4' ? 'selected' : ''}>🎬 MP4 (Universal H.264 / AAC)</option>
    `;
  } else {
    return `
      <option value="webm" selected>⚡ WebM (Standard Web & Mobil — Compatibilitate Totală)</option>
      <option value="mp4" disabled style="opacity: 0.45; color: #94a3b8;">🎬 MP4 (Disponibil exclusiv în aplicația Desktop Vorbello)</option>
    `;
  }
}

function renderAutoRecordLayoutOptions(currentValue?: 'composite' | 'screen_only' | 'cameras_only'): string {
  const saved = (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_default_auto_record_layout') : null) as 'composite' | 'screen_only' | 'cameras_only' | null;
  const val = currentValue || (saved && ['composite', 'screen_only', 'cameras_only'].includes(saved) ? saved : 'composite');
  return `
    <option value="composite" ${val === 'composite' ? 'selected' : ''}>🖼️ Studio PiP (Ecran Partajat + Cameră Prezentator în colț)</option>
    <option value="cameras_only" ${val === 'cameras_only' ? 'selected' : ''}>👤 Cameră Curată (Doar Camera Mea Admin — Cursuri / Prezentări)</option>
    <option value="screen_only" ${val === 'screen_only' ? 'selected' : ''}>🖥️ Doar Ecran Partajat (Fără cameră suprapusă)</option>
  `;
}

function attendanceStorageKey(): string { return localStorage.getItem('vorbello_app_mode') === 'demo' ? 'vorbello_demo_attendance' : 'vorbello_attendance'; }

function getSignalServerUrl(action: string): string {
  const isDirectWebDomain = typeof window !== 'undefined' && window.location.hostname === 'app.vorbello.eu';
  const signalOrigin = isDirectWebDomain ? '' : 'https://app.vorbello.eu';
  return `${signalOrigin}/signal.php?action=${encodeURIComponent(action)}`;
}

let centralSyncResult: SyncResult = {success: false, message: 'Conexiunea de date nu a fost încă verificată.', pending: 0};
let centralClient: SyncClient | null = null;
let centralSyncRevision = 0;
function readSyncState(): SyncState {
  return {rooms: state.prodRooms, meetings: state.prodMeetings, members: state.prodMembers,
    invitations: state.prodInvitations, attendance: state.attendanceRecords, audit: state.auditLogs};
}
function getCentralClient(): SyncClient {
  if (!centralClient) centralClient = new SyncClient(readSyncState, (next, metadata) => {
    centralSyncRevision = metadata.revision;
    const removedEmails = state.prodMembers.filter(m => metadata.deletedIds?.members?.includes(m.id)).map(m => m.email?.toLowerCase());
    state.adminAccounts = state.adminAccounts.filter(a => !removedEmails.includes(a.email?.toLowerCase()));
    if (metadata.deletedIds?.members?.includes(state.user.id)) { state.isAdminLoggedIn=false; localStorage.removeItem('vorbello_admin_authenticated'); state.appRoleView='user'; state.user.role='MEMBER'; }
    const changed = JSON.stringify(readSyncState()) !== JSON.stringify(next);
    state.prodRooms = next.rooms as RoomItem[]; state.prodMeetings = next.meetings as MeetingItem[];
    state.prodMembers = next.members as MemberItem[]; state.prodInvitations = next.invitations as InvitationItem[];
    if (state.appMode === 'production') state.attendanceRecords = next.attendance as AttendanceRecord[]; state.auditLogs = next.audit as typeof state.auditLogs;
    for (const [key, rows] of Object.entries({vorbello_prod_rooms: next.rooms, vorbello_prod_meetings: next.meetings,
      vorbello_prod_members: next.members, vorbello_prod_invitations: next.invitations, vorbello_attendance: next.attendance, vorbello_audit_logs: next.audit})) localStorage.setItem(key, JSON.stringify(rows));
    state.deletedRoomIds = metadata.deletedIds?.rooms || []; state.deletedMeetingIds = metadata.deletedIds?.meetings || [];
    state.deletedMemberIds = metadata.deletedIds?.members || [];
    localStorage.setItem('vorbello_deleted_room_ids', JSON.stringify(state.deletedRoomIds));
    localStorage.setItem('vorbello_deleted_meeting_ids', JSON.stringify(state.deletedMeetingIds));
    localStorage.setItem('vorbello_deleted_member_ids', JSON.stringify(state.deletedMemberIds));
    const byId = state.prodMembers.find(m => m.id === state.user.id);
    const emailMatches = state.prodMembers.filter(m => m.email && m.email.toLowerCase() === state.user.email?.toLowerCase());
    const current = byId || (emailMatches.length === 1 ? emailMatches[0] : undefined);
    if (current && state.appMode === 'production') {
      state.user.id = current.id; state.user.name = current.name; state.user.email = current.email; state.user.role = current.role as any;
      for (const [key,value] of Object.entries({id: current.id, name: current.name, email: current.email, role: current.role})) localStorage.setItem('vorbello_user_' + key, value);
      localStorage.setItem('vorbello_user', JSON.stringify(state.user));
    }
    // Cached login profiles must use the same role on the next login as the synchronized member.
    for (const account of state.adminAccounts) {
      const member = state.prodMembers.find(m => m.email?.toLowerCase() === account.email?.toLowerCase());
      if (member) { account.role = member.role as any; account.status = member.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'; }
    }
    localStorage.setItem('vorbello_admin_accounts', JSON.stringify(state.adminAccounts));
    if (changed && !document.body.classList.contains('in-conference') && !['INPUT','TEXTAREA','SELECT'].includes(document.activeElement?.tagName || '')) renderApp();
  }, localStorage, getSignalServerUrl('sync_v2'), fetch, result => {
    const changed = centralSyncResult.success !== result.success || centralSyncResult.message !== result.message || centralSyncResult.pending !== result.pending;
    centralSyncResult = result;
    if (changed) console.info('[Vorbello Sync]', result.success ? 'confirmed' : result.message, 'pending:', result.pending);
    if (changed && !state.activeModal && !document.body.classList.contains('in-conference') && !['INPUT','TEXTAREA','SELECT'].includes(document.activeElement?.tagName || '')) renderApp();
  });
  if (!localStorage.getItem('vorbello_sync_tombstones_migrated_v2')) {
    if (state.deletedRoomIds.length) centralClient.remove('rooms', state.deletedRoomIds);
    if (state.deletedMeetingIds.length) centralClient.remove('meetings', state.deletedMeetingIds);
    if (state.deletedMemberIds.length) centralClient.remove('members', state.deletedMemberIds);
    localStorage.setItem('vorbello_sync_tombstones_migrated_v2', 'true');
  }
  return centralClient;
}
function recordAudit(item: AuditLogItem) {
  const client = state.appMode === 'production' ? getCentralClient() : null;
  state.auditLogs.unshift(item);
  localStorage.setItem('vorbello_audit_logs', JSON.stringify(state.auditLogs));
  if (client) { client.capture('audit'); void syncAllDataWithServer(false); }
}
function requireConfirmedSync(result: SyncResult) {
  if (!result.success) { showToast('Salvat local, în așteptarea serverului: ' + result.message); throw new Error(result.message); }
}
export async function syncAllDataWithServer(triggerRender = false, forcePush = false,
  deleteAction?: {collection: string; id: string}, options?: {clearAttendance?: boolean; replaceAttendance?: boolean; replaceMembers?: boolean}): Promise<SyncResult> {
  if (state.appMode !== 'production') return {success: true, message: 'Mod Demo: modificare locală.', pending: 0};
  try {
    const client = getCentralClient();
    if (deleteAction) client.remove(deleteAction.collection as any, [deleteAction.id]);
    if (options?.clearAttendance) client.clearAttendance();
    if (forcePush) client.capture();
    const result = await client.sync();
    if (triggerRender && !['INPUT','TEXTAREA','SELECT'].includes(document.activeElement?.tagName || '') && !state.activeModal) renderApp();
    return result;
  } catch (error) {
    centralSyncResult = {success: false, message: error instanceof Error ? error.message : 'Sincronizare indisponibilă.', pending: 1};
    return centralSyncResult;
  }
}

export async function syncRoomsWithServer(triggerRender = false) {
  return syncAllDataWithServer(triggerRender);
}

export async function syncMeetingsWithServer(triggerRender = false) {
  return syncAllDataWithServer(triggerRender);
}

export async function pushRoomToServer(_room: RoomItem) { return syncAllDataWithServer(true, true); }
export async function deleteRoomFromServer(roomId: string) {
  state.prodRooms = state.prodRooms.filter(r => r.id !== roomId); state.rooms = state.rooms.filter(r => r.id !== roomId);
  saveRoomsToStorage(false); requireConfirmedSync(await syncAllDataWithServer(true, false, {collection: 'rooms', id: roomId}));
}
export async function pushAllRoomsToServer(_rooms?: RoomItem[]) { return syncAllDataWithServer(false, true); }
export async function pushMeetingToServer(_meeting: MeetingItem) { return syncAllDataWithServer(true, true); }
export async function deleteMeetingFromServer(meetingId: string) {
  state.prodMeetings = state.prodMeetings.filter(m => m.id !== meetingId); state.meetings = state.meetings.filter(m => m.id !== meetingId);
  saveMeetingsToStorage(false); requireConfirmedSync(await syncAllDataWithServer(true, false, {collection: 'meetings', id: meetingId}));
}
export async function pushAllMeetingsToServer(_meetings?: MeetingItem[]) { return syncAllDataWithServer(false, true); }
export async function deleteMemberFromServer(memberId: string) {
  const client = state.appMode === 'production' ? getCentralClient() : null;
  const emails = [...state.prodMembers, ...state.members].filter(m => m.id === memberId).map(m => m.email?.toLowerCase());
  state.adminAccounts = state.adminAccounts.filter(a => !emails.includes(a.email?.toLowerCase()));
  localStorage.setItem('vorbello_admin_accounts', JSON.stringify(state.adminAccounts));
  state.prodMembers = state.prodMembers.filter(m => m.id !== memberId); state.members = state.members.filter(m => m.id !== memberId);
  saveMembersToStorage(false); client?.remove('members', [memberId]);
  requireConfirmedSync(await syncAllDataWithServer(true));
}
export async function deleteAttendanceSessionFromServer(sessionId: string, _roomSlug?: string, _dateStr?: string) {
  const client = state.appMode === 'production' ? getCentralClient() : null;
  client?.removeSession(sessionId);
  state.attendanceRecords = state.attendanceRecords.filter(r => (r.academySessionId ? 'pp-' + r.academySessionId + '-' + r.roomSlug : 'sess-' + r.roomSlug + '-' + r.joinedAt.split(' ')[0]) !== sessionId);
  localStorage.removeItem('vorbello_synced_' + sessionId); localStorage.removeItem('vorbello_synced_at_' + sessionId);
  saveAttendanceToStorage(false); requireConfirmedSync(await syncAllDataWithServer(true));
}
export async function deleteAttendanceRecordFromServer(recordId: string) {
  const client = state.appMode === 'production' ? getCentralClient() : null;
  client?.remove('attendance', [recordId]); state.attendanceRecords = state.attendanceRecords.filter(r => r.id !== recordId);
  saveAttendanceToStorage(false); requireConfirmedSync(await syncAllDataWithServer(true));
}
export async function resetAttendanceRegistry() {
  const client = state.appMode === 'production' ? getCentralClient() : null;
  client?.clearAttendance(); state.attendanceRecords = []; saveAttendanceToStorage(false);
  Object.keys(localStorage).filter(k => k.startsWith('vorbello_synced_')).forEach(k => localStorage.removeItem(k));
  requireConfirmedSync(await syncAllDataWithServer(true));
}

async function verifyLicenseWithVorbelloEu(key?: string): Promise<boolean> {
  const licenseKey = key || state.organizationLicense?.licenseKey;
  if (!licenseKey || licenseKey.startsWith('VRB-DEMO')) return true;

  try {
    const res = await fetch(`https://vorbello.eu/wp-json/vorbello/v1/license/validate?license_key=${encodeURIComponent(licenseKey)}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      cache: 'no-store',
    });

    if (res.ok) {
      const data = await res.json();
      if (data && data.status) {
        if (data.status === 'SUSPENDED') {
          state.organizationLicense.status = 'SUSPENDED';
          localStorage.setItem('vorbello_org_license', JSON.stringify(state.organizationLicense));
          renderApp();
          console.warn('[Vorbello License Sync] Abonamentul este marcat ca SUSPENDAT pe vorbello.eu');
          return false;
        } else if (data.status === 'ACTIVE' && data.valid) {
          state.organizationLicense.status = 'ACTIVE';
          if (data.plan_name) state.organizationLicense.planName = data.plan_name;
          if (data.max_participants_per_room) state.organizationLicense.maxParticipantsPerRoom = data.max_participants_per_room;
          if (data.expires_at) state.organizationLicense.expiresAt = data.expires_at;
          localStorage.setItem('vorbello_org_license', JSON.stringify(state.organizationLicense));
          renderApp();
          console.info(`[Vorbello License Sync] Licență verificată online cu vorbello.eu: ${data.plan_name} (${data.status})`);
          return true;
        }
      }
    }
  } catch (err) {
    console.warn('[Vorbello License Sync] Serverul vorbello.eu nu a putut fi contactat:', err);
  }
  return true;
}

async function syncRecordingsWithDisk(notifyUser = false): Promise<void> {
  const desktop = (window as any).vorbelloDesktop;
  if (!desktop || typeof desktop.listDiskRecordings !== 'function') {
    return;
  }

  try {
    const diskFiles: Array<{
      fileName: string;
      fullPath: string;
      sizeBytes: number;
      sizeFormatted: string;
      modifiedTime: string;
      exists: boolean;
    }> = await desktop.listDiskRecordings();

    const currentRecordings: RecordingItem[] = state.recordings || [];
    let changed = false;
    let purgedCount = 0;
    let addedCount = 0;

    const validRecordings: RecordingItem[] = [];

    // 1. Verify existing records against real disk files
    for (const rec of currentRecordings) {
      if (rec.storageType === 'disk' || rec.filePath || (rec.fileName && !rec.downloadUrl)) {
        const diskMatch = diskFiles.find(df => 
          (rec.filePath && df.fullPath.toLowerCase() === rec.filePath.toLowerCase()) ||
          (rec.fileName && df.fileName.toLowerCase() === rec.fileName.toLowerCase()) ||
          (rec.fileName && df.fileName.toLowerCase().replace(/\.(mp4|webm|mkv)$/, '') === rec.fileName.toLowerCase().replace(/\.(mp4|webm|mkv)$/, ''))
        );

        if (diskMatch) {
          rec.filePath = diskMatch.fullPath;
          rec.fileName = diskMatch.fileName;
          rec.fileSize = diskMatch.sizeFormatted;
          rec.storageType = 'disk';
          validRecordings.push(rec);
        } else {
          // File was deleted from disk/PC! Automatically remove it
          purgedCount++;
          changed = true;
          console.info(`[SyncRecordings] Eliminat din catalog fișierul șters de pe disc: ${rec.fileName || rec.meetingTitle}`);
        }
      } else {
        // Downloads or cloud items
        validRecordings.push(rec);
      }
    }

    // 2. Discover any new files saved on disk in Vorbello Recordings
    for (const df of diskFiles) {
      const alreadyInCatalog = validRecordings.some(r => 
        (r.filePath && r.filePath.toLowerCase() === df.fullPath.toLowerCase()) ||
        (r.fileName && r.fileName.toLowerCase() === df.fileName.toLowerCase()) ||
        (r.fileName && r.fileName.toLowerCase().replace(/\.(mp4|webm|mkv)$/, '') === df.fileName.toLowerCase().replace(/\.(mp4|webm|mkv)$/, ''))
      );

      if (!alreadyInCatalog) {
        let roomSlug = 'vorbello';
        let format: 'video-hd' | 'video-fhd' | 'audio' = 'video-hd';
        let createdAt = new Date(df.modifiedTime).toISOString().replace('T', ' ').substring(0, 19);

        const parts = df.fileName.replace(/\.(mp4|webm|mkv)$/i, '').split('_');
        if (parts.length >= 2) {
          roomSlug = parts[1] || 'vorbello';
        }
        if (df.fileName.includes('1080p') || df.fileName.includes('fhd')) {
          format = 'video-fhd';
        } else if (df.fileName.includes('audio')) {
          format = 'audio';
        }

        validRecordings.unshift({
          id: `rec-disk-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          roomName: roomSlug,
          roomSlug: roomSlug,
          meetingTitle: `Înregistrare ${roomSlug}`,
          createdAt: createdAt,
          duration: 'Complet',
          fileSize: df.sizeFormatted,
          format: format,
          recordedBy: state.user?.name || 'Danny laptop',
          fileName: df.fileName,
          filePath: df.fullPath,
          storageType: 'disk'
        });
        addedCount++;
        changed = true;
      }
    }

    if (changed || validRecordings.length !== currentRecordings.length) {
      state.recordings = validRecordings;
      localStorage.setItem('vorbello_recordings', JSON.stringify(validRecordings));
      renderApp();
    }

    if (notifyUser) {
      let msg = `Catalog sincronizat cu discul (${diskFiles.length} fișiere active).`;
      if (purgedCount > 0) msg += ` S-au eliminat ${purgedCount} fișiere șterse de pe disc.`;
      if (addedCount > 0) msg += ` S-au detectat ${addedCount} fișiere noi.`;
      showToast(msg);
    }
  } catch (err) {
    console.warn('[SyncRecordings] Eroare sincronizare cu discul:', err);
  }
}

// Storage Persistence Helpers
async function saveRoomsToStorage(syncWithServer = true): Promise<SyncResult> {
  localStorage.setItem('vorbello_prod_rooms', JSON.stringify(state.prodRooms)); localStorage.setItem('vorbello_demo_rooms', JSON.stringify(state.rooms));
  return syncWithServer ? syncAllDataWithServer(false, true) : {success:true,message:'Salvat local',pending:0};
}
async function saveMeetingsToStorage(syncWithServer = true): Promise<SyncResult> {
  localStorage.setItem('vorbello_prod_meetings', JSON.stringify(state.prodMeetings)); localStorage.setItem('vorbello_demo_meetings', JSON.stringify(state.meetings));
  return syncWithServer ? syncAllDataWithServer(false, true) : {success:true,message:'Salvat local',pending:0};
}
async function saveInvitationsToStorage(syncWithServer = true): Promise<SyncResult> {
  localStorage.setItem('vorbello_prod_invitations', JSON.stringify(state.prodInvitations)); localStorage.setItem('vorbello_demo_invitations', JSON.stringify(state.invitations));
  return syncWithServer ? syncAllDataWithServer(false, true) : {success:true,message:'Salvat local',pending:0};
}
async function saveMembersToStorage(syncWithServer = true): Promise<SyncResult> {
  localStorage.setItem('vorbello_prod_members', JSON.stringify(state.prodMembers)); localStorage.setItem('vorbello_demo_members', JSON.stringify(state.members));
  return syncWithServer ? syncAllDataWithServer(false, true) : {success:true,message:'Salvat local',pending:0};
}
export async function saveAttendanceToStorage(syncWithServer = true): Promise<SyncResult> {
  localStorage.setItem(attendanceStorageKey(), JSON.stringify(state.attendanceRecords));
  return syncWithServer ? syncAllDataWithServer(false, true) : {success:true,message:'Salvat local',pending:0};
}


function showToast(msg: string) {
  state.toastMessage = msg;
  renderApp();
  setTimeout(() => {
    state.toastMessage = null;
    renderApp();
  }, 4000);
}

function openConfirmDialog(opts: {
  title: string;
  description: string;
  icon?: string;
  confirmText?: string;
  danger?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  state.pendingConfirm = opts;
  state.activeModal = 'confirm-dialog';
  renderApp();
}

let deferredPrompt: any = (typeof window !== 'undefined' && (window as any).__vorbelloPrompt) || null;
const isNativeContainer = typeof window !== 'undefined' && (
  /VorbelloNative|VorbelloShield|vorbelloDesktop/i.test(navigator.userAgent) ||
  (window as any).vorbelloDesktop !== undefined ||
  (window as any).vorbelloNative === true ||
  (window as any).__vorbelloNativeApp === true
);
const isRunningInStandaloneApp = typeof window !== 'undefined' && (
  isNativeContainer ||
  window.matchMedia('(display-mode: standalone)').matches ||
  window.matchMedia('(display-mode: fullscreen)').matches ||
  window.matchMedia('(display-mode: minimal-ui)').matches ||
  (window.navigator as any).standalone === true ||
  new URLSearchParams(window.location.search).get('source') === 'pwa'
);

// If running inside the installed standalone/native app, isAppInstalled is true.
// When visiting in a normal browser tab (Chrome/Safari), we NEVER permanently block the install banner with localStorage,
// because if the user uninstalls the app from Android/Tablet, they must be able to reinstall immediately without clearing cookies!
let isAppInstalled: boolean = isRunningInStandaloneApp || (
  typeof sessionStorage !== 'undefined' && sessionStorage.getItem('vorbello_pwa_dismissed') === 'true'
);

if (typeof window !== 'undefined') {
  // Clear any legacy stale localStorage lock from previous versions
  try {
    localStorage.removeItem('vorbello_pwa_installed');
  } catch (_) {}

  const onInstallPromptReady = (e: any) => {
    deferredPrompt = e.detail || e;
    (window as any).__vorbelloPrompt = deferredPrompt;
    console.info('[Vorbello PWA] Event beforeinstallprompt disponibil!');
    // If the browser fires beforeinstallprompt, the app is 100% NOT installed in this browser!
    if (!isRunningInStandaloneApp) {
      isAppInstalled = false;
    }
    renderApp();
  };

  window.addEventListener('vorbello-pwa-captured', onInstallPromptReady);
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    onInstallPromptReady(e);
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    if (typeof window !== 'undefined') (window as any).__vorbelloPrompt = null;
    isAppInstalled = true;
    console.info('[Vorbello PWA] Aplicația a fost instalată cu succes!');
    showToast('🎉 Vorbello a fost instalată pe ecranul principal!');
    renderApp();
  });

  // Query native installed related apps API (supported by modern Chrome on Android)
  if ('getInstalledRelatedApps' in navigator && !isRunningInStandaloneApp) {
    (navigator as any).getInstalledRelatedApps().then((relatedApps: any[]) => {
      console.info('[Vorbello PWA] getInstalledRelatedApps result:', relatedApps);
      if (!relatedApps || relatedApps.length === 0) {
        // App is uninstalled! Immediately reveal install option
        isAppInstalled = false;
        renderApp();
      }
    }).catch(() => {});
  }
}

async function handlePwaInstall(): Promise<void> {
  let promptEvent = deferredPrompt || (typeof window !== 'undefined' && (window as any).__vorbelloPrompt);

  if (!promptEvent && typeof window !== 'undefined' && (window as any).__vorbelloPromptPromise) {
    try {
      showToast('📲 Se pregătește instalarea aplicației Vorbello...');
      promptEvent = await Promise.race([
        (window as any).__vorbelloPromptPromise,
        new Promise(resolve => setTimeout(resolve, 800))
      ]);
    } catch (_) {}
  }

  if (promptEvent && typeof promptEvent.prompt === 'function') {
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      console.info('[Vorbello PWA] User install choice:', choice?.outcome);
      if (choice?.outcome === 'accepted') {
        showToast('🎉 Vorbello se instalează pe ecranul principal!');
        isAppInstalled = true;
        renderApp();
      }
      deferredPrompt = null;
      if (typeof window !== 'undefined') (window as any).__vorbelloPrompt = null;
      return;
    } catch (err) {
      console.warn('[Vorbello PWA] Prompt execution error:', err);
    }
  }

  // Fallback for browsers without beforeinstallprompt or on prompt cooldown
  const ua = (typeof window !== 'undefined' ? window.navigator.userAgent : '').toLowerCase();
  const isIos = /iphone|ipad|ipod/.test(ua);
  const isChrome = /chrome/.test(ua) || /crios/.test(ua);

  if (isIos) {
    showToast('📲 Pe iOS: Apăsați butonul Partajare (Share ⎋) și alegeți „Adăugați pe ecranul principal”.');
  } else if (isChrome) {
    showToast('📲 Pentru instalare: atingeți pictograma ⬇️ din bara de adrese sau Meniu (⋮) ➔ „Instalați aplicația”.');
  } else {
    showToast('📲 Pentru instalare: atingeți Meniu (⋮ sau ☰) ➔ „Adăugați pe ecranul principal”.');
  }
}

function escapeHtml(str: string): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function parseDeepLinkParams(): { roomSlug?: string; directJoin?: boolean } {
  try {
    const url = new URL(window.location.href);
    const roomParam = url.searchParams.get('room') || url.searchParams.get('meet') || url.searchParams.get('cameră') || url.searchParams.get('sala');
    if (roomParam) {
      return { roomSlug: roomParam, directJoin: url.searchParams.get('auto') === '1' };
    }
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if ((pathParts[0] === 'meet' || pathParts[0] === 'room' || pathParts[0] === 'invite') && pathParts[1]) {
      return { roomSlug: decodeURIComponent(pathParts[1]), directJoin: false };
    }
    if (window.location.hash) {
      const hash = window.location.hash.replace(/^#\/?/, '');
      if (hash.startsWith('meet/') || hash.startsWith('room/') || hash.startsWith('invite/')) {
        const slug = hash.split('/')[1];
        if (slug) return { roomSlug: decodeURIComponent(slug), directJoin: false };
      }
      const hashParams = new URLSearchParams(hash);
      const hashRoom = hashParams.get('room') || hashParams.get('meet');
      if (hashRoom) return { roomSlug: hashRoom, directJoin: false };
    }
  } catch (e) {
    console.warn('[Vorbello] Deep link parse warning:', e);
  }
  return {};
}

function resolveAndValidateRoom(input: string): {
  valid: boolean;
  errorReason?: string;
  room?: RoomItem;
  meeting?: MeetingItem;
  resolvedSlug?: string;
  resolvedName?: string;
  requiresPin?: boolean;
  pinCode?: string;
} {
  const raw = (input || '').trim();
  const academy = getAcademyAccess();
  if (academy && raw === academy.roomSlug) {
    return { valid: true, resolvedSlug: academy.roomSlug, resolvedName: academy.sessionTitle,
      room: { id: academy.roomSlug, slug: academy.roomSlug, name: academy.sessionTitle,
        description: 'Sesiune PP cu invitație nominală', visibility: 'private', maxParticipants: 150,
        activeParticipants: 0, defaultVideoEnabled: true, createdAt: academy.startsAt,
        attendanceTracking: true, waitForHost: false, allowJoinBeforeHost: true } };
  }
  if (!raw) {
    return { valid: false, errorReason: 'Vă rugăm să introduceți numărul, numele sau link-ul sălii.' };
  }

  // 1. Clean candidate slug / id from full URL, path, or hash
  let candidate = raw;
  try {
    if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
      const url = new URL(candidate);
      const roomParam = url.searchParams.get('room') || url.searchParams.get('meet') || url.searchParams.get('cameră') || url.searchParams.get('sala');
      if (roomParam) {
        candidate = roomParam;
      } else {
        const parts = url.pathname.split('/').filter(Boolean);
        if ((parts[0] === 'meet' || parts[0] === 'room' || parts[0] === 'invite') && parts[1]) {
          candidate = decodeURIComponent(parts[1]);
        }
      }
    } else if (candidate.includes('/meet/')) {
      candidate = candidate.split('/meet/')[1]?.split('?')[0] || candidate;
    } else if (candidate.includes('/room/')) {
      candidate = candidate.split('/room/')[1]?.split('?')[0] || candidate;
    } else if (candidate.includes('/invite/')) {
      candidate = candidate.split('/invite/')[1]?.split('?')[0] || candidate;
    } else if (candidate.includes('?room=')) {
      candidate = candidate.split('?room=')[1]?.split('&')[0] || candidate;
    }
  } catch (_) {}

  candidate = candidate.trim().replace(/^#\/?/, '').replace(/^\/+|\/+$/g, '');
  const candLower = candidate.toLowerCase();

  // 2. Search in all active rooms (both prod and demo collections)
  const allRooms = [...state.prodRooms, ...state.rooms];
  const matchedRoom = allRooms.find(r => 
    (r.slug && r.slug.toLowerCase() === candLower) || 
    (r.id && r.id.toLowerCase() === candLower) ||
    (r.name && r.name.toLowerCase() === candLower)
  );

  if (matchedRoom) {
    return {
      valid: true,
      room: matchedRoom,
      resolvedSlug: matchedRoom.slug || matchedRoom.id,
      resolvedName: matchedRoom.name,
      requiresPin: Boolean(matchedRoom.pinCode && matchedRoom.pinCode.trim().length > 0),
      pinCode: matchedRoom.pinCode?.trim(),
    };
  }

  // 3. Search in all scheduled / live meetings
  const allMeetings = [...state.prodMeetings, ...state.meetings];
  const matchedMeeting = allMeetings.find(m =>
    (m.id && m.id.toLowerCase() === candLower) ||
    (m.roomId && m.roomId.toLowerCase() === candLower) ||
    (m.title && m.title.toLowerCase() === candLower)
  );

  if (matchedMeeting) {
    const meetRoom = allRooms.find(r => r.id === matchedMeeting.roomId || r.slug === matchedMeeting.roomId) || {
      id: matchedMeeting.roomId,
      name: matchedMeeting.roomName,
      slug: matchedMeeting.roomId,
      description: matchedMeeting.agenda || 'Ședință programată',
      visibility: matchedMeeting.joinPolicy === 'org_members' ? 'internal' : 'private',
      maxParticipants: 100,
      activeParticipants: 0,
      defaultVideoEnabled: true,
      createdAt: matchedMeeting.startsAt,
    };
    return {
      valid: true,
      room: meetRoom,
      meeting: matchedMeeting,
      resolvedSlug: meetRoom.slug || meetRoom.id,
      resolvedName: `${matchedMeeting.title} (${meetRoom.name})`,
      requiresPin: Boolean((matchedMeeting.pinCode && matchedMeeting.pinCode.trim().length > 0) || (meetRoom.pinCode && meetRoom.pinCode.trim().length > 0)),
      pinCode: matchedMeeting.pinCode?.trim() || meetRoom.pinCode?.trim(),
    };
  }

  // 4. Search in invitations
  const allInvitations = [...state.prodInvitations, ...state.invitations];
  const matchedInvite = allInvitations.find(inv =>
    (inv.token && inv.token.toLowerCase() === candLower) ||
    (inv.roomId && inv.roomId.toLowerCase() === candLower)
  );

  if (matchedInvite) {
    if (new Date(matchedInvite.expiresAt).getTime() < Date.now()) {
      return {
        valid: false,
        errorReason: 'Link-ul de invitație a expirat. Vă rugăm să solicitați o invitație nouă de la organizator.',
      };
    }
    const invRoom = allRooms.find(r => r.id === matchedInvite.roomId || r.slug === matchedInvite.roomId) || {
      id: matchedInvite.roomId,
      name: matchedInvite.roomName,
      slug: matchedInvite.roomId,
      description: 'Invitație de acces în sală',
      visibility: 'private',
      maxParticipants: 100,
      activeParticipants: 0,
      defaultVideoEnabled: true,
      createdAt: new Date().toISOString(),
    };
    return {
      valid: true,
      room: invRoom,
      resolvedSlug: invRoom.slug || invRoom.id,
      resolvedName: invRoom.name,
      requiresPin: Boolean(invRoom.pinCode && invRoom.pinCode.trim().length > 0),
      pinCode: invRoom.pinCode?.trim(),
    };
  }

  // 5. Default: Room DOES NOT EXIST!
  return {
    valid: false,
    errorReason: `Sala sau link-ul introdus („${candidate}”) nu există sau nu este valabil. Vă rugăm să verificați codul sălii primit de la organizator.`,
  };
}

let prejoinConnController: AbortController | null = null;

async function updatePrejoinConnectionStatus(): Promise<void> {
  const badge = document.getElementById('prejoin-conn-status-badge');
  const textEl = document.getElementById('prejoin-conn-status-text');
  if (!badge) return;

  if (prejoinConnController) {
    try { prejoinConnController.abort(); } catch (_) {}
  }
  prejoinConnController = new AbortController();

  badge.className = 'prejoin-device-badge checking';
  if (textEl) textEl.textContent = 'Verificare Rețea...';
  badge.title = 'Se testează comunicarea în timp real cu serverul Vorbello...';
  badge.onclick = null;
  badge.style.cursor = 'default';

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    badge.className = 'prejoin-device-badge error';
    if (textEl) textEl.textContent = 'Fără Internet (Offline)';
    badge.title = 'Dispozitivul nu este conectat la internet. Verificați conexiunea Wi-Fi sau datele mobile.';
    return;
  }

  const startTime = performance.now();
  try {
    const timer = setTimeout(() => {
      if (prejoinConnController) prejoinConnController.abort();
    }, 4500);

    const signalOrigin = (typeof window !== 'undefined' && window.location.protocol === 'file:') ? 'https://app.vorbello.eu' : '';
    const res = await fetch(`${signalOrigin}/signal.php?action=health`, {
      signal: prejoinConnController.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);

    if (res.ok) {
      const data = await res.json().catch(() => null);
      const pingMs = Math.round(performance.now() - startTime);
      if (data && data.status === 'ok') {
        badge.className = 'prejoin-device-badge ok';
        if (textEl) textEl.textContent = `Conexiune Stabilă (${pingMs}ms) • Gata`;
        badge.title = `Conexiune WebRTC verificată cu succes către app.vorbello.eu (Latență RTT: ${pingMs}ms)`;
        return;
      }
    }
    throw new Error('Semnal server incorect');
  } catch (err: any) {
    console.warn('[Prejoin] Connection health check failed:', err);
    badge.className = 'prejoin-device-badge error';
    if (textEl) textEl.textContent = 'Server Inaccesibil (Reîncearcă)';
    badge.title = 'Nu s-a putut contacta serverul Vorbello. Apasă aici pentru a reîncerca verificarea.';
    badge.style.cursor = 'pointer';
    badge.onclick = (e) => {
      e.stopPropagation();
      updatePrejoinConnectionStatus();
    };
  }
}

let prejoinStream: MediaStream | null = null;

async function startPrejoinPreview(): Promise<void> {
  const video = document.getElementById('prejoin-camera-preview') as HTMLVideoElement | null;
  const avatar = document.getElementById('prejoin-avatar-placeholder');
  if (!video) return;

  if (!state.userJoinCamEnabled) {
    stopPrejoinPreview();
    video.style.display = 'none';
    if (avatar) avatar.style.display = 'flex';
    return;
  }

  // If already active, keep playing
  if (prejoinStream && prejoinStream.active) {
    video.srcObject = prejoinStream;
    video.style.display = 'block';
    if (avatar) avatar.style.display = 'none';
    await video.play().catch(() => {});
    return;
  }

  try {
    const preferredCamId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_cam') : null;
    const stream = await acquireUserCameraStream(preferredCamId);
    prejoinStream = stream;
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.srcObject = stream;
    video.style.display = 'block';
    if (avatar) avatar.style.display = 'none';
    await video.play().catch(() => {});
  } catch (err) {
    console.warn('[Prejoin Preview] Webcam unavailable:', err);
    video.style.display = 'none';
    if (avatar) avatar.style.display = 'flex';
  }
}

function stopPrejoinPreview(): void {
  if (prejoinStream) {
    try {
      prejoinStream.getTracks().forEach(t => t.stop());
    } catch (e) {}
    prejoinStream = null;
  }
  const video = document.getElementById('prejoin-camera-preview') as HTMLVideoElement | null;
  if (video) {
    video.srcObject = null;
  }
}

function formatRomanianDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr;
  const year = parts[0];
  const monthIdx = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  const monthsRo = [
    'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
    'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
  ];
  const monthName = monthsRo[monthIdx] || parts[1];
  return `${day} ${monthName} ${year}`;
}

function groupAttendanceSessions(records: AttendanceRecord[]): MeetingSession[] {
  const sessionMap = new Map<string, MeetingSession>();

  records.forEach(r => {
    const dateStr = r.joinedAt.split(' ')[0] || new Date().toISOString().split('T')[0];
    const meetingKey = `${r.academySessionId || ''}___${r.roomSlug}___${dateStr}___${r.meetingTitle}`;
    const sessionId = r.academySessionId ? `pp-${r.academySessionId}-${r.roomSlug}` : `sess-${r.roomSlug}-${dateStr}`;

    let session = sessionMap.get(meetingKey);
    if (!session) {
      const isSynced = localStorage.getItem(`vorbello_synced_${sessionId}`) === 'true';
      const lastSync = localStorage.getItem(`vorbello_synced_at_${sessionId}`) || undefined;
      session = {
        id: sessionId,
        meetingTitle: r.meetingTitle || r.roomSlug,
        roomSlug: r.roomSlug,
        dateStr: dateStr,
        displayDate: formatRomanianDate(dateStr),
        startTime: r.joinedAt.split(' ')[1]?.substring(0, 5) || '10:00',
        endTime: 'În curs',
        isLive: false,
        totalParticipants: 0,
        activeCount: 0,
        durationFormatted: '0 min',
        records: [],
        syncStatus: isSynced ? 'synced' : 'pending',
        lastSyncedAt: lastSync,
      };
      sessionMap.set(meetingKey, session);
    }

    session.records.push(r);
  });

  const sessions = Array.from(sessionMap.values());
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const nowMs = now.getTime();
  let hasAutoClosedRecords = false;

  sessions.forEach(session => {
    // Sort records in session by joinedAt ascending
    session.records.sort((a, b) => new Date(a.joinedAt.replace(' ', 'T')).getTime() - new Date(b.joinedAt.replace(' ', 'T')).getTime());

    // Auto-heal abandoned records (where leftAt is null from past days or older than 2 hours)
    session.records.forEach(r => {
      if (!r.leftAt) {
        const joinMs = new Date(r.joinedAt.replace(' ', 'T')).getTime();
        const rDate = r.joinedAt.split(' ')[0] || '';
        const ageHours = (nowMs - joinMs) / (1000 * 60 * 60);
        if (rDate < todayStr || ageHours > 2) {
          const autoEndMs = joinMs + (45 * 60 * 1000);
          const endDate = new Date(autoEndMs);
          const endStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')} ${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}:${String(endDate.getSeconds()).padStart(2, '0')}`;
          r.leftAt = endStr;
          r.durationFormatted = '45 min';
          hasAutoClosedRecords = true;
        }
      }
    });

    session.sessionId = session.id;
    session.dateKey = session.dateStr;
    session.totalParticipants = session.records.length;
    session.uniqueParticipants = new Set(session.records.map(r => r.participantName)).size;
    session.presentCount = session.records.filter(r => r.status === 'PRESENT').length;
    session.isSynced = session.syncStatus === 'synced';

    // A session is LIVE only if the meeting date is TODAY and has unclosed participants who joined within the last 2 hours
    const activeRecentRecords = session.records.filter(r => {
      if (r.leftAt) return false;
      const joinMs = new Date(r.joinedAt.replace(' ', 'T')).getTime();
      return (nowMs - joinMs) < (2 * 60 * 60 * 1000);
    });
    session.activeCount = activeRecentRecords.length;
    session.isLive = (session.dateStr === todayStr && session.activeCount > 0);

    const firstJoin = session.records[0]?.joinedAt;
    if (firstJoin) {
      session.startTime = firstJoin.split(' ')[1]?.substring(0, 5) || '00:00';
    }

    if (session.isLive) {
      session.endTime = 'În desfășurare';
      session.durationFormatted = 'Activă acum';
    } else {
      let maxLeaveTime = '';
      session.records.forEach(r => {
        if (r.leftAt && r.leftAt > maxLeaveTime) {
          maxLeaveTime = r.leftAt;
        }
      });
      if (maxLeaveTime) {
        session.endTime = maxLeaveTime.split(' ')[1]?.substring(0, 5) || session.startTime;
        const startMs = new Date(firstJoin.replace(' ', 'T')).getTime();
        const endMs = new Date(maxLeaveTime.replace(' ', 'T')).getTime();
        const diffMins = Math.max(1, Math.round((endMs - startMs) / 60000));
        if (diffMins >= 60) {
          const h = Math.floor(diffMins / 60);
          const m = diffMins % 60;
          session.durationFormatted = `${h}h ${m}m`;
        } else {
          session.durationFormatted = `${diffMins} min`;
        }
      } else {
        session.endTime = session.startTime;
        session.durationFormatted = 'Finalizată';
      }
    }
  });

  if (hasAutoClosedRecords) {
    if (state.appMode === 'production') { getCentralClient().capture('attendance'); void syncAllDataWithServer(false); }
    try {
      localStorage.setItem(attendanceStorageKey(), JSON.stringify(state.attendanceRecords));
    } catch (_) {}
  }

  // Sort sessions: live sessions first, then chronological descending
  sessions.sort((a, b) => {
    if (a.isLive && !b.isLive) return -1;
    if (!a.isLive && b.isLive) return 1;
    const dateComp = b.dateStr.localeCompare(a.dateStr);
    if (dateComp !== 0) return dateComp;
    return b.startTime.localeCompare(a.startTime);
  });

  return sessions;
}

async function syncSessionToWebsite(session: MeetingSession): Promise<{ success: boolean; message: string }> {
  if (session.records.some(r => r.academySessionId)) {
    return { success: false, message: 'Prezența PP este transmisă direct de serverul video către Academy. Raportul complet se descarcă din site-ul PP.' };
  }
  if (state.appMode === 'production') {
    const pull = await syncAllDataWithServer(false);
    if (!pull.success) return {success: false, message: pull.message};
    const refreshed = groupAttendanceSessions(state.attendanceRecords).find(s => s.id === session.id);
    if (!refreshed) return {success: false, message: 'Ședința nu mai există în registrul serverului.'};
    session = refreshed;
  }
  const config = state.websiteSyncConfig;
  const url = config.endpointUrl || 'https://vorbello.eu/wp-json/vorbello/v1/attendance';

  const payload = {
    sessionId: session.id,
    meetingTitle: session.meetingTitle,
    roomSlug: session.roomSlug,
    date: session.dateStr,
    displayDate: session.displayDate,
    startTime: session.startTime,
    endTime: session.endTime,
    isLive: session.isLive,
    duration: session.durationFormatted,
    totalAttendees: session.totalParticipants,
    attendees: session.records.map(r => ({
      name: r.participantName,
      email: r.participantEmail || '',
      organization: r.organization,
      role: r.role,
      joinedAt: r.joinedAt,
      leftAt: r.leftAt,
      duration: r.durationFormatted,
      status: r.status,
    })),
    source: 'vorbello-app',
    revision: centralSyncRevision,
    syncedAt: new Date().toISOString(),
  };

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (config.apiKey) {
      headers['X-Vorbello-Api-Key'] = config.apiKey;
      headers['Authorization'] = `Bearer ${config.apiKey}`;
    }

    const confirmation = await fetchConfirmedJson(url, payload, headers);
    if (confirmation.session_id !== session.id || confirmation.attendees_saved !== payload.attendees.length) throw new Error('Site-ul nu a confirmat raportul și numărul de participanți. Actualizează Vorbello Manager și verifică URL-ul.');
    const nowStr = new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
    localStorage.setItem(`vorbello_synced_${session.id}`, 'true');
    localStorage.setItem(`vorbello_synced_at_${session.id}`, nowStr);
    session.syncStatus = 'synced';
    session.lastSyncedAt = nowStr;
    state.websiteSyncConfig.lastSyncTime = nowStr;
    localStorage.setItem('vorbello_sync_last_time', nowStr);

    return {success: true, message: 'Prezența pentru "' + session.meetingTitle + '" a fost confirmată de site.'};
  } catch (err) {
    localStorage.removeItem(`vorbello_synced_${session.id}`);
    session.syncStatus = 'pending';
    return { success: false, message: err instanceof Error ? err.message : 'Prezența a rămas locală; site-ul nu a confirmat sincronizarea.' };
  }
}

async function syncAllSessionsToWebsite(): Promise<void> {
  const sessions = groupAttendanceSessions(state.attendanceRecords);
  if (sessions.length === 0) {
    showToast('Nu există ședințe sau înregistrări de prezență pentru sincronizare.');
    return;
  }
  showToast(`Se sincronizează ${sessions.length} ședințe cu site-ul web...`);
  let count = 0;
  let lastError = '';
  for (const s of sessions) {
    try {
      const result = await syncSessionToWebsite(s);
      if (result.success) count++; else lastError = result.message;
    } catch (_) {}
  }
  showToast(count === sessions.length ? 'Site-ul a confirmat toate cele ' + count + ' ședințe.' : 'Confirmate: ' + count + '/' + sessions.length + '. ' + lastError);
  renderApp();
}

function recordAttendanceEntry(
  roomSlug: string,
  roomName: string,
  userName: string,
  userEmail?: string,
  userRole?: string,
  orgName?: string
) {
  try {
    const records = loadFromStorage<AttendanceRecord[]>(attendanceStorageKey(), state.appMode === 'demo' ? DEFAULT_ATTENDANCE_RECORDS : []);
    const now = new Date();
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    
    // Check if there is already an active entry for this user and room
    const existingIndex = records.findIndex(r => r.roomSlug === roomSlug && r.participantName === userName && !r.leftAt);
    if (existingIndex >= 0) {
      return;
    }

    const newRecord: AttendanceRecord = {
      academySessionId: getAcademyAccess()?.sessionId,
      memberNumber: getAcademyAccess()?.memberNumber,
      joinedAtUtc: now.toISOString(),
      sessionStartsAtUtc: getAcademyAccess()?.startsAt,
      sessionEndsAtUtc: getAcademyAccess()?.endsAt,
      id: `att-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      meetingTitle: roomName,
      roomSlug: roomSlug,
      participantName: userName,
      participantEmail: userEmail || `${userName.toLowerCase().replace(/[^a-z0-9]/g, '')}@membru.vorbello`,
      organization: orgName || 'Organizație Vorbello',
      role: (userRole as any) || 'MEMBER',
      joinedAt: timeStr,
      leftAt: null,
      durationFormatted: 'Conectat (Activ)',
      status: 'PRESENT',
      quorumVerified: !getAcademyAccess(),
    };

    records.unshift(newRecord);
    localStorage.setItem(attendanceStorageKey(), JSON.stringify(records));
    state.attendanceRecords = records;
    syncAllDataWithServer(false, true);
  } catch (e) {
    console.warn('[Attendance] Error recording entrance:', e);
  }
}

function recordAttendanceExit(roomSlug: string, userName: string) {
  try {
    const records = loadFromStorage<AttendanceRecord[]>(attendanceStorageKey(), state.appMode === 'demo' ? DEFAULT_ATTENDANCE_RECORDS : []);
    const now = new Date();
    const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    
    const entry = records.find(r => r.roomSlug === roomSlug && r.participantName === userName && !r.leftAt);
    if (entry) {
      entry.leftAtUtc = now.toISOString();
      entry.leftAt = timeStr;
      const startMs = new Date(entry.joinedAt.replace(' ', 'T')).getTime();
      const endMs = now.getTime();
      const diffMins = Math.max(1, Math.round((endMs - startMs) / 60000));
      if (diffMins >= 60) {
        const h = Math.floor(diffMins / 60);
        const m = diffMins % 60;
        entry.durationFormatted = `${h}h ${m}m`;
      } else {
        entry.durationFormatted = `${diffMins} min`;
      }
      localStorage.setItem(attendanceStorageKey(), JSON.stringify(records));
      state.attendanceRecords = records;
      syncAllDataWithServer(false, true);

      // Automatic sync to website on exit if enabled!
      if (state.websiteSyncConfig.autoSync) {
        const sessions = groupAttendanceSessions(records);
        const currentSession = sessions.find(s => s.roomSlug === roomSlug);
        if (currentSession) {
          syncSessionToWebsite(currentSession).catch(err => {
            console.warn('[Attendance Auto-Sync Notice]:', err);
          });
        }
      }
    }
  } catch (e) {
    console.warn('[Attendance] Error recording exit:', e);
  }
}

async function launchConference(
  roomSlugOrId: string,
  roomDisplayName?: string,
  customUserName?: string,
  initialCam?: boolean,
  initialMic?: boolean
) {
  let academyAccess = getAcademyAccess();
  if (hasAcademyInvitation()) {
    try { academyAccess = await loadAcademyInvitation(true); }
    catch (error) {
      state.userJoinError = error instanceof Error ? error.message : 'Accesul PP nu a putut fi emis.';
      renderApp(); return;
    }
    if (roomSlugOrId !== academyAccess.roomSlug) {
      state.userJoinError = 'Invitația personală este valabilă doar pentru sesiunea indicată.';
      renderApp(); return;
    }
  }
  // Transfer active prejoin camera stream seamlessly without killing hardware HAL on mobile/tablet!
  const activeStreamToTransfer = (initialCam !== false && prejoinStream && prejoinStream.active) ? prejoinStream : null;
  prejoinStream = null; // Detach from lounge preview
  const prejoinVid = document.getElementById('prejoin-camera-preview') as HTMLVideoElement | null;
  if (prejoinVid) prejoinVid.srcObject = null;
  if (!activeStreamToTransfer) {
    stopPrejoinPreview();
  }

  const isActuallyAuthedAdmin = academyAccess ? academyAccess.isHost : Boolean(state.isAdminLoggedIn && localStorage.getItem('vorbello_admin_authenticated') === 'true' && ['ADMIN','CLIENT_ADMIN'].includes(state.user.role));
  const check = resolveAndValidateRoom(roomSlugOrId);

  if (!check.valid && !isActuallyAuthedAdmin) {
    showToast(`⛔ Sala „${roomSlugOrId}” nu există sau link-ul nu este valabil!`);
    state.userJoinError = `Sala „${roomSlugOrId}” nu există sau nu este activă. Vă rugăm să verificați codul sau link-ul primit.`;
    state.appRoleView = 'user';
    renderApp();
    return;
  }

  const allRooms = state.appMode === 'production' ? state.prodRooms : state.rooms;
  const room = check.room || allRooms.find(r => r.id === roomSlugOrId || r.slug === roomSlugOrId) 
    || {
      id: `room-${roomSlugOrId}`,
      name: roomDisplayName || check.resolvedName || `Sală: ${roomSlugOrId}`,
      slug: roomSlugOrId,
      description: 'Sală privată accesată prin cod de participare sau link direct.',
      visibility: 'internal',
      maxParticipants: 100,
      activeParticipants: 0,
      defaultVideoEnabled: true,
      createdAt: new Date().toISOString(),
    };

  const displayName = academyAccess?.displayName || customUserName?.trim() || state.user.name || 'Participant Oaspete';
  if (customUserName?.trim() && !academyAccess) {
    state.user.name = customUserName.trim();
    localStorage.setItem('vorbello_user_name', customUserName.trim());
  }

  let livekitUrl = academyAccess?.livekitUrl || (state.appMode === 'production' ? state.vpsConfig.livekitUrl : 'ws://localhost:7880');
  if (state.appMode === 'production' && !academyAccess) {
    if (!livekitUrl || livekitUrl.includes('app.vorbello.eu') || livekitUrl.includes('domeniul-tau.ro') || livekitUrl.includes('217.217.233.20') || livekitUrl.includes(':7880') || !livekitUrl.startsWith('wss://')) {
      livekitUrl = 'wss://live.vorbello.eu';
      state.vpsConfig.livekitUrl = 'wss://live.vorbello.eu';
      localStorage.setItem('vorbello_vps_livekit', 'wss://live.vorbello.eu');
    }
  }
  showToast(`Conectare la camera "${room.name}" (${state.appMode === 'production' ? 'Server Cloud Securizat' : 'Mod Demo'})...`);

  const isAdminUser = isActuallyAuthedAdmin;
  const isModUser = academyAccess ? academyAccess.isHost : isAdminUser || (state.user?.role === 'MODERATOR');
  const activeRole: 'ADMIN' | 'MODERATOR' | 'MEMBER' = isAdminUser ? 'ADMIN' : (!academyAccess && state.user?.role === 'MODERATOR' ? 'MODERATOR' : 'MEMBER');

  // Apply room policies if configured (e.g. Mute on entry / camera off)
  if (!isAdminUser) {
    if (room.muteOnEntry && initialMic === undefined) {
      initialMic = false;
    }
    if (room.cameraOffOnEntry && initialCam === undefined) {
      initialCam = false;
    }
  }

  let token = 'mock_or_live_livekit_token';
  try {
    token = academyAccess?.token || await createLiveKitToken({
      apiKey: 'devkey',
      apiSecret: 'secret9876543210abcdef',
      roomName: room.slug || room.name,
      name: displayName,
      identity: `${state.user.id || 'usr'}-${getStableClientDeviceId()}`,
      metadata: JSON.stringify({
        role: activeRole,
        isAdmin: isAdminUser,
        isModerator: isModUser,
      }),
    });
  } catch (err) {
    console.error('Failed to create LiveKit token:', err);
  }

  // Save active conference session so Refresh (F5) automatically reconnects!
  try {
    sessionStorage.setItem('vorbello_active_conference', JSON.stringify({
      roomSlug: room.slug || room.id,
      roomDisplayName: room.name,
      displayName,
      initialCam: initialCam !== false,
      initialMic: initialMic !== false,
      joinedAt: Date.now(),
    }));
    // Update URL query string (?room=slug) without reloading page
    const cleanSlug = encodeURIComponent(room.slug || room.id);
    const currentParams = new URLSearchParams(window.location.search);
    const simulatedParticipants = parseInt(currentParams.get('participants') || '0', 10);
    const extraParams = academyAccess ? '&academy=1' : (simulatedParticipants > 0 ? `&participants=${simulatedParticipants}` : '');
    const newUrl = `${window.location.pathname}?room=${cleanSlug}${extraParams}`;
    window.history.replaceState({ inConference: true, roomSlug: cleanSlug }, '', newUrl);
  } catch (e) {
    console.warn('[Vorbello] Failed to persist conference session:', e);
  }

  const currentParamsForConf = new URLSearchParams(window.location.search);
  const pCount = parseInt(currentParamsForConf.get('participants') || '0', 10);

  const myDeviceId = getStableClientDeviceId();
  const isCreatorDevice = Boolean(room.creatorDeviceId && room.creatorDeviceId === myDeviceId);
  const isCreatorUser = Boolean(room.creatorId && state.user?.id && room.creatorId === state.user.id);
  const isRoomHost = academyAccess ? academyAccess.isHost : Boolean(isAdminUser && (isCreatorDevice || isCreatorUser || !room.creatorDeviceId));

  const allMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
  const currentMeeting = allMeetings.find(m => (m.roomId === room.id || m.roomId === room.slug) && (m.status === 'live' || m.status === 'scheduled'));

  const conf = new ConferenceUI({
    roomName: room.name,
    roomSlug: room.slug || room.id || 'sala-vps-live',
    token: token,
    requiresVerifiedServer: Boolean(academyAccess),
    onServerConnected: () => {
      if (room.attendanceTracking !== false) {
        recordAttendanceEntry(room.slug || room.id, room.name, displayName, academyAccess?.email || state.user.email, activeRole, state.user.organizationName);
      }
    },
    livekitUrl: livekitUrl,
    displayName: displayName,
    appMode: state.appMode,
    simulatedParticipants: !academyAccess && pCount > 0 ? pCount : undefined,
    initialCameraEnabled: initialCam !== undefined ? initialCam : true,
    initialMicEnabled: initialMic !== undefined ? initialMic : true,
    initialStream: activeStreamToTransfer,
    isAdmin: isAdminUser,
    isModerator: isModUser,
    isHost: isRoomHost,
    role: activeRole,
    roomSettings: {
      type: room.type || 'permanent',
      muteOnEntry: room.muteOnEntry,
      cameraOffOnEntry: room.cameraOffOnEntry,
      waitingRoomEnabled: room.waitingRoomEnabled,
      waitForHost: room.waitForHost !== false,
      allowJoinBeforeHost: room.allowJoinBeforeHost === true,
      allowGuestInvites: room.allowGuestInvites,
      allowScreenShare: room.allowScreenShare,
      attendanceTracking: room.attendanceTracking,
      autoRecord: Boolean(room.autoRecord || currentMeeting?.autoRecord),
      autoRecordFormat: room.autoRecordFormat || currentMeeting?.autoRecordFormat,
      autoRecordLayout: room.autoRecordLayout || currentMeeting?.autoRecordLayout || (typeof localStorage !== 'undefined' ? (localStorage.getItem('vorbello_default_auto_record_layout') as any) : null) || 'composite',
      autoRecordContainer: ((room.autoRecordContainer === 'mp4' || currentMeeting?.autoRecordContainer === 'mp4') && isMp4RecordingAllowed()) ? 'mp4' : 'webm',
      autoRecordNotify: room.autoRecordNotify !== undefined ? room.autoRecordNotify : (currentMeeting?.autoRecordNotify !== undefined ? currentMeeting.autoRecordNotify : true),
      creatorName: room.creatorName,
      pinCode: room.pinCode || currentMeeting?.pinCode,
    },
    onLeave: (reason?: string) => {
      if (room.attendanceTracking !== false) {
        recordAttendanceExit(room.slug || room.id, displayName);
      }
      try {
        sessionStorage.removeItem('vorbello_active_conference');
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) {}
      renderApp();
      showToast(reason || `Ați părăsit camera "${room.name}".`);
    },
  });

  state.toastMessage = null;
  document.querySelectorAll('.toast-notification').forEach(el => el.remove());

  try { await conf.start(); }
  catch (error) {
    sessionStorage.removeItem('vorbello_active_conference');
    state.userJoinError = error instanceof Error ? error.message : 'Conectarea video nu a reușit.';
    renderApp();
  }
}

// High-Contrast SVG Vector Icons for Pre-Join & User Portal
const ICON_CAM_ON = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>`;
const ICON_CAM_OFF = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M21 15.5l2 1.5V7l-7 5"></path><path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2"></path><path d="M10.66 6H14a2 2 0 0 1 2 2v2.34"></path></svg>`;
const ICON_MIC_ON = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>`;
const ICON_MIC_OFF = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="display:block;"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>`;
const ICON_USER = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="display:block; flex-shrink: 0;"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>`;

function renderUserPortalHtml(): string {
  const userInitials = (state.user.name || 'Vorbello').trim().substring(0, 2).toUpperCase();

  return `
    <div class="user-portal-container">
      ${!isAppInstalled ? `
        <div id="pwa-top-install-banner" class="pwa-top-install-banner">
          <div class="pwa-top-banner-brand">
            <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" class="pwa-top-banner-logo" />
            <div class="pwa-top-banner-info">
              <div class="pwa-top-banner-title">Instalare Aplicația Vorbello</div>
              <div class="pwa-top-banner-desc">Instalează aplicația pe ecranul principal pentru acces rapid</div>
            </div>
          </div>
          <div class="pwa-top-banner-actions">
            <button type="button" id="btn-top-banner-install" class="pwa-top-banner-btn-install">
              <span>📲</span>
              <span>Instalează</span>
            </button>
            <button type="button" id="btn-top-banner-dismiss" class="pwa-top-banner-btn-dismiss" title="Închide" style="background:none; border:none; color:#94a3b8; font-size:1.4rem; line-height:1; cursor:pointer; padding:4px 8px; margin-left:4px;">
              &times;
            </button>
          </div>
        </div>
      ` : ''}

      <header class="user-portal-header">
        <div class="user-portal-brand">
          <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" />
          <div style="display: flex; align-items: baseline; gap: 0.4rem;">
            <span class="user-portal-brand-title">Vorbello</span>
            <span class="user-portal-brand-badge">Portal Participanți</span>
          </div>
        </div>

        <div class="user-portal-actions" style="display: flex; align-items: center; gap: 0.65rem;">
          <button id="btn-topbar-version" title="Versiune & Detalii Sistem" style="display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 20px; color: #38bdf8; font-size: 0.78rem; font-weight: 700; cursor: pointer;">
            <span style="display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #38bdf8;"></span>
            <span>v${APP_VERSION}</span>
          </button>

          ${state.isAdminLoggedIn ? `
            <button id="btn-switch-to-admin" class="btn btn-secondary btn-sm" title="Deschide Consola de Administrare și Gestiune Licențe Gazdă" style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 8px; font-weight: 600; font-size: 0.8rem; border-color: rgba(16, 185, 129, 0.4); background: rgba(16, 185, 129, 0.12); color: #6ee7b7;">
              <span style="width: 7px; height: 7px; border-radius: 50%; background: #10b981; box-shadow: 0 0 6px #10b981;"></span>
              <span>Panou Gazdă & Admin</span>
            </button>
            <button id="btn-user-portal-logout" class="btn btn-secondary btn-sm" title="Deconectare din contul de Administrator" style="display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; border-radius: 8px; font-weight: 600; font-size: 0.8rem; border-color: rgba(239, 68, 68, 0.35); background: rgba(239, 68, 68, 0.1); color: #fca5a5; cursor: pointer;">
              <span>🚪</span>
              <span>Ieșire</span>
            </button>
          ` : `
            <button id="btn-switch-to-admin" class="btn btn-secondary btn-sm" title="Autentificare în Consola de Administrare & Gazdă" style="display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 8px; font-weight: 600; font-size: 0.8rem; border-color: rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.05); color: #cbd5e1; cursor: pointer;">
              <span>🔐</span>
              <span>Autentificare Gazdă</span>
            </button>
          `}
        </div>
      </header>

      ${state.toastMessage ? `
        <div class="toast-notification" style="position: fixed; top: 1.5rem; left: 50%; transform: translateX(-50%); z-index: 9999; background: var(--accent-gradient); color: #fff; padding: 0.75rem 1.4rem; border-radius: 10px; font-weight: 600; box-shadow: 0 10px 30px rgba(0,0,0,0.5);">
          ${escapeHtml(state.toastMessage)}
        </div>
      ` : ''}

      <div class="user-portal-body">
        <div class="user-portal-lounge">
          <!-- Left Column: Camera / Audio Live Pre-Join Preview -->
          <div class="prejoin-preview-panel">
            <div class="prejoin-preview-viewport">
              <video id="prejoin-camera-preview" class="prejoin-video-feed" autoplay playsinline muted style="${state.userJoinCamEnabled ? '' : 'display: none;'}"></video>

              <div id="prejoin-avatar-placeholder" class="prejoin-avatar-box" style="${state.userJoinCamEnabled ? 'display: none;' : ''}">
                <div class="prejoin-avatar-circle">
                  ${escapeHtml(userInitials)}
                </div>
                <div style="font-size: 0.85rem; color: #94a3b8; font-weight: 600; display: flex; align-items: center; gap: 5px;">
                  <span>📷</span> Cameră Oprită
                </div>
              </div>

              <!-- Overlaid top bar: Full Name + Status (never cramped or cut off) -->
              <div class="prejoin-overlay-top">
                <div class="prejoin-user-label">
                  ${ICON_USER}
                  <span class="prejoin-user-label-text" id="prejoin-user-label-text">${escapeHtml(state.user.name || 'Participant')}</span>
                </div>
                <div class="prejoin-device-badge checking" id="prejoin-conn-status-badge" title="Stare conexiune rețea și server">
                  <span class="prejoin-live-dot"></span>
                  <span id="prejoin-conn-status-text">Verificare Conexiune...</span>
                </div>
              </div>

              <!-- Overlaid bottom bar: Action Toggles -->
              <div class="prejoin-overlay-bottom">
                <div class="prejoin-overlay-toggles">
                  <button type="button" class="prejoin-overlay-btn ${state.userJoinCamEnabled ? 'on' : 'off'}" id="btn-toggle-prejoin-cam" title="Pornește / Oprește Camera">
                    ${state.userJoinCamEnabled ? ICON_CAM_ON : ICON_CAM_OFF}
                  </button>
                  <button type="button" class="prejoin-overlay-btn ${state.userJoinMicEnabled ? 'on' : 'off'}" id="btn-toggle-prejoin-mic" title="Pornește / Oprește Microfonul">
                    ${state.userJoinMicEnabled ? ICON_MIC_ON : ICON_MIC_OFF}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: Join Form Card (Compact & Directly Accessible) -->
          <div class="user-portal-card">
            <div class="user-portal-card-header">
              <div style="display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.25rem;">
                <img src="${VORBELLO_LOGO}" alt="Vorbello" style="width: 34px; height: 34px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.6));" />
                <span style="font-family: var(--font-display); font-size: 1.25rem; font-weight: 800; background: var(--accent-gradient); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">Vorbello</span>
              </div>
              <h1 class="user-portal-title">Conectare la Conferință</h1>
              <p class="user-portal-subtitle">
                Introduceți codul sălii sau link-ul primit de la organizator.
              </p>
            </div>

            ${getAcademyAccess() ? `<div style="padding:12px 16px;margin:12px 0;border:1px solid #38bdf8;border-radius:12px;color:#e2e8f0">Invitație PP verificată · ${escapeHtml(getAcademyAccess()!.sessionTitle)}<br><small>Identitatea este completată automat. Prezența este confirmată de server după conectare.</small></div>` : ''}
            <form class="user-portal-form" id="form-user-join">
              <div class="user-input-group">
                <label class="user-input-label" for="input-user-name">
                  <span>Numele Tău (Afișat în Sală):</span>
                </label>
                <input 
                  type="text" 
                  class="user-input-field" 
                  id="input-user-name" 
                  ${getAcademyAccess() ? 'readonly aria-readonly="true"' : ''}
                  placeholder="Introduceți numele dumneavoastră..." 
                  value="${escapeHtml(state.userJoinNameInput || state.user.name || '')}" 
                  required 
                />
              </div>

              <div class="user-input-group">
                <label class="user-input-label" for="input-user-room">
                  <span>Număr Sală, Nume sau Link Invitație:</span>
                </label>
                <input 
                  type="text" 
                  class="user-input-field ${state.userJoinError ? 'input-error' : ''}" 
                  id="input-user-room" 
                  ${getAcademyAccess() ? 'readonly aria-readonly="true"' : ''}
                  placeholder="Introduceți numărul sau numele sălii sau accesați prin link..." 
                  value="${escapeHtml(state.userJoinRoomInput || '')}" 
                  required 
                />
              </div>

              ${state.userJoinError ? `
                <div style="background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.45); color: #fca5a5; padding: 0.75rem 0.95rem; border-radius: 10px; font-size: 0.83rem; margin-bottom: 1.15rem; display: flex; align-items: flex-start; gap: 0.6rem; text-align: left; line-height: 1.45; animation: shake 0.3s ease-in-out;">
                  <span style="font-size: 1.2rem; flex-shrink: 0; line-height: 1;">⛔</span>
                  <div>
                    <strong style="display: block; color: #fff; margin-bottom: 2px;">Eroare Conectare:</strong>
                    ${escapeHtml(state.userJoinError)}
                  </div>
                </div>
              ` : ''}

              <button type="submit" class="user-btn-join-main" id="btn-submit-user-join">
                <span>🚀</span>
                <span>Intră în Conferință</span>
              </button>
            </form>

            ${!isAppInstalled ? `
              <div id="pwa-mobile-banner" class="pwa-install-card" style="margin-top: 1.1rem; padding: 12px 14px; background: rgba(99, 102, 241, 0.12); border: 1px solid rgba(99, 102, 241, 0.35); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; gap: 10px;">
                <div style="display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1;">
                  <div style="width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, #6366f1, #8b5cf6); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; flex-shrink: 0;">📲</div>
                  <div style="text-align: left; min-width: 0;">
                    <div style="font-weight: 700; font-size: 0.86rem; color: #fff;">Instalează pe Telefon</div>
                    <div style="font-size: 0.74rem; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Acces direct din ecranul principal</div>
                  </div>
                </div>
                <button type="button" id="btn-banner-install-pwa" class="btn btn-sm" style="padding: 7px 14px; font-weight: 700; border-radius: 8px; font-size: 0.8rem; white-space: nowrap; background: #6366f1; color: #fff; border: none; cursor: pointer; flex-shrink: 0;">Instalează</button>
              </div>
            ` : ''}

            <div class="user-footer-note">
              🔒 Conexiune Privată Securizată Vorbello
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals if opened in user view (like version info) -->
    ${renderModal()}
  `;
}

function attachModalCommonHandlers(): void {
  // Modal Backdrop click to close
  document.querySelectorAll('.modal-overlay').forEach(overlay => {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        state.activeModal = null;
        state.selectedRoomForAction = null;
        state.selectedMeetingForInvite = null;
        state.pendingConfirm = null;
        state.adminAuthError = null;
        renderApp();
      }
    });
  });

  // Modal close buttons
  document.getElementById('btn-close-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    state.selectedRoomForAction = null;
    state.selectedMeetingForInvite = null;
    state.pendingConfirm = null;
    state.adminAuthError = null;
    renderApp();
  });
  document.getElementById('btn-cancel-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    state.selectedRoomForAction = null;
    state.selectedMeetingForInvite = null;
    state.pendingConfirm = null;
    state.adminAuthError = null;
    renderApp();
  });
  document.getElementById('btn-cancel-confirm-dialog')?.addEventListener('click', () => {
    state.activeModal = null;
    state.pendingConfirm = null;
    renderApp();
  });
  document.getElementById('btn-ok-confirm-dialog')?.addEventListener('click', () => {
    if (state.pendingConfirm) {
      const action = state.pendingConfirm.onConfirm;
      state.activeModal = null;
      state.pendingConfirm = null;
      Promise.resolve().then(action).catch(error => showToast(error instanceof Error ? error.message : 'Operația nu a fost confirmată.'));
    }
  });

  // Version Info Modal Open & Close
  document.getElementById('btn-topbar-version')?.addEventListener('click', () => {
    state.activeModal = 'version-info';
    renderApp();
  });
  document.getElementById('btn-ok-version-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    renderApp();
  });

  // Form: Admin Login Submit
  document.getElementById('form-admin-login')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = ((document.getElementById('input-admin-email') as HTMLInputElement)?.value || '').trim().toLowerCase();
    const pass = ((document.getElementById('input-admin-password') as HTMLInputElement)?.value || '');
    const currentMasterPass = localStorage.getItem('vorbello_admin_master_password') || 'Testoso2026!!!???';

    const isMaster = (email === 'dannyopr@gmail.com') && (pass === currentMasterPass);
    const matchedAccount = state.adminAccounts.find(
      acc => acc.email.toLowerCase() === email && acc.password === pass && acc.status === 'ACTIVE'
    );

    if (isMaster) {
      state.isAdminLoggedIn = true;
      localStorage.setItem('vorbello_admin_authenticated', 'true');
      state.user.email = 'dannyopr@gmail.com';
      state.user.name = localStorage.getItem('vorbello_user_name') || 'Danny Master Admin';
      state.user.role = 'ADMIN';
      localStorage.setItem('vorbello_user_email', 'dannyopr@gmail.com');
      state.appRoleView = 'admin';
      localStorage.setItem('vorbello_app_role_view', 'admin');
      state.activeModal = null;
      state.adminAuthError = null;
      showToast(`✅ Bun venit în panoul de control, Danny!`);
      renderApp();
    } else if (matchedAccount) {
      state.isAdminLoggedIn = true;
      localStorage.setItem('vorbello_admin_authenticated', 'true');
      state.user.email = matchedAccount.email;
      state.user.name = matchedAccount.name;
      state.user.role = matchedAccount.role;
      localStorage.setItem('vorbello_user_email', matchedAccount.email);
      localStorage.setItem('vorbello_user_name', matchedAccount.name);
      localStorage.setItem('vorbello_user_role', matchedAccount.role);
      state.appRoleView = 'admin';
      localStorage.setItem('vorbello_app_role_view', 'admin');
      state.activeModal = null;
      state.adminAuthError = null;
      showToast(`✅ Autentificare reușită: ${matchedAccount.name} (${matchedAccount.role})`);
      renderApp();
    } else {
      state.adminAuthError = 'Acces refuzat: Email sau parolă de administrator incorectă!';
      renderApp();
    }
  });

  // Toggle Show/Hide Password
  document.getElementById('btn-toggle-show-pass')?.addEventListener('click', () => {
    const input = document.getElementById('input-admin-password') as HTMLInputElement | null;
    if (input) {
      input.type = input.type === 'password' ? 'text' : 'password';
    }
  });

  // Form: Verify PIN (Protected Conference Room)
  document.getElementById('form-verify-pin')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const enteredPin = ((document.getElementById('input-pin-code') as HTMLInputElement)?.value || '').trim();
    if (!state.pendingPinVerification) return;

    if (enteredPin !== state.pendingPinVerification.correctPin) {
      state.pinError = 'Codul PIN introdus este incorect! Vă rugăm să reîncercați.';
      renderApp();
      return;
    }

    const p = state.pendingPinVerification;
    state.activeModal = null;
    state.pendingPinVerification = null;
    state.pinError = null;

    launchConference(
      p.roomSlug,
      p.roomName,
      p.userName,
      p.camEnabled,
      p.micEnabled
    );
  });

  // PIN generator buttons for Create & Edit Room Modals
  document.getElementById('btn-generate-room-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-room-pin') as HTMLInputElement | null;
    if (pinInput) {
      pinInput.value = Math.floor(1000 + Math.random() * 9000).toString();
    }
  });
  document.getElementById('btn-clear-room-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-room-pin') as HTMLInputElement | null;
    if (pinInput) pinInput.value = '';
  });
  document.getElementById('btn-generate-edit-room-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-edit-room-pin') as HTMLInputElement | null;
    if (pinInput) {
      pinInput.value = Math.floor(1000 + Math.random() * 9000).toString();
    }
  });
  document.getElementById('btn-clear-edit-room-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-edit-room-pin') as HTMLInputElement | null;
    if (pinInput) pinInput.value = '';
  });

  // PIN generator buttons for Schedule & Edit Meeting Modals
  document.getElementById('btn-generate-meet-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-meet-pin') as HTMLInputElement | null;
    if (pinInput) {
      pinInput.value = Math.floor(1000 + Math.random() * 9000).toString();
      pinInput.focus();
    }
  });
  document.getElementById('btn-clear-meet-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-meet-pin') as HTMLInputElement | null;
    if (pinInput) pinInput.value = '';
  });
  document.getElementById('btn-edit-gen-meet-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-edit-meet-pin') as HTMLInputElement | null;
    if (pinInput) {
      pinInput.value = Math.floor(1000 + Math.random() * 9000).toString();
      pinInput.focus();
    }
  });
  document.getElementById('btn-edit-clear-meet-pin')?.addEventListener('click', () => {
    const pinInput = document.getElementById('input-edit-meet-pin') as HTMLInputElement | null;
    if (pinInput) pinInput.value = '';
  });

  // Native Date & Time Pickers on direct input click
  ['input-meet-date', 'input-meet-time', 'input-edit-meet-date', 'input-edit-meet-time'].forEach(id => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (el) {
      el.addEventListener('click', () => {
        if (typeof (el as any).showPicker === 'function') {
          try { (el as any).showPicker(); } catch { el.focus(); }
        }
      });
    }
  });

  // Toggle auto-record options panels dynamically
  document.getElementById('input-edit-room-auto-record')?.addEventListener('change', (e) => {
    const isChecked = (e.target as HTMLInputElement).checked;
    const panel = document.getElementById('panel-edit-room-auto-record-options');
    if (panel) panel.style.display = isChecked ? 'block' : 'none';
  });
  document.getElementById('input-room-auto-record')?.addEventListener('change', (e) => {
    const isChecked = (e.target as HTMLInputElement).checked;
    const panel = document.getElementById('panel-room-auto-record-options');
    if (panel) panel.style.display = isChecked ? 'block' : 'none';
  });
  document.getElementById('input-meet-record')?.addEventListener('change', (e) => {
    const isChecked = (e.target as HTMLInputElement).checked;
    const panel = document.getElementById('panel-meet-auto-record-options');
    if (panel) panel.style.display = isChecked ? 'block' : 'none';
  });
  document.getElementById('input-edit-meet-record')?.addEventListener('change', (e) => {
    const isChecked = (e.target as HTMLInputElement).checked;
    const panel = document.getElementById('panel-edit-meet-auto-record-options');
    if (panel) panel.style.display = isChecked ? 'block' : 'none';
  });
}

function attachUserPortalEventHandlers(): void {
  // Switch to Admin Portal (requires login if not already authenticated)
  document.getElementById('btn-switch-to-admin')?.addEventListener('click', () => {
    stopPrejoinPreview();
    if (state.isAdminLoggedIn) {
      state.appRoleView = 'admin';
      localStorage.setItem('vorbello_app_role_view', 'admin');
      renderApp();
    } else {
      state.activeModal = 'admin-login';
      state.adminAuthError = null;
      renderApp();
    }
  });

  // User Portal Logout
  document.getElementById('btn-user-portal-logout')?.addEventListener('click', () => {
    stopPrejoinPreview();
    state.isAdminLoggedIn = false;
    localStorage.removeItem('vorbello_admin_authenticated');
    state.appRoleView = 'user';
    localStorage.setItem('vorbello_app_role_view', 'user');
    showToast('🚪 V-ați deconectat din contul de Administrator.');
    renderApp();
  });

  // Pre-join Camera Toggle
  document.getElementById('btn-toggle-prejoin-cam')?.addEventListener('click', () => {
    state.userJoinCamEnabled = !state.userJoinCamEnabled;
    const btn = document.getElementById('btn-toggle-prejoin-cam');
    if (btn) {
      btn.className = `prejoin-overlay-btn ${state.userJoinCamEnabled ? 'on' : 'off'}`;
      btn.innerHTML = state.userJoinCamEnabled ? ICON_CAM_ON : ICON_CAM_OFF;
    }
    startPrejoinPreview();
  });

  // Pre-join Mic Toggle
  document.getElementById('btn-toggle-prejoin-mic')?.addEventListener('click', () => {
    state.userJoinMicEnabled = !state.userJoinMicEnabled;
    const btn = document.getElementById('btn-toggle-prejoin-mic');
    if (btn) {
      btn.className = `prejoin-overlay-btn ${state.userJoinMicEnabled ? 'on' : 'off'}`;
      btn.innerHTML = state.userJoinMicEnabled ? ICON_MIC_ON : ICON_MIC_OFF;
    }
  });

  // Live Real-Time Name & Room Input Sync
  const nameInputEl = document.getElementById('input-user-name') as HTMLInputElement | null;
  nameInputEl?.addEventListener('input', () => {
    const val = (nameInputEl.value || '').trim() || 'Participant';
    state.userJoinNameInput = nameInputEl.value;
    state.user.name = val;
    const labelText = document.getElementById('prejoin-user-label-text');
    if (labelText) labelText.textContent = val;
    const avatarCircle = document.querySelector('.prejoin-avatar-circle') as HTMLElement | null;
    if (avatarCircle) {
      avatarCircle.textContent = val.substring(0, 2).toUpperCase();
    }
  });

  const roomInputEl = document.getElementById('input-user-room') as HTMLInputElement | null;
  roomInputEl?.addEventListener('input', () => {
    state.userJoinRoomInput = roomInputEl.value;
  });

  // Quick Room Selection
  document.querySelectorAll('.user-quick-room-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const slug = chip.getAttribute('data-room-slug') || '';
      const input = document.getElementById('input-user-room') as HTMLInputElement | null;
      if (input && slug) {
        input.value = slug;
        state.userJoinRoomInput = slug;
        input.focus();
        showToast(`Sală selectată: ${slug}`);
      }
    });
  });

  // PWA Install Handlers
  document.getElementById('btn-top-banner-install')?.addEventListener('click', () => {
    handlePwaInstall();
  });
  document.getElementById('btn-banner-install-pwa')?.addEventListener('click', () => {
    handlePwaInstall();
  });
  document.getElementById('btn-top-banner-dismiss')?.addEventListener('click', () => {
    try {
      sessionStorage.setItem('vorbello_pwa_dismissed', 'true');
    } catch (_) {}
    isAppInstalled = true;
    document.getElementById('pwa-top-install-banner')?.remove();
  });

  // Form Submit: Join Conference
  document.getElementById('form-user-join')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const roomInput = (document.getElementById('input-user-room') as HTMLInputElement | null)?.value.trim() || '';
    const nameInput = (document.getElementById('input-user-name') as HTMLInputElement | null)?.value.trim() || '';

    state.userJoinError = null;
    try {
      if (captureAcademyInvitation(roomInput)) {
        const access = await loadAcademyInvitation();
        state.appMode = 'production';
        state.userJoinRoomInput = access.roomSlug;
        state.userJoinNameInput = access.displayName;
        window.history.replaceState({}, '', window.location.pathname + '?room=' + encodeURIComponent(access.roomSlug) + '&academy=1');
        renderApp();
        return;
      }
    } catch (error) {
      state.userJoinError = error instanceof Error ? error.message : 'Invitația nu a putut fi verificată.';
      renderApp(); return;
    }

    if (!roomInput) {
      showToast('⚠️ Vă rugăm introduceți numărul/numele sălii sau link-ul de conectare!');
      return;
    }
    if (!nameInput) {
      showToast('⚠️ Vă rugăm introduceți numele dumneavoastră!');
      return;
    }

    state.userJoinRoomInput = roomInput;
    state.userJoinNameInput = nameInput;

    // Validate room / link existence
    const check = resolveAndValidateRoom(roomInput);
    if (!check.valid) {
      state.userJoinError = check.errorReason || `Sala sau link-ul introdus nu există sau nu este valabil. Vă rugăm să verificați codul sălii primit de la organizator.`;
      showToast(`⛔ ${state.userJoinError}`);
      renderApp();
      return;
    }

    const isAuthedAdmin = Boolean(state.isAdminLoggedIn && localStorage.getItem('vorbello_admin_authenticated') === 'true');

    // PIN check
    if (check.requiresPin && check.pinCode && !isAuthedAdmin) {
      state.pendingPinVerification = {
        roomSlug: check.resolvedSlug!,
        roomName: check.resolvedName || check.resolvedSlug!,
        userName: nameInput,
        correctPin: check.pinCode,
        camEnabled: state.userJoinCamEnabled,
        micEnabled: state.userJoinMicEnabled,
      };
      state.pinError = null;
      state.activeModal = 'pin-verify';
      renderApp();
      return;
    }

    launchConference(
      check.resolvedSlug!,
      check.resolvedName,
      nameInput,
      state.userJoinCamEnabled,
      state.userJoinMicEnabled
    );
  });

  // Common modal handlers
  attachModalCommonHandlers();

  // Start live webcam preview on mount if camera enabled
  if (state.userJoinCamEnabled) {
    startPrejoinPreview();
  }

  // Real connection health check to verify network and signaling before entering room
  updatePrejoinConnectionStatus();
}

function renderAdminPortalHtml(): string {
  return `
    <div class="app-container">
      <!-- Sidebar Navigation -->
      <aside class="sidebar">
        <div class="sidebar-header">
          <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" style="width: 48px; height: 48px; object-fit: contain; filter: drop-shadow(0 0 14px rgba(56, 189, 248, 0.6)); flex-shrink: 0;" />
          <div>
            <div class="brand-name gradient-text" style="font-size: 1.45rem; font-weight: 800; letter-spacing: -0.02em; line-height: 1.2;">Vorbello</div>
            <div style="font-size: 0.72rem; color: var(--text-muted); font-weight: 500;">Videoconferință Privată</div>
          </div>
        </div>

        <nav class="sidebar-nav">
          <a class="nav-item ${state.currentTab === 'rooms' ? 'active' : ''}" data-tab="rooms">
            <span>📹</span> Camere Active (${(state.appMode === 'production' ? state.prodRooms : state.rooms).length})
          </a>
          <a class="nav-item ${state.currentTab === 'meetings' ? 'active' : ''}" data-tab="meetings">
            <span>📅</span> Întâlniri Programate (${(state.appMode === 'production' ? state.prodMeetings : state.meetings).length})
          </a>
          <a class="nav-item ${state.currentTab === 'recordings' ? 'active' : ''}" data-tab="recordings">
            <span>🎥</span> Înregistrări (${state.recordings.length})
          </a>
          <a class="nav-item ${state.currentTab === 'attendance' ? 'active' : ''}" data-tab="attendance">
            <span>📋</span> Prezență Întâlniri (${groupAttendanceSessions(state.attendanceRecords).length})
          </a>
          <a class="nav-item ${state.currentTab === 'invitations' ? 'active' : ''}" data-tab="invitations">
            <span>✉️</span> Invitații & Acces (${(state.appMode === 'production' ? state.prodInvitations : state.invitations).length})
          </a>
          <a class="nav-item ${state.currentTab === 'members' ? 'active' : ''}" data-tab="members">
            <span>👥</span> Membri & Roluri (${(state.appMode === 'production' ? state.prodMembers : state.members).length})
          </a>
          <a class="nav-item ${state.currentTab === 'audit' ? 'active' : ''}" data-tab="audit">
            <span>🛡️</span> Jurnal de Audit
          </a>
        </nav>

        <div style="padding: 1rem 0.5rem; border-top: 1px solid var(--border-subtle);">
          <div style="font-size: 0.7rem; color: var(--text-muted); text-transform: uppercase;">Organizație Activă</div>
          <div style="font-weight: 600; font-size: 0.875rem; margin-top: 0.2rem;">${state.user.organizationName}</div>
        </div>
      </aside>

      <!-- Main Content Area -->
      <main class="main-content">
        ${!isAppInstalled ? `
          <div id="pwa-top-install-banner-admin" class="pwa-top-install-banner" style="margin: -1.5rem -1.5rem 1.5rem -1.5rem; border-radius: 0;">
            <div class="pwa-top-banner-brand">
              <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" class="pwa-top-banner-logo" />
              <div class="pwa-top-banner-info">
                <div class="pwa-top-banner-title">Instalare Aplicația Vorbello</div>
                <div class="pwa-top-banner-desc">Instalează aplicația pe ecranul principal pentru acces rapid</div>
              </div>
            </div>
            <div class="pwa-top-banner-actions">
              <button type="button" id="btn-top-banner-install-admin" class="pwa-top-banner-btn-install">
                <span>📲</span>
                <span>Instalează</span>
              </button>
              <button type="button" id="btn-top-banner-dismiss-admin" class="pwa-top-banner-btn-dismiss" title="Închide" style="background:none; border:none; color:#94a3b8; font-size:1.4rem; line-height:1; cursor:pointer; padding:4px 8px; margin-left:4px;">
                &times;
              </button>
            </div>
          </div>
        ` : ''}
        <header class="topbar">
          <div class="topbar-mode-badge" style="display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0;">
            ${(state.user.role === 'CLIENT_ADMIN' || state.clientViewActive) ? `
              <span style="display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.78rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.12); padding: 4px 10px; border-radius: 20px; border: 1px solid rgba(56, 189, 248, 0.28); white-space: nowrap;">
                <span style="width: 7px; height: 7px; border-radius: 50%; background: #38bdf8; box-shadow: 0 0 8px #38bdf8;"></span>
                👑 ${state.organizationLicense.planName}
              </span>
            ` : `
              <span style="display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.78rem; font-weight: 700; color: #34d399; background: rgba(16, 185, 129, 0.12); padding: 4px 10px; border-radius: 20px; border: 1px solid rgba(16, 185, 129, 0.28); white-space: nowrap;">
                <span style="width: 7px; height: 7px; border-radius: 50%; background: #34d399; box-shadow: 0 0 8px #34d399;"></span>
                🚀 Server Activ
              </span>
            `}
          </div>

          <div class="topbar-right">
            <!-- Quick Client View / Super Admin View Switcher (for Admins) -->
            ${(state.isAdminLoggedIn || state.user.role === 'ADMIN') ? `
              <button id="btn-toggle-client-admin-view" class="btn btn-secondary btn-sm" style="padding: 4px 9px; font-size: 0.75rem; border-color: rgba(56, 189, 248, 0.4); color: #38bdf8; font-weight: 700; background: rgba(56, 189, 248, 0.12); display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; border-radius: 8px;" title="Comută între vederea tehnică de Super-Admin și vederea curată de Client fără server">
                ${(state.user.role === 'CLIENT_ADMIN' || state.clientViewActive) ? '🛡️ Super-Admin' : '🏢 Vedere Client'}
              </button>
            ` : ''}

            <!-- Version Indicator Button -->
            <button id="btn-topbar-version" title="Panou Versiune Vorbello & Diagnostic Sistem" style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 9px; background: rgba(56, 189, 248, 0.12); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 20px; color: #38bdf8; font-size: 0.75rem; font-weight: 700; cursor: pointer; transition: all 0.2s; white-space: nowrap;">
              <span style="display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: #38bdf8; box-shadow: 0 0 6px #38bdf8;"></span>
              <span>v${APP_VERSION}</span>
            </button>

            <!-- Switch to User Portal Button -->
            <button class="btn btn-secondary btn-sm" id="btn-switch-to-user" title="Comutare în modul Utilizator (Portal Participanți)" style="display: inline-flex; align-items: center; gap: 0.35rem; padding: 4px 9px; font-size: 0.75rem; border-radius: 8px; border-color: rgba(99, 102, 241, 0.4); background: rgba(99, 102, 241, 0.12); color: #a5b4fc; font-weight: 600; white-space: nowrap;">
              <span>👤</span> <span class="topbar-btn-text-full">Mod Utilizator</span><span class="topbar-btn-text-short">Utilizator</span>
            </button>

            <!-- Admin Logout Button -->
            <button class="btn btn-secondary btn-sm" id="btn-admin-logout" title="Deconectare din contul de Administrator" style="display: inline-flex; align-items: center; gap: 0.35rem; padding: 4px 9px; font-size: 0.75rem; border-radius: 8px; border-color: rgba(239, 68, 68, 0.4); background: rgba(239, 68, 68, 0.12); color: #fca5a5; font-weight: 600; cursor: pointer; white-space: nowrap;">
              <span>🚪</span> <span class="topbar-btn-text-full">Deconectare</span><span class="topbar-btn-text-short">Ieșire</span>
            </button>

            <!-- Server Settings Button (Hidden for Clients) -->
            ${!(state.user.role === 'CLIENT_ADMIN' || state.clientViewActive) ? `
              <button class="btn btn-secondary btn-sm" id="btn-open-vps-modal" title="Configurare conexiune server și ghid activare" style="padding: 4px 9px; font-size: 0.75rem; display: inline-flex; align-items: center; gap: 0.35rem; border-radius: 8px; white-space: nowrap;">
                <span>⚙️</span> <span class="topbar-btn-text-full">Configurare Server</span><span class="topbar-btn-text-short">Server</span>
              </button>
            ` : ''}

            <!-- User Badge (Clickable to Edit Profile & Set Master/Admin) -->
            <div class="user-badge" id="btn-open-profile" title="Clic pentru a edita profilul și a seta rolul de Master / Admin pe acest computer" style="cursor: pointer; transition: all 0.2s; border: 1px solid rgba(255, 255, 255, 0.15); padding: 3px 8px; gap: 0.5rem; white-space: nowrap;">
              <div class="user-avatar" style="position: relative; width: 28px; height: 28px; font-size: 0.8rem;">
                ${state.user.name.charAt(0)}
                <span style="position: absolute; bottom: -1px; right: -1px; width: 7px; height: 7px; border-radius: 50%; background: #10b981; border: 1.5px solid var(--bg-card);"></span>
              </div>
              <div style="line-height: 1.2;">
                <div style="font-size: 0.78rem; font-weight: 600; display: flex; align-items: center; gap: 0.25rem;">
                  <span>${state.user.name}</span>
                  <span style="font-size: 0.65rem; opacity: 0.7;">✏️</span>
                </div>
                <div class="user-email" style="font-size: 0.65rem; color: var(--text-muted);">${state.user.email}</div>
              </div>
              <span class="role-pill" style="font-size: 0.68rem; padding: 2px 6px; ${state.user.role === 'ADMIN' ? 'background: rgba(245, 158, 11, 0.2); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.4);' : (state.user.role === 'CLIENT_ADMIN' ? 'background: rgba(56, 189, 248, 0.2); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.4);' : '')}">${state.user.role === 'CLIENT_ADMIN' ? 'CLIENT' : state.user.role}</span>
            </div>
          </div>
        </header>

        <!-- Notification Toast -->
        ${state.toastMessage ? `
          <div style="position: fixed; top: 20px; right: 20px; z-index: 999; background: var(--accent-gradient); color: #fff; padding: 0.85rem 1.5rem; border-radius: 10px; font-weight: 600; box-shadow: var(--accent-glow);">
            ${state.toastMessage}
          </div>
        ` : ''}

        <div class="page-body">
          <!-- Suspended License Lockout Banner -->
          ${state.organizationLicense.status === 'SUSPENDED' ? `
            <div style="background: linear-gradient(90deg, rgba(239, 68, 68, 0.18), rgba(153, 27, 27, 0.28)); border: 1px solid rgba(239, 68, 68, 0.5); border-radius: 12px; padding: 0.95rem 1.25rem; margin-bottom: 1.25rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;">
              <div style="display: flex; align-items: center; gap: 0.85rem;">
                <span style="font-size: 1.6rem;">⚠️</span>
                <div>
                  <div style="font-weight: 800; color: #fca5a5; font-size: 0.95rem;">
                    Abonament Vorbello Suspendat sau Neplătit (Licență: <code style="color: #fde68a;">${state.organizationLicense.licenseKey}</code>)
                  </div>
                  <div style="font-size: 0.8rem; color: #fecaca; margin-top: 2px;">
                    Găzduirea și inițierea de noi conferințe video sunt blocate temporar până la confirmarea plății pe vorbello.eu.
                  </div>
                </div>
              </div>
              <div style="display: flex; gap: 0.5rem; align-items: center;">
                <a href="https://vorbello.eu" target="_blank" class="btn btn-primary btn-sm" style="background: linear-gradient(135deg, #ef4444, #dc2626); border-color: #ef4444; font-weight: 700; text-decoration: none; padding: 0.5rem 1rem;">
                  💳 Reînnoiește pe vorbello.eu
                </a>
                ${state.isAdminLoggedIn ? `
                  <button type="button" class="btn btn-secondary btn-sm btn-quick-resume-lic" style="font-size: 0.75rem; border-color: rgba(16, 185, 129, 0.4); color: #6ee7b7; padding: 0.5rem 0.8rem;">
                    ▶️ Activează (Admin)
                  </button>
                ` : ''}
              </div>
            </div>
          ` : ''}

          <!-- Client Plan Banner vs Super-Admin VPS Banner -->
          ${(state.user.role === 'CLIENT_ADMIN' || state.clientViewActive) ? `
            <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 12px; padding: 0.85rem 1.25rem; margin-bottom: 1.25rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;">
              <div style="display: flex; align-items: center; gap: 0.85rem;">
                <div style="width: 40px; height: 40px; border-radius: 10px; background: rgba(56, 189, 248, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.3rem; border: 1px solid rgba(56, 189, 248, 0.35);">
                  👑
                </div>
                <div>
                  <div style="font-weight: 700; color: #fff; font-size: 0.95rem; display: flex; align-items: center; gap: 0.5rem;">
                    <span>${state.organizationLicense.planName}</span>
                    <span style="font-size: 0.675rem; background: ${state.organizationLicense.status === 'ACTIVE' ? '#059669' : '#dc2626'}; color: #fff; padding: 2px 7px; border-radius: 4px; font-weight: 700;">
                      ${state.organizationLicense.status === 'ACTIVE' ? 'ABONAMENT ACTIV' : 'SUSPENDAT'}
                    </span>
                  </div>
                  <div style="font-size: 0.8rem; color: #94a3b8; margin-top: 2px;">
                    Organizație: <strong style="color: #cbd5e1;">${state.user.organizationName}</strong> • Capacitate: <strong style="color: #38bdf8;">${state.organizationLicense.maxParticipantsPerRoom} participanți / conferință</strong> • Licență: <code style="color: #fde68a;">${state.organizationLicense.licenseKey}</code>
                  </div>
                </div>
              </div>
              <div style="display: flex; align-items: center; gap: 0.65rem;">
                <span style="font-size: 0.8rem; color: #94a3b8;">📅 Valabil până la: <strong style="color: #cbd5e1;">${state.organizationLicense.expiresAt}</strong></span>
              </div>
            </div>
          ` : (state.appMode === 'production' ? `
            <div style="background: linear-gradient(90deg, rgba(16, 185, 129, 0.12), rgba(6, 78, 59, 0.22)); border: 1px solid rgba(16, 185, 129, 0.35); border-radius: 12px; padding: 0.85rem 1.25rem; margin-bottom: 1.25rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;">
              <div style="display: flex; align-items: center; gap: 0.85rem;">
                <div style="width: 38px; height: 38px; border-radius: 10px; background: rgba(16, 185, 129, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.25rem; border: 1px solid rgba(16, 185, 129, 0.4);">
                  🌐
                </div>
                <div>
                  <div style="font-weight: 700; color: #6ee7b7; font-size: 0.95rem; display: flex; align-items: center; gap: 0.5rem;">
                    <span>Infrastructură Dedicată Vorbello Cloud</span>
                    <span style="font-size: 0.675rem; background: #059669; color: #fff; padding: 2px 7px; border-radius: 4px; font-weight: 700;">CONECTAT LIVE</span>
                  </div>
                  <div style="font-size: 0.8rem; color: #94a3b8; margin-top: 2px;">
                    Punct Conectare: <code style="color: #6ee7b7; background: rgba(0,0,0,0.3); padding: 1px 5px; border-radius: 4px;">${state.vpsConfig.apiUrl}</code> • Nod Audio-Video: <code style="color: #6ee7b7; background: rgba(0,0,0,0.3); padding: 1px 5px; border-radius: 4px;">${state.vpsConfig.livekitUrl}</code>
                  </div>
                </div>
              </div>
              <div style="display: flex; gap: 0.5rem;">
                <button class="btn btn-secondary btn-sm" id="btn-quick-config-vps" style="font-size: 0.8rem; padding: 0.45rem 0.9rem; display: flex; align-items: center; gap: 0.35rem;">
                  <span>⚙️</span> Infrastructură Server
                </button>
              </div>
            </div>
          ` : `
            <div style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.22); border-radius: 12px; padding: 0.75rem 1.25rem; margin-bottom: 1.25rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <span style="font-size: 1.3rem;">🧪</span>
                <div>
                  <div style="font-weight: 600; color: #fde68a; font-size: 0.9rem;">Rulați în Mod Demo (Simulare Locală)</div>
                  <div style="font-size: 0.775rem; color: #94a3b8;">Camerele, conferințele și participanții sunt simulați pentru testare fără a necesita un server VPS activ.</div>
                </div>
              </div>
              <button class="btn btn-secondary btn-sm" id="btn-switch-to-prod-banner" style="font-size: 0.8rem; padding: 0.45rem 0.95rem; border-color: rgba(245, 158, 11, 0.4); color: #fde68a; font-weight: 600; cursor: pointer;">
                🚀 Treci pe Modul Producție (VPS) ➔
              </button>
            </div>
          `)}

          ${renderTabContent()}
        </div>
      </main>
    </div>

    <!-- Modals -->
    ${renderModal()}
  `;
}

function renderApp() {
  const root = document.getElementById('app');
  if (!root || document.body.classList.contains('in-conference')) return;

  if (state.appRoleView === 'user') {
    root.innerHTML = renderUserPortalHtml();
    attachUserPortalEventHandlers();
  } else {
    stopPrejoinPreview();
    root.innerHTML = renderAdminPortalHtml();
    attachEventHandlers();
  }
  if (state.appMode === 'production' && !centralSyncResult.success) {
    const notice = document.createElement('div'); notice.setAttribute('role','status'); notice.style.cssText='padding:12px 18px;border-bottom:1px solid #f59e0b;background:#292314;color:#fde68a;font-size:13px';
    notice.textContent = 'Sincronizare date: ' + centralSyncResult.message + (centralSyncResult.pending ? ' (' + centralSyncResult.pending + ' operații în așteptare)' : '');
    root.prepend(notice);
  }
}

function renderTabContent() {
  switch (state.currentTab) {
    case 'rooms':
      return renderRoomsTab();
    case 'meetings':
      return renderMeetingsTab();
    case 'recordings':
      return renderRecordingsTab();
    case 'attendance':
      return renderAttendanceTab();
    case 'invitations':
      return renderInvitationsTab();
    case 'members':
      return renderMembersTab();
    case 'audit':
      return renderAuditTab();
    default:
      return '';
  }
}

function renderPaginationControls(currentPage: number, totalItems: number, pageSize: number = 10, pageType: string): string {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  if (totalPages <= 1) return '';

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  let pageButtons = '';
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
      pageButtons += `
        <button class="btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-secondary'} btn-pagination-page" data-page-type="${pageType}" data-page="${i}" style="min-width: 32px; padding: 0.35rem 0.6rem; font-weight: 700; ${i === currentPage ? 'box-shadow: 0 0 10px rgba(99,102,241,0.4);' : ''}">
          ${i}
        </button>
      `;
    } else if (i === currentPage - 2 || i === currentPage + 2) {
      pageButtons += `<span style="color: #64748b; padding: 0 4px; font-weight: 700;">…</span>`;
    }
  }

  return `
    <div class="pagination-bar" style="display: flex; justify-content: space-between; align-items: center; margin-top: 1.25rem; padding: 0.85rem 1rem; background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; flex-wrap: wrap; gap: 0.75rem;">
      <div style="font-size: 0.8rem; color: #94a3b8;">
        Afișare <strong style="color: #f1f5f9;">${startItem} - ${endItem}</strong> din <strong style="color: #38bdf8;">${totalItems}</strong> înregistrări (pagina ${currentPage} din ${totalPages})
      </div>
      <div style="display: flex; align-items: center; gap: 0.4rem;">
        <button class="btn btn-secondary btn-sm btn-pagination-prev" data-page-type="${pageType}" data-target-page="${currentPage - 1}" ${currentPage <= 1 ? 'disabled style="opacity: 0.4; cursor: not-allowed;"' : ''}>
          ◀ Înapoi
        </button>
        ${pageButtons}
        <button class="btn btn-secondary btn-sm btn-pagination-next" data-page-type="${pageType}" data-target-page="${currentPage + 1}" ${currentPage >= totalPages ? 'disabled style="opacity: 0.4; cursor: not-allowed;"' : ''}>
          Următoarea ▶
        </button>
      </div>
    </div>
  `;
}

// 1. Rooms Tab View
function renderRoomsTab() {
  const currentRooms = state.appMode === 'production' ? state.prodRooms : state.rooms;
  const totalRooms = currentRooms.length;
  const pageSize = 10;
  const totalPages = Math.ceil(totalRooms / pageSize) || 1;
  if (state.roomsPage > totalPages) state.roomsPage = totalPages;
  if (state.roomsPage < 1) state.roomsPage = 1;
  const pageRooms = currentRooms.slice((state.roomsPage - 1) * pageSize, state.roomsPage * pageSize);

  return `
    <div class="page-header">
      <div class="page-header-title-box">
        <h1 class="page-title">${state.appMode === 'production' ? 'Săli de Conferință Private' : 'Camere de Conferință Vorbello'}</h1>
        <p class="page-subtitle">${state.appMode === 'production' ? 'Spații audio-video securizate de înaltă performanță pentru conferințe și webinarii private.' : 'Gestionați spațiile de comunicare audio-video private și interne.'}</p>
      </div>
      <div class="page-header-actions">
        <button class="btn btn-secondary btn-sm" id="btn-reset-default-rooms" title="Restaurează lista de camere la valorile implicite" style="display: flex; align-items: center; gap: 0.35rem;">
          <span>↺</span> Resetează Implicite
        </button>
        <button class="btn btn-primary btn-sm" id="btn-open-create-room" style="display: flex; align-items: center; gap: 0.35rem;">
          <span>+</span> Creează Cameră Nouă
        </button>
      </div>
    </div>

    ${currentRooms.length === 0 ? `
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px dashed rgba(255, 255, 255, 0.15); border-radius: 16px; padding: 3.5rem 2rem; text-align: center; max-width: 600px; margin: 2rem auto;">
        <div style="font-size: 2.5rem; margin-bottom: 0.75rem;">📹</div>
        <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem;">Nu există camere create în acest mod</h3>
        <p style="color: var(--text-muted); font-size: 0.875rem; margin-bottom: 1.5rem; line-height: 1.5;">
          Puteți crea o sală nouă personalizată sau puteți restaura camerele implicite predefinite pentru a începe imediat conferința pe server.
        </p>
        <div style="display: flex; justify-content: center; gap: 0.75rem; flex-wrap: wrap;">
          <button class="btn btn-primary" id="btn-empty-create-room"><span>+</span> Creează Cameră Nouă</button>
          <button class="btn btn-secondary" id="btn-empty-reset-rooms"><span>↺</span> Încarcă Camere Implicite</button>
        </div>
      </div>
    ` : `
      <div class="rooms-stack-list">
        ${pageRooms.map(room => {
          const typeBadge = (room.type === 'meeting')
            ? '<span style="background: rgba(168, 85, 247, 0.2); color: #c084fc; border: 1px solid rgba(168, 85, 247, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;">📅 Ședință</span>'
            : '<span style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;">🏢 Sală 24/7</span>';

          const pinBadge = room.pinCode
            ? `<span style="background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Securizată cu cod PIN (${escapeHtml(room.pinCode)})">🔑 PIN Activ</span>`
            : '';

          const waitingBadge = room.waitingRoomEnabled
            ? '<span style="background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Sală de Așteptare activată">🛡️ Waiting Room</span>'
            : '';

          const autoRecordBadge = room.autoRecord
            ? '<span style="background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Înregistrare automată la conectare">🔴 Auto-Record</span>'
            : '';

          const muteBadge = room.muteOnEntry
            ? '<span style="background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Microfon oprit automat la intrare">🎙️ Mute</span>'
            : '';

          const camBadge = room.cameraOffOnEntry
            ? '<span style="background: rgba(148, 163, 184, 0.15); color: #cbd5e1; border: 1px solid rgba(148, 163, 184, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Cameră oprită automat la intrare">📷 Cam Oprită</span>'
            : '';

          const inviteBadge = (room.allowGuestInvites === false)
            ? '<span style="background: rgba(129, 140, 248, 0.15); color: #a5b4fc; border: 1px solid rgba(129, 140, 248, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Doar Gazda/Adminul poate genera și trimite link de invitație">🔒 Doar Gazda Invită</span>'
            : '';

          const attendanceBadge = (room.attendanceTracking !== false)
            ? '<span style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.35); padding: 2px 7px; border-radius: 6px; font-size: 0.7rem; font-weight: 700;" title="Prezență automată & verificare cvorum active">📋 Cvorum Activ</span>'
            : '';

          return `
          <div class="room-card-wide">
            <div class="room-wide-header">
              <div style="flex: 1; min-width: 280px;">
                <div style="display: flex; align-items: center; gap: 0.65rem; flex-wrap: wrap;">
                  <h3 class="room-card-title" style="margin: 0; font-size: 1.18rem; color: #fff;">${room.name}</h3>
                  ${typeBadge}
                  <span class="room-badge ${room.visibility}">${room.visibility === 'internal' ? 'Intern' : 'Privat'}</span>
                  ${pinBadge}
                  ${waitingBadge}
                  ${autoRecordBadge}
                  ${muteBadge}
                  ${camBadge}
                  ${inviteBadge}
                  ${attendanceBadge}
                </div>
                <p class="room-desc" style="margin-top: 0.45rem; font-size: 0.86rem; color: #94a3b8; line-height: 1.45;">${room.description || 'Fără descriere specificată.'}</p>
              </div>

              <div style="text-align: right; font-size: 0.78rem; color: var(--text-muted); flex-shrink: 0; line-height: 1.5;">
                <div>Slug LiveKit: <code style="color: #fde68a; font-family: monospace; font-size: 0.8rem;">${room.slug}</code></div>
                <div>Video implicit: <strong>${room.defaultVideoEnabled ? 'Pornit' : 'Oprit'}</strong>${state.appMode === 'production' ? ' • Server: <strong style="color: #6ee7b7;">Cloud Securizat</strong>' : ''}</div>
              </div>
            </div>

            <div class="room-wide-footer">
              <div class="participant-count" style="display: flex; align-items: center; gap: 0.75rem; font-size: 0.85rem;">
                <span>👥 <strong>${room.activeParticipants}</strong> / ${room.maxParticipants} max</span>
                ${room.pinCode ? `
                  <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(245, 158, 11, 0.12); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.3); padding: 2px 8px; border-radius: 6px; font-size: 0.76rem; font-weight: 700;">
                    <span>🔑 PIN:</span> <code style="font-family: monospace; letter-spacing: 1px;">${room.pinCode}</code>
                  </span>
                ` : ''}
              </div>

              <div class="room-wide-actions">
                <button class="btn btn-secondary btn-sm btn-view-room-attendance" data-room-slug="${room.slug}" title="Vezi registrul de prezență al acestei săli">
                  📋 Prezență
                </button>
                <button class="btn btn-secondary btn-sm btn-invite-room" data-room-id="${room.id}" title="Generează invitație">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                  Invită
                </button>
                <button class="btn btn-secondary btn-sm btn-edit-room" data-room-id="${room.id}" title="Editează setările camerei">
                  ✏️ Edit
                </button>
                <button class="btn btn-danger btn-sm btn-delete-room" data-room-id="${room.id}" title="Șterge definitiv camera" style="padding: 0.35rem 0.65rem;">
                  🗑️
                </button>
                <button class="btn btn-primary btn-sm btn-join-room" data-room-id="${room.id}">
                  🚀 Conectare
                </button>
              </div>
            </div>
          </div>
        `;
        }).join('')}
      </div>
      ${renderPaginationControls(state.roomsPage, totalRooms, pageSize, 'rooms')}
    `}
  `;
}


// 2. Meetings Tab View (Google Meet Style Calendar & Full-Width Agenda Cards)
function renderMeetingsTab() {
  const currentMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
  const tz = state.selectedTimezone;
  const now = new Date();
  const todayIso = getDateKeyInTimezone(now, tz);

  // 1. Compute 7-day calendar strip (Monday to Sunday) based on week offset
  // Use noon (12:00) to ensure zero UTC daylight savings shifts
  const targetDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (state.meetingCalendarWeekOffset * 7), 12, 0, 0);
  const dayOfWeek = (targetDate.getDay() + 6) % 7; // 0 = Mon, 6 = Sun
  const monday = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() - dayOfWeek, 12, 0, 0);

  const dayNamesRo = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];
  const monthNamesRo = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Noi', 'Dec'];

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i, 12, 0, 0);
    const iso = getDateKeyInTimezone(d, tz);
    const dayMeetings = currentMeetings.filter(m => {
      try {
        const mIso = getDateKeyInTimezone(m.startsAt, m.timezone || tz);
        return mIso === iso;
      } catch {
        return false;
      }
    });
    weekDays.push({
      iso,
      dayName: dayNamesRo[i],
      dayNum: d.getDate(),
      monthName: monthNamesRo[d.getMonth()],
      isToday: iso === todayIso,
      meetingsCount: dayMeetings.length,
      isSelected: state.meetingDateFilter === iso,
    });
  }

  // Week range label (e.g. "19 Oct — 25 Oct 2026")
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6, 12, 0, 0);
  const weekRangeLabel = `${monday.getDate()} ${monthNamesRo[monday.getMonth()]} — ${sunday.getDate()} ${monthNamesRo[sunday.getMonth()]} ${sunday.getFullYear()}`;

  // 2. Classify into Upcoming vs Past
  const upcomingMeetings = currentMeetings.filter(m => !isMeetingPast(m, tz));
  const pastMeetings = currentMeetings.filter(m => isMeetingPast(m, tz));

  // Determine meetings to display based on active subtab
  let baseMeetings = currentMeetings;
  if (state.meetingSubTab === 'upcoming') {
    baseMeetings = upcomingMeetings;
  } else if (state.meetingSubTab === 'past') {
    baseMeetings = pastMeetings;
  }

  let filteredMeetings = baseMeetings;
  if (state.meetingDateFilter) {
    filteredMeetings = currentMeetings.filter(m => {
      try {
        const mIso = getDateKeyInTimezone(m.startsAt, m.timezone || tz);
        return mIso === state.meetingDateFilter;
      } catch {
        return false;
      }
    });
  }

  // Sort meetings:
  // For 'past': newest past meetings first (descending)
  // For 'upcoming' and 'all': LIVE first, then startsAt ascending
  filteredMeetings = [...filteredMeetings].sort((a, b) => {
    if (state.meetingSubTab === 'past' && !state.meetingDateFilter) {
      return new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime();
    }
    if (a.status === 'live' && b.status !== 'live') return -1;
    if (b.status === 'live' && a.status !== 'live') return 1;
    return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
  });

  const totalMeetings = filteredMeetings.length;
  const pageSize = 10;
  const totalPages = Math.ceil(totalMeetings / pageSize) || 1;
  if (state.meetingsPage > totalPages) state.meetingsPage = totalPages;
  if (state.meetingsPage < 1) state.meetingsPage = 1;
  const pageMeetings = filteredMeetings.slice((state.meetingsPage - 1) * pageSize, state.meetingsPage * pageSize);

  // 3. Find spotlight meeting (Live or next genuinely upcoming meeting across agenda)
  // Will NEVER show expired/past meetings in the spotlight
  const spotlightMeeting: MeetingItem | null = (state.meetingSubTab === 'past') ? null : (
    currentMeetings.find(m => m.status === 'live') ||
    [...upcomingMeetings]
      .filter(m => m.status === 'scheduled')
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())[0] || null
  );

  return `
    <div class="meetings-calendar-wrapper">
      <!-- Page Header -->
      <div class="page-header" style="margin-bottom: 0.75rem;">
        <div class="page-header-title-box">
          <h1 class="page-title">${state.appMode === 'production' ? 'Întâlniri Programate (Cloud Securizat)' : 'Întâlniri Programate (Mod Demo)'}</h1>
          <p class="page-subtitle">${state.appMode === 'production' ? 'Conferințe planificate pe infrastructura cloud dedicată Vorbello.' : 'Planificați și coordonați conferințele viitoare cu acces securizat.'}</p>
        </div>
        <div class="page-header-actions">
          <button class="btn btn-primary btn-sm" id="btn-open-schedule-meeting" style="display: inline-flex; align-items: center; gap: 6px; font-weight: 700;">
            <span>+</span> Programează Întâlnire
          </button>
        </div>
      </div>

      <!-- Google Meet Style Calendar Strip -->
      <div class="calendar-top-bar">
        <div class="calendar-nav-header">
          <div class="calendar-nav-title" style="display: flex; align-items: center; gap: 0.65rem; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 6px;">
              <span style="font-size: 1.2rem;">📅</span>
              <span style="font-weight: 700;">${weekRangeLabel}</span>
            </div>
            ${state.meetingDateFilter ? `
              <span style="font-size: 0.78rem; font-weight: 600; background: rgba(99, 102, 241, 0.2); color: #a5b4fc; border: 1px solid rgba(99, 102, 241, 0.4); padding: 2px 8px; border-radius: 6px;">
                Filtru activ: ${state.meetingDateFilter}
              </span>
            ` : ''}

            <div style="display: inline-flex; align-items: center; gap: 5px; margin-left: 0.5rem; background: rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.12); padding: 2px 8px; border-radius: 8px;">
              <span style="font-size: 0.75rem; color: #94a3b8;">🌐 Fus Orar:</span>
              <select id="select-global-timezone" class="form-select" style="padding: 2px 6px; font-size: 0.75rem; background: transparent; border: none; color: #38bdf8; font-weight: 700; cursor: pointer; outline: none;">
                ${TIMEZONE_OPTIONS.map(t => `<option value="${t.value}" ${t.value === tz ? 'selected' : ''}>${t.label}</option>`).join('')}
              </select>
            </div>
          </div>

          <div class="calendar-nav-controls">
            <button type="button" class="btn btn-secondary btn-sm btn-cal-prev-week" title="Săptămâna anterioară" style="padding: 0.45rem 0.75rem;">
              ◀
            </button>
            <button type="button" class="btn btn-secondary btn-sm btn-cal-today" title="Mergi la săptămâna curentă" style="padding: 0.45rem 0.85rem; font-weight: 600;">
              Astăzi
            </button>
            <button type="button" class="btn btn-secondary btn-sm btn-cal-next-week" title="Săptămâna următoare" style="padding: 0.45rem 0.75rem;">
              ▶
            </button>
            <button type="button" class="btn btn-secondary btn-sm btn-cal-clear-filter" style="padding: 0.45rem 0.85rem; margin-left: 0.35rem; ${state.meetingDateFilter ? 'background: rgba(99, 102, 241, 0.25); border-color: #818cf8; color: #fff;' : ''}">
              Toate (${currentMeetings.length})
            </button>
          </div>
        </div>

        <div class="calendar-week-strip">
          ${weekDays.map(d => `
            <div class="calendar-day-card ${d.isToday ? 'today' : ''} ${d.isSelected ? 'selected' : ''}" data-day-iso="${d.iso}">
              <span class="calendar-day-name">${d.dayName}${d.isToday ? ' (Azi)' : ''}</span>
              <span class="calendar-day-number">${d.dayNum}</span>
              <span class="calendar-day-badge ${d.meetingsCount > 0 ? '' : 'empty'}">
                ● ${d.meetingsCount} ${d.meetingsCount === 1 ? 'ședință' : 'ședințe'}
              </span>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Sub-Tabs: Viitoare (Upcoming) / Istoric (Past) / Toate (All) -->
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-top: 1.25rem; margin-bottom: 0.65rem; flex-wrap: wrap;">
        <div style="display: inline-flex; align-items: center; gap: 6px; background: rgba(0, 0, 0, 0.35); border: 1px solid rgba(255, 255, 255, 0.1); padding: 4px 6px; border-radius: 12px;">
          <button type="button" class="btn btn-sm btn-subtab-meetings ${state.meetingSubTab === 'upcoming' ? 'btn-primary' : 'btn-secondary'}" data-subtab="upcoming" style="border-radius: 8px; padding: 0.45rem 1.05rem; font-weight: 700; font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; ${state.meetingSubTab === 'upcoming' ? 'background: linear-gradient(135deg, #6366f1, #3b82f6); box-shadow: 0 2px 10px rgba(99, 102, 241, 0.35);' : ''}">
            <span>⏳</span> Viitoare (${upcomingMeetings.length})
          </button>
          <button type="button" class="btn btn-sm btn-subtab-meetings ${state.meetingSubTab === 'past' ? 'btn-primary' : 'btn-secondary'}" data-subtab="past" style="border-radius: 8px; padding: 0.45rem 1.05rem; font-weight: 700; font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; ${state.meetingSubTab === 'past' ? 'background: linear-gradient(135deg, #6366f1, #3b82f6); box-shadow: 0 2px 10px rgba(99, 102, 241, 0.35);' : ''}">
            <span>📁</span> Istoric / Trecute (${pastMeetings.length})
          </button>
          <button type="button" class="btn btn-sm btn-subtab-meetings ${state.meetingSubTab === 'all' ? 'btn-primary' : 'btn-secondary'}" data-subtab="all" style="border-radius: 8px; padding: 0.45rem 1.05rem; font-weight: 700; font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; ${state.meetingSubTab === 'all' ? 'background: linear-gradient(135deg, #6366f1, #3b82f6); box-shadow: 0 2px 10px rgba(99, 102, 241, 0.35);' : ''}">
            <span>📋</span> Toate (${currentMeetings.length})
          </button>
        </div>

        ${state.meetingDateFilter ? `
          <button type="button" class="btn btn-secondary btn-sm btn-cal-clear-filter" style="display: inline-flex; align-items: center; gap: 5px; font-size: 0.8rem; padding: 0.45rem 0.85rem; border-color: rgba(99, 102, 241, 0.4); color: #a5b4fc;">
            <span>✕</span> Șterge filtrul de zi (${state.meetingDateFilter})
          </button>
        ` : ''}
      </div>

      <!-- Spotlight: Next Up / Live Meeting Banner -->
      ${spotlightMeeting ? `
        <div class="next-meeting-spotlight">
          <div style="display: flex; align-items: center; gap: 1.1rem; min-width: 0;">
            <div style="font-size: 2rem; background: ${spotlightMeeting.status === 'live' ? 'rgba(239, 68, 68, 0.25)' : 'rgba(56, 189, 248, 0.2)'}; border: 1px solid ${spotlightMeeting.status === 'live' ? 'rgba(239, 68, 68, 0.5)' : 'rgba(56, 189, 248, 0.4)'}; width: 52px; height: 52px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              ${spotlightMeeting.status === 'live' ? '🔴' : '⏳'}
            </div>
            <div style="min-width: 0;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; color: ${spotlightMeeting.status === 'live' ? '#f87171' : '#38bdf8'};">
                  ${spotlightMeeting.status === 'live' ? 'În Desfășurare Acum' : 'Următoarea Întâlnire'}
                </span>
                <span style="font-size: 0.72rem; color: #94a3b8;">• Cameră: <strong style="color: #fde68a;">${spotlightMeeting.roomName}</strong></span>
              </div>
              <div style="font-size: 1.15rem; font-weight: 800; color: #fff; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                ${spotlightMeeting.title}
              </div>
              <div style="font-size: 0.8rem; color: #cbd5e1; margin-top: 2px;">
                Programată: <strong>${formatDateTimeInTimezone(spotlightMeeting.startsAt, spotlightMeeting.timezone || tz)}</strong>
                <span style="display: inline-flex; align-items: center; gap: 4px; background: rgba(56, 189, 248, 0.12); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.25); padding: 1px 7px; border-radius: 5px; font-size: 0.72rem; margin-left: 6px; font-weight: 600;">
                  🕒 ${getTimezoneLabel(spotlightMeeting.timezone || tz)}
                </span>
                • Organizator: <strong>${spotlightMeeting.creatorName}</strong>
              </div>
            </div>
          </div>

          <div style="display: flex; align-items: center; gap: 0.65rem; flex-shrink: 0;">
            ${spotlightMeeting.status === 'live' ? `
              <button class="btn btn-primary btn-join-meeting" data-room-id="${spotlightMeeting.roomId}" style="padding: 0.65rem 1.3rem; font-weight: 700; background: linear-gradient(135deg, #ef4444, #dc2626); box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);">
                <span>🔥</span> Intră Acum în Sală
              </button>
            ` : `
              <button class="btn btn-primary btn-start-meeting" data-meet-id="${spotlightMeeting.id}" style="padding: 0.65rem 1.3rem; font-weight: 700; background: linear-gradient(135deg, #6366f1, #3b82f6); box-shadow: 0 4px 20px rgba(99, 102, 241, 0.4);">
                <span>🚀</span> Pornește Ședința
              </button>
            `}
            <button class="btn btn-secondary btn-invite-meeting" data-meet-id="${spotlightMeeting.id}" title="Trimite link sau invitație" style="padding: 0.65rem 1rem;">
              <span>✉️</span> Invită
            </button>
          </div>
        </div>
      ` : ''}

      <!-- Meetings List (Full-Width Agenda Cards — ZERO Horizontal Scroll!) -->
      <div style="display: flex; flex-direction: column; gap: 0.85rem; width: 100%;">
        ${filteredMeetings.length === 0 ? `
          <div style="background: rgba(255, 255, 255, 0.03); border: 1px dashed rgba(255, 255, 255, 0.15); border-radius: 16px; padding: 3.5rem 2rem; text-align: center; max-width: 600px; margin: 1.5rem auto; width: 100%;">
            <div style="font-size: 2.5rem; margin-bottom: 0.75rem;">📅</div>
            <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem;">
              ${state.meetingDateFilter
                ? `Nicio întâlnire în data de ${state.meetingDateFilter}`
                : state.meetingSubTab === 'past'
                ? 'Nu există întâlniri trecute în istoric'
                : state.meetingSubTab === 'upcoming'
                ? 'Nicio întâlnire viitoare programată'
                : 'Nicio întâlnire găsită'}
            </h3>
            <p style="color: var(--text-muted); font-size: 0.875rem; margin-bottom: 1.5rem; line-height: 1.5;">
              ${state.meetingDateFilter
                ? 'Puteți alege altă zi din calendar sau șterge filtrul pentru a vedea lista completă.'
                : state.meetingSubTab === 'past'
                ? 'Întâlnirile care își depășesc data programată vor fi arhivate automat aici.'
                : 'Apăsați pe butonul de mai jos pentru a planifica o sesiune nouă.'}
            </p>
            <div style="display: flex; justify-content: center; gap: 0.75rem; flex-wrap: wrap;">
              ${state.meetingDateFilter ? `
                <button class="btn btn-secondary btn-cal-clear-filter">Vezi Toate Zilele</button>
              ` : ''}
              ${state.meetingSubTab === 'past' ? `
                <button class="btn btn-secondary btn-subtab-meetings" data-subtab="upcoming">Vezi Întâlnirile Viitoare</button>
              ` : `
                <button class="btn btn-primary" id="btn-empty-schedule-meeting"><span>+</span> Programează Întâlnire</button>
              `}
            </div>
          </div>
        ` : pageMeetings.map(m => {
          let startDate = new Date();
          try { startDate = new Date(m.startsAt); } catch {}
          const meetTz = m.timezone || tz;
          const monthStr = monthNamesRo[startDate.getMonth()] || 'OCT';
          const dayNumStr = startDate.getDate();
          const timeStr = formatTimeInTimezone(m.startsAt, meetTz);
          const isLive = m.status === 'live';
          const isPast = isMeetingPast(m, tz);

          const statusBadge = isLive
            ? '<span class="status-pill live" style="font-size: 0.7rem; font-weight: 800; background: rgba(239, 68, 68, 0.2); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.45); padding: 2px 8px; border-radius: 6px;">🔴 LIVE ACUM</span>'
            : isPast
            ? '<span class="status-pill ended" style="font-size: 0.7rem; font-weight: 700; background: rgba(148, 163, 184, 0.15); color: #cbd5e1; border: 1px solid rgba(148, 163, 184, 0.3); padding: 2px 8px; border-radius: 6px;">⏱️ FINALIZATĂ</span>'
            : m.status === 'scheduled'
            ? '<span class="status-pill scheduled" style="font-size: 0.7rem; font-weight: 700; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); padding: 2px 8px; border-radius: 6px;">⏳ PROGRAMATĂ</span>'
            : m.status === 'ended'
            ? '<span class="status-pill ended" style="font-size: 0.7rem; font-weight: 700; background: rgba(148, 163, 184, 0.15); color: #cbd5e1; border: 1px solid rgba(148, 163, 184, 0.3); padding: 2px 8px; border-radius: 6px;">FINALIZATĂ</span>'
            : '<span class="status-pill cancelled" style="font-size: 0.7rem; font-weight: 700; background: rgba(244, 63, 94, 0.15); color: #fb7185; border: 1px solid rgba(244, 63, 94, 0.3); padding: 2px 8px; border-radius: 6px;">ANULATĂ</span>';

          return `
            <div class="meeting-agenda-card ${isLive ? 'is-live' : ''} ${isPast ? 'is-past' : ''}" style="${isPast ? 'opacity: 0.88; border-color: rgba(255,255,255,0.08);' : ''}">
              <div class="meeting-agenda-left">
                <!-- Visual Date Badge -->
                <div class="meeting-date-box" style="${isPast ? 'background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.08);' : ''}">
                  <div class="meeting-date-box-month" style="${isPast ? 'color: #94a3b8;' : ''}">${monthStr}</div>
                  <div class="meeting-date-box-day" style="${isPast ? 'color: #cbd5e1;' : ''}">${dayNumStr}</div>
                  <div class="meeting-date-box-time">${timeStr}</div>
                </div>

                <!-- Info Body -->
                <div class="meeting-info-body">
                  <div class="meeting-title-row">
                    <h3 class="meeting-title-text" style="${isPast ? 'color: #e2e8f0;' : ''}">${m.title}</h3>
                    ${statusBadge}
                    ${m.pinCode ? `
                      <span style="font-size: 0.72rem; font-weight: 700; background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.35); padding: 2px 7px; border-radius: 6px;" title="Securizat cu PIN">
                        🔑 PIN: <code style="font-family: monospace;">${m.pinCode}</code>
                      </span>
                    ` : ''}
                    ${m.autoRecord ? `
                      <span style="font-size: 0.7rem; font-weight: 700; background: rgba(239, 68, 68, 0.15); color: #f87171; border: 1px solid rgba(239, 68, 68, 0.35); padding: 2px 6px; border-radius: 6px;">
                        🔴 Auto-Record
                      </span>
                    ` : ''}
                  </div>

                  <div class="meeting-meta-row">
                    <div class="meeting-meta-item">
                      <span>🏢 Cameră:</span> <strong style="color: #fde68a;">${m.roomName}</strong>
                    </div>
                    <div class="meeting-meta-item">
                      <span>👤 Organizator:</span> <strong style="color: #cbd5e1;">${m.creatorName}</strong>
                    </div>
                    <div class="meeting-meta-item">
                      <span>🕒 Fus Orar:</span> <strong style="color: #38bdf8;">${getTimezoneLabel(meetTz)}</strong>
                    </div>
                    <div class="meeting-meta-item">
                      <span>🛡️ Politică:</span> <strong style="color: #94a3b8;">${m.joinPolicy === 'org_members' ? 'Membri Organizație' : 'Doar cu Invitație'}</strong>
                    </div>
                  </div>

                  ${m.agenda ? `
                    <div style="font-size: 0.82rem; color: #94a3b8; line-height: 1.4; margin-top: 2px;">
                      ${m.agenda}
                    </div>
                  ` : ''}
                </div>
              </div>

              <!-- Action Buttons (Crisp, modern, zero scrollbar!) -->
              <div class="meeting-agenda-actions">
                ${isPast ? `
                  <span style="font-size: 0.76rem; color: #94a3b8; font-weight: 600; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); padding: 0.45rem 0.75rem; border-radius: 8px; display: inline-flex; align-items: center; gap: 4px;" title="Această întâlnire a trecut și nu mai poate fi pornită.">
                    🔒 Expirată
                  </span>
                  <button class="btn btn-secondary btn-sm btn-invite-meeting" data-meet-id="${m.id}" title="Vezi detalii invitație" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.85rem;">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <span>Detalii</span>
                  </button>
                  <button class="btn btn-secondary btn-sm btn-edit-meeting" data-meet-id="${m.id}" title="Reprogramează data" style="display: inline-flex; align-items: center; gap: 4px; padding: 0.5rem 0.75rem;">
                    <span>✏️</span> Reprogramează
                  </button>
                  <button class="btn btn-danger btn-sm btn-delete-meeting" data-meet-id="${m.id}" title="Șterge definitiv întâlnirea" style="width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px;">
                    🗑️
                  </button>
                ` : m.status === 'scheduled' ? `
                  <button class="btn btn-primary btn-sm btn-start-meeting" data-meet-id="${m.id}" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.95rem; font-weight: 700;">
                    <span>🚀</span> Pornește
                  </button>
                  <button class="btn btn-secondary btn-sm btn-invite-meeting" data-meet-id="${m.id}" title="Trimite link sau invitație" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.85rem;">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <span>Invită</span>
                  </button>
                  <button class="btn btn-secondary btn-sm btn-cal-export" data-meet-id="${m.id}" title="Adaugă în Google Calendar / Outlook" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.85rem;">
                    <span>📅</span> Calendar
                  </button>
                  <button class="btn btn-secondary btn-sm btn-edit-meeting" data-meet-id="${m.id}" title="Modifică detaliile" style="display: inline-flex; align-items: center; gap: 4px; padding: 0.5rem 0.75rem;">
                    <span>✏️</span> Edit
                  </button>
                  <button class="btn btn-secondary btn-sm btn-cancel-meeting" data-meet-id="${m.id}" title="Anulează această întâlnire" style="padding: 0.5rem 0.75rem; color: #fca5a5;">
                    Anulează
                  </button>
                  <button class="btn btn-danger btn-sm btn-delete-meeting" data-meet-id="${m.id}" title="Șterge definitiv întâlnirea" style="width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px;">
                    🗑️
                  </button>
                ` : isLive ? `
                  <button class="btn btn-primary btn-sm btn-join-meeting" data-room-id="${m.roomId}" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.55rem 1.1rem; font-weight: 700; background: linear-gradient(135deg, #ef4444, #dc2626);">
                    <span>🔥</span> Intră Acum
                  </button>
                  <button class="btn btn-secondary btn-sm btn-invite-meeting" data-meet-id="${m.id}" title="Trimite invitație" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.85rem;">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <span>Invită</span>
                  </button>
                  <button class="btn btn-secondary btn-sm btn-edit-meeting" data-meet-id="${m.id}" title="Modifică detaliile" style="display: inline-flex; align-items: center; gap: 4px; padding: 0.5rem 0.75rem;">
                    <span>✏️</span> Edit
                  </button>
                  <button class="btn btn-secondary btn-sm btn-end-meeting" data-meet-id="${m.id}" title="Încheie sesiunea live" style="padding: 0.5rem 0.75rem;">
                    Încheie
                  </button>
                  <button class="btn btn-danger btn-sm btn-delete-meeting" data-meet-id="${m.id}" title="Șterge întâlnirea" style="width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px;">
                    🗑️
                  </button>
                ` : `
                  <button class="btn btn-secondary btn-sm btn-invite-meeting" data-meet-id="${m.id}" title="Vezi detalii invitație" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.85rem;">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                    <span>Detalii</span>
                  </button>
                  <button class="btn btn-secondary btn-sm btn-edit-meeting" data-meet-id="${m.id}" title="Modifică detaliile" style="display: inline-flex; align-items: center; gap: 4px; padding: 0.5rem 0.75rem;">
                    <span>✏️</span> Edit
                  </button>
                  <button class="btn btn-danger btn-sm btn-delete-meeting" data-meet-id="${m.id}" title="Șterge întâlnirea" style="width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px;">
                    🗑️
                  </button>
                `}
              </div>
            </div>
          `;
        }).join('')}
      </div>
      ${renderPaginationControls(state.meetingsPage, totalMeetings, pageSize, 'meetings')}
    </div>
  `;
}

// 2b. Recordings Tab View
function renderRecordingsTab() {
  const isDesktopApp = typeof window !== 'undefined' && Boolean((window as any).vorbelloDesktop);
  const recordings = state.recordings;
  const totalCount = recordings.length;
  const diskCount = recordings.filter(r => r.storageType === 'disk' || Boolean(r.filePath)).length;
  const downloadCount = recordings.filter(r => r.storageType === 'download').length;
  const fhdCount = recordings.filter(r => r.format === 'video-fhd').length;
  const hdCount = recordings.filter(r => r.format === 'video-hd').length;
  const audioCount = recordings.filter(r => r.format === 'audio').length;

  const pageSize = 10;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  if (state.recordingsPage > totalPages) state.recordingsPage = totalPages;
  if (state.recordingsPage < 1) state.recordingsPage = 1;
  const pageRecordings = recordings.slice((state.recordingsPage - 1) * pageSize, state.recordingsPage * pageSize);

  return `
    <div class="page-header">
      <div class="page-header-title-box">
        <h1 class="page-title">🎥 Înregistrări Video & Audio</h1>
        <p class="page-subtitle">Înregistrări locale și de arhivă ale conferințelor Vorbello. Puteți reda direct, accesa fișierele de pe disc sau gestiona catalogul.</p>
      </div>
      <div class="page-header-actions">
        ${isDesktopApp ? `
          <button class="btn btn-secondary btn-sm" id="btn-open-recordings-folder" style="display: flex; align-items: center; gap: 0.35rem; font-weight: 600;">
            <span>📂</span> Deschide Folder Disc
          </button>
        ` : ''}
        <button class="btn btn-primary btn-sm" id="btn-refresh-recordings" style="display: flex; align-items: center; gap: 0.35rem; font-weight: 600;">
          <span>🔄</span> Reîmprospătează & Sincronizează Disc
        </button>
      </div>
    </div>

    <!-- Quick Metrics Row -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem; margin-bottom: 1.25rem;">
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Total Înregistrări Active</div>
        <div style="font-size: 1.5rem; font-weight: 800; color: #38bdf8; margin-top: 2px;">${totalCount}</div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Fișiere sincronizate în catalog</div>
      </div>
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Locație Stocare</div>
        <div style="display: flex; align-items: baseline; gap: 8px; margin-top: 2px;">
          <span style="font-size: 1.5rem; font-weight: 800; color: #4ade80;">${diskCount}</span>
          <span style="font-size: 0.72rem; color: #94a3b8;">pe disc</span>
          <span style="font-size: 1.5rem; font-weight: 800; color: #38bdf8; margin-left: 6px;">${downloadCount}</span>
          <span style="font-size: 0.72rem; color: #94a3b8;">downloads</span>
        </div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Local PC (Videos) & Descărcări Browser</div>
      </div>
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Calitate Video & Audio</div>
        <div style="font-size: 1.5rem; font-weight: 800; color: #a855f7; margin-top: 2px;">${fhdCount} <span style="font-size: 0.8rem; color: #94a3b8; font-weight: 500;">FHD</span> • ${hdCount + audioCount} <span style="font-size: 0.8rem; color: #94a3b8; font-weight: 500;">HD</span></div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Full HD 1080p, HD 720p & Audio</div>
      </div>
    </div>

    ${recordings.length === 0 ? `
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px dashed rgba(255, 255, 255, 0.15); border-radius: 16px; padding: 3.5rem 2rem; text-align: center; max-width: 600px; margin: 2rem auto;">
        <div style="font-size: 2.8rem; margin-bottom: 0.75rem;">⏺️</div>
        <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem;">Nicio înregistrare disponibilă</h3>
        <p style="color: var(--text-muted); font-size: 0.875rem; margin-bottom: 1.5rem; line-height: 1.5;">
          Fișierele șterse de pe disc au fost curățate automat din catalog. Puteți porni o nouă înregistrare în orice conferință dacă sunteți Administrator sau dacă sala are configurată „Înregistrare Automată”.
        </p>
      </div>
    ` : `
      <div class="rooms-stack-list">
        ${pageRecordings.map(rec => {
          const isFhd = rec.format === 'video-fhd';
          const isAudio = rec.format === 'audio';
          const formatBadge = isFhd 
            ? '<span style="background: rgba(168, 85, 247, 0.2); color: #c084fc; border: 1px solid rgba(168, 85, 247, 0.4); padding: 3px 10px; border-radius: 6px; font-size: 0.72rem; font-weight: 700; white-space: nowrap;">🎥 FHD 1080p</span>'
            : isAudio
            ? '<span style="background: rgba(245, 158, 11, 0.2); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.4); padding: 3px 10px; border-radius: 6px; font-size: 0.72rem; font-weight: 700; white-space: nowrap;">🎙️ Audio HD</span>'
            : '<span style="background: rgba(56, 189, 248, 0.2); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.4); padding: 3px 10px; border-radius: 6px; font-size: 0.72rem; font-weight: 700; white-space: nowrap;">🎥 HD 720p</span>';

          const isDisk = rec.storageType === 'disk' || Boolean(rec.filePath);
          const locationBadge = isDisk
            ? '<span style="background: rgba(34, 197, 94, 0.15); color: #4ade80; border: 1px solid rgba(34, 197, 94, 0.35); padding: 3px 9px; border-radius: 6px; font-size: 0.72rem; font-weight: 700; display: inline-flex; align-items: center; gap: 4px;" title="Fișier salvat pe PC în Videos/Vorbello Recordings">💾 Pe Disc Local</span>'
            : '<span style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); padding: 3px 9px; border-radius: 6px; font-size: 0.72rem; font-weight: 700; display: inline-flex; align-items: center; gap: 4px;" title="Descărcat prin browser în folderul Downloads">📥 În Descărcări (Browser)</span>';

          return `
            <div class="room-card-wide" style="padding: 1.15rem 1.45rem;">
              <div class="room-wide-header" style="align-items: center;">
                <div style="flex: 1; min-width: 260px;">
                  <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
                    <h3 class="room-card-title" style="margin: 0; font-size: 1.15rem; color: #fff;">${rec.meetingTitle || rec.roomName}</h3>
                    ${formatBadge}
                    ${locationBadge}
                    <span style="font-size: 0.78rem; color: #94a3b8; background: rgba(255,255,255,0.04); padding: 2px 8px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.08);">Cameră: <code style="color: #fde68a;">${rec.roomSlug}</code></span>
                  </div>
                </div>

                <div class="room-wide-actions" style="gap: 0.5rem;">
                  <button class="btn btn-primary btn-sm btn-play-recording" data-rec-id="${rec.id}" style="display: inline-flex; align-items: center; gap: 6px; padding: 0.5rem 1rem; font-weight: 600;">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    <span>Redare</span>
                  </button>
                  ${isDisk ? `
                    <button class="btn btn-secondary btn-sm btn-show-folder-recording" data-rec-id="${rec.id}" title="Deschide dosarul pe disc în File Explorer" style="display: inline-flex; align-items: center; gap: 5px; padding: 0.5rem 0.85rem;">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                      <span>Dosar</span>
                    </button>
                  ` : `
                    <button class="btn btn-secondary btn-sm btn-download-recording" data-rec-id="${rec.id}" title="Descarcă fișierul" style="display: inline-flex; align-items: center; gap: 6px; padding: 0.5rem 0.9rem;">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      <span>Descarcă</span>
                    </button>
                  `}
                  <button class="btn btn-danger btn-sm btn-delete-recording" data-rec-id="${rec.id}" title="Șterge înregistrarea" style="width: 34px; height: 34px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 8px;">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                  </button>
                </div>
              </div>

              <div class="room-wide-footer" style="padding-top: 0.75rem; margin-top: 0.35rem; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; font-size: 0.825rem;">
                <div style="display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap;">
                  <div>
                    <span style="color: var(--text-muted);">Data:</span>
                    <strong style="color: #e2e8f0; margin-left: 4px;">${rec.createdAt}</strong>
                  </div>
                  <div>
                    <span style="color: var(--text-muted);">Durată:</span>
                    <strong style="color: #38bdf8; margin-left: 4px;">⏱️ ${rec.duration}</strong>
                  </div>
                  <div>
                    <span style="color: var(--text-muted);">Dimensiune:</span>
                    <strong style="color: #10b981; margin-left: 4px;">📦 ${rec.fileSize}</strong>
                  </div>
                  <div>
                    <span style="color: var(--text-muted);">Înregistrat de:</span>
                    <strong style="color: #cbd5e1; margin-left: 4px;">👤 ${rec.recordedBy}</strong>
                  </div>
                  <div>
                    <span style="color: var(--text-muted);">Fișier:</span>
                    <code style="color: #93c5fd; font-size: 0.75rem; margin-left: 4px;">${rec.fileName || (rec.filePath ? rec.filePath.split(/[\\/]/).pop() : 'Inregistrare.webm')}</code>
                  </div>
                </div>
              </div>
            </div>
          `;
        }).join('')}
      </div>
      ${renderPaginationControls(state.recordingsPage, totalCount, pageSize, 'recordings')}
    `}
  `;
}

function generateAttendanceReportHTML(
  records: AttendanceRecord[],
  filterSlug?: string,
  sessionObj?: MeetingSession
): string {
  const isSingleSession = !!sessionObj;
  const filteredRecords = isSingleSession
    ? sessionObj.records
    : (filterSlug ? records.filter(r => r.roomSlug === filterSlug || r.meetingTitle === filterSlug) : records);

  const totalEntries = filteredRecords.length;
  const presentCount = filteredRecords.filter(r => r.status === 'PRESENT').length;
  const uniqueParticipants = new Set(filteredRecords.map(r => r.participantName)).size;
  const sessionTitle = isSingleSession ? sessionObj.meetingTitle : (filterSlug || 'Toate Sălile & Ședințele Înregistrate');
  const nowStr = new Date().toLocaleString('ro-RO', { 
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' 
  });
  const auditCode = `VRB-AUDIT-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

  return `<!DOCTYPE html>
<html lang="ro">
<head>
  <meta charset="utf-8">
  <title>Proces-Verbal Prezență Ședință — ${sessionTitle}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 12mm 14mm;
    }
    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      background: #ffffff;
      font-size: 11pt;
      line-height: 1.45;
      padding: 20px;
    }
    .report-header {
      border-bottom: 2px solid #0f172a;
      padding-bottom: 14px;
      margin-bottom: 18px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .brand-title {
      font-size: 16pt;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.5px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .brand-subtitle {
      font-size: 9pt;
      color: #64748b;
      font-weight: 500;
      margin-top: 2px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .doc-badge {
      text-align: right;
    }
    .doc-badge-pill {
      display: inline-block;
      background: #0f172a;
      color: #ffffff;
      padding: 4px 10px;
      font-size: 8.5pt;
      font-weight: 700;
      border-radius: 4px;
      letter-spacing: 0.5px;
    }
    .doc-audit-code {
      font-size: 7.5pt;
      color: #64748b;
      margin-top: 4px;
      font-family: Consolas, monospace;
    }
    .report-title-block {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-left: 4px solid #4f46e5;
      padding: 12px 16px;
      border-radius: 6px;
      margin-bottom: 20px;
    }
    .doc-heading {
      font-size: 14pt;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 4px;
    }
    .doc-meta-row {
      display: flex;
      gap: 16px;
      font-size: 8.5pt;
      color: #475569;
      flex-wrap: wrap;
    }
    .doc-meta-item strong {
      color: #0f172a;
    }
    .metrics-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin-bottom: 20px;
    }
    .metric-card {
      border: 1px solid #e2e8f0;
      background: #ffffff;
      padding: 10px 12px;
      border-radius: 6px;
      text-align: center;
    }
    .metric-label {
      font-size: 7.5pt;
      text-transform: uppercase;
      color: #64748b;
      font-weight: 600;
      letter-spacing: 0.5px;
    }
    .metric-value {
      font-size: 14pt;
      font-weight: 800;
      color: #0f172a;
      margin-top: 2px;
    }
    table.attendance-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
      font-size: 8.5pt;
    }
    table.attendance-table th {
      background: #0f172a;
      color: #ffffff;
      padding: 8px 10px;
      text-align: left;
      font-weight: 600;
      font-size: 8pt;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    table.attendance-table td {
      padding: 7px 10px;
      border-bottom: 1px solid #e2e8f0;
      color: #1e293b;
    }
    table.attendance-table tr:nth-child(even) td {
      background: #f8fafc;
    }
    .status-badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 4px;
      font-weight: 700;
      font-size: 7.5pt;
    }
    .status-present {
      background: #dcfce7;
      color: #166534;
    }
    .status-late {
      background: #fef3c7;
      color: #92400e;
    }
    .status-left {
      background: #f1f5f9;
      color: #475569;
    }
    .legal-notice {
      background: #f8fafc;
      border: 1px dashed #cbd5e1;
      padding: 10px 14px;
      border-radius: 6px;
      font-size: 7.5pt;
      color: #475569;
      line-height: 1.45;
      margin-bottom: 25px;
    }
    .signatures {
      display: flex;
      justify-content: space-between;
      gap: 30px;
      margin-top: 30px;
      page-break-inside: avoid;
    }
    .sign-box {
      flex: 1;
      border-top: 1px solid #0f172a;
      padding-top: 8px;
      font-size: 8.5pt;
      color: #1e293b;
    }
    .sign-role {
      font-weight: 700;
      margin-bottom: 40px;
      text-transform: uppercase;
      font-size: 8pt;
      color: #475569;
    }
    .sign-dots {
      color: #64748b;
      font-size: 8pt;
    }
  </style>
</head>
<body>
  <div class="report-header">
    <div>
      <div class="brand-title">VORBELLO</div>
      <div class="brand-subtitle">Platformă de Videoconferință & Audit Prezență</div>
    </div>
    <div class="doc-badge">
      <div class="doc-badge-pill">JURNAL OFICIAL DE PREZENȚĂ</div>
      <div class="doc-audit-code">COD: ${auditCode}</div>
    </div>
  </div>

  <div class="report-title-block">
    <h1 class="doc-heading">Proces-Verbal Oficial de Prezență</h1>
    <div class="doc-meta-row">
      <div class="doc-meta-item">Ședință / Sală: <strong>${sessionTitle}</strong></div>
      ${isSingleSession ? `<div class="doc-meta-item">Dată: <strong>${sessionObj.displayDate}</strong></div>` : ''}
      ${isSingleSession ? `<div class="doc-meta-item">Interval Orar: <strong>${sessionObj.startTime} — ${sessionObj.endTime}</strong></div>` : ''}
      <div class="doc-meta-item">Generat la: <strong>${nowStr}</strong></div>
    </div>
  </div>

  <div class="metrics-grid">
    <div class="metric-card">
      <div class="metric-label">Total Participanți</div>
      <div class="metric-value">${totalEntries}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Persoane Unice</div>
      <div class="metric-value">${uniqueParticipants}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Prezenți la Apel</div>
      <div class="metric-value" style="color: #16a34a;">${presentCount}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Stare Ședință</div>
      <div class="metric-value" style="font-size: 11pt; color: #4f46e5;">
        ${isSingleSession ? (sessionObj.isLive ? 'În Desfășurare' : 'Finalizată') : 'Registru Arhivat'}
      </div>
    </div>
  </div>

  <table class="attendance-table">
    <thead>
      <tr>
        <th style="width: 30px;">#</th>
        <th>Nume & Prenume Participant</th>
        <th>Filială / Organizație</th>
        <th>Rol</th>
        <th>Ora Intrării</th>
        <th>Ora Ieșirii</th>
        <th>Timp Conectat</th>
        <th>Statut Prezență</th>
      </tr>
    </thead>
    <tbody>
      ${filteredRecords.map((r, idx) => {
        const statusHtml = r.status === 'PRESENT'
          ? '<span class="status-badge status-present">PREZENT</span>'
          : r.status === 'LATE'
          ? '<span class="status-badge status-late">ÎNTÂRZIAT</span>'
          : '<span class="status-badge status-left">PĂRĂSIT</span>';

        return `
          <tr>
            <td style="text-align: center; color: #64748b; font-weight: 600;">${idx + 1}</td>
            <td>
              <strong style="color: #0f172a;">${r.participantName}</strong>
              ${r.participantEmail ? `<div style="font-size: 7.5pt; color: #64748b;">${r.participantEmail}</div>` : ''}
            </td>
            <td>${r.organization || 'Structură Centrală'}</td>
            <td><strong style="font-size: 8pt;">${r.role || 'MEMBER'}</strong></td>
            <td style="font-family: Consolas, monospace; font-size: 8.5pt;">${r.joinedAt || '—'}</td>
            <td style="font-family: Consolas, monospace; font-size: 8.5pt;">${r.leftAt || 'Conectat (Activ)'}</td>
            <td style="font-weight: 600; font-size: 8.5pt;">${r.durationFormatted || '—'}</td>
            <td>${statusHtml}</td>
          </tr>
        `;
      }).join('')}
    </tbody>
  </table>

  <div class="legal-notice">
    <strong>Certificare Tehnică Automată:</strong> Prezentul raport este generat în mod automat de nodul de conferință Vorbello (LiveKit/WebRTC) pe baza semnalizării criptografice și a fluxurilor media active. Ora de intrare, ora de ieșire și timpii de conectare constituie probă tehnică de prezență conform normelor de desfășurare a cursurilor, examenelor, webinariilor și adunărilor la distanță.
  </div>

  <div class="signatures">
    <div class="sign-box">
      <div class="sign-role">Secretar de Ședință / Asistent Curs</div>
      <div class="sign-dots">Nume & Semnătură: ........................................................</div>
    </div>
    <div class="sign-box">
      <div class="sign-role">Președinte de Ședință / Lector / Gazdă</div>
      <div class="sign-dots">Nume & Semnătură: ........................................................</div>
    </div>
  </div>
</body>
</html>`;
}

// 2c. Attendance by Meeting Session View
function renderAttendanceTab() {
  const records = state.attendanceRecords;
  const allSessions = groupAttendanceSessions(records);
  const filterRoom = state.selectedRoomAttendanceFilter || '';
  const searchQ = (state.attendanceSearchQuery || '').toLowerCase().trim();
  const statusFilter = state.attendanceStatusFilter || 'all';

  const uniqueRooms = Array.from(new Set(records.map(r => r.roomSlug))).filter(Boolean);

  let filteredSessions = allSessions;
  if (filterRoom) {
    filteredSessions = filteredSessions.filter(s => s.roomSlug === filterRoom || s.meetingTitle === filterRoom);
  }
  if (statusFilter === 'live') {
    filteredSessions = filteredSessions.filter(s => s.isLive);
  } else if (statusFilter === 'completed') {
    filteredSessions = filteredSessions.filter(s => !s.isLive);
  }
  if (searchQ) {
    filteredSessions = filteredSessions.filter(s => 
      s.meetingTitle.toLowerCase().includes(searchQ) ||
      s.displayDate.toLowerCase().includes(searchQ) ||
      s.roomSlug.toLowerCase().includes(searchQ) ||
      s.records.some(r => r.participantName.toLowerCase().includes(searchQ) || (r.participantEmail && r.participantEmail.toLowerCase().includes(searchQ)))
    );
  }

  const totalFilteredSessions = filteredSessions.length;
  const pageSize = 10;
  const totalPages = Math.ceil(totalFilteredSessions / pageSize) || 1;
  if (state.attendancePage > totalPages) state.attendancePage = totalPages;
  if (state.attendancePage < 1) state.attendancePage = 1;
  const pageSessions = filteredSessions.slice((state.attendancePage - 1) * pageSize, state.attendancePage * pageSize);

  const totalSessions = allSessions.length;
  const liveSessions = allSessions.filter(s => s.isLive).length;
  const uniqueParticipantsTotal = new Set(records.map(r => r.participantName)).size;

  return `
    ${records.some(r => r.academySessionId) ? '<div style="padding:16px;margin-bottom:16px;border:1px solid #38bdf8;border-radius:12px;color:#e2e8f0">Pentru sesiunile PP, raportul complet și prezența confirmată de server sunt disponibile în <a style="color:#7dd3fc" target="_blank" rel="noopener noreferrer" href="https://puterea-poporului.ro/wp-admin/admin.php?page=mpd_academy_vorbello">Academia → Vorbello & ședințe</a>. În această aplicație sunt păstrate și conexiunile locale ale dispozitivului.</div>' : ''}
    ${!centralSyncResult.success && state.appMode === 'production' ? '<div role="status" style="padding:14px;border:1px solid #f59e0b;border-radius:12px;margin-bottom:16px">Sincronizare date în așteptare: ' + escapeHtml(centralSyncResult.message) + '</div>' : ''}
    <div class="page-header">
      <div class="page-header-title-box">
        <h1 class="page-title">📋 Registru Prezență per Ședință & Curs</h1>
        <p class="page-subtitle">Evidență organizată pe fiecare întâlnire: data, ora, participanții prezenți și durata de conectare. Sincronizare automată cu site-ul web.</p>
      </div>
      <div class="page-header-actions">
        <button class="btn btn-secondary btn-sm" id="btn-config-website-sync" style="display: flex; align-items: center; gap: 0.35rem;">
          <span>🌐</span> Sincronizare Site Web
        </button>
        <button class="btn btn-primary btn-sm" id="btn-sync-all-attendance" style="display: flex; align-items: center; gap: 0.35rem;">
          <span>⚡</span> Trimite Tot pe Site
        </button>
        <button class="btn btn-secondary btn-sm" id="btn-export-attendance-csv" style="display: flex; align-items: center; gap: 0.35rem;">
          <span>📄</span> Export Complet CSV
        </button>
      </div>
    </div>

    <!-- Attendance Metrics -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem; margin-bottom: 1.25rem;">
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Total Întâlniri Înregistrate</div>
        <div style="font-size: 1.5rem; font-weight: 800; color: #38bdf8; margin-top: 2px;">${totalSessions}</div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Sesiuni distincte cu jurnal propriu</div>
      </div>
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Ședințe Live Acum</div>
        <div style="font-size: 1.5rem; font-weight: 800; color: ${liveSessions > 0 ? '#10b981' : '#64748b'}; margin-top: 2px;">${liveSessions}</div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Conferințe cu membri conectați</div>
      </div>
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Participanți Unici Total</div>
        <div style="font-size: 1.5rem; font-weight: 800; color: #a855f7; margin-top: 2px;">${uniqueParticipantsTotal}</div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Persoane auditate în sistem</div>
      </div>
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1.1rem;">
        <div style="font-size: 0.72rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Sincronizare Pagina Web</div>
        <div style="font-size: 1.05rem; font-weight: 700; color: #34d399; margin-top: 5px; display: flex; align-items: center; gap: 6px;">
          <span>${state.websiteSyncConfig.autoSync ? 'Auto-sync configurat' : '🟡 Manual'}</span>
        </div>
        <div style="font-size: 0.7rem; color: #94a3b8; margin-top: 2px;">Ultima confirmare: ${escapeHtml(state.websiteSyncConfig.lastSyncTime || 'niciuna')}</div>
      </div>
    </div>

    <!-- Filter & Search Bar -->
    <div style="display: flex; justify-content: space-between; align-items: center; gap: 1rem; margin-bottom: 1.25rem; flex-wrap: wrap;">
      <div style="display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; flex: 1;">
        <div style="position: relative; min-width: 260px; flex: 1; max-width: 380px;">
          <input type="text" id="input-attendance-search" class="form-input" placeholder="🔍 Caută întâlnire, dată, participant..." value="${state.attendanceSearchQuery || ''}" style="padding-left: 2.2rem; font-size: 0.85rem;" />
          <span style="position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); opacity: 0.5; pointer-events: none;">🔍</span>
        </div>

        <select class="form-select" id="select-attendance-status" style="width: auto; padding: 0.45rem 0.85rem; font-size: 0.825rem;">
          <option value="all" ${statusFilter === 'all' ? 'selected' : ''}>Toate Stările</option>
          <option value="live" ${statusFilter === 'live' ? 'selected' : ''}>● Doar Live Acum</option>
          <option value="completed" ${statusFilter === 'completed' ? 'selected' : ''}>✓ Finalizate</option>
        </select>

        <select class="form-select" id="select-attendance-filter" style="width: auto; min-width: 200px; padding: 0.45rem 0.85rem; font-size: 0.825rem;">
          <option value="" ${filterRoom === '' ? 'selected' : ''}>Toate Sălile</option>
          ${uniqueRooms.map(r => `<option value="${r}" ${filterRoom === r ? 'selected' : ''}>${r}</option>`).join('')}
        </select>
      </div>

      <div style="display: flex; gap: 0.4rem;">
        <button class="btn btn-secondary btn-sm" id="btn-refresh-attendance" title="Reîmprospătează lista de prezență" style="padding: 0.45rem 0.85rem;">
          🔄 Reîmprospătează
        </button>
        <button class="btn btn-danger btn-sm" id="btn-reset-attendance" title="Șterge istoricul de prezență" style="padding: 0.45rem 0.85rem;">
          🗑️ Resetează Registru
        </button>
      </div>
    </div>

    <!-- Meetings Sessions List -->
    ${filteredSessions.length === 0 ? `
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px dashed rgba(255, 255, 255, 0.15); border-radius: 16px; padding: 3.5rem 2rem; text-align: center; max-width: 600px; margin: 2rem auto;">
        <div style="font-size: 2.8rem; margin-bottom: 0.75rem;">📋</div>
        <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem;">Nicio întâlnire găsită</h3>
        <p style="color: var(--text-muted); font-size: 0.875rem; margin-bottom: 1.5rem; line-height: 1.5;">
          Fiecare conferință sau curs creează automat o fișă de prezență unică. Ajustați filtrele de căutare de mai sus.
        </p>
      </div>
    ` : `
      <div class="attendance-sessions-container" style="display: flex; flex-direction: column; gap: 1rem;">
        ${pageSessions.map(session => {
          const sid = session.sessionId || session.id;
          const isExpanded = state.expandedAttendanceSessions.includes(sid);
          return `
            <div class="attendance-session-card ${session.isLive ? 'session-live' : ''}" data-session-id="${session.sessionId}" style="background: rgba(255, 255, 255, 0.03); border: 1px solid ${session.isLive ? 'rgba(16, 185, 129, 0.35)' : 'rgba(255, 255, 255, 0.08)'}; border-radius: 14px; overflow: hidden; transition: all 0.2s ease;">
              <!-- Session Header Summary -->
              <div class="session-header" style="display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; gap: 1rem; flex-wrap: wrap; background: ${session.isLive ? 'rgba(16, 185, 129, 0.06)' : 'rgba(255, 255, 255, 0.015)'};">
                <div style="display: flex; align-items: center; gap: 0.85rem; flex: 1; min-width: 280px; cursor: pointer;" class="btn-toggle-session" data-session-id="${session.sessionId}">
                  <span style="font-size: 1.1rem; transform: rotate(${isExpanded ? '90deg' : '0deg'}); transition: transform 0.2s ease; display: inline-block; color: #94a3b8;">▶</span>
                  <div>
                    <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                      ${session.isLive 
                        ? `<span class="badge-live-pulse" style="background: rgba(16, 185, 129, 0.2); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.4); padding: 2px 8px; border-radius: 6px; font-weight: 800; font-size: 0.7rem; display: inline-flex; align-items: center; gap: 4px;">● LIVE ACUM</span>`
                        : `<span class="badge-completed" style="background: rgba(148, 163, 184, 0.15); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.3); padding: 2px 8px; border-radius: 6px; font-weight: 700; font-size: 0.7rem;">✓ Finalizată</span>`
                      }
                      <h3 style="font-size: 1.05rem; font-weight: 700; color: #fff; margin: 0;">${session.meetingTitle}</h3>
                      <span style="font-size: 0.75rem; color: #94a3b8; font-family: monospace; background: rgba(255,255,255,0.06); padding: 2px 6px; border-radius: 4px;">${session.roomSlug}</span>
                    </div>

                    <div style="display: flex; align-items: center; gap: 1rem; margin-top: 0.4rem; font-size: 0.8rem; color: #94a3b8; flex-wrap: wrap;">
                      <span>📅 <strong style="color: #e2e8f0;">${session.displayDate}</strong></span>
                      <span>⏰ <strong style="color: #e2e8f0;">${session.startTime} — ${session.endTime}</strong></span>
                      <span>⏱️ Durată: <strong style="color: #38bdf8;">${session.durationFormatted}</strong></span>
                      <span>👥 Participanți: <strong style="color: #a855f7;">${session.uniqueParticipants} persoane</strong> (${session.presentCount} prezenți la apel)</span>
                      ${session.isSynced 
                        ? `<span class="badge-sync-ok" style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.3); padding: 1px 6px; border-radius: 4px; font-size: 0.72rem; font-weight: 600;">✓ Sincronizat Web</span>`
                        : `<span style="color: #94a3b8; font-size: 0.72rem;">⏳ Nesincronizat</span>`
                      }
                    </div>
                  </div>
                </div>

                <!-- Session Actions -->
                <div style="display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;">
                  <button class="btn btn-secondary btn-sm btn-print-session" data-session-id="${session.sessionId}" title="Printează sau salvează raport PDF pentru această întâlnire" style="padding: 0.4rem 0.75rem; font-size: 0.78rem;">
                    🖨️ Raport PDF
                  </button>
                  <button class="btn btn-secondary btn-sm btn-csv-session" data-session-id="${session.sessionId}" title="Exportă CSV pentru această întâlnire" style="padding: 0.4rem 0.75rem; font-size: 0.78rem;">
                    📄 CSV
                  </button>
                  <button class="btn btn-primary btn-sm btn-sync-session" data-session-id="${session.sessionId}" title="Trimite prezența acestei întâlniri pe site-ul web" style="padding: 0.4rem 0.8rem; font-size: 0.78rem;">
                    🌐 Sincronizează Site
                  </button>
                  <button class="btn btn-danger btn-sm btn-delete-attendance-session" data-session-id="${session.sessionId}" data-room-slug="${session.roomSlug}" data-date="${session.dateStr}" data-title="${session.meetingTitle.replace(/"/g, '&quot;')}" title="Șterge definitiv această ședință din registru" style="padding: 0.4rem 0.75rem; font-size: 0.78rem; display: inline-flex; align-items: center; gap: 4px;">
                    🗑️ Șterge
                  </button>
                  <button class="btn btn-secondary btn-sm btn-toggle-session" data-session-id="${session.sessionId}" style="padding: 0.4rem 0.65rem; font-size: 0.78rem;">
                    ${isExpanded ? 'Restrânge ▲' : 'Vezi Prezența ▼'}
                  </button>
                </div>
              </div>

              <!-- Expanded Attendee Roster Table (Only for this specific meeting) -->
              ${isExpanded ? `
                <div style="border-top: 1px solid rgba(255, 255, 255, 0.08); padding: 1rem 1.25rem; background: rgba(0, 0, 0, 0.2);">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;">
                    <div style="font-weight: 700; font-size: 0.875rem; color: #e2e8f0;">
                      Lista Participanților la această Întâlnire (${session.records.length} înregistrări de acces)
                    </div>
                    <div style="font-size: 0.75rem; color: #94a3b8;">
                      Audit ID: <code style="color: #38bdf8;">${session.sessionId}</code>
                    </div>
                  </div>

                  <div class="data-table-container" style="margin: 0; box-shadow: none; border: 1px solid rgba(255, 255, 255, 0.06);">
                    <table class="data-table session-attendee-table">
                      <thead>
                        <tr>
                          <th style="width: 32px;">#</th>
                          <th>Nume & Prenume Participant</th>
                          <th>Organizație / Filială</th>
                          <th>Rol</th>
                          <th>Ora Intrării</th>
                          <th>Ora Ieșirii</th>
                          <th>Timp Conectat</th>
                          <th>Statut Prezență</th>
                          <th style="width: 50px; text-align: center;">Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${session.records.map((r, idx) => {
                          const statusPill = r.status === 'PRESENT'
                            ? '<span style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.35); padding: 2px 8px; border-radius: 6px; font-weight: 700; font-size: 0.72rem; display: inline-flex; align-items: center; gap: 4px;">✅ Prezent</span>'
                            : r.status === 'LATE'
                            ? '<span style="background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.35); padding: 2px 8px; border-radius: 6px; font-weight: 700; font-size: 0.72rem; display: inline-flex; align-items: center; gap: 4px;">⏱️ Întârziat</span>'
                            : '<span style="background: rgba(148, 163, 184, 0.15); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.35); padding: 2px 8px; border-radius: 6px; font-weight: 700; font-size: 0.72rem; display: inline-flex; align-items: center; gap: 4px;">🚪 Părăsit</span>';

                          const rolePill = r.role === 'ADMIN'
                            ? '<span class="role-pill" style="background: rgba(245, 158, 11, 0.2); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.4);">ADMINISTRATOR</span>'
                            : r.role === 'MODERATOR'
                            ? '<span class="role-pill" style="background: rgba(99, 102, 241, 0.2); color: #818cf8; border: 1px solid rgba(99, 102, 241, 0.4);">MODERATOR</span>'
                            : '<span class="role-pill" style="background: rgba(16, 185, 129, 0.15); color: #34d399; border: 1px solid rgba(16, 185, 129, 0.35);">PARTICIPANT</span>';

                          return `
                            <tr>
                              <td style="text-align: center; color: #64748b; font-weight: 600;">${idx + 1}</td>
                              <td>
                                <div style="font-weight: 700; color: #fff; font-size: 0.85rem;">${r.participantName}</div>
                                <div style="font-size: 0.72rem; color: var(--text-muted);">${r.participantEmail || ''}</div>
                              </td>
                              <td>
                                <span style="font-weight: 600; color: #e2e8f0;">${r.organization || '—'}</span>
                              </td>
                              <td>${rolePill}</td>
                              <td style="font-size: 0.8rem; color: #94a3b8; font-family: monospace;">${r.joinedAt}</td>
                              <td style="font-size: 0.8rem; color: #94a3b8; font-family: monospace;">${r.leftAt || '<span style="color: #34d399; font-weight: 700;">● În curs</span>'}</td>
                              <td>
                                <span style="font-weight: 700; color: ${r.leftAt ? '#38bdf8' : '#34d399'}; font-size: 0.8rem;">
                                  ${r.durationFormatted}
                                </span>
                              </td>
                              <td>${statusPill}</td>
                              <td style="text-align: center;">
                                <button class="btn btn-danger btn-sm btn-delete-attendee-record" data-record-id="${r.id}" data-name="${r.participantName.replace(/"/g, '&quot;')}" title="Elimină această intrare de prezență" style="padding: 0.25rem 0.5rem; font-size: 0.72rem;">
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          `;
                        }).join('')}
                      </tbody>
                    </table>
                  </div>
                </div>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
      ${renderPaginationControls(state.attendancePage, totalFilteredSessions, pageSize, 'attendance')}
    `}
  `;
}

// 3. Invitations Tab View
function renderInvitationsTab() {
  const currentInvitations = state.appMode === 'production' ? state.prodInvitations : state.invitations;
  return `
    <div class="page-header">
      <div class="page-header-title-box">
        <h1 class="page-title">${state.appMode === 'production' ? 'Invitații & Acces Oaspeți (Cloud Securizat)' : 'Invitații & Acces Oaspeți (Mod Demo)'}</h1>
        <p class="page-subtitle">${state.appMode === 'production' ? 'Linkuri securizate de acces generate pe platforma securizată Vorbello.' : 'Linkuri securizate cu expirare pentru acces temporar la conferințe.'}</p>
      </div>
    </div>

    <div class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Cameră Atribuită</th>
            <th>Destinatar / Email</th>
            <th>Expiră La</th>
            <th>Creat De</th>
            <th>Link Securizat</th>
            <th>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          ${currentInvitations.length === 0 ? `
            <tr>
              <td colspan="6" style="text-align: center; padding: 2.5rem; color: var(--text-muted);">
                <div style="font-size: 1.5rem; margin-bottom: 0.5rem;">✉️</div>
                <div style="color: #fff; font-weight: 600;">Nu există invitații active pe serverul de producție</div>
                <div style="font-size: 0.8rem; margin-top: 0.25rem;">Generați o invitație din lista de camere sau dintr-o întâlnire programată.</div>
              </td>
            </tr>
          ` : currentInvitations.map(inv => `
            <tr>
              <td style="font-weight: 600; color: #fff;">${inv.roomName}</td>
              <td>${inv.email || 'Acces Public prin Link'}</td>
              <td>${new Date(inv.expiresAt).toLocaleString('ro-RO')}</td>
              <td>${inv.creatorName}</td>
              <td><code>/invite/${inv.token.substring(0, 16)}...</code></td>
              <td>
                <div style="display: flex; gap: 0.4rem;">
                  <button class="btn btn-secondary btn-sm btn-copy-invite" data-token="${inv.token}">
                    📋 Copiază
                  </button>
                  <button class="btn btn-danger btn-sm btn-revoke-invite" data-inv-id="${inv.id}">
                    Revocare
                  </button>
                </div>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

// 4. Members Tab View
function renderMembersTab() {
  const currentMembers = state.appMode === 'production' ? state.prodMembers : state.members;
  const hostCount = currentMembers.filter(m => m.hasHostLicense).length;
  return `
    <div class="page-header">
      <div class="page-header-title-box">
        <h1 class="page-title">${state.appMode === 'production' ? 'Membri & Roluri Organizație' : 'Membri & Roluri Organizație'}</h1>
        <p class="page-subtitle">${state.appMode === 'production' ? 'Echipa și administratorii activi pe infrastructura cloud securizată Vorbello.' : 'Controlul accesului pe bază de rol (RBAC: Admin, Moderator, Membru) și alocarea licențelor de conferință.'}</p>
      </div>
      <div class="page-header-actions">
        <button class="btn btn-secondary btn-sm" id="btn-open-issued-licenses" style="display: flex; align-items: center; gap: 0.4rem; background: rgba(56, 189, 248, 0.12); border-color: rgba(56, 189, 248, 0.4); color: #38bdf8; font-weight: 700;" title="Gestiune și emitere chei de licență / abonamente viitoare">
          <span>👑</span> Emitere & Abonamente Licențe (${state.issuedLicenses.length})
        </button>
        <button class="btn btn-secondary btn-sm" id="btn-open-change-password" style="display: flex; align-items: center; gap: 0.4rem; color: #fde68a; border-color: rgba(245, 158, 11, 0.35); font-weight: 600;" title="Schimbă parola de acces pentru dannyopr@gmail.com">
          <span>🔑</span> Schimbă Parola Master
        </button>
        <button class="btn btn-secondary btn-sm" id="btn-open-license-modal" style="display: flex; align-items: center; gap: 0.4rem;" title="Politicile active pentru conferințele organizației">
          <span>⚙️</span> Politici Sesiuni
        </button>
        <button class="btn btn-primary btn-sm" id="btn-open-add-member" style="display: flex; align-items: center; gap: 0.4rem;">
          <span>+</span> Adaugă Membru / Admin
        </button>
      </div>
    </div>

    <!-- License Summary Cards -->
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 0.75rem; margin-bottom: 1.25rem;">
      <div class="license-card">
        <div style="width: 44px; height: 44px; border-radius: 12px; background: rgba(245, 158, 11, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; border: 1px solid rgba(245, 158, 11, 0.3);">
          👑
        </div>
        <div>
          <div style="font-size: 0.75rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Licențe Gazdă (Host) Alocate</div>
          <div style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-top: 2px;">
            ${hostCount} / ${state.organizationLicense.maxHosts} <span style="font-size: 0.8rem; font-weight: 600; color: #6ee7b7;">Alocate</span>
          </div>
          <div style="font-size: 0.75rem; color: #94a3b8; margin-top: 2px;">Doar utilizatorii cu licență pot iniția conferințe</div>
        </div>
      </div>

      <div class="license-card">
        <div style="width: 44px; height: 44px; border-radius: 12px; background: rgba(99, 102, 241, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; border: 1px solid rgba(99, 102, 241, 0.3);">
          👥
        </div>
        <div>
          <div style="font-size: 0.75rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Capacitate per Conferință</div>
          <div style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-top: 2px;">
            ${state.organizationLicense.maxParticipantsPerRoom} participanți <span style="font-size: 0.8rem; font-weight: 400; color: #38bdf8;">/ sală</span>
          </div>
          <div style="font-size: 0.75rem; color: #94a3b8; margin-top: 2px;">Oaspeții externi intră gratuit fără cont</div>
        </div>
      </div>

      <div class="license-card">
        <div style="width: 44px; height: 44px; border-radius: 12px; background: rgba(16, 185, 129, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; border: 1px solid rgba(16, 185, 129, 0.3);">
          🛡️
        </div>
        <div>
          <div style="font-size: 0.75rem; color: var(--text-muted); text-transform: uppercase; font-weight: 600;">Politică Inițiere Sesiuni</div>
          <div style="font-size: 0.95rem; font-weight: 700; color: #6ee7b7; margin-top: 4px;">
            ${state.organizationLicense.creationPolicy === 'admins_hosts' ? 'Admini & Gazde Licențiate' : state.organizationLicense.creationPolicy === 'admins_only' ? 'Strict Administratori' : 'Toți Membrii'}
          </div>
          <div style="font-size: 0.75rem; color: #94a3b8; margin-top: 2px;">Control securitate anti-abuz</div>
        </div>
      </div>
    </div>

    <div class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Nume & Email</th>
            <th>Rol în Organizație</th>
            <th>Licență Gazdă (Host)</th>
            <th>Status Cont</th>
            <th>Membru Din</th>
            <th>Schimbă Rol (RBAC)</th>
            <th>Alocă Licență</th>
            <th>Acțiuni & Gestiune Cont</th>
          </tr>
        </thead>
        <tbody>
          ${currentMembers.map(m => `
            <tr>
              <td>
                <div style="font-weight: 600; color: #fff;">${m.name}</div>
                <div style="font-size: 0.8rem; color: var(--text-muted);">${m.email}</div>
              </td>
              <td><span class="role-pill">${m.role}</span></td>
              <td>
                <span class="license-pill ${m.hasHostLicense ? 'host' : 'member'}">
                  ${m.hasHostLicense ? '👑 Gazdă (Host)' : '👤 Participant'}
                </span>
              </td>
              <td>
                <span class="status-pill ${m.status === 'ACTIVE' ? 'live' : 'cancelled'}">
                  ${m.status === 'ACTIVE' ? 'ACTIV' : 'BLOCAT'}
                </span>
              </td>
              <td style="font-size: 0.8rem; white-space: nowrap;">${m.joinedAt}</td>
              <td>
                <select class="form-select select-role" data-user-id="${m.id}" style="padding: 0.35rem 0.6rem; font-size: 0.8rem;">
                  <option value="ADMIN" ${m.role === 'ADMIN' ? 'selected' : ''}>ADMIN</option>
                  <option value="CLIENT_ADMIN" ${m.role === 'CLIENT_ADMIN' ? 'selected' : ''}>CLIENT</option>
                  <option value="MODERATOR" ${m.role === 'MODERATOR' ? 'selected' : ''}>MODERATOR</option>
                  <option value="MEMBER" ${m.role === 'MEMBER' ? 'selected' : ''}>MEMBER</option>
                </select>
              </td>
              <td>
                <button class="btn ${m.hasHostLicense ? 'btn-secondary' : 'btn-primary'} btn-sm btn-toggle-host-license" data-user-id="${m.id}" style="font-size: 0.75rem; padding: 0.3rem 0.6rem; white-space: nowrap;">
                  ${m.hasHostLicense ? 'Retrage Licență' : '👑 Alocă Licență'}
                </button>
              </td>
              <td style="white-space: nowrap;">
                <div style="display: flex; gap: 0.35rem; align-items: center;">
                  ${(m.id === state.user.id || m.name === state.user.name) ? `
                    <span style="font-size: 0.72rem; color: #fbbf24; font-weight: 700; padding: 0.3rem 0.55rem; background: rgba(245, 158, 11, 0.15); border-radius: 6px; border: 1px solid rgba(245, 158, 11, 0.35); display: inline-flex; align-items: center; gap: 4px;">
                      👑 Master Admin (Activ)
                    </span>
                  ` : `
                    <button class="btn btn-secondary btn-sm btn-set-master-admin" data-user-id="${m.id}" title="Setează acest utilizator ca Master Admin pe această stație" style="padding: 0.3rem 0.6rem; font-size: 0.75rem; color: #fde68a; border-color: rgba(245, 158, 11, 0.4); display: inline-flex; align-items: center; gap: 4px;">
                      ⭐ Setează Master Admin
                    </button>
                  `}

                  ${m.status === 'ACTIVE' ? `
                    <button class="btn btn-secondary btn-sm btn-toggle-status" data-user-id="${m.id}" data-new-status="BLOCKED" title="Blochează cont" style="padding: 0.3rem 0.55rem; font-size: 0.75rem;">
                      🔒
                    </button>
                  ` : `
                    <button class="btn btn-secondary btn-sm btn-toggle-status" data-user-id="${m.id}" data-new-status="ACTIVE" title="Deblochează cont" style="padding: 0.3rem 0.55rem; font-size: 0.75rem;">
                      🔓
                    </button>
                  `}

                  <button class="btn btn-danger btn-sm btn-delete-member" data-user-id="${m.id}" title="Șterge definitiv acest cont" style="padding: 0.3rem 0.55rem; font-size: 0.75rem;">
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;
}

// 5. Audit Log Tab View
function renderAuditTab() {
  const totalLogs = state.auditLogs.length;
  const pageSize = 10;
  const totalPages = Math.ceil(totalLogs / pageSize) || 1;
  if (state.auditPage > totalPages) state.auditPage = totalPages;
  if (state.auditPage < 1) state.auditPage = 1;
  const pageLogs = state.auditLogs.slice((state.auditPage - 1) * pageSize, state.auditPage * pageSize);

  return `
    <div class="page-header">
      <div class="page-header-title-box">
        <h1 class="page-title">Jurnal de Audit Securizat</h1>
        <p class="page-subtitle">Înregistrări cronologice imutabile cu IP mascat conform cerințelor de securitate.</p>
      </div>
    </div>

    <div class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Data & Ora</th>
            <th>Actor (Utilizator)</th>
            <th>Acțiune Înregistrată</th>
            <th>Tip Țintă</th>
            <th>IP Hash (GDPR)</th>
            <th>Metadate Tranzacție</th>
          </tr>
        </thead>
        <tbody>
          ${pageLogs.map(log => `
            <tr>
              <td>${new Date(log.createdAt).toLocaleString('ro-RO')}</td>
              <td style="font-weight: 600; color: #fff;">${log.actorName}</td>
              <td><code>${log.action}</code></td>
              <td>${log.targetType}</td>
              <td><code>${log.ipHash}</code></td>
              <td style="font-size: 0.8rem; color: var(--text-muted); font-family: monospace;">
                ${log.metadata}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    ${renderPaginationControls(state.auditPage, totalLogs, pageSize, 'audit')}
  `;
}

// Modal Dialogs
function renderModal() {
  if (!state.activeModal) return '';

  if (state.activeModal === 'confirm-dialog' && state.pendingConfirm) {
    const { title, description, icon = '🗑️', confirmText = 'Confirmă', danger = true } = state.pendingConfirm;
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 440px; text-align: center; padding: 2.2rem 2rem; border-radius: 20px; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16);">
          <div style="width: 64px; height: 64px; border-radius: 50%; background: ${danger ? 'rgba(239, 68, 68, 0.15)' : 'rgba(99, 102, 241, 0.15)'}; border: 1px solid ${danger ? 'rgba(239, 68, 68, 0.35)' : 'rgba(99, 102, 241, 0.35)'}; display: flex; align-items: center; justify-content: center; font-size: 1.8rem; margin: 0 auto 1.25rem auto;">
            ${icon}
          </div>
          <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.5rem; letter-spacing: -0.01em;">${title}</h3>
          <p style="font-size: 0.85rem; color: #94a3b8; line-height: 1.5; margin-bottom: 1.75rem;">${description}</p>
          <div style="display: flex; gap: 0.75rem; justify-content: center;">
            <button type="button" class="btn btn-secondary" id="btn-cancel-confirm-dialog" style="flex: 1; padding: 0.7rem 1rem; font-weight: 600;">
              Anulează
            </button>
            <button type="button" class="btn ${danger ? 'btn-danger' : 'btn-primary'}" id="btn-ok-confirm-dialog" style="flex: 1; padding: 0.7rem 1rem; font-weight: 700;">
              ${confirmText}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'version-info') {
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 480px; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(24px); border: 1px solid rgba(56, 189, 248, 0.4); box-shadow: 0 25px 70px rgba(0, 0, 0, 0.9); border-radius: 18px;">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" style="width: 36px; height: 36px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.5));" />
              <div>
                <h3 class="modal-title" style="font-size: 1.15rem; margin: 0; display: flex; align-items: center; gap: 0.5rem; color: #fff;">
                  <span>Informații Versiune</span>
                  <span style="background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 0.7rem; padding: 2px 8px; border-radius: 20px; font-weight: 700;">v${APP_VERSION}</span>
                </h3>
                <div style="font-size: 0.74rem; color: var(--text-muted); margin-top: 2px;">Vorbello Secure Communications • ${APP_BUILD_TAG}</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <div style="display: flex; flex-direction: column; gap: 0.65rem; margin: 1.15rem 0;">
            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <span style="font-size: 0.83rem; color: #cbd5e1;">Versiune Aplicație:</span>
              <strong style="color: #38bdf8; font-size: 0.92rem; font-family: monospace;">v${APP_VERSION} (${APP_BUILD_TAG})</strong>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <span style="font-size: 0.83rem; color: #cbd5e1;">Data Lansare / Build:</span>
              <span style="color: #fff; font-size: 0.83rem; font-weight: 600;">${APP_BUILD_DATE}</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <span style="font-size: 0.83rem; color: #cbd5e1;">Canal Distribuție:</span>
              <span style="color: #6ee7b7; font-size: 0.83rem; font-weight: 600;">Enterprise Stable (Securizat)</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <span style="font-size: 0.83rem; color: #cbd5e1;">Criptare & Securitate:</span>
              <span style="color: #34d399; font-size: 0.83rem; font-weight: 600;">End-to-End DTLS-SRTP 256-bit</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <span style="font-size: 0.83rem; color: #cbd5e1;">Stare Licențiere:</span>
              <span style="color: #38bdf8; font-size: 0.83rem; font-weight: 600;">Licență Activată & Verificată</span>
            </div>

            ${state.isAdminLoggedIn ? `
              <div style="margin-top: 0.4rem; padding: 0.75rem 0.95rem; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: 10px;">
                <div style="font-size: 0.75rem; font-weight: 700; color: #6ee7b7; margin-bottom: 0.35rem;">🔒 Panou Diagnostic Administrator:</div>
                <div style="font-size: 0.74rem; color: #cbd5e1; display: flex; flex-direction: column; gap: 3px;">
                  <div>• Server Semnalizare: <code>app.vorbello.eu/signal.php</code></div>
                  <div>• Conexiune SFU: <code>${state.vpsConfig.livekitUrl || 'wss://live.vorbello.eu'}</code></div>
                  <div>• Mod Curent: <strong>${state.appMode === 'production' ? 'Producție' : 'Demo Local'}</strong></div>
                </div>
              </div>
            ` : ''}
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 0.85rem;">
            <button class="btn btn-primary" id="btn-ok-version-modal" style="padding: 0.5rem 1.4rem; font-size: 0.825rem;">
              Închide
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'admin-login') {
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 450px; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(99, 102, 241, 0.35); border-radius: 20px; padding: 2rem; box-shadow: 0 25px 65px rgba(0, 0, 0, 0.85);">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem; margin-bottom: 1.15rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <img src="${VORBELLO_LOGO}" alt="Vorbello" style="width: 38px; height: 38px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(99, 102, 241, 0.5));" />
              <div>
                <h3 class="modal-title" style="font-size: 1.2rem; margin: 0; color: #fff;">Autentificare Administrator</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Panou de Control & Gestiune Gazdă</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          ${state.adminAuthError ? `
            <div style="background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #fca5a5; padding: 0.65rem 0.9rem; border-radius: 10px; font-size: 0.8rem; margin-bottom: 1.15rem; display: flex; align-items: center; gap: 0.5rem;">
              <span>⚠️</span> <span>${escapeHtml(state.adminAuthError)}</span>
            </div>
          ` : ''}

          <form id="form-admin-login">
            <div class="form-group" style="margin-bottom: 0.95rem;">
              <label class="form-label" style="font-size: 0.825rem;">Email sau Utilizator Administrator:</label>
              <input type="text" class="form-input" id="input-admin-email" placeholder="dannyopr@gmail.com" value="${escapeHtml(state.user.email || 'dannyopr@gmail.com')}" required style="font-size: 0.9rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 1.25rem;">
              <label class="form-label" style="font-size: 0.825rem;">Parolă Administrator:</label>
              <div style="position: relative;">
                <input type="password" class="form-input" id="input-admin-password" placeholder="Introduceți parola de administrator..." required style="width: 100%; font-size: 0.9rem; padding-right: 42px;" autofocus />
                <button type="button" id="btn-toggle-show-pass" style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #94a3b8; font-size: 1.05rem;" title="Arată / Ascunde Parola">
                  👁️
                </button>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1.15rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary" style="font-weight: 700; padding: 0.65rem 1.4rem; display: inline-flex; align-items: center; gap: 0.45rem;">
                <span>🔓</span> Conectare în Panou
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'change-password') {
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 480px; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(245, 158, 11, 0.4); border-radius: 20px; padding: 2rem; box-shadow: 0 25px 65px rgba(0, 0, 0, 0.85);">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem; margin-bottom: 1.15rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 40px; height: 40px; border-radius: 10px; background: rgba(245, 158, 11, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.3rem; border: 1px solid rgba(245, 158, 11, 0.4);">
                🔑
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0; color: #fff;">Schimbare Parolă Administrator</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Cont Master: <b style="color: #38bdf8;">dannyopr@gmail.com</b></div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-change-password">
            <div class="form-group" style="margin-bottom: 0.95rem;">
              <label class="form-label" style="font-size: 0.825rem;">Parolă Actuală:</label>
              <input type="password" class="form-input" id="input-curr-pass" placeholder="Introduceți parola curentă..." required style="font-size: 0.9rem;" autofocus />
            </div>

            <div class="form-group" style="margin-bottom: 0.95rem;">
              <label class="form-label" style="font-size: 0.825rem;">Parolă Nouă:</label>
              <input type="password" class="form-input" id="input-new-pass" placeholder="Introduceți noua parolă (minim 6 caractere)..." required minlength="6" style="font-size: 0.9rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 1.25rem;">
              <label class="form-label" style="font-size: 0.825rem;">Confirmare Parolă Nouă:</label>
              <input type="password" class="form-input" id="input-confirm-pass" placeholder="Reintroduceți noua parolă..." required minlength="6" style="font-size: 0.9rem;" />
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1.15rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary" style="font-weight: 700; background: linear-gradient(135deg, #f59e0b, #d97706); border-color: #f59e0b;">
                <span>💾</span> Salvează Noua Parolă
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'issued-licenses') {
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 820px; width: 95%; max-height: 85vh; display: flex; flex-direction: column; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(56, 189, 248, 0.4); border-radius: 20px; padding: 1.75rem; box-shadow: 0 25px 70px rgba(0, 0, 0, 0.9);">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem; margin-bottom: 1rem; flex-shrink: 0;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 42px; height: 42px; border-radius: 12px; background: rgba(56, 189, 248, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; border: 1px solid rgba(56, 189, 248, 0.35);">
                👑
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0; color: #fff;">Gestiune Licențe & Abonamente Vorbello</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Generare și emitere chei de acces pentru clienți și administratori</div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <button type="button" class="btn btn-primary btn-sm" id="btn-open-issue-license-modal" style="display: flex; align-items: center; gap: 0.35rem; font-weight: 700; padding: 0.45rem 0.85rem; background: linear-gradient(135deg, #38bdf8, #0284c7); border-color: #38bdf8;">
                <span>+</span> Emite Licență Nouă
              </button>
              <button class="modal-close" id="btn-close-modal">&times;</button>
            </div>
          </div>

          <div style="overflow-y: auto; flex: 1; margin-bottom: 1rem; padding-right: 4px;">
            ${state.issuedLicenses.length === 0 ? `
              <div style="text-align: center; padding: 3rem 1rem; color: #94a3b8;">
                <div style="font-size: 2rem; margin-bottom: 0.5rem;">📜</div>
                <div style="font-weight: 600; color: #fff;">Nu există licențe emise încă.</div>
                <div style="font-size: 0.8rem; margin-top: 0.25rem;">Apăsați pe „+ Emite Licență Nouă” pentru a genera primul abonament.</div>
              </div>
            ` : `
              <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                ${state.issuedLicenses.map(lic => `
                  <div style="background: rgba(255, 255, 255, 0.04); border: 1px solid ${lic.status === 'SUSPENDED' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(255, 255, 255, 0.08)'}; border-radius: 12px; padding: 0.95rem 1.15rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap;">
                    <div style="flex: 1; min-width: 260px;">
                      <div style="display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.25rem;">
                        <span style="font-weight: 700; color: #fff; font-size: 0.95rem;">${escapeHtml(lic.clientName)}</span>
                        <span style="font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 6px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); text-transform: uppercase;">${lic.tier}</span>
                        <span style="font-size: 0.68rem; font-weight: 700; padding: 2px 7px; border-radius: 6px; background: ${lic.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}; color: ${lic.status === 'ACTIVE' ? '#6ee7b7' : '#fca5a5'}; border: 1px solid ${lic.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'};">
                          ${lic.status === 'ACTIVE' ? '🟢 ACTIV' : '🔴 SUSPENDAT (Neplătit)'}
                        </span>
                      </div>
                      <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem;">
                        <code style="font-family: monospace; font-size: 0.825rem; font-weight: 700; color: #fde68a; background: rgba(0, 0, 0, 0.4); padding: 3px 8px; border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.1);">${lic.licenseKey}</code>
                        <button type="button" class="btn btn-secondary btn-sm btn-copy-lic-key" data-key="${lic.licenseKey}" title="Copiază cheia în clipboard" style="padding: 2px 7px; font-size: 0.7rem;">📋 Copiază</button>
                      </div>
                      <div style="font-size: 0.75rem; color: #94a3b8; display: flex; gap: 1rem; flex-wrap: wrap;">
                        <span>👑 <b>${lic.maxHosts}</b> Gazde</span>
                        <span>👥 <b>${lic.maxParticipantsPerRoom}</b> Part./sală</span>
                        <span>📅 Expiră: <b>${lic.expiresAt}</b></span>
                        <span>💳 <b>${escapeHtml(lic.priceTag)}</b></span>
                      </div>
                    </div>

                    <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                      <button type="button" class="btn btn-secondary btn-sm btn-toggle-suspend-lic" data-id="${lic.id}" title="${lic.status === 'ACTIVE' ? 'Pune abonamentul pe pauză (blochează accesul la conferințe)' : 'Re-activează abonamentul clientului'}" style="font-size: 0.75rem; padding: 0.4rem 0.65rem; color: ${lic.status === 'ACTIVE' ? '#fbbf24' : '#6ee7b7'}; border-color: ${lic.status === 'ACTIVE' ? 'rgba(245, 158, 11, 0.35)' : 'rgba(16, 185, 129, 0.35)'};">
                        ${lic.status === 'ACTIVE' ? '⏸️ Pune pe Pauză' : '▶️ Reactivează'}
                      </button>
                      <button type="button" class="btn btn-secondary btn-sm btn-activate-org-lic" data-id="${lic.id}" title="Setează această organizație și licență ca activă pe acest calculator" style="font-size: 0.75rem; padding: 0.4rem 0.75rem; color: #38bdf8; border-color: rgba(56, 189, 248, 0.35);">
                        👑 Încarcă pe PC
                      </button>
                      <button type="button" class="btn btn-danger btn-sm btn-revoke-lic" data-id="${lic.id}" title="Șterge definitiv această licență" style="padding: 0.4rem 0.6rem; font-size: 0.75rem;">
                        🗑️
                      </button>
                    </div>
                  </div>
                `).join('')}
              </div>
            `}
          </div>

          <div style="display: flex; justify-content: flex-end; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 0.85rem; flex-shrink: 0;">
            <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Închide</button>
          </div>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'issue-license') {
    const generatedKey = generateLicenseKey('pro');
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 540px; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(56, 189, 248, 0.4); border-radius: 20px; padding: 2rem; box-shadow: 0 25px 65px rgba(0, 0, 0, 0.85);">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem; margin-bottom: 1.15rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 40px; height: 40px; border-radius: 10px; background: rgba(56, 189, 248, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.3rem;">
                👑
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0; color: #fff;">Emitere Licență & Abonament Nou</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Generare pachet de acces pentru administratori sau companii</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-issue-license">
            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Nume Client / Organizație / Departament:</label>
              <input type="text" class="form-input" id="input-license-client" placeholder="ex: SC Medical Pro SRL" required style="font-size: 0.875rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Tip Pachet / Abonament:</label>
              <select class="form-select" id="select-license-tier" style="font-size: 0.875rem;">
                <option value="starter">Starter Pro (100 Conexiuni) — 100 part., 5 gazde</option>
                <option value="business" selected>Business Cloud (250 Conexiuni) — 250 part., 15 gazde (Recomandat)</option>
                <option value="enterprise_500">Enterprise 500 (500 Conexiuni) — 500 part., 30 gazde</option>
                <option value="enterprise_1000">Enterprise Max (1000 Conexiuni) — 1000 part., Gazde Nelimitate</option>
                <option value="custom">Personalizat / Custom — Configurare manuală</option>
              </select>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 0.85rem;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem;">Număr Gazde (Hosts):</label>
                <input type="number" class="form-input" id="input-license-hosts" value="15" min="1" max="1000" required style="font-size: 0.875rem;" />
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem;">Part./Sală (Capacitate):</label>
                <select class="form-select" id="select-license-capacity" style="font-size: 0.875rem;">
                  <option value="100">100 conexiuni / sală</option>
                  <option value="250" selected>250 conexiuni / sală</option>
                  <option value="500">500 conexiuni / sală</option>
                  <option value="1000">1000 conexiuni / sală</option>
                </select>
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 0.85rem;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem;">Perioadă Valabilitate:</label>
                <select class="form-select" id="select-license-duration" style="font-size: 0.875rem;">
                  <option value="1">1 Lună (Lunar)</option>
                  <option value="3">3 Luni</option>
                  <option value="6">6 Luni</option>
                  <option value="12" selected>1 An (Anual)</option>
                  <option value="24">2 Ani</option>
                  <option value="999">Permanent / Nelimitat</option>
                </select>
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem;">Notă Preț / Abonament:</label>
                <input type="text" class="form-input" id="input-license-price" value="9,99€ / lună (Plan Pro Consigliato)" style="font-size: 0.875rem;" />
              </div>
            </div>

            <div class="form-group" style="margin-bottom: 1.25rem;">
              <label class="form-label" style="font-size: 0.825rem;">Cheie Licență Generată:</label>
              <div style="display: flex; gap: 0.5rem;">
                <input type="text" class="form-input" id="input-license-key-val" value="${generatedKey}" readonly style="font-size: 0.875rem; font-family: monospace; color: #fde68a; font-weight: 700; flex: 1; background: rgba(0,0,0,0.4);" />
                <button type="button" class="btn btn-secondary btn-sm" id="btn-regen-license-key" style="font-size: 0.75rem; white-space: nowrap;">
                  ⚡ Regenerează
                </button>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem;">
              <button type="button" class="btn btn-secondary" id="btn-back-to-licenses">Înapoi la Listă</button>
              <button type="submit" class="btn btn-primary" style="font-weight: 700; background: linear-gradient(135deg, #38bdf8, #0284c7); border-color: #38bdf8;">
                <span>✅</span> Emite și Salvează Licența
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'recording-player' && state.selectedRecordingForPlayer) {
    const rec = state.selectedRecordingForPlayer;
    const isDisk = rec.storageType === 'disk' || Boolean(rec.filePath);
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 720px; width: 95%; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 20px; box-shadow: 0 25px 70px rgba(0,0,0,0.9);">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem;">
            <div>
              <h3 class="modal-title" style="margin: 0; display: flex; align-items: center; gap: 8px;">
                <span>🎥</span> ${rec.meetingTitle || rec.roomName}
              </h3>
              <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 3px;">
                Cameră: <code>${rec.roomSlug}</code> • Data: <strong>${rec.createdAt}</strong> • Durată: <strong>${rec.duration}</strong>
                ${isDisk ? '<span style="color: #4ade80; margin-left: 8px;">💾 Stocat pe PC</span>' : '<span style="color: #38bdf8; margin-left: 8px;">📥 Descărcat în Browser</span>'}
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>
          
          <div style="background: #000; border-radius: 12px 12px 0 0; overflow: hidden; margin-top: 1.25rem; margin-bottom: 0; border: 1px solid rgba(255, 255, 255, 0.12); border-bottom: none; position: relative; min-height: 260px; display: flex; align-items: center; justify-content: center;">
            <div id="player-loading-spinner" style="display: none; position: absolute; background: rgba(0,0,0,0.7); padding: 8px 16px; border-radius: 8px; color: #38bdf8; font-size: 0.85rem; font-weight: 600; z-index: 5;">
              ⏳ Se încarcă datele video de pe disc...
            </div>
            ${rec.format === 'audio' ? `
              <div style="text-align: center; padding: 2.5rem 1.5rem; width: 100%;">
                <div style="font-size: 3.5rem; margin-bottom: 0.75rem;">🎙️</div>
                <div style="font-weight: 700; color: #fff; font-size: 1.1rem; margin-bottom: 4px;">Înregistrare Audio HD Vorbello</div>
                <div style="color: var(--text-muted); font-size: 0.85rem; margin-bottom: 1.25rem;">Fișier: ${rec.fileName || 'Inregistrare_Audio.webm'}</div>
                <audio id="rec-player-media" controls style="width: 100%; max-width: 480px; margin: 0 auto; display: block;" src="${rec.downloadUrl || ''}"></audio>
              </div>
            ` : `
              <video id="rec-player-media" controls autoplay playsinline style="width: 100%; max-height: 420px; object-fit: contain; background: #000; display: block; cursor: pointer;" src="${rec.downloadUrl || ''}">
                Browserul nu poate reda direct formatul. Folosiți butonul „Deschide cu Playerul Windows”.
              </video>
            `}
          </div>

          <!-- Permanent Vorbello Scrub & Playback Controller (NEVER DISAPPEARS) -->
          <div class="vorbello-video-scrub-bar" style="margin-bottom: 1.25rem; border: 1px solid rgba(255,255,255,0.12); border-top: 1px solid rgba(255,255,255,0.06);">
            <button type="button" class="btn btn-secondary btn-sm" id="btn-player-play-pause" style="width: 38px; height: 38px; padding: 0; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; border-radius: 8px;" title="Redare / Pauză">
              ▶️
            </button>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-player-skip-back" title="Înapoi 10 secunde" style="padding: 0.4rem 0.6rem; font-size: 0.75rem; border-radius: 6px;">
              ⏪ 10s
            </button>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-player-skip-fwd" title="Înainte 10 secunde" style="padding: 0.4rem 0.6rem; font-size: 0.75rem; border-radius: 6px;">
              10s ⏩
            </button>
            
            <input type="range" class="vorbello-video-scrub-slider" id="player-scrub-slider" min="0" max="100" value="0" step="0.1" title="Derulează înregistrarea (Scrubbing)">

            <div id="player-time-display" style="font-size: 0.8rem; font-family: monospace; color: #cbd5e1; white-space: nowrap; font-weight: 700; padding: 0 4px;">
              00:00 / 00:00
            </div>

            <button type="button" class="btn btn-secondary btn-sm" id="btn-player-volume" style="width: 36px; height: 36px; padding: 0; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; border-radius: 8px;" title="Sunet Oprit / Pornit">
              🔊
            </button>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-player-fullscreen" style="width: 36px; height: 36px; padding: 0; display: flex; align-items: center; justify-content: center; font-size: 1rem; border-radius: 8px;" title="Ecran Complet">
              ⛶
            </button>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 1.25rem; gap: 0.75rem; flex-wrap: wrap;">
            <div style="font-size: 0.78rem; color: var(--text-muted);">
              Format: <strong style="color: #38bdf8;">${rec.format.toUpperCase()}</strong> • Mărime: <strong>${rec.fileSize}</strong> • Înregistrat de: <strong>${rec.recordedBy}</strong>
            </div>
            <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
              ${isDisk ? `
                <button type="button" class="btn btn-secondary" id="btn-player-open-folder" data-file-path="${escapeHtml(rec.filePath || '')}" style="display: flex; align-items: center; gap: 4px;">
                  📂 Deschide Dosar
                </button>
                <button type="button" class="btn btn-primary" id="btn-player-open-native" data-file-path="${escapeHtml(rec.filePath || '')}" style="display: flex; align-items: center; gap: 5px; font-weight: 700;">
                  🚀 Deschide cu Playerul Windows
                </button>
              ` : `
                <button type="button" class="btn btn-primary btn-modal-download-rec" data-rec-id="${rec.id}">
                  ⬇️ Descarcă Fișierul
                </button>
              `}
              <button type="button" class="btn btn-secondary" id="btn-close-player-modal">Închide</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'pin-verify' && state.pendingPinVerification) {
    const pending = state.pendingPinVerification;
    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 440px; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(245, 158, 11, 0.45); border-radius: 20px; padding: 2rem; box-shadow: 0 25px 70px rgba(0, 0, 0, 0.9); text-align: center;">
          <div style="width: 60px; height: 60px; border-radius: 50%; background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.35); display: flex; align-items: center; justify-content: center; font-size: 1.8rem; margin: 0 auto 1.15rem auto;">
            🔒
          </div>
          <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.35rem;">Conferință Securizată cu PIN</h3>
          <p style="font-size: 0.85rem; color: #94a3b8; line-height: 1.45; margin-bottom: 1.25rem;">
            Sala <strong>„${escapeHtml(pending.roomName)}”</strong> este protejată prin cod PIN. Introduceți codul primit de la organizator pentru a intra în conferință.
          </p>

          ${state.pinError ? `
            <div style="background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #fca5a5; padding: 0.65rem 0.85rem; border-radius: 10px; font-size: 0.82rem; margin-bottom: 1.15rem; display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
              <span>⛔</span> <span>${escapeHtml(state.pinError)}</span>
            </div>
          ` : ''}

          <form id="form-verify-pin">
            <div class="form-group" style="margin-bottom: 1.25rem;">
              <input 
                type="password" 
                class="form-input" 
                id="input-pin-code" 
                placeholder="Introduceți PIN-ul..." 
                required 
                autofocus 
                style="font-size: 1.25rem; text-align: center; font-family: monospace; letter-spacing: 5px; padding: 0.7rem 1rem;" 
              />
            </div>

            <div style="display: flex; gap: 0.75rem; justify-content: center;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal" style="flex: 1; padding: 0.65rem 1rem;">Anulează</button>
              <button type="submit" class="btn btn-primary" style="flex: 1; font-weight: 700; background: linear-gradient(135deg, #f59e0b, #d97706); border-color: #f59e0b; padding: 0.65rem 1rem;">
                🚀 Conectare
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'edit-meeting' && state.selectedMeetingForEdit) {
    const m = state.selectedMeetingForEdit;
    const currentRooms = state.appMode === 'production' ? state.prodRooms : state.rooms;
    let startsValue = '';
    let editDateVal = '';
    let editTimeVal = '15:00';
    try {
      if (m.startsAt) {
        const d = new Date(m.startsAt);
        startsValue = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
        editDateVal = startsValue.slice(0, 10);
        editTimeVal = startsValue.slice(11, 16);
      }
    } catch (_) {}

    return `
      <div class="modal-overlay" style="align-items: center; justify-content: center; z-index: 99999;">
        <div class="modal-content" style="max-width: 680px; width: 95vw; background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); border: 1px solid rgba(99, 102, 241, 0.4); border-radius: 20px; padding: 1.5rem 1.85rem; box-shadow: 0 25px 70px rgba(0, 0, 0, 0.9);">
          <div class="modal-header" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem; margin-bottom: 1.15rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 40px; height: 40px; border-radius: 10px; background: rgba(99, 102, 241, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.3rem;">
                ✏️
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0; color: #fff;">Editează Întâlnirea Programată</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Organizator: <b>${escapeHtml(m.creatorName)}</b></div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-edit-meeting">
            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Cameră Atribuită:</label>
              <select class="form-select" id="input-edit-meet-room" required style="font-size: 0.875rem;">
                ${currentRooms.map(r => `<option value="${r.id}" ${r.id === m.roomId ? 'selected' : ''}>${r.name}</option>`).join('')}
              </select>
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Titlu Întâlnire:</label>
              <input type="text" class="form-input" id="input-edit-meet-title" value="${escapeHtml(m.title)}" required style="font-size: 0.875rem;">
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Agendă & Ordine de Zi (Opțional):</label>
              <textarea class="form-textarea" id="input-edit-meet-agenda" rows="2" style="font-size: 0.85rem;" placeholder="Puncte de discuție, ordine de zi...">${escapeHtml(m.agenda || '')}</textarea>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 0.85rem;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">📅 Dată Început:</label>
                <input type="date" class="form-input" id="input-edit-meet-date" value="${editDateVal}" required style="font-size: 0.85rem; color-scheme: dark; width: 100%; height: 38px; cursor: pointer; box-sizing: border-box;">
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">⏰ Oră Început:</label>
                <input type="time" class="form-input" id="input-edit-meet-time" value="${editTimeVal}" required style="font-size: 0.85rem; color-scheme: dark; width: 100%; height: 38px; cursor: pointer; box-sizing: border-box;">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; margin-bottom: 0.85rem; align-items: start;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">Status Sesiune:</label>
                <select class="form-select" id="input-edit-meet-status" style="font-size: 0.85rem; width: 100%; height: 38px; box-sizing: border-box;">
                  <option value="scheduled" ${m.status === 'scheduled' ? 'selected' : ''}>Programată (SCHEDULED)</option>
                  <option value="live" ${m.status === 'live' ? 'selected' : ''}>În Desfășurare (LIVE)</option>
                  <option value="completed" ${m.status === 'completed' ? 'selected' : ''}>Finalizată (COMPLETED)</option>
                </select>
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">Politică de Acces:</label>
                <select class="form-select" id="input-edit-meet-policy" style="font-size: 0.85rem; width: 100%; height: 38px; box-sizing: border-box;">
                  <option value="org_members" ${m.joinPolicy === 'org_members' ? 'selected' : ''}>Membri Organizație</option>
                  <option value="invite_only" ${m.joinPolicy === 'invite_only' ? 'selected' : ''}>Doar cu Invitație</option>
                </select>
              </div>
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">Fus Orar Conferință:</label>
              <select class="form-select" id="input-edit-meet-timezone" style="font-size: 0.85rem; width: 100%; height: 38px; box-sizing: border-box;">
                ${TIMEZONE_OPTIONS.map(tzOpt => `<option value="${tzOpt.value}" ${(m.timezone || state.selectedTimezone) === tzOpt.value ? 'selected' : ''}>${tzOpt.label}</option>`).join('')}
              </select>
            </div>

            <div class="form-group" style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25); border-radius: 12px; padding: 0.85rem; margin-bottom: 0.85rem;">
              <label class="form-label" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem;">
                <span style="color: #fbbf24; font-weight: 700;">🔑 Cod PIN Întâlnire (Passcode — Opțional)</span>
                <span style="font-size: 0.72rem; color: #cbd5e1;">Acces Securizat</span>
              </label>
              <div style="display: flex; gap: 0.5rem;">
                <input type="text" class="form-input" id="input-edit-meet-pin" value="${m.pinCode || ''}" placeholder="ex: 1234 (lăsați liber fără PIN)" style="font-family: monospace; letter-spacing: 2px; font-weight: 700; flex: 1;">
                <button type="button" class="btn btn-secondary btn-sm" id="btn-edit-gen-meet-pin" title="Generează PIN aleatoriu de 4 cifre" style="white-space: nowrap; font-size: 0.78rem;">
                  🎲 Generează PIN
                </button>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-edit-clear-meet-pin" title="Șterge codul PIN" style="white-space: nowrap; font-size: 0.78rem;">
                  ✕ Fără PIN
                </button>
              </div>
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 4px;">
                Participanții vor trebui să introducă acest cod PIN înainte de a se conecta.
              </div>
            </div>

            <!-- Zoom / Meet Security & Governance Policies -->
            <div style="background: rgba(0,0,0,0.28); border: 1px solid rgba(255,255,255,0.09); border-radius: 12px; padding: 1rem; margin-bottom: 1.25rem;">
              <div style="font-weight: 700; font-size: 0.825rem; color: #38bdf8; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 6px;">
                <span>⚙️</span> Reguli de Acces, Securitate & Cvorum Statutar
              </div>
              
              <div style="display: flex; flex-direction: column; gap: 0.7rem; font-size: 0.825rem;">
                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-wait-for-host" ${m.waitForHost !== false ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>⏳ Participanții așteaptă gazda să înceapă ședința</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Nimeni nu intră în conferință dacă persoana care a creat întâlnirea nu este conectată. Participanții stau în ecranul de așteptare.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-waiting" ${m.waitingRoomEnabled ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🛡️ Sală de Așteptare (Waiting Room / Aprobare manuală)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții necesită aprobare individuală din partea gazdei înainte de a intra.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-record" ${m.autoRecord ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🔴 Înregistrare Automată (Strict pentru Gazda Creatoare)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Pornește automat înregistrarea video/audio doar pe calculatorul gazdei când aceasta intră în sală. Niciun alt participant nu va înregistra.</div>
                  </div>
                </label>

                <!-- Sub-panou Parametri Înregistrare Automată -->
                <div id="panel-edit-meet-auto-record-options" style="display: ${m.autoRecord ? 'block' : 'none'}; margin-left: 26px; margin-top: -2px; margin-bottom: 6px; padding: 12px 14px; background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.28); border-radius: 10px;">
                  <div style="font-size: 0.78rem; font-weight: 700; color: #fca5a5; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                    <span>⚙️</span> Parametrii Înregistrării Automate:
                  </div>

                  <div style="display: flex; flex-direction: column; gap: 9px;">
                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format & Calitate Înregistrare</label>
                      <select class="form-select" id="input-edit-meet-auto-record-format" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        <option value="video-hd" ${(!m.autoRecordFormat || m.autoRecordFormat === 'video-hd') ? 'selected' : ''}>📹 Video HD (720p @ 30fps) — Echilibrat (Recomandat)</option>
                        <option value="video-fhd" ${m.autoRecordFormat === 'video-fhd' ? 'selected' : ''}>🎥 Video Full HD (1080p @ 30fps) — Claritate Maximă</option>
                        <option value="audio" ${m.autoRecordFormat === 'audio' ? 'selected' : ''}>🎙️ Doar Audio (Stereo HD) — Economie Spațiu / Proces-Verbal</option>
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Compoziție Cadru Video (Layout)</label>
                      <select class="form-select" id="input-edit-meet-auto-record-layout" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderAutoRecordLayoutOptions(m.autoRecordLayout)}
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format Fișier (Container Salvare)</label>
                      <select class="form-select" id="input-edit-meet-auto-record-container" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderContainerOptions(m.autoRecordContainer)}
                      </select>
                    </div>

                    <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; color: #e2e8f0; font-size: 0.76rem;">
                      <input type="checkbox" id="input-edit-meet-auto-record-notify" ${m.autoRecordNotify !== false ? 'checked' : ''} style="accent-color: #ef4444; margin-top: 1px;">
                      <span>🔔 Notificare participanți (indicator <b>„🔴 Înregistrare în curs”</b> afișat în sală)</span>
                    </label>
                  </div>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-guest-invites" ${m.allowGuestInvites ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🔗 Permite membrilor simpli să genereze link de invitație</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Dacă este debifat, DOAR Gazda/Adminul poate genera și trimite invitații.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-mute" ${m.muteOnEntry !== false ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🎙️ Microfon oprit automat la intrare (Mute on entry)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Toți oaspeții intră cu microfonul dezactivat pentru a preveni zgomotul de fond.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-cam-entry" ${m.cameraOffOnEntry ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>📹 Cameră oprită automat la intrare</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții intră fără video activ până decid să îl pornească manual.</div>
                  </div>
                </label>

                <div style="margin-top: 0.25rem;">
                  <label class="form-label" style="font-size: 0.78rem; margin-bottom: 4px;">🖥️ Permisiune Partajare Ecran</label>
                  <select class="form-select" id="input-edit-meet-screen-share" style="font-size: 0.8rem; padding: 0.45rem 0.75rem;">
                    <option value="everyone" ${m.allowScreenShare !== 'hosts_only' ? 'selected' : ''}>Toți participanții pot partaja ecranul</option>
                    <option value="hosts_only" ${m.allowScreenShare === 'hosts_only' ? 'selected' : ''}>Doar Gazda și Co-Gazdele pot partaja ecranul</option>
                  </select>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-meet-attendance" ${m.attendanceTracking !== false ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>📋 Prezență automată & Jurnal de Cvorum pentru membri de partid</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Înregistrează automat în tab-ul „Prezență” numele, ora conectării, ieșirii și cvorumul.</div>
                  </div>
                </label>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary" style="font-weight: 700;">💾 Salvează Modificările</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'create-room') {
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 680px; width: 100%;">
          <div class="modal-header">
            <h3 class="modal-title">Creează Cameră Nouă</h3>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>
          <form id="form-create-room">
            <div class="form-group">
              <label class="form-label">Nume Cameră / Spațiu</label>
              <input type="text" class="form-input" id="input-room-name" placeholder="ex: Camera Birou Permanent / Sală Oficială" required>
            </div>
            <div class="form-group">
              <label class="form-label">Tip Spațiu</label>
              <select class="form-select" id="input-room-type">
                <option value="permanent">🏢 Sală Permanentă 24/7 (Deschisă continuu pentru lucru / comitet)</option>
                <option value="meeting">📅 Ședință / Reuniune Programată (Cu agendă & ordine de zi)</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Descriere & Scop</label>
              <textarea class="form-textarea" id="input-room-desc" rows="2" placeholder="Scopul conferinței, membrii eligibili..."></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Vizibilitate</label>
              <select class="form-select" id="input-room-vis">
                <option value="internal">Internă (accesibilă tuturor membrilor organizației)</option>
                <option value="private">Privată (accesibilă doar cu invitație explicită)</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Capacitate Maximă Participanți (2 - 100)</label>
              <input type="number" class="form-input" id="input-room-max" min="2" max="100" value="50">
            </div>

            <!-- PIN Code (Zoom Passcode style) -->
            <div class="form-group" style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25); border-radius: 12px; padding: 0.9rem;">
              <label class="form-label" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem;">
                <span style="color: #fbbf24; font-weight: 700;">🔑 Cod PIN Conectare (Passcode — Opțional)</span>
                <span style="font-size: 0.72rem; color: #cbd5e1;">Cod Acces Securizat</span>
              </label>
              <div style="display: flex; gap: 0.5rem;">
                <input type="text" class="form-input" id="input-room-pin" placeholder="ex: 4821 (lăsați liber pentru acces fără PIN)" style="font-family: monospace; letter-spacing: 2px; font-weight: 700; flex: 1;">
                <button type="button" class="btn btn-secondary btn-sm" id="btn-generate-room-pin" title="Generează PIN aleatoriu de 4 cifre" style="white-space: nowrap; font-size: 0.78rem;">
                  🎲 Generează
                </button>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-clear-room-pin" title="Șterge codul PIN" style="white-space: nowrap; font-size: 0.78rem;">
                  ✕ Fără PIN
                </button>
              </div>
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 4px;">
                Dacă este setat, participanții vor trebui să introducă acest PIN înainte de a intra în sală.
              </div>
            </div>

            <!-- Zoom / Meet Security & Governance Policies -->
            <div style="background: rgba(0,0,0,0.28); border: 1px solid rgba(255,255,255,0.09); border-radius: 12px; padding: 1rem; margin: 1.25rem 0;">
              <div style="font-weight: 700; font-size: 0.825rem; color: #38bdf8; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 6px;">
                <span>⚙️</span> Reguli de Acces, Securitate & Cvorum Statutar
              </div>
              
              <div style="display: flex; flex-direction: column; gap: 0.7rem; font-size: 0.825rem;">
                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-wait-for-host" checked style="margin-top: 3px;">
                  <div>
                    <strong>⏳ Participanții așteaptă gazda să înceapă ședința</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Nimeni nu intră în conferință dacă persoana care a creat întâlnirea nu este conectată. Participanții stau în ecranul de așteptare.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-waiting" style="margin-top: 3px;">
                  <div>
                    <strong>🛡️ Sală de Așteptare (Waiting Room / Aprobare manuală)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții necesită aprobare individuală din partea gazdei înainte de a intra.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-auto-record" style="margin-top: 3px;">
                  <div>
                    <strong>🔴 Înregistrare Automată (Strict pentru Gazda Creatoare)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Pornește automat înregistrarea video/audio doar pe calculatorul gazdei când aceasta intră în sală. Niciun alt participant nu va înregistra.</div>
                  </div>
                </label>

                <!-- Sub-panou Parametri Înregistrare Automată -->
                <div id="panel-room-auto-record-options" style="display: none; margin-left: 26px; margin-top: -2px; margin-bottom: 6px; padding: 12px 14px; background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.28); border-radius: 10px;">
                  <div style="font-size: 0.78rem; font-weight: 700; color: #fca5a5; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                    <span>⚙️</span> Parametrii Înregistrării Automate:
                  </div>

                  <div style="display: flex; flex-direction: column; gap: 9px;">
                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format & Calitate Înregistrare</label>
                      <select class="form-select" id="input-room-auto-record-format" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        <option value="video-hd" selected>📹 Video HD (720p @ 30fps) — Echilibrat (Recomandat)</option>
                        <option value="video-fhd">🎥 Video Full HD (1080p @ 30fps) — Claritate Maximă</option>
                        <option value="audio">🎙️ Doar Audio (Stereo HD) — Economie Spațiu / Proces-Verbal</option>
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Compoziție Cadru Video (Layout)</label>
                      <select class="form-select" id="input-room-auto-record-layout" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderAutoRecordLayoutOptions()}
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format Fișier (Container Salvare)</label>
                      <select class="form-select" id="input-room-auto-record-container" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderContainerOptions('webm')}
                      </select>
                    </div>

                    <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; color: #e2e8f0; font-size: 0.76rem;">
                      <input type="checkbox" id="input-room-auto-record-notify" checked style="accent-color: #ef4444; margin-top: 1px;">
                      <span>🔔 Notificare participanți (indicator <b>„🔴 Înregistrare în curs”</b> afișat în sală)</span>
                    </label>
                  </div>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-guest-invites" style="margin-top: 3px;">
                  <div>
                    <strong>🔗 Permite membrilor simpli să genereze link de invitație</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Dacă este debifat, DOAR Gazda/Adminul poate genera și trimite invitații.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-mute-entry" checked style="margin-top: 3px;">
                  <div>
                    <strong>🎙️ Microfon oprit automat la intrare (Mute on entry)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Toți oaspeții intră cu microfonul dezactivat pentru a preveni zgomotul de fond.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-cam-entry" style="margin-top: 3px;">
                  <div>
                    <strong>📹 Cameră oprită automat la intrare</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții intră fără video activ până decid să îl pornească manual.</div>
                  </div>
                </label>

                <div style="margin-top: 0.25rem;">
                  <label class="form-label" style="font-size: 0.78rem; margin-bottom: 4px;">🖥️ Permisiune Partajare Ecran</label>
                  <select class="form-select" id="input-room-screen-share" style="font-size: 0.8rem; padding: 0.45rem 0.75rem;">
                    <option value="everyone">Toți participanții pot partaja ecranul</option>
                    <option value="hosts_only">Doar Gazda și Co-Gazdele pot partaja ecranul</option>
                  </select>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-room-attendance" checked style="margin-top: 3px;">
                  <div>
                    <strong>📋 Prezență automată & Jurnal de Cvorum pentru membri de partid</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Înregistrează automat în tab-ul „Prezență” numele, ora conectării, ieșirii și cvorumul.</div>
                  </div>
                </label>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary">Creează Camera</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'edit-room' && state.selectedRoomForAction) {
    const room = state.selectedRoomForAction;
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 680px; width: 100%;">
          <div class="modal-header">
            <div>
              <h3 class="modal-title" style="margin: 0;">Editează Camera</h3>
              <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">${room.name} (${room.id})</div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>
          <form id="form-edit-room">
            <div class="form-group">
              <label class="form-label">Nume Cameră</label>
              <input type="text" class="form-input" id="input-edit-room-name" value="${room.name}" required>
            </div>
            <div class="form-group">
              <label class="form-label">Identificator Slug LiveKit (URL)</label>
              <input type="text" class="form-input" id="input-edit-room-slug" value="${room.slug}" required>
            </div>
            <div class="form-group">
              <label class="form-label">Tip Spațiu</label>
              <select class="form-select" id="input-edit-room-type">
                <option value="permanent" ${room.type !== 'meeting' ? 'selected' : ''}>🏢 Sală Permanentă 24/7</option>
                <option value="meeting" ${room.type === 'meeting' ? 'selected' : ''}>📅 Ședință / Reuniune Programată</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Descriere Cameră</label>
              <textarea class="form-textarea" id="input-edit-room-desc" rows="2">${room.description || ''}</textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Vizibilitate Cameră</label>
              <select class="form-select" id="input-edit-room-vis">
                <option value="internal" ${room.visibility === 'internal' ? 'selected' : ''}>Internă (toți membrii organizației)</option>
                <option value="private" ${room.visibility === 'private' ? 'selected' : ''}>Privată (acces doar cu invitație)</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Capacitate Maximă Participanți (2 - 100)</label>
              <input type="number" class="form-input" id="input-edit-room-max" min="2" max="100" value="${room.maxParticipants}">
            </div>

            <!-- PIN Code (Zoom Passcode style) -->
            <div class="form-group" style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25); border-radius: 12px; padding: 0.9rem;">
              <label class="form-label" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem;">
                <span style="color: #fbbf24; font-weight: 700;">🔑 Cod PIN Conectare (Passcode — Opțional)</span>
                <span style="font-size: 0.72rem; color: #cbd5e1;">Cod Acces Securizat</span>
              </label>
              <div style="display: flex; gap: 0.5rem;">
                <input type="text" class="form-input" id="input-edit-room-pin" value="${room.pinCode || ''}" placeholder="ex: 4821 (fără PIN)" style="font-family: monospace; letter-spacing: 2px; font-weight: 700; flex: 1;">
                <button type="button" class="btn btn-secondary btn-sm" id="btn-generate-edit-room-pin" title="Generează PIN aleatoriu de 4 cifre" style="white-space: nowrap; font-size: 0.78rem;">
                  🎲 Generează
                </button>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-clear-edit-room-pin" title="Șterge codul PIN" style="white-space: nowrap; font-size: 0.78rem;">
                  ✕ Fără PIN
                </button>
              </div>
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 4px;">
                Dacă este setat, participanții vor trebui să introducă acest PIN înainte de a intra în sală.
              </div>
            </div>

            <!-- Policies -->
            <div style="background: rgba(0,0,0,0.28); border: 1px solid rgba(255,255,255,0.09); border-radius: 12px; padding: 1rem; margin: 1.25rem 0;">
              <div style="font-weight: 700; font-size: 0.825rem; color: #38bdf8; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 6px;">
                <span>⚙️</span> Reguli de Acces, Securitate & Cvorum Statutar
              </div>
              
              <div style="display: flex; flex-direction: column; gap: 0.7rem; font-size: 0.825rem;">
                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-wait-for-host" ${room.waitForHost !== false ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>⏳ Participanții așteaptă gazda să înceapă ședința</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Nimeni nu intră în conferință dacă persoana care a creat întâlnirea nu este conectată. Participanții stau în ecranul de așteptare.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-waiting" ${room.waitingRoomEnabled ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🛡️ Sală de Așteptare (Waiting Room / Aprobare manuală)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții necesită aprobare individuală din partea gazdei înainte de a intra.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-auto-record" ${room.autoRecord ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🔴 Înregistrare Automată (Strict pentru Gazda Creatoare)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Pornește automat înregistrarea video/audio doar pe calculatorul gazdei când aceasta intră în sală. Niciun alt participant nu va înregistra.</div>
                  </div>
                </label>

                <!-- Sub-panou Parametri Înregistrare Automată -->
                <div id="panel-edit-room-auto-record-options" style="display: ${room.autoRecord ? 'block' : 'none'}; margin-left: 26px; margin-top: -2px; margin-bottom: 6px; padding: 12px 14px; background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.28); border-radius: 10px;">
                  <div style="font-size: 0.78rem; font-weight: 700; color: #fca5a5; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                    <span>⚙️</span> Parametrii Înregistrării Automate:
                  </div>

                  <div style="display: flex; flex-direction: column; gap: 9px;">
                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format & Calitate Înregistrare</label>
                      <select class="form-select" id="input-edit-room-auto-record-format" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        <option value="video-hd" ${(!room.autoRecordFormat || room.autoRecordFormat === 'video-hd') ? 'selected' : ''}>📹 Video HD (720p @ 30fps) — Echilibrat (Recomandat)</option>
                        <option value="video-fhd" ${room.autoRecordFormat === 'video-fhd' ? 'selected' : ''}>🎥 Video Full HD (1080p @ 30fps) — Claritate Maximă</option>
                        <option value="audio" ${room.autoRecordFormat === 'audio' ? 'selected' : ''}>🎙️ Doar Audio (Stereo HD) — Economie Spațiu / Proces-Verbal</option>
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Compoziție Cadru Video (Layout)</label>
                      <select class="form-select" id="input-edit-room-auto-record-layout" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderAutoRecordLayoutOptions(room.autoRecordLayout)}
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format Fișier (Container Salvare)</label>
                      <select class="form-select" id="input-edit-room-auto-record-container" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderContainerOptions(room.autoRecordContainer)}
                      </select>
                    </div>

                    <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; color: #e2e8f0; font-size: 0.76rem;">
                      <input type="checkbox" id="input-edit-room-auto-record-notify" ${room.autoRecordNotify !== false ? 'checked' : ''} style="accent-color: #ef4444; margin-top: 1px;">
                      <span>🔔 Notificare participanți (indicator <b>„🔴 Înregistrare în curs”</b> afișat în sală)</span>
                    </label>
                  </div>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-guest-invites" ${room.allowGuestInvites !== false ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🔗 Permite membrilor simpli să genereze link de invitație</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Dacă este debifat, DOAR Gazda/Adminul poate genera și trimite invitații.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-mute-entry" ${room.muteOnEntry ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>🎙️ Microfon oprit automat la intrare (Mute on entry)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Toți oaspeții intră cu microfonul dezactivat pentru a preveni zgomotul de fond.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-cam-entry" ${room.cameraOffOnEntry ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>📹 Cameră oprită automat la intrare</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții intră fără video activ până decid să îl pornească manual.</div>
                  </div>
                </label>

                <div style="margin-top: 0.25rem;">
                  <label class="form-label" style="font-size: 0.78rem; margin-bottom: 4px;">🖥️ Permisiune Partajare Ecran</label>
                  <select class="form-select" id="input-edit-room-screen-share" style="font-size: 0.8rem; padding: 0.45rem 0.75rem;">
                    <option value="everyone" ${room.allowScreenShare !== 'hosts_only' ? 'selected' : ''}>Toți participanții pot partaja ecranul</option>
                    <option value="hosts_only" ${room.allowScreenShare === 'hosts_only' ? 'selected' : ''}>Doar Gazda și Co-Gazdele pot partaja ecranul</option>
                  </select>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-edit-room-attendance" ${room.attendanceTracking !== false ? 'checked' : ''} style="margin-top: 3px;">
                  <div>
                    <strong>📋 Prezență automată & Jurnal de Cvorum pentru membri de partid</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Înregistrează automat în tab-ul „Prezență” numele, ora conectării, ieșirii și cvorumul.</div>
                  </div>
                </label>
              </div>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 1.5rem; gap: 0.5rem; flex-wrap: nowrap;">
              <button type="button" class="btn btn-danger btn-sm" id="btn-archive-room" data-room-id="${room.id}" style="display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.5rem 0.75rem; font-size: 0.8rem; white-space: nowrap; flex-shrink: 0;">
                <span>🗑️</span> Șterge Camera
              </button>
              <div style="display: flex; gap: 0.5rem; align-items: center; flex-shrink: 0;">
                <button type="button" class="btn btn-secondary btn-sm" id="btn-cancel-modal" style="padding: 0.5rem 0.85rem; font-size: 0.825rem; white-space: nowrap;">Anulează</button>
                <button type="submit" class="btn btn-primary btn-sm" style="font-weight: 600; padding: 0.5rem 0.95rem; font-size: 0.825rem; white-space: nowrap;">💾 Salvează Modificările</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'edit-profile') {
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 480px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.65rem;">
              <div class="user-avatar" style="width: 36px; height: 36px; font-size: 1rem;">${state.user.name.charAt(0)}</div>
              <div>
                <h3 class="modal-title" style="margin: 0; font-size: 1.15rem;">Profil Utilizator (Acest Calculator)</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">ID Dispozitiv: <code>${state.user.id}</code></div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>
          <form id="form-edit-profile">
            <div class="form-group">
              <label class="form-label">Nume Afișat în Conferință & Camere</label>
              <input type="text" class="form-input" id="input-profile-name" value="${state.user.name}" required placeholder="ex: Mihai Director / Computer 1">
            </div>
            <div class="form-group">
              <label class="form-label">Adresă de Email</label>
              <input type="email" class="form-input" id="input-profile-email" value="${state.user.email}" required placeholder="ex: admin@vorbello.ro">
            </div>
            <div class="form-group">
              <label class="form-label">Rol & Permisiuni pe acest Calculator</label>
              <select class="form-select" id="input-profile-role">
                <option value="ADMIN" ${state.user.role === 'ADMIN' ? 'selected' : ''}>👑 ADMIN MASTER (Server VPS, LiveKit, Emitere Licențe)</option>
                <option value="CLIENT_ADMIN" ${state.user.role === 'CLIENT_ADMIN' ? 'selected' : ''}>🏢 CLIENT VORBELLO (Plătitor Abonament - Fără setări server)</option>
                <option value="MODERATOR" ${state.user.role === 'MODERATOR' ? 'selected' : ''}>🛡️ MODERATOR (Poate iniția & modera conferințe)</option>
                <option value="MEMBER" ${state.user.role === 'MEMBER' ? 'selected' : ''}>👤 MEMBRU (Participant standard)</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Nume Organizație / Companie</label>
              <input type="text" class="form-input" id="input-profile-org" value="${state.user.organizationName}" required>
            </div>
            <div style="background: rgba(99, 102, 241, 0.08); border: 1px solid rgba(99, 102, 241, 0.25); border-radius: 8px; padding: 0.75rem; font-size: 0.8rem; color: #94a3b8; margin-top: 0.5rem; line-height: 1.4;">
              💡 <strong>Configurare Multi-Computer:</strong> Puteți seta pe fiecare calculator un nume distinct (ex: <em>Calculator 1 - Birou</em> și <em>Calculator 2 - Mobil</em>) și rolul de <strong>ADMIN (Master)</strong>. Ambele computere intră în aceeași cameră live pe VPS și se conectează instant prin WebRTC.
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.25rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary">💾 Salvează Profilul</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }


  if (state.activeModal === 'schedule-meeting') {
    const defaultMeetDate = state.meetingDateFilter || new Date().toISOString().split('T')[0];
    const defaultMeetTime = '15:00';
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 680px; width: 95vw; padding: 1.5rem 1.85rem;">
          <div class="modal-header">
            <h3 class="modal-title">Programează Întâlnire</h3>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>
          <form id="form-schedule-meeting">
            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Cameră Atribuită</label>
              <select class="form-select" id="input-meet-room" required>
                ${(state.appMode === 'production' ? state.prodRooms : state.rooms).map(r => `<option value="${r.id}">${r.name}</option>`).join('')}
              </select>
            </div>
            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Titlu Întâlnire</label>
              <input type="text" class="form-input" id="input-meet-title" placeholder="ex: Prezentare Proiect Trimestrul IV" required>
            </div>
            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Agendă & Ordine de Zi (Opțional)</label>
              <textarea class="form-textarea" id="input-meet-agenda" rows="2" placeholder="Subiecte abordate, puncte pe ordinea de zi..."></textarea>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; margin-bottom: 0.85rem; align-items: start;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">📅 Dată Început</label>
                <input type="date" class="form-input" id="input-meet-date" value="${defaultMeetDate}" required style="font-size: 0.85rem; color-scheme: dark; width: 100%; height: 38px; cursor: pointer; box-sizing: border-box;">
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">⏰ Oră Început</label>
                <input type="time" class="form-input" id="input-meet-time" value="${defaultMeetTime}" required style="font-size: 0.85rem; color-scheme: dark; width: 100%; height: 38px; cursor: pointer; box-sizing: border-box;">
              </div>
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; margin-bottom: 0.85rem; align-items: start;">
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">Politică de Acces</label>
                <select class="form-select" id="input-meet-policy" style="font-size: 0.85rem; width: 100%; height: 38px; box-sizing: border-box;">
                  <option value="org_members">Toți Membrii Organizației</option>
                  <option value="invite_only">Doar pe Bază de Invitație</option>
                </select>
              </div>
              <div class="form-group" style="margin-bottom: 0;">
                <label class="form-label" style="font-size: 0.825rem; margin-bottom: 6px; display: block;">Fus Orar:</label>
                <select class="form-select" id="input-meet-timezone" style="font-size: 0.85rem; width: 100%; height: 38px; box-sizing: border-box;">
                  ${TIMEZONE_OPTIONS.map(tzOpt => `<option value="${tzOpt.value}" ${tzOpt.value === state.selectedTimezone ? 'selected' : ''}>${tzOpt.label}</option>`).join('')}
                </select>
              </div>
            </div>

            <div class="form-group" style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25); border-radius: 12px; padding: 0.85rem; margin-bottom: 0.85rem;">
              <label class="form-label" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.4rem;">
                <span style="color: #fbbf24; font-weight: 700;">🔑 Cod PIN Întâlnire (Passcode — Opțional)</span>
                <span style="font-size: 0.72rem; color: #cbd5e1;">Acces Securizat</span>
              </label>
              <div style="display: flex; gap: 0.5rem;">
                <input type="text" class="form-input" id="input-meet-pin" placeholder="ex: 1234 (lăsați liber fără PIN)" style="font-family: monospace; letter-spacing: 2px; font-weight: 700; flex: 1;">
                <button type="button" class="btn btn-secondary btn-sm" id="btn-generate-meet-pin" title="Generează PIN aleatoriu de 4 cifre" style="white-space: nowrap; font-size: 0.78rem;">
                  🎲 Generează PIN
                </button>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-clear-meet-pin" title="Șterge codul PIN" style="white-space: nowrap; font-size: 0.78rem;">
                  ✕ Fără PIN
                </button>
              </div>
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 4px;">
                Participanții vor trebui să introducă acest PIN înainte de a intra în sală.
              </div>
            </div>
            <!-- Zoom / Meet Security & Governance Policies -->
            <div style="background: rgba(0,0,0,0.28); border: 1px solid rgba(255,255,255,0.09); border-radius: 12px; padding: 1rem; margin: 1.25rem 0;">
              <div style="font-weight: 700; font-size: 0.825rem; color: #38bdf8; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 6px;">
                <span>⚙️</span> Reguli de Acces, Securitate & Cvorum Statutar
              </div>
              
              <div style="display: flex; flex-direction: column; gap: 0.7rem; font-size: 0.825rem;">
                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-wait-for-host" checked style="margin-top: 3px;">
                  <div>
                    <strong>⏳ Participanții așteaptă gazda să înceapă ședința</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Nimeni nu intră în conferință dacă persoana care a creat întâlnirea nu este conectată. Participanții stau în ecranul de așteptare.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-waiting" style="margin-top: 3px;">
                  <div>
                    <strong>🛡️ Sală de Așteptare (Waiting Room / Aprobare manuală)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții necesită aprobare individuală din partea gazdei înainte de a intra.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-record" style="margin-top: 3px;">
                  <div>
                    <strong>🔴 Înregistrare Automată (Strict pentru Gazda Creatoare)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Pornește automat înregistrarea video/audio doar pe calculatorul gazdei când aceasta intră în sală. Niciun alt participant nu va înregistra.</div>
                  </div>
                </label>

                <!-- Sub-panou Parametri Înregistrare Automată -->
                <div id="panel-meet-auto-record-options" style="display: none; margin-left: 26px; margin-top: -2px; margin-bottom: 6px; padding: 12px 14px; background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.28); border-radius: 10px;">
                  <div style="font-size: 0.78rem; font-weight: 700; color: #fca5a5; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;">
                    <span>⚙️</span> Parametrii Înregistrării Automate:
                  </div>

                  <div style="display: flex; flex-direction: column; gap: 9px;">
                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format & Calitate Înregistrare</label>
                      <select class="form-select" id="input-meet-auto-record-format" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        <option value="video-hd" selected>📹 Video HD (720p @ 30fps) — Echilibrat (Recomandat)</option>
                        <option value="video-fhd">🎥 Video Full HD (1080p @ 30fps) — Claritate Maximă</option>
                        <option value="audio">🎙️ Doar Audio (Stereo HD) — Economie Spațiu / Proces-Verbal</option>
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Compoziție Cadru Video (Layout)</label>
                      <select class="form-select" id="input-meet-auto-record-layout" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderAutoRecordLayoutOptions()}
                      </select>
                    </div>

                    <div>
                      <label class="form-label" style="font-size: 0.74rem; color: #f1f5f9; margin-bottom: 4px;">Format Fișier (Container Salvare)</label>
                      <select class="form-select" id="input-meet-auto-record-container" style="font-size: 0.8rem; padding: 0.45rem 0.75rem; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255, 255, 255, 0.15); color: #fff;">
                        ${renderContainerOptions('webm')}
                      </select>
                    </div>

                    <label style="display: flex; align-items: center; gap: 8px; cursor: pointer; color: #e2e8f0; font-size: 0.76rem;">
                      <input type="checkbox" id="input-meet-auto-record-notify" checked style="accent-color: #ef4444; margin-top: 1px;">
                      <span>🔔 Notificare participanți (indicator <b>„🔴 Înregistrare în curs”</b> afișat în sală)</span>
                    </label>
                  </div>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-guest-invites" style="margin-top: 3px;">
                  <div>
                    <strong>🔗 Permite membrilor simpli să genereze link de invitație</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Dacă este debifat, DOAR Gazda/Adminul poate genera și trimite invitații.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-mute" checked style="margin-top: 3px;">
                  <div>
                    <strong>🎙️ Microfon oprit automat la intrare (Mute on entry)</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Toți oaspeții intră cu microfonul dezactivat pentru a preveni zgomotul de fond.</div>
                  </div>
                </label>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-cam-entry" style="margin-top: 3px;">
                  <div>
                    <strong>📹 Cameră oprită automat la intrare</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Participanții intră fără video activ până decid să îl pornească manual.</div>
                  </div>
                </label>

                <div style="margin-top: 0.25rem;">
                  <label class="form-label" style="font-size: 0.78rem; margin-bottom: 4px;">🖥️ Permisiune Partajare Ecran</label>
                  <select class="form-select" id="input-meet-screen-share" style="font-size: 0.8rem; padding: 0.45rem 0.75rem;">
                    <option value="everyone">Toți participanții pot partaja ecranul</option>
                    <option value="hosts_only">Doar Gazda și Administratorii pot partaja ecranul</option>
                  </select>
                </div>

                <label style="display: flex; align-items: flex-start; gap: 9px; cursor: pointer; color: #e2e8f0;">
                  <input type="checkbox" id="input-meet-attendance" checked style="margin-top: 3px;">
                  <div>
                    <strong>📋 Prezență automată & Jurnal de Cvorum pentru membri de partid</strong>
                    <div style="font-size: 0.72rem; color: var(--text-muted);">Înregistrează automat în tab-ul „Prezență” numele, ora conectării, ieșirii și cvorumul.</div>
                  </div>
                </label>
              </div>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary">Programează</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'create-invite' && state.selectedRoomForAction) {
    const room = state.selectedRoomForAction;
    return `
      <div class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title">Generează Invitație — ${room.name}</h3>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>
          <form id="form-create-invite">
            <div class="form-group">
              <label class="form-label">Email Destinatar (Opțional)</label>
              <input type="email" class="form-input" id="input-invite-email" placeholder="lasă liber pentru link generic de oaspete">
            </div>
            <div class="form-group">
              <label class="form-label">Valabilitate Link (Ore)</label>
              <select class="form-select" id="input-invite-hours">
                <option value="24">24 de ore</option>
                <option value="48" selected>48 de ore</option>
                <option value="168">7 zile</option>
              </select>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.5rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary">Generează Link</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'invite-meeting' && state.selectedMeetingForInvite) {
    const m = state.selectedMeetingForInvite;
    const origin = window.location.origin && window.location.origin.startsWith('http') ? window.location.origin : 'https://app.vorbello.ro';
    const inviteUrl = `${origin}/invite/${m.roomId}?meet=${m.id}`;
    const formattedDate = new Date(m.startsAt).toLocaleString('ro-RO', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
    });

    const emailSubject = encodeURIComponent(`Invitație la conferință Vorbello: ${m.title}`);
    const emailBody = encodeURIComponent(
`Bună ziua,

Vă invităm să participați la videoconferința securizată pe platforma Vorbello.

📌 Subiect: ${m.title}
🚪 Sala: ${m.roomName}
🕒 Data și ora: ${formattedDate}
👤 Organizator: ${m.creatorName}

🔗 Accesați direct conferința făcând clic pe următorul link:
${inviteUrl}

Vă puteți conecta direct din browser sau din aplicația desktop Vorbello.

Cu stimă,
${m.creatorName} — Vorbello Video Conferencing`
    );

    const mailtoHref = `mailto:?subject=${emailSubject}&body=${emailBody}`;
    const gmailHref = `https://mail.google.com/mail/?view=cm&fs=1&su=${emailSubject}&body=${emailBody}`;

    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 600px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 38px; height: 38px; border-radius: 10px; background: rgba(99, 102, 241, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.25rem;">
                ✉️
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.2rem; margin: 0;">Invită Participanți</h3>
                <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">${m.title}</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <!-- Meeting Context Badge -->
          <div style="background: rgba(255, 255, 255, 0.04); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 0.75rem 1rem; margin-bottom: 1.25rem; display: flex; justify-content: space-between; align-items: center; font-size: 0.825rem;">
            <div>
              <div style="font-weight: 600; color: #fff;">${m.roomName}</div>
              <div style="color: #6ee7b7; margin-top: 2px;">🕒 ${formattedDate}</div>
            </div>
            <span class="status-pill ${m.status}">${m.status.toUpperCase()}</span>
          </div>

          <!-- Option 1: Send via Direct Email (SMTP / Vorbello Server) -->
          <form id="form-send-email-invites" style="margin-bottom: 1.25rem; background: rgba(99, 102, 241, 0.06); border: 1px solid rgba(99, 102, 241, 0.25); border-radius: 12px; padding: 1.15rem;">
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;">
              <div style="font-weight: 700; font-size: 0.95rem; color: #fff; display: flex; align-items: center; gap: 0.4rem;">
                <span>🚀</span> 1. Trimitere Automată prin Email
              </div>
              <span style="font-size: 0.7rem; background: #6366f1; color: #fff; padding: 2px 8px; border-radius: 6px; font-weight: 600;">Server SMTP</span>
            </div>
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" style="font-size: 0.8rem;">Adrese de Email (separate prin virgulă):</label>
              <input type="text" class="form-input" id="input-invite-emails" placeholder="ex: coleg@firma.ro, invitat@client.com, partener@gmail.com" required style="font-size: 0.85rem;" />
            </div>
            
            <div class="form-group" style="margin-bottom: 0.75rem;">
              <label class="form-label" style="font-size: 0.8rem;">Mesaj personalizat (opțional):</label>
              <textarea class="form-textarea" id="input-invite-note" rows="2" placeholder="Vă aștept la conferință pentru a discuta punctele de pe ordinea de zi..." style="font-size: 0.85rem;"></textarea>
            </div>

            <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; font-size: 0.8rem; color: var(--text-secondary);">
              <input type="checkbox" id="check-send-self-copy" checked style="accent-color: #6366f1; width: 15px; height: 15px; cursor: pointer;" />
              <label for="check-send-self-copy" style="cursor: pointer; user-select: none;">Trimite o copie de confirmare și pe emailul meu (${state.user.email})</label>
            </div>
            
            <button type="submit" class="btn btn-primary" id="btn-submit-send-emails" style="width: 100%; display: flex; align-items: center; justify-content: center; gap: 0.5rem; font-weight: 600; padding: 0.65rem;">
              <span>📧</span> Trimite Invitațiile pe Email Acum
            </button>
          </form>

          <!-- Option 2: Open in Desktop Email Client or Gmail -->
          <div style="margin-bottom: 1.25rem;">
            <div style="font-weight: 700; font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.5rem; text-transform: uppercase; letter-spacing: 0.05em;">
              2. Sau trimite din clientul tău de email:
            </div>
            <div style="display: flex; gap: 0.6rem;">
              <a href="${mailtoHref}" class="btn btn-secondary" style="flex: 1; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 0.4rem; font-size: 0.825rem; padding: 0.55rem 0.5rem;">
                <span>📨</span> Deschide în Outlook / Mail
              </a>
              <a href="${gmailHref}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary" style="flex: 1; text-decoration: none; display: flex; align-items: center; justify-content: center; gap: 0.4rem; font-size: 0.825rem; padding: 0.55rem 0.5rem;">
                <span>🌐</span> Deschide în Gmail
              </a>
            </div>
          </div>

          <!-- Option 3: Quick Copy (WhatsApp / Slack / Telegram) -->
          <div>
            <div style="font-weight: 700; font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 0.5rem; text-transform: uppercase; letter-spacing: 0.05em;">
              3. Sau copiază rapid pentru WhatsApp / Chat:
            </div>
            <div style="display: flex; gap: 0.5rem; margin-bottom: 0.6rem;">
              <input type="text" readonly value="${inviteUrl}" id="input-quick-copy-url" class="form-input" style="flex: 1; font-size: 0.8rem; background: rgba(0,0,0,0.3); color: #94a3b8;" />
              <button class="btn btn-secondary btn-sm" id="btn-copy-invite-link" style="white-space: nowrap; padding: 0.5rem 0.85rem;">
                📋 Copiază Link
              </button>
            </div>
            <button class="btn btn-secondary btn-sm" id="btn-copy-full-invitation" style="width: 100%; padding: 0.55rem; font-size: 0.8rem; display: flex; align-items: center; justify-content: center; gap: 0.4rem;">
              <span>📑</span> Copiază Tot Textul Invitației (Formatat)
            </button>
          </div>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'vps-config') {
    const isConfigTab = state.vpsModalTab === 'config';
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 680px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 40px; height: 40px; border-radius: 12px; background: rgba(16, 185, 129, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.3rem; border: 1px solid rgba(16, 185, 129, 0.4);">
                ⚙️
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0;">Configurare Server VPS & Mod Producție</h3>
                <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">Conectează aplicația la serverul tău privat sau activează un VPS nou</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <!-- Modal Sub-Tabs -->
          <div style="display: flex; gap: 0.5rem; margin-bottom: 1.25rem; border-bottom: 1px solid var(--border-subtle); padding-bottom: 0.75rem;">
            <button class="btn ${isConfigTab ? 'btn-primary' : 'btn-secondary'} btn-sm" id="btn-vps-tab-config" style="padding: 0.45rem 1rem;">
              🌐 1. Conexiune Server VPS
            </button>
            <button class="btn ${!isConfigTab ? 'btn-primary' : 'btn-secondary'} btn-sm" id="btn-vps-tab-guide" style="padding: 0.45rem 1rem;">
              📖 2. Ghid Activare VPS (3 Comenzi)
            </button>
          </div>

          ${isConfigTab ? `
            <!-- Form Configurare Conexiune -->
            <form id="form-vps-settings">
              <div class="form-group" style="margin-bottom: 1rem;">
                <label class="form-label" style="font-size: 0.825rem; display: flex; justify-content: space-between;">
                  <span>Adresă Backend API VPS (HTTPS Securizat):</span>
                  <span style="color: #6ee7b7; font-size: 0.75rem;">IP Dedicat: 217.217.233.20</span>
                </label>
                <input type="text" class="form-input" id="input-vps-api" value="${state.vpsConfig.apiUrl}" placeholder="ex: https://217.217.233.20" required style="font-size: 0.875rem;" />
              </div>

              <div class="form-group" style="margin-bottom: 1rem;">
                <label class="form-label" style="font-size: 0.825rem; display: flex; justify-content: space-between;">
                  <span>Adresă LiveKit SFU WebSockets (WSS Securizat):</span>
                  <span style="color: #6ee7b7; font-size: 0.75rem;">Port implicit: 443 (WSS)</span>
                </label>
                <input type="text" class="form-input" id="input-vps-livekit" value="${state.vpsConfig.livekitUrl}" placeholder="wss://live.vorbello.eu" required style="font-size: 0.875rem;" />
                <div style="display: flex; gap: 6px; margin-top: 6px; flex-wrap: wrap;">
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-preset-live-wss" style="font-size: 0.72rem; padding: 2px 7px; color: #34d399; font-weight: 600;">
                    🔒 Implicit Securizat (wss://live.vorbello.eu)
                  </button>
                </div>
              </div>

              <div class="form-group" style="margin-bottom: 1.25rem;">
                <label class="form-label" style="font-size: 0.825rem;">Nume Personalizat Server (Opțional):</label>
                <input type="text" class="form-input" id="input-vps-name" value="${state.vpsConfig.serverName}" placeholder="ex: VPS Principal Hetzner (Ubuntu 24.04)" style="font-size: 0.875rem;" />
              </div>

              <!-- Test Ping / Status Diagnostic Box -->
              <div id="vps-ping-status-box" style="background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.25); border-radius: 10px; padding: 0.85rem 1rem; margin-bottom: 1.25rem; display: flex; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 0.65rem;">
                  <span style="font-size: 1.2rem;">📶</span>
                  <div>
                    <div style="font-size: 0.85rem; font-weight: 600; color: #6ee7b7;" id="vps-ping-text">
                      ${state.vpsConfig.statusText}
                    </div>
                    <div style="font-size: 0.75rem; color: #94a3b8;" id="vps-ping-subtext">
                      Latență verificată: <strong>${state.vpsConfig.pingMs} ms</strong> • Gata pentru conferințe video HD
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-secondary btn-sm" id="btn-test-vps-ping" style="white-space: nowrap; font-size: 0.8rem; padding: 0.4rem 0.8rem;">
                  🔄 Testează Conexiunea
                </button>
              </div>

              <div style="display: flex; justify-content: flex-end; gap: 0.75rem;">
                <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Închide</button>
                <button type="submit" class="btn btn-primary" style="background: linear-gradient(135deg, #10b981, #059669); font-weight: 600; display: flex; align-items: center; gap: 0.4rem;">
                  <span>💾</span> Salvează & Conectează
                </button>
              </div>
            </form>
          ` : `
            <!-- Ghid Activare VPS Linux -->
            <div style="font-size: 0.875rem; color: #e2e8f0; line-height: 1.6;">
              <p style="margin-top: 0; color: #94a3b8;">
                Pentru a rula propria instanță <strong>Vorbello</strong> în producție pe un server VPS (Ubuntu 22.04 / 24.04 cu Docker), urmați acești 3 pași rapizi:
              </p>

              <div style="background: rgba(0, 0, 0, 0.4); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 1rem; margin-bottom: 1rem;">
                <div style="font-weight: 700; color: #6ee7b7; margin-bottom: 0.35rem; display: flex; align-items: center; gap: 0.4rem;">
                  <span>1.</span> Conectare SSH & Creare Director
                </div>
                <pre style="margin: 0; background: rgba(0,0,0,0.5); padding: 0.6rem 0.8rem; border-radius: 6px; font-size: 0.8rem; color: #38bdf8; overflow-x: auto;">ssh root@IP_SERVER_TAU
mkdir -p /opt/vorbello && cd /opt/vorbello</pre>
              </div>

              <div style="background: rgba(0, 0, 0, 0.4); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 1rem; margin-bottom: 1rem;">
                <div style="font-weight: 700; color: #6ee7b7; margin-bottom: 0.35rem; display: flex; align-items: center; gap: 0.4rem;">
                  <span>2.</span> Rulare Script Inițializare Automată
                </div>
                <div style="font-size: 0.775rem; color: #94a3b8; margin-bottom: 0.4rem;">(Instalează Docker, configurează firewall-ul pentru WebRTC și generează cheile criptografice)</div>
                <pre style="margin: 0; background: rgba(0,0,0,0.5); padding: 0.6rem 0.8rem; border-radius: 6px; font-size: 0.8rem; color: #38bdf8; overflow-x: auto;">chmod +x infra/scripts/*.sh
sudo bash infra/scripts/setup-server.sh
bash infra/scripts/generate-secrets.sh</pre>
              </div>

              <div style="background: rgba(0, 0, 0, 0.4); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 1rem; margin-bottom: 1rem;">
                <div style="font-weight: 700; color: #6ee7b7; margin-bottom: 0.35rem; display: flex; align-items: center; gap: 0.4rem;">
                  <span>3.</span> Lansare Cluster de Producție (Docker Compose)
                </div>
                <pre style="margin: 0; background: rgba(0,0,0,0.5); padding: 0.6rem 0.8rem; border-radius: 6px; font-size: 0.8rem; color: #38bdf8; overflow-x: auto;">bash infra/scripts/deploy.sh</pre>
              </div>

              <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 1.25rem;">
                <button class="btn btn-secondary btn-sm" id="btn-copy-vps-commands" style="display: flex; align-items: center; gap: 0.4rem;">
                  <span>📋</span> Copiază Comenzile Rapide
                </button>
                <button class="btn btn-primary btn-sm" id="btn-switch-to-config-tab" style="background: #6366f1;">
                  Configurează Adresa VPS ➔
                </button>
              </div>
            </div>
          `}
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'add-member') {
    const hostCount = state.members.filter(m => m.hasHostLicense).length;
    const canAssignHost = hostCount < state.organizationLicense.maxHosts;
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 540px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 38px; height: 38px; border-radius: 10px; background: rgba(99, 102, 241, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.25rem;">
                👥
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0;">Adaugă Membru / Administrator</h3>
                <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">Creează cont de utilizator în organizația Vorbello</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-add-member">
            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Nume Complet:</label>
              <input type="text" class="form-input" id="input-member-name" placeholder="ex: Radu Marinescu" required style="font-size: 0.875rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Adresă de Email:</label>
              <input type="email" class="form-input" id="input-member-email" placeholder="ex: radu@companie.ro" required style="font-size: 0.875rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 1rem;">
              <label class="form-label" style="font-size: 0.825rem;">Rol în Organizație (RBAC):</label>
              <select class="form-select" id="input-member-role" style="font-size: 0.875rem;">
                <option value="MEMBER">MEMBER — Participant (asistă la conferințe)</option>
                <option value="MODERATOR">MODERATOR — Moderator de sală (gestionează chat, muting)</option>
                <option value="ADMIN" selected>ADMIN — Administrator complet (gestionează membri și setări)</option>
              </select>
            </div>

            <div class="form-group" style="margin-bottom: 1rem;">
              <label class="form-label" style="font-size: 0.825rem;">Parolă Acces Panou (pentru Admin / Moderator):</label>
              <div style="display: flex; gap: 0.5rem;">
                <input type="text" class="form-input" id="input-member-password" placeholder="Setați o parolă sau generați..." style="font-size: 0.875rem; flex: 1;" />
                <button type="button" class="btn btn-secondary btn-sm" id="btn-generate-member-pass" style="white-space: nowrap; font-size: 0.75rem;">
                  ⚡ Generează
                </button>
              </div>
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 3px;">
                Permite acestui cont să se conecteze în panoul de administrare cu această adresă de email.
              </div>
            </div>

            <!-- Host License Allocation Option -->
            <div style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.25); border-radius: 10px; padding: 0.85rem 1rem; margin-bottom: 1.25rem;">
              <div style="display: flex; align-items: flex-start; gap: 0.65rem;">
                <input type="checkbox" id="check-member-host-license" ${canAssignHost ? 'checked' : 'disabled'} style="margin-top: 3px; accent-color: #f59e0b; width: 16px; height: 16px; cursor: pointer;" />
                <div>
                  <label for="check-member-host-license" style="font-weight: 600; font-size: 0.85rem; color: #fde68a; cursor: pointer;">
                    Alocă Licență de Gazdă (Host License)
                  </label>
                  <div style="font-size: 0.775rem; color: #94a3b8; margin-top: 2px;">
                    Permite utilizatorului să creeze și să inițieze conferințe video live.
                    (${hostCount} din ${state.organizationLicense.maxHosts} licențe alocate)
                  </div>
                </div>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary" style="font-weight: 600;">
                <span>+</span> Salvează Membru
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'manage-license') {
    const lic = state.organizationLicense;
    const hostCount = state.members.filter(m => m.hasHostLicense).length;
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 600px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 40px; height: 40px; border-radius: 12px; background: rgba(245, 158, 11, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.3rem; border: 1px solid rgba(245, 158, 11, 0.4);">
                👑
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0;">Gestiune Licențe & Politici Sesiuni</h3>
                <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">Platformă Privată Self-Hosted Vorbello</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-manage-license">
            <div style="background: rgba(255, 255, 255, 0.04); border: 1px solid var(--border-subtle); border-radius: 12px; padding: 1rem 1.25rem; margin-bottom: 1.25rem;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem;">
                <div>
                  <div style="font-weight: 700; font-size: 1rem; color: #fff;">${lic.planName}</div>
                  <div style="font-size: 0.75rem; color: #6ee7b7; margin-top: 2px;">● Status: ${lic.status} (Valabil până la ${lic.expiresAt})</div>
                </div>
                <span class="status-pill live">ENTERPRISE</span>
              </div>

              <div style="font-size: 0.8rem; color: #94a3b8; display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; padding-top: 0.6rem; border-top: 1px solid rgba(255, 255, 255, 0.08);">
                <div>Cheie Licență: <code style="color: #6ee7b7;">${lic.licenseKey}</code></div>
                <div>Gazde Active: <strong style="color: #fff;">${hostCount} / ${lic.maxHosts}</strong></div>
                <div>Capacitate Sală: <strong style="color: #fff;">${lic.maxParticipantsPerRoom} max</strong></div>
                <div>Oaspeți Externi: <strong style="color: #6ee7b7;">Nelimitați (Gratuit)</strong></div>
              </div>
            </div>

            <div class="form-group" style="margin-bottom: 1rem;">
              <label class="form-label" style="font-size: 0.85rem; font-weight: 600;">Cine are dreptul să inițieze conferințe noi?</label>
              <select class="form-select" id="select-creation-policy" style="font-size: 0.875rem;">
                <option value="admins_hosts" ${lic.creationPolicy === 'admins_hosts' ? 'selected' : ''}>
                  👑 Administratorii și Gazdele Licențiate (Recomandat)
                </option>
                <option value="admins_only" ${lic.creationPolicy === 'admins_only' ? 'selected' : ''}>
                  🔒 Strict Administratorii Organizației
                </option>
                <option value="all_members" ${lic.creationPolicy === 'all_members' ? 'selected' : ''}>
                  👥 Toți Membrii Organizației (Nelimitat)
                </option>
              </select>
              <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">
                Oaspeții externi pot participa la orice conferință dacă au primit linkul de acces.
              </div>
            </div>

            <div class="form-group" style="margin-bottom: 1.25rem;">
              <label class="form-label" style="font-size: 0.85rem; font-weight: 600;">Număr Maxim de Licențe Gazdă (Host Slots):</label>
              <input type="number" class="form-input" id="input-max-hosts" value="${lic.maxHosts}" min="1" max="100" style="font-size: 0.875rem;" />
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Închide</button>
              <button type="submit" class="btn btn-primary" style="background: linear-gradient(135deg, #f59e0b, #d97706); font-weight: 600;">
                <span>💾</span> Salvează Politicile
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'master-profile') {
    const currentMembers = state.appMode === 'production' ? state.prodMembers : state.members;
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 560px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 42px; height: 42px; border-radius: 12px; background: rgba(245, 158, 11, 0.2); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; border: 1px solid rgba(245, 158, 11, 0.4);">
                👑
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0; color: #fff;">Gestiune Master Admin (Stație Locală)</h3>
                <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">Selectați sau configurați profilul activ afișat pe acest calculator</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-master-profile">
            <!-- Selector din membrii existenti -->
            <div class="form-group" style="margin-bottom: 1.1rem; background: rgba(255, 255, 255, 0.04); padding: 0.85rem 1rem; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.1);">
              <label class="form-label" style="font-size: 0.825rem; font-weight: 700; color: #fde68a; display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.4rem;">
                <span>⚡</span> Selectează rapid dintre membrii existenți:
              </label>
              <select class="form-select" id="select-existing-member" style="font-size: 0.875rem; width: 100%;">
                <option value="">-- Alege un membru pentru a-l seta Master Admin --</option>
                ${currentMembers.map(m => `
                  <option value="${m.id}" data-name="${m.name}" data-email="${m.email}" data-role="${m.role}" ${(m.id === state.user.id || m.name === state.user.name) ? 'selected' : ''}>
                    ${m.name} (${m.role} • ${m.email})
                  </option>
                `).join('')}
              </select>
              <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 5px;">
                Sau editați manual datele profilului în câmpurile de mai jos.
              </div>
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Nume Administrator Master:</label>
              <input type="text" class="form-input" id="input-profile-name" value="${state.user.name}" required style="font-size: 0.875rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Email Administrator:</label>
              <input type="email" class="form-input" id="input-profile-email" value="${state.user.email}" required style="font-size: 0.875rem;" />
            </div>

            <div class="form-group" style="margin-bottom: 0.85rem;">
              <label class="form-label" style="font-size: 0.825rem;">Rol pe această Stație:</label>
              <select class="form-select" id="select-profile-role" style="font-size: 0.875rem;">
                <option value="ADMIN" ${state.user.role === 'ADMIN' ? 'selected' : ''}>ADMIN (Administrator Complet)</option>
                <option value="MODERATOR" ${state.user.role === 'MODERATOR' ? 'selected' : ''}>MODERATOR (Moderator Sală)</option>
                <option value="MEMBER" ${state.user.role === 'MEMBER' ? 'selected' : ''}>MEMBER (Participant)</option>
              </select>
            </div>

            <div class="form-group" style="margin-bottom: 1.25rem;">
              <label class="form-label" style="font-size: 0.825rem;">Nume Organizație:</label>
              <input type="text" class="form-input" id="input-profile-org" value="${state.user.organizationName}" style="font-size: 0.875rem;" />
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem;">
              <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Anulează</button>
              <button type="submit" class="btn btn-primary" style="background: linear-gradient(135deg, #f59e0b, #d97706); font-weight: 700; color: #fff;">
                <span>💾</span> Salvează ca Master Admin
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  if (state.activeModal === 'website-sync-config') {
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 580px;">
          <div class="modal-header">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <div style="width: 42px; height: 42px; border-radius: 12px; background: rgba(56, 189, 248, 0.15); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; border: 1px solid rgba(56, 189, 248, 0.35);">
                🌐
              </div>
              <div>
                <h3 class="modal-title" style="font-size: 1.25rem; margin: 0; color: #fff;">Sincronizare Prezență cu Pagina Web</h3>
                <div style="font-size: 0.8rem; color: var(--text-muted); margin-top: 2px;">Conectare la WordPress REST API (vorbello.eu/wp-json/vorbello/v1/attendance)</div>
              </div>
            </div>
            <button class="modal-close" id="btn-close-modal">&times;</button>
          </div>

          <form id="form-website-sync-config">
            <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 1rem; margin-bottom: 1.25rem;">
              <div style="font-size: 0.825rem; color: #cbd5e1; line-height: 1.5;">
                Fiecare întâlnire cu membrii prezenți, ora de intrare, ora de ieșire și durata este transmisă direct în baza de date WordPress a site-ului dvs. pentru arhivare, evidență și consultare online.
              </div>
            </div>

            <div class="form-group" style="margin-bottom: 1rem;">
              <label class="form-label" style="font-size: 0.825rem; font-weight: 700;">URL Endpoint REST API Site:</label>
              <input type="url" class="form-input" id="input-sync-api-url" value="${state.websiteSyncConfig.apiUrl}" placeholder="https://vorbello.eu/wp-json/vorbello/v1/attendance" required style="font-size: 0.875rem;" />
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 4px;">Ruta REST a pluginului WordPress <code>vorbello-manager</code>.</div>
            </div>

            <div class="form-group" style="margin-bottom: 1rem;">
              <label class="form-label" style="font-size: 0.825rem; font-weight: 700;">Cheie API / Token Secret (Opțional):</label>
              <input type="text" class="form-input" id="input-sync-api-key" value="${state.websiteSyncConfig.apiKey}" placeholder="Secret token definit în WordPress..." style="font-size: 0.875rem;" />
              <div style="font-size: 0.72rem; color: var(--text-muted); margin-top: 4px;">Utilizat pentru autorizare dacă site-ul restricționează accesul public.</div>
            </div>

            <div class="form-group" style="margin-bottom: 1.5rem; display: flex; align-items: center; gap: 0.75rem;">
              <input type="checkbox" id="check-sync-auto" ${state.websiteSyncConfig.autoSync ? 'checked' : ''} style="width: 18px; height: 18px; cursor: pointer; accent-color: #38bdf8;" />
              <label for="check-sync-auto" style="font-size: 0.85rem; color: #fff; cursor: pointer; user-select: none;">
                <strong>Auto-Sincronizare Automată:</strong> Trimite automat prezența pe site la fiecare ieșire din conferință.
              </label>
            </div>

            <div id="website-sync-test-result" style="display: none; margin-bottom: 1rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.825rem;"></div>

            <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem;">
              <button type="button" class="btn btn-secondary" id="btn-test-website-sync" style="display: flex; align-items: center; gap: 0.35rem;">
                <span>🔌</span> Testează Conexiunea
              </button>
              <div style="display: flex; gap: 0.5rem;">
                <button type="button" class="btn btn-secondary" id="btn-cancel-modal">Închide</button>
                <button type="submit" class="btn btn-primary" style="font-weight: 700;">
                  <span>💾</span> Salvează Configurația
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  return '';
}

function attachEventHandlers() {
  // PWA Install Banner (Admin Portal)
  document.getElementById('btn-top-banner-install-admin')?.addEventListener('click', () => {
    handlePwaInstall();
  });
  document.getElementById('btn-top-banner-dismiss-admin')?.addEventListener('click', () => {
    try {
      sessionStorage.setItem('vorbello_pwa_dismissed', 'true');
    } catch (_) {}
    isAppInstalled = true;
    document.getElementById('pwa-top-install-banner-admin')?.remove();
  });

  // Switch to User Portal View
  document.getElementById('btn-switch-to-user')?.addEventListener('click', () => {
    state.appRoleView = 'user';
    localStorage.setItem('vorbello_app_role_view', 'user');
    renderApp();
  });

  // Admin Logout Button
  document.getElementById('btn-admin-logout')?.addEventListener('click', () => {
    openConfirmDialog({
      title: 'Deconectare Administrator',
      description: 'Sunteți sigur că doriți să vă deconectați din Panoul de Administrare Vorbello?',
      confirmText: 'Da, Deconectează-mă',
      danger: true,
      icon: '🚪',
      onConfirm: () => {
        state.isAdminLoggedIn = false;
        localStorage.removeItem('vorbello_admin_authenticated');
        state.appRoleView = 'user';
        localStorage.setItem('vorbello_app_role_view', 'user');
        showToast('🚪 V-ați deconectat cu succes din Panoul de Administrare.');
        renderApp();
      }
    });
  });

  // Common modal handlers (close, backdrop, admin login, pass toggle)
  attachModalCommonHandlers();

  // Navigation tabs
  document.querySelectorAll('.nav-item').forEach(el => {
    el.addEventListener('click', (e) => {
      const tab = (e.currentTarget as HTMLElement).getAttribute('data-tab') as Tab;
      if (tab) {
        state.currentTab = tab;
        if (tab === 'recordings') state.recordingsPage = 1;
        if (tab === 'audit') state.auditPage = 1;
        if (tab === 'rooms') state.roomsPage = 1;
        if (tab === 'meetings') state.meetingsPage = 1;
        if (tab === 'attendance') state.attendancePage = 1;
        renderApp();
        syncAllDataWithServer(true);
        if (tab === 'recordings') {
          syncRecordingsWithDisk(false);
        }
      }
    });
  });

  // Pagination Handlers (Recordings, Audit, Rooms, Meetings, Attendance)
  document.querySelectorAll('.btn-pagination-page, .btn-pagination-prev, .btn-pagination-next').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLElement;
      const pageType = target.getAttribute('data-page-type');
      const targetPageStr = target.getAttribute('data-page') || target.getAttribute('data-target-page');
      if (!pageType || !targetPageStr) return;
      const targetPage = parseInt(targetPageStr, 10);
      if (isNaN(targetPage) || targetPage < 1) return;
      if (pageType === 'recordings') state.recordingsPage = targetPage;
      else if (pageType === 'audit') state.auditPage = targetPage;
      else if (pageType === 'rooms') state.roomsPage = targetPage;
      else if (pageType === 'meetings') state.meetingsPage = targetPage;
      else if (pageType === 'attendance') state.attendancePage = targetPage;
      renderApp();
    });
  });

  const checkCanCreateRoom = (): boolean => {
    if (state.organizationLicense?.status === 'SUSPENDED') {
      showToast(`⚠️ Abonamentul (${state.organizationLicense.licenseKey}) este suspendat pentru neplată. Reînnoiți pe vorbello.eu.`);
      return false;
    }
    const isUserAdmin = state.isAdminLoggedIn && ['ADMIN','CLIENT_ADMIN'].includes(state.user.role);
    if (isUserAdmin) return true;
    const policy = state.organizationLicense?.creationPolicy || 'admins_hosts';
    if (policy === 'admins_only') {
      showToast('⚠️ Crearea de săli noi este permisă exclusiv Administratorilor cu licență comercială.');
      return false;
    }
    const currentMember = state.members.find(m => m.email === state.user?.email || m.name === state.user?.name);
    if (policy === 'admins_hosts' && !currentMember?.hasHostLicense) {
      showToast('⚠️ Este necesară o Licență de Gazdă (Host License) activă pentru a deschide o sală nouă. Contactați administratorul.');
      return false;
    }
    return true;
  };

  // Modal open buttons
  document.getElementById('btn-open-create-room')?.addEventListener('click', () => {
    if (!checkCanCreateRoom()) return;
    state.activeModal = 'create-room';
    renderApp();
  });

  document.getElementById('btn-empty-create-room')?.addEventListener('click', () => {
    if (!checkCanCreateRoom()) return;
    state.activeModal = 'create-room';
    renderApp();
  });

  document.getElementById('btn-open-schedule-meeting')?.addEventListener('click', () => {
    if (state.organizationLicense?.status === 'SUSPENDED') {
      showToast(`⚠️ Abonamentul (${state.organizationLicense.licenseKey}) este suspendat pentru neplată. Reînnoiți pe vorbello.eu.`);
      return;
    }
    state.activeModal = 'schedule-meeting';
    renderApp();
  });

  // Version Info Modal Open
  document.getElementById('btn-topbar-version')?.addEventListener('click', () => {
    state.activeModal = 'version-info';
    renderApp();
  });
  document.getElementById('btn-ok-version-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    renderApp();
  });

  // Modal close
  document.getElementById('btn-close-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    state.selectedRoomForAction = null;
    state.selectedMeetingForInvite = null;
    state.pendingConfirm = null;
    renderApp();
  });
  document.getElementById('btn-cancel-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    state.selectedRoomForAction = null;
    state.selectedMeetingForInvite = null;
    state.pendingConfirm = null;
    renderApp();
  });
  document.getElementById('btn-cancel-confirm-dialog')?.addEventListener('click', () => {
    state.activeModal = null;
    state.pendingConfirm = null;
    renderApp();
  });
  document.getElementById('btn-ok-confirm-dialog')?.addEventListener('click', () => {
    if (state.pendingConfirm) {
      const action = state.pendingConfirm.onConfirm;
      state.activeModal = null;
      state.pendingConfirm = null;
      Promise.resolve().then(action).catch(error => showToast(error instanceof Error ? error.message : 'Operația nu a fost confirmată.'));
    }
  });

  // Helper to find a room across production and demo collections
  const findRoomById = (id: string | null) => {
    if (!id) return null;
    return state.prodRooms.find(r => r.id === id) || state.rooms.find(r => r.id === id) || null;
  };

  // Reset default rooms
  const handleResetRooms = () => {
    openConfirmDialog({
      title: 'Restaurați Camerele Implicite?',
      description: 'Sigur doriți să restaurați lista de camere la cele inițiale predefinite ale platformei?',
      icon: '↺',
      confirmText: 'Da, Resetează',
      danger: false,
      onConfirm: async () => {
        if (state.appMode === 'production') {
          state.prodRooms = JSON.parse(JSON.stringify(DEFAULT_PROD_ROOMS));
        } else {
          state.rooms = JSON.parse(JSON.stringify(DEFAULT_DEMO_ROOMS));
        }
        requireConfirmedSync(await saveRoomsToStorage());
        showToast('Camerele au fost restaurate la valorile implicite.');
        renderApp();
      }
    });
  };
  document.getElementById('btn-reset-default-rooms')?.addEventListener('click', handleResetRooms);
  document.getElementById('btn-empty-reset-rooms')?.addEventListener('click', handleResetRooms);

  // Form: User Profile Submit
  document.getElementById('form-edit-profile')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (document.getElementById('input-profile-name') as HTMLInputElement).value.trim();
    const email = (document.getElementById('input-profile-email') as HTMLInputElement).value.trim();
    const role = (document.getElementById('input-profile-role') as HTMLSelectElement).value;
    const org = (document.getElementById('input-profile-org') as HTMLInputElement).value.trim();

    state.user.name = name;
    state.user.email = email;
    state.user.role = role as 'ADMIN' | 'MODERATOR' | 'MEMBER';
    state.user.organizationName = org;

    localStorage.setItem('vorbello_user_name', name);
    localStorage.setItem('vorbello_user_email', email);
    localStorage.setItem('vorbello_user_role', role);
    localStorage.setItem('vorbello_user_org', org);

    // Update in prodMembers
    const myProd = state.prodMembers.find(m => m.id === state.user.id || m.email === email);
    if (myProd) {
      myProd.name = name;
      myProd.email = email;
      myProd.role = role;
      myProd.hasHostLicense = role === 'ADMIN' || role === 'MODERATOR';
    } else {
      state.prodMembers.unshift({
        id: state.user.id,
        name,
        email,
        role,
        status: 'ACTIVE',
        joinedAt: new Date().toISOString().split('T')[0],
        hasHostLicense: role === 'ADMIN' || role === 'MODERATOR',
      });
    }
    requireConfirmedSync(await saveMembersToStorage());

    state.activeModal = null;
    showToast(`Profil actualizat! Nume: ${name} • Rol: ${role}`);
    renderApp();
  });

  // Form: Create Room
  document.getElementById('form-create-room')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!checkCanCreateRoom()) return;
    const name = (document.getElementById('input-room-name') as HTMLInputElement).value.trim();
    const type = ((document.getElementById('input-room-type') as HTMLSelectElement)?.value as 'permanent' | 'meeting') || 'permanent';
    const description = (document.getElementById('input-room-desc') as HTMLTextAreaElement).value.trim();
    const visibility = (document.getElementById('input-room-vis') as HTMLSelectElement).value;
    const maxParticipants = parseInt((document.getElementById('input-room-max') as HTMLInputElement).value, 10) || 50;

    const waitingRoomEnabled = (document.getElementById('input-room-waiting') as HTMLInputElement)?.checked ?? false;
    const waitForHost = (document.getElementById('input-room-wait-for-host') as HTMLInputElement)?.checked ?? true;
    const allowJoinBeforeHost = !waitForHost;
    const autoRecord = (document.getElementById('input-room-auto-record') as HTMLInputElement)?.checked ?? false;
    const autoRecordFormat = ((document.getElementById('input-room-auto-record-format') as HTMLSelectElement)?.value as 'video-fhd' | 'video-hd' | 'audio') || 'video-hd';
    const autoRecordLayout = ((document.getElementById('input-room-auto-record-layout') as HTMLSelectElement)?.value as 'composite' | 'screen_only' | 'cameras_only') || 'composite';
    try { localStorage.setItem('vorbello_default_auto_record_layout', autoRecordLayout); } catch (_) {}
    const autoRecordContainer = (((document.getElementById('input-room-auto-record-container') as HTMLSelectElement)?.value as 'webm' | 'mp4') === 'mp4' && isMp4RecordingAllowed()) ? 'mp4' : 'webm';
    try { localStorage.setItem('vorbello_default_auto_record_container', autoRecordContainer); } catch (_) {}
    const autoRecordNotify = (document.getElementById('input-room-auto-record-notify') as HTMLInputElement)?.checked ?? true;
    const allowGuestInvites = (document.getElementById('input-room-guest-invites') as HTMLInputElement)?.checked ?? true;
    const muteOnEntry = (document.getElementById('input-room-mute-entry') as HTMLInputElement)?.checked ?? true;
    const cameraOffOnEntry = (document.getElementById('input-room-cam-entry') as HTMLInputElement)?.checked ?? false;
    const allowScreenShare = ((document.getElementById('input-room-screen-share') as HTMLSelectElement)?.value as 'everyone' | 'hosts_only') || 'everyone';
    const attendanceTracking = (document.getElementById('input-room-attendance') as HTMLInputElement)?.checked ?? true;
    const pinCode = (document.getElementById('input-room-pin') as HTMLInputElement)?.value.trim() || undefined;

    const hostDeviceId = getStableClientDeviceId();
    const creatorName = state.user?.name || 'Administrator';
    const creatorId = state.user?.id || 'admin';

    const newRoom: RoomItem = {
      id: `room-${Date.now()}`,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'),
      description,
      visibility,
      maxParticipants,
      activeParticipants: 0,
      defaultVideoEnabled: true,
      createdAt: new Date().toISOString(),
      type,
      creatorId,
      creatorName,
      creatorDeviceId: hostDeviceId,
      waitingRoomEnabled,
      waitForHost,
      allowJoinBeforeHost,
      autoRecord,
      autoRecordFormat,
      autoRecordLayout,
      autoRecordContainer,
      autoRecordNotify,
      allowGuestInvites,
      muteOnEntry,
      cameraOffOnEntry,
      allowScreenShare,
      attendanceTracking,
      pinCode,
    };

    const targetRooms = state.appMode === 'production' ? state.prodRooms : state.rooms;
    targetRooms.unshift(newRoom);
    requireConfirmedSync(await saveRoomsToStorage());
    pushRoomToServer(newRoom);

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'room.created',
      actorName: state.user.name,
      targetType: 'room',
      targetId: newRoom.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ name: newRoom.name, visibility: newRoom.visibility, type, pinProtected: Boolean(pinCode) }),
    });

    state.activeModal = null;
    showToast(`Camera "${name}" a fost creată cu succes!`);
    renderApp();
  });

  // Edit Room Trigger
  document.querySelectorAll('.btn-edit-room').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const roomId = (e.currentTarget as HTMLElement).getAttribute('data-room-id');
      state.selectedRoomForAction = findRoomById(roomId);
      state.activeModal = 'edit-room';
      renderApp();
    });
  });

  // Form: Edit Room Submit
  document.getElementById('form-edit-room')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!state.selectedRoomForAction) return;

    const name = (document.getElementById('input-edit-room-name') as HTMLInputElement).value.trim();
    const slug = (document.getElementById('input-edit-room-slug') as HTMLInputElement).value.trim() || name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
    const type = ((document.getElementById('input-edit-room-type') as HTMLSelectElement)?.value as 'permanent' | 'meeting') || 'permanent';
    const description = (document.getElementById('input-edit-room-desc') as HTMLTextAreaElement).value.trim();
    const visibility = (document.getElementById('input-edit-room-vis') as HTMLSelectElement).value;
    const max = parseInt((document.getElementById('input-edit-room-max') as HTMLInputElement).value, 10) || 50;

    const waitingRoomEnabled = (document.getElementById('input-edit-room-waiting') as HTMLInputElement)?.checked ?? false;
    const waitForHost = (document.getElementById('input-edit-room-wait-for-host') as HTMLInputElement)?.checked ?? true;
    const allowJoinBeforeHost = !waitForHost;
    const autoRecord = (document.getElementById('input-edit-room-auto-record') as HTMLInputElement)?.checked ?? false;
    const autoRecordFormat = ((document.getElementById('input-edit-room-auto-record-format') as HTMLSelectElement)?.value as 'video-fhd' | 'video-hd' | 'audio') || 'video-hd';
    const autoRecordLayout = ((document.getElementById('input-edit-room-auto-record-layout') as HTMLSelectElement)?.value as 'composite' | 'screen_only' | 'cameras_only') || 'composite';
    try { localStorage.setItem('vorbello_default_auto_record_layout', autoRecordLayout); } catch (_) {}
    const autoRecordContainer = (((document.getElementById('input-edit-room-auto-record-container') as HTMLSelectElement)?.value as 'webm' | 'mp4') === 'mp4' && isMp4RecordingAllowed()) ? 'mp4' : 'webm';
    try { localStorage.setItem('vorbello_default_auto_record_container', autoRecordContainer); } catch (_) {}
    const autoRecordNotify = (document.getElementById('input-edit-room-auto-record-notify') as HTMLInputElement)?.checked ?? true;
    const allowGuestInvites = (document.getElementById('input-edit-room-guest-invites') as HTMLInputElement)?.checked ?? true;
    const muteOnEntry = (document.getElementById('input-edit-room-mute-entry') as HTMLInputElement)?.checked ?? true;
    const cameraOffOnEntry = (document.getElementById('input-edit-room-cam-entry') as HTMLInputElement)?.checked ?? false;
    const allowScreenShare = ((document.getElementById('input-edit-room-screen-share') as HTMLSelectElement)?.value as 'everyone' | 'hosts_only') || 'everyone';
    const attendanceTracking = (document.getElementById('input-edit-room-attendance') as HTMLInputElement)?.checked ?? true;
    const pinCode = (document.getElementById('input-edit-room-pin') as HTMLInputElement)?.value.trim() || undefined;

    state.selectedRoomForAction.name = name;
    state.selectedRoomForAction.slug = slug;
    state.selectedRoomForAction.type = type;
    state.selectedRoomForAction.description = description;
    state.selectedRoomForAction.visibility = visibility;
    state.selectedRoomForAction.maxParticipants = max;
    state.selectedRoomForAction.waitingRoomEnabled = waitingRoomEnabled;
    state.selectedRoomForAction.waitForHost = waitForHost;
    state.selectedRoomForAction.allowJoinBeforeHost = allowJoinBeforeHost;
    state.selectedRoomForAction.autoRecord = autoRecord;
    state.selectedRoomForAction.autoRecordFormat = autoRecordFormat;
    state.selectedRoomForAction.autoRecordLayout = autoRecordLayout;
    state.selectedRoomForAction.autoRecordContainer = autoRecordContainer;
    state.selectedRoomForAction.autoRecordNotify = autoRecordNotify;
    state.selectedRoomForAction.allowGuestInvites = allowGuestInvites;
    state.selectedRoomForAction.muteOnEntry = muteOnEntry;
    state.selectedRoomForAction.cameraOffOnEntry = cameraOffOnEntry;
    state.selectedRoomForAction.allowScreenShare = allowScreenShare;
    state.selectedRoomForAction.attendanceTracking = attendanceTracking;
    state.selectedRoomForAction.pinCode = pinCode;

    requireConfirmedSync(await saveRoomsToStorage());
    pushRoomToServer(state.selectedRoomForAction);

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'room.updated',
      actorName: state.user.name,
      targetType: 'room',
      targetId: state.selectedRoomForAction.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ name, slug, visibility, type }),
    });

    state.activeModal = null;
    state.selectedRoomForAction = null;
    showToast(`Modificările pentru "${name}" au fost salvate!`);
    renderApp();
  });

  // Delete / Archive Room Execution
  const executeDeleteRoom = (roomId: string) => {
    const room = findRoomById(roomId);
    const roomName = room ? room.name : 'această cameră';

    openConfirmDialog({
      title: 'Ștergeți Sala de Conferință?',
      description: `Sunteți pe cale să ștergeți definitiv sala "<strong>${roomName}</strong>". Această acțiune este ireversibilă.`,
      confirmText: 'Da, Șterge Sala',
      icon: '🗑️',
      danger: true,
      onConfirm: async () => {
        let removedName = '';
        const prodIdx = state.prodRooms.findIndex(r => r.id === roomId);
        if (prodIdx !== -1) {
          removedName = state.prodRooms.splice(prodIdx, 1)[0].name;
        } else {
          const demoIdx = state.rooms.findIndex(r => r.id === roomId);
          if (demoIdx !== -1) {
            removedName = state.rooms.splice(demoIdx, 1)[0].name;
          }
        }

        if (removedName) {
          requireConfirmedSync(await saveRoomsToStorage());
          await deleteRoomFromServer(roomId);
          recordAudit({
            id: `log-${Date.now()}`,
            action: 'room.deleted',
            actorName: state.user.name,
            targetType: 'room',
            targetId: roomId,
            ipHash: '8f4b2c1a...e7',
            createdAt: new Date().toISOString(),
            metadata: JSON.stringify({ name: removedName }),
          });
          state.activeModal = null;
          state.selectedRoomForAction = null;
          showToast(`Camera "${removedName}" a fost ștearsă.`);
          renderApp();
        }
      },
    });
  };

  document.getElementById('btn-archive-room')?.addEventListener('click', (e) => {
    const roomId = (e.currentTarget as HTMLElement).getAttribute('data-room-id');
    if (roomId) executeDeleteRoom(roomId);
  });

  document.querySelectorAll('.btn-delete-room').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const roomId = (e.currentTarget as HTMLElement).getAttribute('data-room-id');
      if (roomId) executeDeleteRoom(roomId);
    });
  });

  // Invite to Room Modal Trigger
  document.querySelectorAll('.btn-invite-room').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const roomId = (e.currentTarget as HTMLElement).getAttribute('data-room-id');
      state.selectedRoomForAction = findRoomById(roomId);
      state.activeModal = 'create-invite';
      renderApp();
    });
  });

  // Form: Create Invite Submit
  document.getElementById('form-create-invite')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!state.selectedRoomForAction) return;

    const email = (document.getElementById('input-invite-email') as HTMLInputElement).value.trim();
    const hours = parseInt((document.getElementById('input-invite-hours') as HTMLSelectElement).value, 10) || 24;

    const token = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    const newInvite: InvitationItem = {
      id: `inv-${Date.now()}`,
      roomId: state.selectedRoomForAction.id,
      roomName: state.selectedRoomForAction.name,
      email: email || null,
      token,
      expiresAt: new Date(Date.now() + hours * 3600 * 1000).toISOString(),
      status: 'Activ',
      creatorName: state.user.name,
    };

    const targetInvites = state.appMode === 'production' ? state.prodInvitations : state.invitations;
    targetInvites.unshift(newInvite);
    requireConfirmedSync(await saveInvitationsToStorage());

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'room.invitation_created',
      actorName: state.user.name,
      targetType: 'invitation',
      targetId: newInvite.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ email: newInvite.email, roomId: newInvite.roomId }),
    });

    state.activeModal = null;
    showToast(`Invitație generată pentru ${state.selectedRoomForAction.name}!`);
    renderApp();
  });

  // Copy Invite Token
  document.querySelectorAll('.btn-copy-invite').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const token = (e.currentTarget as HTMLElement).getAttribute('data-token');
      const origin = window.location.origin && window.location.origin.startsWith('http') ? window.location.origin : 'https://app.vorbello.ro';
      const url = `${origin}/invite/${token}`;
      navigator.clipboard?.writeText(url);
      showToast(`Linkul de invitație a fost copiat în clipboard!`);
    });
  });

  // Revoke Invite
  document.querySelectorAll('.btn-revoke-invite').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const invId = (e.currentTarget as HTMLElement).getAttribute('data-inv-id');
      const targetInvites = state.appMode === 'production' ? state.prodInvitations : state.invitations;
      const idx = targetInvites.findIndex(i => i.id === invId);
      if (idx !== -1) {
        targetInvites.splice(idx, 1);
        requireConfirmedSync(await saveInvitationsToStorage());
        showToast('Invitația a fost revocată.');
        renderApp();
      }
    });
  });

  // Schedule Meeting
  document.getElementById('form-schedule-meeting')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const roomId = (document.getElementById('input-meet-room') as HTMLSelectElement).value;
    const title = (document.getElementById('input-meet-title') as HTMLInputElement).value;
    const agenda = (document.getElementById('input-meet-agenda') as HTMLTextAreaElement)?.value.trim() || undefined;
    const meetDate = (document.getElementById('input-meet-date') as HTMLInputElement)?.value || new Date().toISOString().split('T')[0];
    const meetTime = (document.getElementById('input-meet-time') as HTMLInputElement)?.value || '15:00';
    const startsAtStr = `${meetDate}T${meetTime}`;
    const parsedDate = new Date(startsAtStr);
    const validStartsAt = isNaN(parsedDate.getTime()) ? new Date().toISOString() : parsedDate.toISOString();
    const joinPolicy = (document.getElementById('input-meet-policy') as HTMLSelectElement).value;
    const pinCode = (document.getElementById('input-meet-pin') as HTMLInputElement)?.value.trim() || undefined;
    const timezone = (document.getElementById('input-meet-timezone') as HTMLSelectElement)?.value || state.selectedTimezone;

    const waitForHost = (document.getElementById('input-meet-wait-for-host') as HTMLInputElement)?.checked ?? true;
    const waitingRoomEnabled = (document.getElementById('input-meet-waiting') as HTMLInputElement)?.checked ?? false;
    const muteOnEntry = (document.getElementById('input-meet-mute') as HTMLInputElement)?.checked ?? true;
    const cameraOffOnEntry = (document.getElementById('input-meet-cam-entry') as HTMLInputElement)?.checked ?? false;
    const allowGuestInvites = (document.getElementById('input-meet-guest-invites') as HTMLInputElement)?.checked ?? false;
    const allowScreenShare = ((document.getElementById('input-meet-screen-share') as HTMLSelectElement)?.value as 'everyone' | 'hosts_only') || 'everyone';
    const attendanceTracking = (document.getElementById('input-meet-attendance') as HTMLInputElement)?.checked ?? true;
    const autoRecord = (document.getElementById('input-meet-record') as HTMLInputElement)?.checked ?? false;
    const autoRecordNotify = (document.getElementById('input-meet-auto-record-notify') as HTMLInputElement)?.checked ?? true;
    const autoRecordFormat = ((document.getElementById('input-meet-auto-record-format') as HTMLSelectElement)?.value as 'video-fhd' | 'video-hd' | 'audio') || 'video-hd';
    const autoRecordLayout = ((document.getElementById('input-meet-auto-record-layout') as HTMLSelectElement)?.value as 'composite' | 'screen_only' | 'cameras_only') || 'composite';
    try { localStorage.setItem('vorbello_default_auto_record_layout', autoRecordLayout); } catch (_) {}
    const autoRecordContainer = (((document.getElementById('input-meet-auto-record-container') as HTMLSelectElement)?.value as 'webm' | 'mp4') === 'mp4' && isMp4RecordingAllowed()) ? 'mp4' : 'webm';
    try { localStorage.setItem('vorbello_default_auto_record_container', autoRecordContainer); } catch (_) {}

    const room = findRoomById(roomId);

    const newMeeting: MeetingItem = {
      id: `meet-${Date.now()}`,
      roomId,
      roomName: room?.name || 'Cameră',
      title,
      agenda,
      startsAt: validStartsAt,
      endsAt: null,
      status: 'scheduled',
      joinPolicy,
      timezone,
      creatorName: state.user.name,
      creatorId: state.user?.id || 'admin',
      creatorDeviceId: getStableClientDeviceId(),
      waitForHost,
      allowJoinBeforeHost: !waitForHost,
      pinCode,
      waitingRoomEnabled,
      muteOnEntry,
      cameraOffOnEntry,
      allowGuestInvites,
      allowScreenShare,
      attendanceTracking,
      autoRecord,
      autoRecordNotify,
      autoRecordFormat,
      autoRecordLayout,
      autoRecordContainer,
    };

    const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
    targetMeetings.unshift(newMeeting);
    requireConfirmedSync(await saveMeetingsToStorage());

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'meeting.scheduled',
      actorName: state.user.name,
      targetType: 'meeting',
      targetId: newMeeting.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ title, roomId, pinProtected: Boolean(pinCode) }),
    });

    state.activeModal = null;
    showToast(`Întâlnirea "${title}" a fost programată!`);
    renderApp();
  });

  // Edit Meeting Trigger
  document.querySelectorAll('.btn-edit-meeting').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (meeting) {
        state.selectedMeetingForEdit = meeting;
        state.activeModal = 'edit-meeting';
        renderApp();
      }
    });
  });

  // Form: Edit Meeting Submit
  document.getElementById('form-edit-meeting')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!state.selectedMeetingForEdit) return;

    const roomId = (document.getElementById('input-edit-meet-room') as HTMLSelectElement).value;
    const title = (document.getElementById('input-edit-meet-title') as HTMLInputElement).value.trim();
    const agenda = (document.getElementById('input-edit-meet-agenda') as HTMLTextAreaElement)?.value.trim() || undefined;
    const meetDate = (document.getElementById('input-edit-meet-date') as HTMLInputElement)?.value;
    const meetTime = (document.getElementById('input-edit-meet-time') as HTMLInputElement)?.value || '15:00';
    const startsAtStr = meetDate ? `${meetDate}T${meetTime}` : state.selectedMeetingForEdit.startsAt;
    const parsedDate = new Date(startsAtStr);
    const validStartsAt = isNaN(parsedDate.getTime()) ? state.selectedMeetingForEdit.startsAt : parsedDate.toISOString();
    const status = ((document.getElementById('input-edit-meet-status') as HTMLSelectElement).value) as 'scheduled' | 'live' | 'ended' | 'cancelled';
    const joinPolicy = (document.getElementById('input-edit-meet-policy') as HTMLSelectElement).value;
    const pinCode = (document.getElementById('input-edit-meet-pin') as HTMLInputElement)?.value.trim() || undefined;
    const timezone = (document.getElementById('input-edit-meet-timezone') as HTMLSelectElement)?.value || state.selectedTimezone;

    const waitForHost = (document.getElementById('input-edit-meet-wait-for-host') as HTMLInputElement)?.checked ?? true;
    const waitingRoomEnabled = (document.getElementById('input-edit-meet-waiting') as HTMLInputElement)?.checked ?? false;
    const muteOnEntry = (document.getElementById('input-edit-meet-mute') as HTMLInputElement)?.checked ?? true;
    const cameraOffOnEntry = (document.getElementById('input-edit-meet-cam-entry') as HTMLInputElement)?.checked ?? false;
    const allowGuestInvites = (document.getElementById('input-edit-meet-guest-invites') as HTMLInputElement)?.checked ?? false;
    const allowScreenShare = ((document.getElementById('input-edit-meet-screen-share') as HTMLSelectElement)?.value as 'everyone' | 'hosts_only') || 'everyone';
    const attendanceTracking = (document.getElementById('input-edit-meet-attendance') as HTMLInputElement)?.checked ?? true;
    const autoRecord = (document.getElementById('input-edit-meet-record') as HTMLInputElement)?.checked ?? false;
    const autoRecordNotify = (document.getElementById('input-edit-meet-auto-record-notify') as HTMLInputElement)?.checked ?? true;
    const autoRecordFormat = ((document.getElementById('input-edit-meet-auto-record-format') as HTMLSelectElement)?.value as 'video-fhd' | 'video-hd' | 'audio') || 'video-hd';
    const autoRecordLayout = ((document.getElementById('input-edit-meet-auto-record-layout') as HTMLSelectElement)?.value as 'composite' | 'screen_only' | 'cameras_only') || 'composite';
    try { localStorage.setItem('vorbello_default_auto_record_layout', autoRecordLayout); } catch (_) {}
    const autoRecordContainer = (((document.getElementById('input-edit-meet-auto-record-container') as HTMLSelectElement)?.value as 'webm' | 'mp4') === 'mp4' && isMp4RecordingAllowed()) ? 'mp4' : 'webm';
    try { localStorage.setItem('vorbello_default_auto_record_container', autoRecordContainer); } catch (_) {}

    const room = findRoomById(roomId);

    state.selectedMeetingForEdit.roomId = roomId;
    state.selectedMeetingForEdit.roomName = room?.name || state.selectedMeetingForEdit.roomName;
    state.selectedMeetingForEdit.title = title;
    state.selectedMeetingForEdit.agenda = agenda;
    state.selectedMeetingForEdit.startsAt = validStartsAt;
    state.selectedMeetingForEdit.status = status;
    state.selectedMeetingForEdit.joinPolicy = joinPolicy;
    state.selectedMeetingForEdit.pinCode = pinCode;
    state.selectedMeetingForEdit.timezone = timezone;
    state.selectedMeetingForEdit.waitForHost = waitForHost;
    state.selectedMeetingForEdit.allowJoinBeforeHost = !waitForHost;
    state.selectedMeetingForEdit.waitingRoomEnabled = waitingRoomEnabled;
    state.selectedMeetingForEdit.muteOnEntry = muteOnEntry;
    state.selectedMeetingForEdit.cameraOffOnEntry = cameraOffOnEntry;
    state.selectedMeetingForEdit.allowGuestInvites = allowGuestInvites;
    state.selectedMeetingForEdit.allowScreenShare = allowScreenShare;
    state.selectedMeetingForEdit.attendanceTracking = attendanceTracking;
    state.selectedMeetingForEdit.autoRecord = autoRecord;
    state.selectedMeetingForEdit.autoRecordNotify = autoRecordNotify;
    state.selectedMeetingForEdit.autoRecordFormat = autoRecordFormat;
    state.selectedMeetingForEdit.autoRecordLayout = autoRecordLayout;
    state.selectedMeetingForEdit.autoRecordContainer = autoRecordContainer;

    requireConfirmedSync(await saveMeetingsToStorage());

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'meeting.updated',
      actorName: state.user.name,
      targetType: 'meeting',
      targetId: state.selectedMeetingForEdit.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ title, roomId, status }),
    });

    state.activeModal = null;
    state.selectedMeetingForEdit = null;
    showToast(`Întâlnirea "${title}" a fost actualizată!`);
    renderApp();
  });

  // Start Meeting
  document.querySelectorAll('.btn-start-meeting').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (meeting) {
        if (isMeetingPast(meeting)) {
          showToast('⚠️ Această întâlnire a trecut și nu mai poate fi pornită.');
          return;
        }
        meeting.status = 'live';
        requireConfirmedSync(await saveMeetingsToStorage());
        recordAudit({
          id: `log-${Date.now()}`,
          action: 'meeting.started',
          actorName: state.user.name,
          targetType: 'meeting',
          targetId: meetId!,
          ipHash: '8f4b2c1a...e7',
          createdAt: new Date().toISOString(),
          metadata: JSON.stringify({ title: meeting.title }),
        });
        showToast(`Întâlnirea "${meeting.title}" este acum LIVE!`);
        renderApp();
      }
    });
  });

  // Sub-tabs navigation (Viitoare / Istoric / Toate)
  document.querySelectorAll('.btn-subtab-meetings').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const tab = (e.currentTarget as HTMLElement).getAttribute('data-subtab') as 'upcoming' | 'past' | 'all';
      if (tab) {
        state.meetingSubTab = tab;
        try { localStorage.setItem('vorbello_meeting_subtab', tab); } catch (_) {}
        renderApp();
      }
    });
  });

  // End Meeting
  document.querySelectorAll('.btn-end-meeting').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (meeting) {
        meeting.status = 'ended';
        requireConfirmedSync(await saveMeetingsToStorage());
        showToast(`Întâlnirea "${meeting.title}" a fost finalizată.`);
        renderApp();
      }
    });
  });

  // Cancel Meeting
  document.querySelectorAll('.btn-cancel-meeting').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (meeting) {
        meeting.status = 'cancelled';
        requireConfirmedSync(await saveMeetingsToStorage());
        showToast(`Întâlnirea "${meeting.title}" a fost anulată.`);
        renderApp();
      }
    });
  });

  // Delete Meeting
  document.querySelectorAll('.btn-delete-meeting').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (!meeting) return;

      openConfirmDialog({
        title: 'Ștergeți Întâlnirea?',
        description: `Sunteți sigur că doriți să ștergeți întâlnirea "<strong>${meeting.title}</strong>"?`,
        confirmText: 'Da, Șterge Întâlnirea',
        icon: '🗑️',
        danger: true,
        onConfirm: async () => {
          const idx = targetMeetings.findIndex(m => m.id === meetId);
          if (idx !== -1) {
            targetMeetings.splice(idx, 1);
            requireConfirmedSync(await saveMeetingsToStorage());
            if (meetId) {
              await deleteMeetingFromServer(meetId);
            }
            showToast(`Întâlnirea "${meeting.title}" a fost ștearsă.`);
            renderApp();
          }
        },
      });
    });
  });

  // Calendar Week Controls
  document.querySelectorAll('.btn-cal-prev-week').forEach(btn => {
    btn.addEventListener('click', () => {
      state.meetingCalendarWeekOffset--;
      renderApp();
    });
  });

  document.querySelectorAll('.btn-cal-next-week').forEach(btn => {
    btn.addEventListener('click', () => {
      state.meetingCalendarWeekOffset++;
      renderApp();
    });
  });

  document.querySelectorAll('.btn-cal-today').forEach(btn => {
    btn.addEventListener('click', () => {
      state.meetingCalendarWeekOffset = 0;
      state.meetingDateFilter = new Date().toISOString().split('T')[0];
      state.meetingSubTab = 'upcoming';
      renderApp();
    });
  });

  document.querySelectorAll('.btn-cal-clear-filter').forEach(btn => {
    btn.addEventListener('click', () => {
      state.meetingDateFilter = null;
      renderApp();
    });
  });

  // Calendar Day Click Filter
  document.querySelectorAll('.calendar-day-card').forEach(card => {
    card.addEventListener('click', (e) => {
      const dayIso = (e.currentTarget as HTMLElement).getAttribute('data-day-iso');
      if (dayIso) {
        if (state.meetingDateFilter === dayIso) {
          state.meetingDateFilter = null;
        } else {
          state.meetingDateFilter = dayIso;
          const todayIso = getDateKeyInTimezone(new Date(), state.selectedTimezone);
          if (dayIso < todayIso && state.meetingSubTab === 'upcoming') {
            state.meetingSubTab = 'past';
            try { localStorage.setItem('vorbello_meeting_subtab', 'past'); } catch (_) {}
          } else if (dayIso >= todayIso && state.meetingSubTab === 'past') {
            state.meetingSubTab = 'upcoming';
            try { localStorage.setItem('vorbello_meeting_subtab', 'upcoming'); } catch (_) {}
          }
        }
        renderApp();
      }
    });
  });

  // Empty State Schedule Meeting Button
  document.getElementById('btn-empty-schedule-meeting')?.addEventListener('click', () => {
    state.activeModal = 'schedule-meeting';
    renderApp();
  });

  // Google Calendar Export
  document.querySelectorAll('.btn-cal-export').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (!meeting) return;

      let start = new Date();
      try { start = new Date(meeting.startsAt); } catch {}
      const end = new Date(start.getTime() + 60 * 60 * 1000); // 1h default

      const pad = (n: number) => n.toString().padStart(2, '0');
      const fmtUtc = (d: Date) => 
        d.getUTCFullYear().toString() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) + 'T' +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        pad(d.getUTCSeconds()) + 'Z';

      const origin = window.location.origin.startsWith('http') ? window.location.origin : 'https://app.vorbello.ro';
      const details = `Conferință securizată Vorbello în camera: ${meeting.roomName}\nLink conectare: ${origin}\nCreat de: ${meeting.creatorName}`;
      const gUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(meeting.title)}&dates=${fmtUtc(start)}/${fmtUtc(end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(meeting.roomName)}`;

      window.open(gUrl, '_blank');
      showToast(`S-a deschis Google Calendar pentru adăugarea ședinței "${meeting.title}"!`);
    });
  });

  // Invite to Meeting Modal Open
  document.querySelectorAll('.btn-invite-meeting').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const meetId = (e.currentTarget as HTMLElement).getAttribute('data-meet-id');
      const targetMeetings = state.appMode === 'production' ? state.prodMeetings : state.meetings;
      const meeting = targetMeetings.find(m => m.id === meetId);
      if (meeting) {
        state.selectedMeetingForInvite = meeting;
        state.activeModal = 'invite-meeting';
        renderApp();
      }
    });
  });

  // Form: Send Meeting Email Invites
  document.getElementById('form-send-email-invites')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!state.selectedMeetingForInvite) return;
    const m = state.selectedMeetingForInvite;

    const emailInput = (document.getElementById('input-invite-emails') as HTMLInputElement)?.value || '';
    const note = (document.getElementById('input-invite-note') as HTMLTextAreaElement)?.value || '';
    void note;
    const sendCopy = (document.getElementById('check-send-self-copy') as HTMLInputElement)?.checked ?? true;

    const rawEmails = emailInput.split(/[,;\s]+/).map(em => em.trim().toLowerCase()).filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = rawEmails.filter(em => emailRegex.test(em));

    if (validEmails.length === 0) {
      showToast('⚠️ Vă rugăm introduceți cel puțin o adresă de email validă.');
      return;
    }

    const recipients = [...validEmails];
    if (sendCopy && !recipients.includes(state.user.email.toLowerCase())) {
      recipients.push(state.user.email.toLowerCase());
    }

    const targetInvites = state.appMode === 'production' ? state.prodInvitations : state.invitations;
    recipients.forEach(email => {
      const token = Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      targetInvites.unshift({
        id: `inv-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        roomId: m.roomId,
        roomName: m.roomName,
        email: email,
        token,
        expiresAt: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
        status: 'Activ',
        creatorName: state.user.name,
      });
    });

    requireConfirmedSync(await saveInvitationsToStorage());
    state.activeModal = null;
    renderApp();
    showToast(`✉️ Invitația a fost expediată către ${validEmails.length} adresă(e) de email!`);
  });

  // Copy Direct Link inside Invite Modal
  document.getElementById('btn-copy-invite-link')?.addEventListener('click', () => {
    const input = document.getElementById('input-quick-copy-url') as HTMLInputElement;
    if (input && input.value) {
      navigator.clipboard?.writeText(input.value);
      showToast('📋 Linkul direct de acces a fost copiat în clipboard!');
    }
  });

  // Copy Full Formatted Invitation inside Invite Modal
  document.getElementById('btn-copy-full-invitation')?.addEventListener('click', () => {
    if (!state.selectedMeetingForInvite) return;
    const m = state.selectedMeetingForInvite;
    const origin = window.location.origin && window.location.origin.startsWith('http') ? window.location.origin : 'https://app.vorbello.ro';
    const inviteUrl = `${origin}/invite/${m.roomId}?meet=${m.id}`;
    const formattedDate = new Date(m.startsAt).toLocaleString('ro-RO', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
    });

    const fullInvitation = 
`Bună ziua,

Vă invităm să participați la videoconferința securizată pe platforma Vorbello.

📌 Subiect: ${m.title}
🚪 Sala: ${m.roomName}
🕒 Data și ora: ${formattedDate}
👤 Organizator: ${m.creatorName}

🔗 Accesați direct conferința făcând clic pe linkul de mai jos:
${inviteUrl}

(Vă puteți conecta instant din orice browser modern sau din aplicația dedicată Vorbello Desktop)

Cu stimă,
${m.creatorName} — Vorbello Video Conferencing`;

    navigator.clipboard?.writeText(fullInvitation);
    showToast('📑 Textul complet al invitației a fost copiat în clipboard!');
  });

  // Mode Switcher Handlers
  document.getElementById('btn-toggle-demo-mode')?.addEventListener('click', () => {
    state.appMode = 'demo';
    localStorage.setItem('vorbello_app_mode', 'demo');
    state.attendanceRecords = loadFromStorage<AttendanceRecord[]>('vorbello_demo_attendance', DEFAULT_ATTENDANCE_RECORDS);
    showToast('🧪 Ați trecut în Modul Demo (Simulare Locală).');
    renderApp();
  });

  document.getElementById('btn-toggle-prod-mode')?.addEventListener('click', () => {
    state.appMode = 'production';
    localStorage.setItem('vorbello_app_mode', 'production');
    state.attendanceRecords = loadFromStorage<AttendanceRecord[]>('vorbello_attendance', []);
    void syncAllDataWithServer(true);
    showToast('🚀 Ați trecut în Modul Producție (Server VPS Live)!');
    renderApp();
  });

  document.getElementById('btn-switch-to-prod-banner')?.addEventListener('click', () => {
    state.appMode = 'production';
    localStorage.setItem('vorbello_app_mode', 'production');
    state.attendanceRecords = loadFromStorage<AttendanceRecord[]>('vorbello_attendance', []);
    void syncAllDataWithServer(true);
    showToast('🚀 Ați trecut în Modul Producție (Server VPS Live)!');
    renderApp();
  });

  // Open VPS Config Modal
  document.querySelectorAll('#btn-open-vps-modal, #btn-quick-config-vps').forEach(btn => {
    btn.addEventListener('click', () => {
      state.activeModal = 'vps-config';
      renderApp();
    });
  });

  // VPS Modal sub-tabs
  document.getElementById('btn-vps-tab-config')?.addEventListener('click', () => {
    state.vpsModalTab = 'config';
    renderApp();
  });

  document.getElementById('btn-vps-tab-guide')?.addEventListener('click', () => {
    state.vpsModalTab = 'guide';
    renderApp();
  });

  document.getElementById('btn-switch-to-config-tab')?.addEventListener('click', () => {
    state.vpsModalTab = 'config';
    renderApp();
  });

  // Preset Buttons for LiveKit URL
  document.getElementById('btn-preset-wss-host')?.addEventListener('click', () => {
    const input = document.getElementById('input-vps-livekit') as HTMLInputElement | null;
    if (input) input.value = `wss://${window.location.host}`;
  });
  document.getElementById('btn-preset-live-wss')?.addEventListener('click', () => {
    const input = document.getElementById('input-vps-livekit') as HTMLInputElement | null;
    if (input) input.value = 'wss://live.vorbello.eu';
  });
  document.getElementById('btn-preset-vps-direct')?.addEventListener('click', () => {
    const input = document.getElementById('input-vps-livekit') as HTMLInputElement | null;
    if (input) input.value = 'ws://217.217.233.20:7880';
  });

  // Form: VPS Settings Submit
  document.getElementById('form-vps-settings')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const apiUrl = (document.getElementById('input-vps-api') as HTMLInputElement)?.value.trim();
    const livekitUrl = (document.getElementById('input-vps-livekit') as HTMLInputElement)?.value.trim();
    const serverName = (document.getElementById('input-vps-name') as HTMLInputElement)?.value.trim();

    if (apiUrl) {
      state.vpsConfig.apiUrl = apiUrl;
      localStorage.setItem('vorbello_vps_api', apiUrl);
    }
    if (livekitUrl) {
      state.vpsConfig.livekitUrl = livekitUrl;
      localStorage.setItem('vorbello_vps_livekit', livekitUrl);
    }
    if (serverName) {
      state.vpsConfig.serverName = serverName;
      localStorage.setItem('vorbello_vps_name', serverName);
    }

    state.appMode = 'production';
    localStorage.setItem('vorbello_app_mode', 'production');
    state.attendanceRecords = loadFromStorage<AttendanceRecord[]>('vorbello_attendance', []);
    void syncAllDataWithServer(true);
    state.activeModal = null;
    showToast('💾 Setările serverului VPS au fost salvate și conectate!');
    renderApp();
  });

  // Test VPS Ping Button
  document.getElementById('btn-test-vps-ping')?.addEventListener('click', async () => {
    const pingText = document.getElementById('vps-ping-text');
    const pingSubtext = document.getElementById('vps-ping-subtext');
    const btn = document.getElementById('btn-test-vps-ping') as HTMLButtonElement | null;

    if (btn) btn.innerText = '⏳ Verificare...';
    if (pingText) pingText.innerText = 'Se trimite ping către serverul VPS...';
    if (pingSubtext) pingSubtext.innerText = 'Verificare disponibilitate port LiveKit...';

    const startTime = performance.now();
    try {
      const livekitInput = (document.getElementById('input-vps-livekit') as HTMLInputElement)?.value.trim() || state.vpsConfig.livekitUrl;
      const probeUrl = livekitInput.startsWith('wss://') 
        ? livekitInput.replace('wss://', 'https://') 
        : livekitInput.replace('ws://', 'http://');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);

      // Probe LiveKit SFU endpoint directly
      await fetch(probeUrl, { method: 'GET', mode: 'no-cors', signal: controller.signal });
      clearTimeout(timeoutId);

      const latency = Math.max(15, Math.round(performance.now() - startTime));
      state.vpsConfig.pingMs = latency;
      state.vpsConfig.status = 'connected';
      state.vpsConfig.statusText = 'Server VPS Conectat • LiveKit SFU activ';
      localStorage.setItem('vorbello_vps_status', 'connected');

      const qualityBadge = latency < 80 ? 'Excelentă (Sub 80ms)' : latency < 150 ? 'Bună' : 'Acceptabilă';

      if (pingText) pingText.innerText = `✅ Server VPS Conectat (${latency} ms)`;
      if (pingSubtext) pingSubtext.innerHTML = `Latență verificată: <b>${latency} ms</b> (${qualityBadge}) • LiveKit SFU răspunde prompt pe portul 7880.`;
      if (btn) btn.innerText = '🔄 Re-testează';
      showToast(`✅ Conexiune confirmată cu serverul VPS (${latency} ms)!`);
    } catch (err: any) {
      // Diagnostic check: check if cloud web signaling is reachable
      try {
        const fbStart = performance.now();
        const signalOrigin = (typeof window !== 'undefined' && window.location.protocol === 'file:') ? 'https://app.vorbello.eu' : '';
        const signalRes = await fetch(`${signalOrigin}/signal.php?action=health`, { cache: 'no-store' });
        if (signalRes.ok) {
          const fbLatency = Math.max(15, Math.round(performance.now() - fbStart));
          state.vpsConfig.pingMs = fbLatency;
          if (pingText) pingText.innerText = `⚠️ Cloud Activ (${fbLatency} ms) • LiveKit SFU Offline`;
          if (pingSubtext) pingSubtext.innerHTML = `Latență rețea: <b>${fbLatency} ms</b> • Domeniul răspunde prompt, dar containerul LiveKit (port 7880) este oprit pe VPS. Porniți containerele cu: <code>docker compose up -d</code>.`;
          if (btn) btn.innerText = '🔄 Re-testează';
          return;
        }
      } catch (_) {}

      if (pingText) pingText.innerText = '❌ Server Inaccesibil';
      if (pingSubtext) pingSubtext.innerText = 'Nu s-a putut stabili conexiunea cu serverul VPS. Verificați IP-ul sau porniți containerele pe VPS.';
      if (btn) btn.innerText = '🔄 Re-testează';
    }
  });

  // Modal Backdrop click to close
  document.querySelectorAll('.modal-overlay').forEach(overlay => {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        state.activeModal = null;
        renderApp();
      }
    });
  });

  // Connect to LiveKit Room
  document.querySelectorAll('.btn-join-room, .btn-join-meeting').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const roomId = (e.currentTarget as HTMLElement).getAttribute('data-room-id');
      const allRooms = state.appMode === 'production' ? state.prodRooms : state.rooms;
      const room = allRooms.find(r => r.id === roomId) || state.rooms.find(r => r.id === roomId);
      if (!room) return;

      await launchConference(room.slug || room.id, room.name, state.user.name);
    });
  });

  // Member Role Change
  document.querySelectorAll('.select-role').forEach(select => {
    select.addEventListener('change', async (e) => {
      const userId = (e.currentTarget as HTMLElement).getAttribute('data-user-id');
      const newRole = (e.currentTarget as HTMLSelectElement).value;
      const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
      const member = targetMembers.find(m => m.id === userId);
      if (member) {
        member.role = newRole;
        if (member.id === state.user.id || (member.email && member.email === state.user.email)) {
          state.user.id = member.id;
          state.user.role = newRole as any;
          localStorage.setItem('vorbello_user_id', state.user.id);
          localStorage.setItem('vorbello_user_role', newRole);
          localStorage.setItem('vorbello_user', JSON.stringify(state.user));
        }
        if (member.email) {
          const acc = state.adminAccounts.find(a => a.email.toLowerCase() === member.email!.toLowerCase());
          if (acc) {
            acc.role = newRole as any;
            localStorage.setItem('vorbello_admin_accounts', JSON.stringify(state.adminAccounts));
          }
        }
        requireConfirmedSync(await saveMembersToStorage());
        requireConfirmedSync(await syncAllDataWithServer(true, true));
        recordAudit({
          id: `log-${Date.now()}`,
          action: 'member.role_updated',
          actorName: state.user.name,
          targetType: 'membership',
          targetId: userId!,
          ipHash: '8f4b2c1a...e7',
          createdAt: new Date().toISOString(),
          metadata: JSON.stringify({ newRole, targetUser: member.name }),
        });
        showToast(`Rolul lui ${member.name} a fost actualizat la ${newRole}.`);
        renderApp();
      }
    });
  });

  // Switch Active User Profile ("🔑 Conectare")
  document.querySelectorAll('.btn-switch-user').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const userId = (e.currentTarget as HTMLElement).getAttribute('data-user-id');
      const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
      const member = targetMembers.find(m => m.id === userId);
      if (!member) return;

      state.user = {
        id: member.id,
        name: member.name,
        email: member.email,
        role: member.role as 'ADMIN' | 'MODERATOR' | 'MEMBER',
        organizationName: state.user.organizationName,
        organizationId: state.user.organizationId,
      };
      localStorage.setItem('vorbello_user_id', member.id);
      localStorage.setItem('vorbello_user_name', member.name);
      localStorage.setItem('vorbello_user_email', member.email);
      localStorage.setItem('vorbello_user_role', member.role);
      localStorage.setItem('vorbello_user', JSON.stringify(state.user));
      showToast(`🔑 V-ați conectat ca: ${member.name} (${member.role})`);
      renderApp();
    });
  });

  // Set Master Admin on this workstation ("⭐ Setează Master Admin")
  document.querySelectorAll('.btn-set-master-admin').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const userId = (e.currentTarget as HTMLElement).getAttribute('data-user-id');
      const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
      const member = targetMembers.find(m => m.id === userId);
      if (!member) return;

      state.user = {
        id: member.id,
        name: member.name,
        email: member.email,
        role: member.role as 'ADMIN' | 'MODERATOR' | 'MEMBER',
        organizationName: state.user.organizationName,
        organizationId: state.user.organizationId,
      };
      localStorage.setItem('vorbello_user_id', member.id);
      localStorage.setItem('vorbello_user_name', member.name);
      localStorage.setItem('vorbello_user_email', member.email);
      localStorage.setItem('vorbello_user_role', member.role);
      localStorage.setItem('vorbello_user', JSON.stringify(state.user));
      showToast(`⭐ "${member.name}" este acum Master Admin pe această stație!`);
      renderApp();
    });
  });

  // Delete Member ("🗑️ Șterge")
  document.querySelectorAll('.btn-delete-member').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const userId = (e.currentTarget as HTMLElement).getAttribute('data-user-id');
      const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
      const member = targetMembers.find(m => m.id === userId);
      if (!member) return;

      openConfirmDialog({
        title: 'Ștergeți Utilizatorul?',
        description: `Sunteți sigur că doriți să ștergeți definitiv contul "<strong>${member.name}</strong>" (${member.email})?`,
        confirmText: 'Da, Șterge Contul',
        icon: '🗑️',
        danger: true,
        onConfirm: async () => {
          await deleteMemberFromServer(member.id);

          // If the deleted member was the active master admin / user:
          if (member.id === state.user.id || (member.email && member.email === state.user.email)) {
            const remaining = state.appMode === 'production' ? state.prodMembers : state.members;
            if (remaining.length > 0) {
              const nextAdmin = remaining[0];
              state.user.id = nextAdmin.id;
              state.user.name = nextAdmin.name;
              state.user.email = nextAdmin.email;
              state.user.role = nextAdmin.role as any;
            } else {
              state.user.id = `usr-${Date.now()}`;
              state.user.name = 'Administrator Master';
              state.user.email = 'admin@vorbello.local';
              state.user.role = 'ADMIN';
            }
            localStorage.setItem('vorbello_user_id', state.user.id);
            localStorage.setItem('vorbello_user_name', state.user.name);
            localStorage.setItem('vorbello_user_email', state.user.email);
            localStorage.setItem('vorbello_user_role', state.user.role);
            localStorage.setItem('vorbello_user', JSON.stringify(state.user));
          }

          showToast(`Utilizatorul "${member.name}" a fost eliminat definitiv.`);
          renderApp();
        },
      });
    });
  });

  // Open Master Admin Profile Modal
  document.getElementById('btn-open-profile')?.addEventListener('click', () => {
    state.activeModal = 'master-profile';
    renderApp();
  });

  // Select existing member inside master profile modal
  document.getElementById('select-existing-member')?.addEventListener('change', (e) => {
    const select = e.currentTarget as HTMLSelectElement;
    const opt = select.selectedOptions[0];
    if (opt && opt.value) {
      const nameInput = document.getElementById('input-profile-name') as HTMLInputElement;
      const emailInput = document.getElementById('input-profile-email') as HTMLInputElement;
      const roleSelect = document.getElementById('select-profile-role') as HTMLSelectElement;
      if (nameInput) nameInput.value = opt.getAttribute('data-name') || '';
      if (emailInput) emailInput.value = opt.getAttribute('data-email') || '';
      if (roleSelect) roleSelect.value = opt.getAttribute('data-role') || 'ADMIN';
    }
  });

  // Form: Save Master Admin Profile
  document.getElementById('form-master-profile')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (document.getElementById('input-profile-name') as HTMLInputElement)?.value.trim();
    const email = (document.getElementById('input-profile-email') as HTMLInputElement)?.value.trim();
    const role = (document.getElementById('select-profile-role') as HTMLSelectElement)?.value || 'ADMIN';
    const org = (document.getElementById('input-profile-org') as HTMLInputElement)?.value.trim() || state.user.organizationName;

    if (!name || !email) return;

    state.user.name = name;
    state.user.email = email;
    state.user.role = role as any;
    state.user.organizationName = org;

    const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
    let found = targetMembers.find(m => m.id === state.user.id || (m.email && m.email === email));
    if (found) {
      state.user.id = found.id;
      found.name = name;
      found.email = email;
      found.role = role;
    } else {
      targetMembers.unshift({
        id: state.user.id,
        name,
        email,
        role,
        status: 'ACTIVE',
        joinedAt: new Date().toISOString().split('T')[0],
        hasHostLicense: true,
      });
    }

    localStorage.setItem('vorbello_user_id', state.user.id);
    localStorage.setItem('vorbello_user_name', name);
    localStorage.setItem('vorbello_user_email', email);
    localStorage.setItem('vorbello_user_role', role);
    localStorage.setItem('vorbello_user_org', org);
    localStorage.setItem('vorbello_user', JSON.stringify(state.user));

    requireConfirmedSync(await saveMembersToStorage());
    requireConfirmedSync(await syncAllDataWithServer(true, true));

    state.activeModal = null;
    showToast(`⭐ Profilul Master Admin a fost actualizat: "${name}" (${role})!`);
    renderApp();
  });

  // Member Status Toggle
  document.querySelectorAll('.btn-toggle-status').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const userId = (e.currentTarget as HTMLElement).getAttribute('data-user-id');
      const newStatus = (e.currentTarget as HTMLElement).getAttribute('data-new-status');
      const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
      const member = targetMembers.find(m => m.id === userId);
      if (member && newStatus) {
        member.status = newStatus;
        requireConfirmedSync(await saveMembersToStorage());
        showToast(`Statusul utilizatorului ${member.name} a fost modificat: ${newStatus}.`);
        renderApp();
      }
    });
  });

  // Open Add Member Modal
  document.getElementById('btn-open-add-member')?.addEventListener('click', () => {
    state.activeModal = 'add-member';
    renderApp();
  });

  // Open License Policies Modal
  document.getElementById('btn-open-license-modal')?.addEventListener('click', () => {
    state.activeModal = 'manage-license';
    renderApp();
  });

  // Open Issued Licenses Management Modal
  document.getElementById('btn-open-issued-licenses')?.addEventListener('click', () => {
    state.activeModal = 'issued-licenses';
    renderApp();
  });

  // Open Master Password Change Modal
  document.getElementById('btn-open-change-password')?.addEventListener('click', () => {
    state.activeModal = 'change-password';
    renderApp();
  });

  // Open Issue New License Modal
  document.getElementById('btn-open-issue-license-modal')?.addEventListener('click', () => {
    state.activeModal = 'issue-license';
    renderApp();
  });

  // Back to Licenses List
  document.getElementById('btn-back-to-licenses')?.addEventListener('click', () => {
    state.activeModal = 'issued-licenses';
    renderApp();
  });

  // Regenerate License Key Button
  document.getElementById('btn-regen-license-key')?.addEventListener('click', () => {
    const tier = (document.getElementById('select-license-tier') as HTMLSelectElement)?.value || 'pro';
    const input = document.getElementById('input-license-key-val') as HTMLInputElement | null;
    if (input) {
      input.value = generateLicenseKey(tier);
    }
  });

  // Tier Selector Auto-populate
  document.getElementById('select-license-tier')?.addEventListener('change', (e) => {
    const tier = (e.target as HTMLSelectElement).value;
    const hostsInput = document.getElementById('input-license-hosts') as HTMLInputElement | null;
    const capacitySelect = document.getElementById('select-license-capacity') as HTMLSelectElement | null;
    const priceInput = document.getElementById('input-license-price') as HTMLInputElement | null;
    const keyInput = document.getElementById('input-license-key-val') as HTMLInputElement | null;

    if (keyInput) keyInput.value = generateLicenseKey(tier);

    if (tier === 'starter') {
      if (hostsInput) hostsInput.value = '1';
      if (capacitySelect) capacitySelect.value = '50';
      if (priceInput) priceInput.value = '29€ / lună (Facturat Anual)';
    } else if (tier === 'pro') {
      if (hostsInput) hostsInput.value = '5';
      if (capacitySelect) capacitySelect.value = '100';
      if (priceInput) priceInput.value = '79€ / lună (Facturat Anual)';
    } else if (tier === 'enterprise') {
      if (hostsInput) hostsInput.value = '10';
      if (capacitySelect) capacitySelect.value = '250';
      if (priceInput) priceInput.value = '199€ / lună (Facturat Anual)';
    } else {
      if (hostsInput) hostsInput.value = '25';
      if (capacitySelect) capacitySelect.value = '250';
      if (priceInput) priceInput.value = 'Personalizat (Oferit prin Contract)';
    }
  });

  // Generate Random Password for Member
  document.getElementById('btn-generate-member-pass')?.addEventListener('click', () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let generated = 'Vrb-';
    for (let i = 0; i < 8; i++) {
      generated += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const input = document.getElementById('input-member-password') as HTMLInputElement | null;
    if (input) input.value = generated;
  });

  // Form: Change Master Password Submit
  document.getElementById('form-change-password')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const currPass = ((document.getElementById('input-curr-pass') as HTMLInputElement)?.value || '');
    const newPass = ((document.getElementById('input-new-pass') as HTMLInputElement)?.value || '');
    const confirmPass = ((document.getElementById('input-confirm-pass') as HTMLInputElement)?.value || '');
    const savedMasterPass = localStorage.getItem('vorbello_admin_master_password') || 'Testoso2026!!!???';

    if (currPass !== savedMasterPass) {
      showToast('⚠️ Parola actuală este incorectă!');
      return;
    }
    if (newPass.length < 6) {
      showToast('⚠️ Noua parolă trebuie să conțină cel puțin 6 caractere!');
      return;
    }
    if (newPass !== confirmPass) {
      showToast('⚠️ Parolele nu coincid! Verificați confirmarea.');
      return;
    }

    localStorage.setItem('vorbello_admin_master_password', newPass);
    state.masterAdminPassword = newPass;

    // Also update Danny's account in adminAccounts
    const dannyAcc = state.adminAccounts.find(a => a.email.toLowerCase() === 'dannyopr@gmail.com');
    if (dannyAcc) {
      dannyAcc.password = newPass;
      localStorage.setItem('vorbello_admin_accounts', JSON.stringify(state.adminAccounts));
    }

    state.activeModal = null;
    showToast('✅ Parola de Master Administrator a fost actualizată cu succes!');
    renderApp();
  });

  // Form: Add Member & Admin Account Submit
  document.getElementById('form-add-member')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (document.getElementById('input-member-name') as HTMLInputElement)?.value.trim();
    const email = (document.getElementById('input-member-email') as HTMLInputElement)?.value.trim().toLowerCase();
    const role = ((document.getElementById('input-member-role') as HTMLSelectElement)?.value || 'MEMBER') as 'ADMIN' | 'MODERATOR' | 'MEMBER';
    const memberPass = (document.getElementById('input-member-password') as HTMLInputElement)?.value.trim();
    const hasHostLicense = (document.getElementById('check-member-host-license') as HTMLInputElement)?.checked ?? false;

    if (!name || !email) return;

    const newMember: MemberItem = {
      id: `usr-${Date.now()}`,
      name,
      email,
      role,
      status: 'ACTIVE',
      joinedAt: new Date().toISOString().split('T')[0],
      hasHostLicense,
    };

    const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
    targetMembers.unshift(newMember);
    requireConfirmedSync(await saveMembersToStorage());

    // If role is ADMIN or MODERATOR, or password was specified, save to adminAccounts
    if (role === 'ADMIN' || role === 'MODERATOR' || memberPass) {
      const assignedPass = memberPass || 'Vorbello2026!';
      const existingAccIndex = state.adminAccounts.findIndex(a => a.email.toLowerCase() === email);
      if (existingAccIndex >= 0) {
        state.adminAccounts[existingAccIndex].password = assignedPass;
        state.adminAccounts[existingAccIndex].role = role;
      } else {
        state.adminAccounts.push({
          id: `admin-${Date.now()}`,
          name,
          email,
          password: assignedPass,
          role,
          status: 'ACTIVE',
          createdAt: new Date().toISOString().split('T')[0],
        });
      }
      localStorage.setItem('vorbello_admin_accounts', JSON.stringify(state.adminAccounts));
    }

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'member.created',
      actorName: state.user.name,
      targetType: 'membership',
      targetId: newMember.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ name, email, role, hasHostLicense, hasAdminLogin: !!memberPass }),
    });

    state.activeModal = null;
    showToast(`Utilizatorul "${name}" a fost adăugat cu succes ca ${role}!`);
    renderApp();
  });

  // Form: Issue License Submit
  document.getElementById('form-issue-license')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const clientName = (document.getElementById('input-license-client') as HTMLInputElement)?.value.trim() || 'Client Vorbello';
    const tier = (document.getElementById('select-license-tier') as HTMLSelectElement)?.value as any || 'pro';
    const maxHosts = parseInt((document.getElementById('input-license-hosts') as HTMLInputElement)?.value, 10) || 5;
    const maxParticipants = parseInt((document.getElementById('select-license-capacity') as HTMLSelectElement)?.value, 10) || 100;
    const durationMonths = parseInt((document.getElementById('select-license-duration') as HTMLSelectElement)?.value, 10) || 12;
    const priceTag = (document.getElementById('input-license-price') as HTMLInputElement)?.value.trim() || 'Abonament Standard';
    const licenseKey = (document.getElementById('input-license-key-val') as HTMLInputElement)?.value.trim() || generateLicenseKey(tier);

    const now = new Date();
    const expDate = new Date();
    if (durationMonths >= 999) {
      expDate.setFullYear(2099, 11, 31);
    } else {
      expDate.setMonth(expDate.getMonth() + durationMonths);
    }

    const planNames: Record<string, string> = {
      standard: 'Vorbello Standard (50 Part.)',
      pro: 'Vorbello Pro Business (100 Part.)',
      enterprise: 'Vorbello Enterprise Dedicated',
      starter: 'Vorbello Starter Host',
      custom: 'Vorbello Custom Subscription',
    };

    const newLicense: IssuedLicense = {
      id: `lic-${Date.now()}`,
      licenseKey,
      planName: planNames[tier] || 'Vorbello Custom License',
      tier,
      clientName,
      maxHosts,
      maxParticipantsPerRoom: maxParticipants,
      durationMonths,
      issuedAt: now.toISOString().split('T')[0],
      expiresAt: expDate.toISOString().split('T')[0],
      status: 'ACTIVE',
      priceTag,
    };

    state.issuedLicenses.unshift(newLicense);
    localStorage.setItem('vorbello_issued_licenses', JSON.stringify(state.issuedLicenses));

    recordAudit({
      id: `log-${Date.now()}`,
      action: 'license.issued',
      actorName: state.user.name,
      targetType: 'license',
      targetId: newLicense.id,
      ipHash: '8f4b2c1a...e7',
      createdAt: new Date().toISOString(),
      metadata: JSON.stringify({ licenseKey, clientName, maxHosts, priceTag }),
    });

    state.activeModal = 'issued-licenses';
    showToast(`✅ Licență nouă emisă: ${licenseKey}`);
    renderApp();
  });

  // Copy License Key Button
  document.querySelectorAll('.btn-copy-lic-key').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const key = (e.currentTarget as HTMLElement).getAttribute('data-key') || '';
      if (key && navigator.clipboard) {
        navigator.clipboard.writeText(key).then(() => {
          showToast(`📋 Cheia de licență ${key} a fost copiată în clipboard!`);
        });
      }
    });
  });

  // Activate License on Current Organization
  document.querySelectorAll('.btn-activate-org-lic').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const licId = (e.currentTarget as HTMLElement).getAttribute('data-id') || '';
      const lic = state.issuedLicenses.find(l => l.id === licId);
      if (!lic) return;

      state.organizationLicense.licenseKey = lic.licenseKey;
      state.organizationLicense.planName = lic.planName;
      state.organizationLicense.maxHosts = lic.maxHosts;
      state.organizationLicense.maxParticipantsPerRoom = lic.maxParticipantsPerRoom;
      state.organizationLicense.expiresAt = lic.expiresAt;
      state.organizationLicense.status = lic.status;
      localStorage.setItem('vorbello_org_license', JSON.stringify(state.organizationLicense));

      state.activeModal = null;
      showToast(`👑 Licența "${lic.planName}" (${lic.licenseKey}) a fost activată pentru organizație!`);
      renderApp();
    });
  });

  // Toggle Pause / Resume (Suspend / Activate) License
  document.querySelectorAll('.btn-toggle-suspend-lic').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const licId = (e.currentTarget as HTMLElement).getAttribute('data-id') || '';
      const lic = state.issuedLicenses.find(l => l.id === licId);
      if (!lic) return;

      const newStatus = lic.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
      lic.status = newStatus;
      localStorage.setItem('vorbello_issued_licenses', JSON.stringify(state.issuedLicenses));

      if (state.organizationLicense.licenseKey === lic.licenseKey) {
        state.organizationLicense.status = newStatus;
        localStorage.setItem('vorbello_org_license', JSON.stringify(state.organizationLicense));
      }

      if (newStatus === 'SUSPENDED') {
        showToast(`⏸️ Licența "${lic.clientName}" a fost pusă pe PAUZĂ (Suspendată)!`);
      } else {
        showToast(`✅ Licența "${lic.clientName}" a fost REACTIVATĂ!`);
      }
      renderApp();
    });
  });

  // Quick Resume from Suspended Warning Banner
  document.querySelector('.btn-quick-resume-lic')?.addEventListener('click', () => {
    state.organizationLicense.status = 'ACTIVE';
    localStorage.setItem('vorbello_org_license', JSON.stringify(state.organizationLicense));
    const matchingLic = state.issuedLicenses.find(l => l.licenseKey === state.organizationLicense.licenseKey);
    if (matchingLic) {
      matchingLic.status = 'ACTIVE';
      localStorage.setItem('vorbello_issued_licenses', JSON.stringify(state.issuedLicenses));
    }
    showToast('✅ Abonamentul a fost reactivat cu succes!');
    renderApp();
  });

  // Toggle Client View / Super-Admin View
  document.getElementById('btn-toggle-client-admin-view')?.addEventListener('click', () => {
    state.clientViewActive = !state.clientViewActive;
    localStorage.setItem('vorbello_client_view_active', String(state.clientViewActive));
    if (state.clientViewActive) {
      showToast('🏢 Ați trecut în Modul CLIENT (Vedere curată fără setări tehnice de server)');
    } else {
      showToast('🛡️ Ați revenit în Modul SUPER-ADMIN (Acces complet VPS, LiveKit și Setări)');
    }
    renderApp();
  });

  // Tier selection auto-fill inside issue-license
  document.getElementById('select-license-tier')?.addEventListener('change', (e) => {
    const tier = (e.target as HTMLSelectElement).value;
    const hostsInput = document.getElementById('input-license-hosts') as HTMLInputElement | null;
    const capacitySelect = document.getElementById('select-license-capacity') as HTMLSelectElement | null;
    const priceInput = document.getElementById('input-license-price') as HTMLInputElement | null;
    const keyInput = document.getElementById('input-license-key-val') as HTMLInputElement | null;

    if (keyInput) keyInput.value = generateLicenseKey(tier);

    if (tier === 'standard') {
      if (hostsInput) hostsInput.value = '1';
      if (capacitySelect) capacitySelect.value = '50';
      if (priceInput) priceInput.value = '5,99€ / lună (Plan Standard)';
    } else if (tier === 'pro') {
      if (hostsInput) hostsInput.value = '5';
      if (capacitySelect) capacitySelect.value = '100';
      if (priceInput) priceInput.value = '9,99€ / lună (Consigliato Business)';
    } else if (tier === 'enterprise') {
      if (hostsInput) hostsInput.value = '10';
      if (capacitySelect) capacitySelect.value = '250';
      if (priceInput) priceInput.value = 'Su Richiesta (Enterprise Dedicat)';
    }
  });

  // Revoke / Delete License Button
  document.querySelectorAll('.btn-revoke-lic').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const licId = (e.currentTarget as HTMLElement).getAttribute('data-id') || '';
      state.issuedLicenses = state.issuedLicenses.filter(l => l.id !== licId);
      localStorage.setItem('vorbello_issued_licenses', JSON.stringify(state.issuedLicenses));
      showToast('🗑️ Licența a fost ștearsă din registrul de licențe.');
      renderApp();
    });
  });

  // Form: Manage License Submit
  document.getElementById('form-manage-license')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const policy = (document.getElementById('select-creation-policy') as HTMLSelectElement)?.value as any;
    const maxHosts = parseInt((document.getElementById('input-max-hosts') as HTMLInputElement)?.value, 10) || 10;

    state.organizationLicense.creationPolicy = policy;
    state.organizationLicense.maxHosts = maxHosts;

    state.activeModal = null;
    showToast('Politicile de inițiere conferințe și cotele de licență au fost actualizate!');
    renderApp();
  });

  // Toggle Host License Button
  document.querySelectorAll('.btn-toggle-host-license').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const userId = (e.currentTarget as HTMLElement).getAttribute('data-user-id');
      const targetMembers = state.appMode === 'production' ? state.prodMembers : state.members;
      const member = targetMembers.find(m => m.id === userId);
      if (!member) return;

      const currentHosts = targetMembers.filter(m => m.hasHostLicense).length;
      if (!member.hasHostLicense && currentHosts >= state.organizationLicense.maxHosts) {
        showToast(`⚠️ Limita de licențe Gazdă (${state.organizationLicense.maxHosts}) a fost atinsă! Măriți numărul din Gestiune Licențe.`);
        return;
      }

      member.hasHostLicense = !member.hasHostLicense;
      requireConfirmedSync(await saveMembersToStorage());
      requireConfirmedSync(await syncAllDataWithServer(true, true));

      recordAudit({
        id: `log-${Date.now()}`,
        action: 'member.license_updated',
        actorName: state.user.name,
        targetType: 'membership',
        targetId: userId!,
        ipHash: '8f4b2c1a...e7',
        createdAt: new Date().toISOString(),
        metadata: JSON.stringify({ targetUser: member.name, hasHostLicense: member.hasHostLicense }),
      });

      showToast(member.hasHostLicense 
        ? `👑 Licență de Gazdă (Host) alocată pentru ${member.name}!` 
        : `Licența de Gazdă a fost retrasă pentru ${member.name}.`);
      renderApp();
    });
  });

  // Shortcut from room card to attendance
  document.querySelectorAll('.btn-view-room-attendance').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const slug = (e.currentTarget as HTMLElement).getAttribute('data-room-slug');
      state.selectedRoomAttendanceFilter = slug || '';
      state.currentTab = 'attendance';
      renderApp();
    });
  });

  // Recordings tab & player handlers
  document.getElementById('btn-close-player-modal')?.addEventListener('click', () => {
    state.activeModal = null;
    state.selectedRecordingForPlayer = null;
    renderApp();
  });

  document.querySelectorAll('.btn-modal-download-rec').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const recId = (e.currentTarget as HTMLElement).getAttribute('data-rec-id');
      const rec = state.recordings.find(r => r.id === recId) || state.selectedRecordingForPlayer;
      if (rec && rec.downloadUrl) {
        const a = document.createElement('a');
        a.href = rec.downloadUrl;
        a.download = rec.fileName || `${rec.roomSlug || 'conferinta'}-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast(`Se descarcă: ${a.download}`);
      } else {
        showToast('Linkul de descărcare nu este disponibil.');
      }
    });
  });

  // Open native player button inside player modal
  document.getElementById('btn-player-open-native')?.addEventListener('click', (e) => {
    const filePath = (e.currentTarget as HTMLElement).getAttribute('data-file-path');
    const desktop = (window as any).vorbelloDesktop;
    if (desktop && typeof desktop.openRecordingFile === 'function' && filePath) {
      desktop.openRecordingFile(filePath);
      showToast('🚀 Înregistrarea a fost deschisă în playerul nativ Windows.');
    }
  });

  // Open folder button inside player modal
  document.getElementById('btn-player-open-folder')?.addEventListener('click', (e) => {
    const filePath = (e.currentTarget as HTMLElement).getAttribute('data-file-path');
    const desktop = (window as any).vorbelloDesktop;
    if (desktop && typeof desktop.showInFolder === 'function') {
      desktop.showInFolder(filePath || null);
    }
  });

  // Global Timezone Selector in Meetings Tab
  document.getElementById('select-global-timezone')?.addEventListener('change', (e) => {
    const val = (e.target as HTMLSelectElement).value;
    state.selectedTimezone = val;
    localStorage.setItem('vorbello_timezone', val);
    showToast(`🌐 Fusul orar a fost setat pe: ${val}`);
    renderApp();
  });

  // Permanent Vorbello Video Scrubber & Playback Controls in Player Modal (NEVER DISAPPEARS)
  const mediaEl = document.getElementById('rec-player-media') as HTMLMediaElement | null;
  const playBtn = document.getElementById('btn-player-play-pause');
  const scrubSlider = document.getElementById('player-scrub-slider') as HTMLInputElement | null;
  const timeDisplay = document.getElementById('player-time-display');
  const volumeBtn = document.getElementById('btn-player-volume');
  const fsBtn = document.getElementById('btn-player-fullscreen');
  const skipBackBtn = document.getElementById('btn-player-skip-back');
  const skipFwdBtn = document.getElementById('btn-player-skip-fwd');

  if (mediaEl) {
    mediaEl.volume = 1.0;
    mediaEl.muted = false;

    const formatTimeSecs = (secs: number) => {
      if (isNaN(secs) || secs < 0) return '00:00';
      const m = Math.floor(secs / 60);
      const s = Math.floor(secs % 60);
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const updateSliderAndTime = () => {
      const cur = mediaEl.currentTime || 0;
      const dur = mediaEl.duration || 0;
      if (scrubSlider && dur > 0) {
        scrubSlider.max = String(dur);
        if (document.activeElement !== scrubSlider) {
          scrubSlider.value = String(cur);
        }
      }
      if (timeDisplay) {
        timeDisplay.textContent = `${formatTimeSecs(cur)} / ${formatTimeSecs(dur)}`;
      }
    };

    mediaEl.addEventListener('timeupdate', updateSliderAndTime);
    mediaEl.addEventListener('loadedmetadata', updateSliderAndTime);
    mediaEl.addEventListener('durationchange', updateSliderAndTime);

    mediaEl.addEventListener('play', () => {
      if (playBtn) playBtn.textContent = '⏸️';
    });
    mediaEl.addEventListener('pause', () => {
      if (playBtn) playBtn.textContent = '▶️';
    });

    playBtn?.addEventListener('click', () => {
      if (mediaEl.paused) {
        mediaEl.play().catch(() => {});
      } else {
        mediaEl.pause();
      }
    });

    mediaEl.addEventListener('click', () => {
      if (mediaEl.paused) {
        mediaEl.play().catch(() => {});
      } else {
        mediaEl.pause();
      }
    });

    skipBackBtn?.addEventListener('click', () => {
      mediaEl.currentTime = Math.max(0, (mediaEl.currentTime || 0) - 10);
    });

    skipFwdBtn?.addEventListener('click', () => {
      const maxTime = mediaEl.duration || 999999;
      mediaEl.currentTime = Math.min(maxTime, (mediaEl.currentTime || 0) + 10);
    });

    scrubSlider?.addEventListener('input', () => {
      const targetTime = Number(scrubSlider.value);
      if (!isNaN(targetTime)) {
        mediaEl.currentTime = targetTime;
      }
    });

    volumeBtn?.addEventListener('click', () => {
      mediaEl.muted = !mediaEl.muted;
      volumeBtn.textContent = mediaEl.muted ? '🔇' : '🔊';
    });

    fsBtn?.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        mediaEl.requestFullscreen?.().catch(() => {});
      } else {
        document.exitFullscreen?.().catch(() => {});
      }
    });
  }

  // Open recordings folder button in page header
  document.getElementById('btn-open-recordings-folder')?.addEventListener('click', () => {
    const desktop = (window as any).vorbelloDesktop;
    if (desktop && typeof desktop.showInFolder === 'function') {
      desktop.showInFolder(null);
      showToast('📂 S-a deschis folderul Vorbello Recordings în File Explorer.');
    }
  });

  // Show folder from recording card
  document.querySelectorAll('.btn-show-folder-recording').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const recId = (e.currentTarget as HTMLElement).getAttribute('data-rec-id');
      const rec = state.recordings.find(r => r.id === recId);
      const desktop = (window as any).vorbelloDesktop;
      if (desktop && typeof desktop.showInFolder === 'function') {
        desktop.showInFolder(rec?.filePath || null);
        showToast('📂 Se deschide folderul Vorbello Recordings în File Explorer.');
      }
    });
  });

  document.querySelectorAll('.btn-play-recording').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const recId = (e.currentTarget as HTMLElement).getAttribute('data-rec-id');
      const rec = state.recordings.find(r => r.id === recId);
      if (!rec) return;

      const desktop = (window as any).vorbelloDesktop;
      const isDisk = rec.storageType === 'disk' || Boolean(rec.filePath);

      if (isDisk && desktop && typeof desktop.checkDiskFileExists === 'function') {
        const exists = await desktop.checkDiskFileExists(rec.filePath || rec.fileName);
        if (!exists) {
          state.recordings = state.recordings.filter(r => r.id !== rec.id);
          localStorage.setItem('vorbello_recordings', JSON.stringify(state.recordings));
          showToast(`⚠️ Fișierul a fost șters din PC și a fost eliminat automat din catalog.`);
          renderApp();
          return;
        }
      }

      state.selectedRecordingForPlayer = rec;
      state.activeModal = 'recording-player';
      renderApp();

      if (isDisk && desktop && typeof desktop.readRecordingFileAsBase64 === 'function' && !rec.downloadUrl) {
        const spinner = document.getElementById('player-loading-spinner');
        if (spinner) spinner.style.display = 'block';
        try {
          const base64Data = await desktop.readRecordingFileAsBase64(rec.filePath || rec.fileName);
          const mediaEl = document.getElementById('rec-player-media') as HTMLMediaElement | null;
          if (mediaEl && base64Data) {
            mediaEl.src = base64Data;
            mediaEl.volume = 1.0;
            mediaEl.muted = false;
            mediaEl.play().catch(() => {});
          }
        } catch (err) {
          console.warn('[Player] Eroare citire fișier local:', err);
        } finally {
          if (spinner) spinner.style.display = 'none';
        }
      }
    });
  });

  document.querySelectorAll('.btn-download-recording').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const recId = (e.currentTarget as HTMLElement).getAttribute('data-rec-id');
      const rec = state.recordings.find(r => r.id === recId);
      if (!rec) return;

      if (rec.storageType === 'disk' || rec.filePath) {
        const desktop = (window as any).vorbelloDesktop;
        if (desktop && typeof desktop.showInFolder === 'function') {
          desktop.showInFolder(rec.filePath || null);
          showToast(`📂 Fișierul se află deja pe disc în folderul Vorbello Recordings.`);
        }
      } else if (rec.downloadUrl) {
        const a = document.createElement('a');
        a.href = rec.downloadUrl;
        a.download = rec.fileName || `${rec.roomSlug || 'conferinta'}-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast(`Se descarcă: ${a.download}`);
      } else {
        showToast('Adresa de descărcare nu este disponibilă.');
      }
    });
  });

  document.querySelectorAll('.btn-delete-recording').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const recId = (e.currentTarget as HTMLElement).getAttribute('data-rec-id');
      const rec = state.recordings.find(r => r.id === recId);
      if (!rec) return;

      const isDisk = rec.storageType === 'disk' || Boolean(rec.filePath);
      const promptText = isDisk
        ? `Sigur doriți să ștergeți definitiv înregistrarea pentru "<strong>${rec.meetingTitle || rec.roomName}</strong>"? Fișierul fizic va fi șters și de pe disc.`
        : `Sigur doriți să eliminați din catalog înregistrarea pentru "<strong>${rec.meetingTitle || rec.roomName}</strong>"?`;

      openConfirmDialog({
        title: 'Ștergeți Înregistrarea?',
        description: promptText,
        icon: '🗑️',
        confirmText: 'Da, Șterge Definitiv',
        danger: true,
        onConfirm: async () => {
          const desktop = (window as any).vorbelloDesktop;
          if (isDisk && desktop && typeof desktop.deleteDiskRecording === 'function') {
            await desktop.deleteDiskRecording(rec.filePath || rec.fileName);
          }
          state.recordings = state.recordings.filter(r => r.id !== recId);
          localStorage.setItem('vorbello_recordings', JSON.stringify(state.recordings));
          showToast('Înregistrarea și fișierul au fost șterse.');
          renderApp();
        }
      });
    });
  });

  document.getElementById('btn-refresh-recordings')?.addEventListener('click', async () => {
    await syncRecordingsWithDisk(true);
  });

  // Attendance tab handlers
  document.getElementById('select-attendance-filter')?.addEventListener('change', (e) => {
    const val = (e.target as HTMLSelectElement).value;
    state.selectedRoomAttendanceFilter = val || '';
    renderApp();
  });

  document.getElementById('btn-refresh-attendance')?.addEventListener('click', async () => {
    const result = await syncAllDataWithServer(true);
    showToast(result.success ? 'Registrul este actualizat de pe server.' : result.message);
  });

  document.getElementById('btn-reset-attendance')?.addEventListener('click', () => {
    openConfirmDialog({
      title: 'Resetați Registrul de Prezență?',
      description: 'Sigur doriți să ștergeți toate datele de prezență înregistrate până acum? Toate ședințele vor fi eliminate definitiv atât local cât și pe server.',
      icon: '🗑️',
      confirmText: 'Da, Resetează',
      danger: true,
      onConfirm: async () => {
        await resetAttendanceRegistry();
        showToast('Registrul de prezență a fost resetat complet (local și pe server).');
        renderApp();
      }
    });
  });

  document.getElementById('btn-print-attendance')?.addEventListener('click', () => {
    const records = state.selectedRoomAttendanceFilter
      ? state.attendanceRecords.filter(r => r.roomSlug === state.selectedRoomAttendanceFilter)
      : state.attendanceRecords;

    if (records.length === 0) {
      showToast('Nu există date de prezență pentru generarea raportului.');
      return;
    }

    const html = generateAttendanceReportHTML(state.attendanceRecords, state.selectedRoomAttendanceFilter);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, 2000);
      }, 350);
    }
  });

  document.getElementById('btn-download-attendance-html')?.addEventListener('click', () => {
    const records = state.selectedRoomAttendanceFilter
      ? state.attendanceRecords.filter(r => r.roomSlug === state.selectedRoomAttendanceFilter)
      : state.attendanceRecords;

    if (records.length === 0) {
      showToast('Nu există date de prezență pentru descărcare.');
      return;
    }

    const html = generateAttendanceReportHTML(state.attendanceRecords, state.selectedRoomAttendanceFilter);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Proces_Verbal_Prezenta_Vorbello_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Procesul-verbal oficial (HTML/PDF) a fost descărcat cu succes!');
  });

  document.getElementById('btn-export-attendance-csv')?.addEventListener('click', () => {
    const records = state.selectedRoomAttendanceFilter
      ? state.attendanceRecords.filter(r => r.roomSlug === state.selectedRoomAttendanceFilter)
      : state.attendanceRecords;

    if (records.length === 0) {
      showToast('Nu există date de prezență pentru export.');
      return;
    }

    const headers = ['ID', 'Nume Participant', 'Email', 'Organizatie / Filiala', 'Rol Statutar', 'Sedinta / Sala', 'Slug', 'Ora Intrarii', 'Ora Iesirii', 'Durata Conectare', 'Statut Cvorum'];
    const rows = records.map(r => [
      `"${r.id}"`,
      `"${r.participantName}"`,
      `"${r.participantEmail || ''}"`,
      `"${r.organization}"`,
      `"${r.role}"`,
      `"${r.meetingTitle}"`,
      `"${r.roomSlug}"`,
      `"${r.joinedAt}"`,
      `"${r.leftAt || 'Activ'}"`,
      `"${r.durationFormatted}"`,
      `"${r.status}"`
    ]);

    const csvContent = records.every(r => r.academySessionId)
      ? createPpAttendanceCsv(records)
      : '\uFEFF' + [headers.join(','), ...rows.map(row => row.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Proces_Verbal_Prezenta_Vorbello_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showToast('Procesul-verbal (CSV) a fost descărcat cu succes!');
  });

  // Attendance per Meeting Sessions & Website Sync Handlers
  const searchInput = document.getElementById('input-attendance-search') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      state.attendanceSearchQuery = (e.target as HTMLInputElement).value;
      renderApp();
      const newInput = document.getElementById('input-attendance-search') as HTMLInputElement | null;
      if (newInput) {
        newInput.focus();
        newInput.setSelectionRange(newInput.value.length, newInput.value.length);
      }
    });
  }

  document.getElementById('select-attendance-status')?.addEventListener('change', (e) => {
    state.attendanceStatusFilter = (e.target as HTMLSelectElement).value as any;
    renderApp();
  });

  document.getElementById('btn-config-website-sync')?.addEventListener('click', () => {
    state.activeModal = 'website-sync-config';
    renderApp();
  });

  document.getElementById('btn-sync-all-attendance')?.addEventListener('click', async () => {
    const btn = document.getElementById('btn-sync-all-attendance') as HTMLButtonElement | null;
    if (btn) btn.innerText = '⏳ Se sincronizează...';
    await syncAllSessionsToWebsite();
    if (btn) btn.innerHTML = '<span>⚡</span> Trimite Tot pe Site';
  });

  document.getElementById('form-website-sync-config')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const apiUrl = (document.getElementById('input-sync-api-url') as HTMLInputElement)?.value.trim() || 'https://vorbello.eu/wp-json/vorbello/v1/attendance';
    const apiKey = (document.getElementById('input-sync-api-key') as HTMLInputElement)?.value.trim() || '';
    const autoSync = (document.getElementById('check-sync-auto') as HTMLInputElement)?.checked ?? true;

    state.websiteSyncConfig = { endpointUrl: apiUrl, apiUrl, apiKey, autoSync, lastSyncTime: state.websiteSyncConfig.lastSyncTime };
    localStorage.setItem('vorbello_sync_endpoint', apiUrl);
    localStorage.setItem('vorbello_sync_api_key', apiKey);
    localStorage.setItem('vorbello_sync_auto', autoSync ? 'true' : 'false');
    state.activeModal = null;
    showToast('💾 Configurația de sincronizare cu site-ul a fost salvată!');
    renderApp();
  });

  document.getElementById('btn-test-website-sync')?.addEventListener('click', async () => {
    const resultEl = document.getElementById('website-sync-test-result');
    const apiUrl = (document.getElementById('input-sync-api-url') as HTMLInputElement)?.value.trim() || state.websiteSyncConfig.apiUrl || state.websiteSyncConfig.endpointUrl || 'https://vorbello.eu/wp-json/vorbello/v1/attendance';
    const apiKey = (document.getElementById('input-sync-api-key') as HTMLInputElement)?.value.trim() || state.websiteSyncConfig.apiKey;
    const testBtn = document.getElementById('btn-test-website-sync') as HTMLButtonElement | null;

    if (testBtn) testBtn.innerText = '⏳ Verificare conexiune...';
    if (resultEl) {
      resultEl.style.display = 'block';
      resultEl.style.background = 'rgba(56, 189, 248, 0.1)';
      resultEl.style.color = '#38bdf8';
      resultEl.innerText = 'Se trimite cerere de probă către endpoint-ul WordPress...';
    }

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (apiKey) headers['X-Vorbello-Api-Key'] = apiKey;

      const data = await fetchConfirmedJson(apiUrl, {action: 'ping', source: 'vorbello-test'}, headers);
      if (data.capabilities?.attendance !== true) throw new Error('Actualizează Vorbello Manager: serverul nu confirmă verificarea fără salvarea unui raport.');
      if (resultEl) {
        resultEl.style.background = 'rgba(16, 185, 129, 0.15)'; resultEl.style.color = '#34d399';
        resultEl.innerText = 'Conexiune verificată cu Vorbello Manager ' + data.version + '. Nu s-au creat înregistrări de probă.';
      }
    } catch (err: any) {
      if (resultEl) {
        resultEl.style.background = 'rgba(245, 158, 11, 0.15)';
        resultEl.style.color = '#fbbf24';
        resultEl.innerHTML = `⚠️ Nu s-a putut contacta serverul: ${err.message || err}. Asigurați-vă că serverul permite CORS pentru cereri REST.`;
      }
    } finally {
      if (testBtn) testBtn.innerHTML = '<span>🔌</span> Testează Conexiunea';
    }
  });

  document.querySelectorAll('.btn-toggle-session').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const sessionId = (btn as HTMLElement).dataset.sessionId;
      if (!sessionId) return;
      const idx = state.expandedAttendanceSessions.indexOf(sessionId);
      if (idx >= 0) {
        state.expandedAttendanceSessions.splice(idx, 1);
      } else {
        state.expandedAttendanceSessions.push(sessionId);
      }
      renderApp();
    });
  });

  document.querySelectorAll('.btn-print-session').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const sessionId = (btn as HTMLElement).dataset.sessionId;
      const allSessions = groupAttendanceSessions(state.attendanceRecords);
      const session = allSessions.find(s => s.sessionId === sessionId);
      if (!session) return;

      const html = generateAttendanceReportHTML(session.records, undefined, session);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(html);
        doc.close();
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          setTimeout(() => {
            if (iframe.parentNode) document.body.removeChild(iframe);
          }, 2000);
        }, 350);
      }
    });
  });

  document.querySelectorAll('.btn-csv-session').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const sessionId = (btn as HTMLElement).dataset.sessionId;
      const allSessions = groupAttendanceSessions(state.attendanceRecords);
      const session = allSessions.find(s => s.sessionId === sessionId);
      if (!session || session.records.length === 0) return;

      const headers = ['#', 'Nume Participant', 'Email', 'Organizatie', 'Rol', 'Intrare', 'Iesire', 'Durata', 'Statut'];
      const rows = session.records.map((r, i) => [
        i + 1,
        `"${r.participantName}"`,
        `"${r.participantEmail || ''}"`,
        `"${r.organization}"`,
        `"${r.role}"`,
        `"${r.joinedAt}"`,
        `"${r.leftAt || 'Activ'}"`,
        `"${r.durationFormatted}"`,
        `"${r.status}"`
      ]);

      const csvContent = session.records.every(r => r.academySessionId)
        ? createPpAttendanceCsv(session.records)
        : '\uFEFF' + [
        `"Sedinta: ${session.meetingTitle}"`,
        `"Data: ${session.displayDate}"`,
        `"Interval: ${session.startTime} - ${session.endTime}"`,
        `"Total Participanti: ${session.uniqueParticipants}"`,
        '',
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\r\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Prezenta_${session.roomSlug}_${session.dateKey}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(`Export CSV generat pentru ${session.meetingTitle}!`);
    });
  });

  document.querySelectorAll('.btn-sync-session').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const sessionId = (btn as HTMLElement).dataset.sessionId;
      const allSessions = groupAttendanceSessions(state.attendanceRecords);
      const session = allSessions.find(s => s.sessionId === sessionId);
      if (!session) return;

      const btnEl = btn as HTMLButtonElement;
      const res = await syncSessionToWebsite(session);
      btnEl.innerText = '🌐 Sincronizează Site';
      showToast(res.message);
      if (res.success) {
        renderApp();
      }
    });
  });

  document.querySelectorAll('.btn-delete-attendance-session').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const el = btn as HTMLElement;
      const sessionId = el.dataset.sessionId || '';
      const roomSlug = el.dataset.roomSlug || '';
      const dateStr = el.dataset.date || '';
      const title = el.dataset.title || 'această ședință';

      openConfirmDialog({
        title: 'Ștergeți Ședința din Registru?',
        description: `Sigur doriți să ștergeți definitiv datele de prezență pentru ședința «<strong>${escapeHtml(title)}</strong>»?`,
        confirmText: 'Da, Șterge Ședința',
        icon: '🗑️',
        danger: true,
        onConfirm: async () => {
          await deleteAttendanceSessionFromServer(sessionId, roomSlug, dateStr);
          showToast(`Ședința «${title}» a fost ștearsă din registru.`);
          renderApp();
        }
      });
    });
  });

  document.querySelectorAll('.btn-delete-attendee-record').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const el = btn as HTMLElement;
      const recordId = el.dataset.recordId || '';
      const name = el.dataset.name || 'acest participant';

      openConfirmDialog({
        title: 'Eliminați Înregistrarea de Prezență?',
        description: `Sigur doriți să eliminați înregistrarea pentru «<strong>${escapeHtml(name)}</strong>»?`,
        confirmText: 'Da, Elimină',
        icon: '🗑️',
        danger: true,
        onConfirm: async () => {
          await deleteAttendanceRecordFromServer(recordId);
          showToast(`Înregistrarea pentru ${name} a fost eliminată.`);
          renderApp();
        }
      });
    });
  });
}

// Global Escape Key Listener for Modals
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && state.activeModal) {
    state.activeModal = null;
    renderApp();
  }
});

// Check for active conference session in sessionStorage (preserves room on Refresh/F5)
let activeConfSession: {
  roomSlug: string;
  roomDisplayName?: string;
  displayName?: string;
  initialCam?: boolean;
  initialMic?: boolean;
} | null = null;

try {
  const raw = sessionStorage.getItem('vorbello_active_conference');
  if (raw) {
    activeConfSession = JSON.parse(raw);
  }
} catch (e) {}

// Check for deep link parameters (?room=slug, ?meet=slug, /meet/:slug, #room=slug)
const deepLink = parseDeepLinkParams();

let academyStartup = false;
try {
  academyStartup = captureAcademyInvitation(window.location.href);
  if (!academyStartup && new URLSearchParams(window.location.search).get('academy') === '1') {
    academyStartup = restoreAcademyInvitation();
    if (!academyStartup) throw new Error('Deschide din nou linkul personal din email pentru această sesiune.');
  }
} catch (error) {
  state.userJoinError = error instanceof Error ? error.message : 'Invitație invalidă.';
  academyStartup = true;
}

if (academyStartup) {
  state.appRoleView = 'user';
  state.appMode = 'production';
  renderApp();
  if (hasAcademyInvitation()) {
    loadAcademyInvitation().then(access => {
      state.userJoinNameInput = access.displayName;
      state.userJoinRoomInput = access.roomSlug;
      window.history.replaceState({}, '', window.location.pathname + '?room=' + encodeURIComponent(access.roomSlug) + '&academy=1');
      renderApp();
    }).catch(error => { state.userJoinError = error.message || 'Invitația nu a putut fi verificată.'; renderApp(); });
  }
} else if (activeConfSession && activeConfSession.roomSlug) {
  // User refreshed while inside a conference -> seamlessly reconnect without showing home page!
  console.info('[Vorbello] Refresh detectat în timpul conferinței. Reconectare automată la:', activeConfSession.roomSlug);
  state.userJoinRoomInput = activeConfSession.roomSlug;
  if (activeConfSession.displayName) {
    state.user.name = activeConfSession.displayName;
  }

  const appEl = document.getElementById('app');
  if (appEl) {
    appEl.innerHTML = `
      <div style="min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #0a0d14; color: #fff; font-family: Inter, system-ui, sans-serif; text-align: center; padding: 1.5rem;">
        <img src="${VORBELLO_LOGO}" alt="Vorbello" style="width: 58px; height: 58px; margin-bottom: 1.25rem; filter: drop-shadow(0 0 16px rgba(56, 189, 248, 0.45));" />
        <div style="font-size: 1.15rem; font-weight: 700; color: #f8fafc; margin-bottom: 0.35rem;">Reconectare automată...</div>
        <div style="font-size: 0.825rem; color: #94a3b8; max-width: 320px;">Revenire în sala „${escapeHtml(activeConfSession.roomDisplayName || activeConfSession.roomSlug)}”</div>
        <div style="margin-top: 1.5rem; width: 34px; height: 34px; border: 3px solid rgba(56, 189, 248, 0.2); border-top-color: #38bdf8; border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
      </div>
    `;
  }

  showToast(`🔄 Reconectare automată la sala "${activeConfSession.roomDisplayName || activeConfSession.roomSlug}"...`);

  setTimeout(() => {
    launchConference(
      activeConfSession!.roomSlug,
      activeConfSession!.roomDisplayName,
      activeConfSession!.displayName || state.user.name,
      activeConfSession!.initialCam !== false,
      activeConfSession!.initialMic !== false
    );
  }, 100);
} else if (deepLink.roomSlug) {
  state.userJoinRoomInput = deepLink.roomSlug;
  const check = resolveAndValidateRoom(deepLink.roomSlug);
  if (!check.valid) {
    state.userJoinError = check.errorReason || `Sala sau link-ul introdus nu există sau nu este valabil. Vă rugăm să verificați codul sălii primit de la organizator.`;
    renderApp();
  } else if (check.requiresPin && check.pinCode) {
    state.pendingPinVerification = {
      roomSlug: check.resolvedSlug!,
      roomName: check.resolvedName || check.resolvedSlug!,
      userName: state.user.name || '',
      correctPin: check.pinCode,
      camEnabled: state.userJoinCamEnabled,
      micEnabled: state.userJoinMicEnabled,
    };
    state.pinError = null;
    state.activeModal = 'pin-verify';
    renderApp();
  } else if (deepLink.directJoin && state.user.name) {
    setTimeout(() => {
      launchConference(check.resolvedSlug!, check.resolvedName, state.user.name);
    }, 350);
  } else {
    renderApp();
  }
} else {
  // Initial Render (Lounge Portal)
  renderApp();
  syncAllDataWithServer(true);
  verifyLicenseWithVorbelloEu();
  setTimeout(() => {
    syncRecordingsWithDisk(false);
  }, 400);

}

// Pulls never re-upload stale local snapshots. Pending mutations retry on focus/network recovery.
setInterval(() => { if (state.appMode === 'production') void syncAllDataWithServer(false); }, 3500);
window.addEventListener('focus', () => { void syncAllDataWithServer(true); });
window.addEventListener('online', () => { void syncAllDataWithServer(true); });
if (state.appMode === 'production') void syncAllDataWithServer(false);

// Register PWA Service Worker (active on production domains / localhost)
function initPwaServiceWorker(): void {
  if ('serviceWorker' in navigator && (window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((reg) => {
      console.info('[Vorbello PWA] Service Worker înregistrat cu succes. Scope:', reg.scope);
      reg.update().catch(() => {});
    }).catch((err) => {
      console.warn('[Vorbello PWA] Înregistrare Service Worker eșuată:', err);
    });
  }
}

if (document.readyState === 'complete') {
  initPwaServiceWorker();
} else {
  window.addEventListener('load', initPwaServiceWorker);
}

